import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

const LANGUAGE_STORAGE_KEY = 'termreportLng';
const LANGUAGE_CACHE_TTL_MS = 60 * 60 * 1000;

type CachedLanguage = {
  language: string;
  savedAt: number;
};

const normalizeDetectedLanguage = (language: string): string =>
  language.trim().toLowerCase().replace(/_/g, '-').split('-')[0];

const isCachedLanguage = (value: unknown): value is CachedLanguage => {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return typeof record.language === 'string' && typeof record.savedAt === 'number';
};

const readCachedLanguage = (): CachedLanguage | undefined => {
  if (typeof window === 'undefined') return undefined;

  try {
    const raw = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (!raw) return undefined;

    const parsed: unknown = JSON.parse(raw);
    if (!isCachedLanguage(parsed)) {
      window.localStorage.removeItem(LANGUAGE_STORAGE_KEY);
      return undefined;
    }

    return parsed;
  } catch {
    try {
      window.localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    } catch {
      // private mode
    }
    return undefined;
  }
};

const queryLanguageDetector = {
  name: 'termreportQuerystring',
  lookup: (): string | undefined => {
    if (typeof window === 'undefined') return undefined;

    try {
      const params = new URLSearchParams(window.location.search);
      for (const key of ['lng', 'lang']) {
        const language = params.get(key)?.trim();
        if (language) return language;
      }
    } catch {
      return undefined;
    }

    return undefined;
  },
};

const cachedLanguageDetector = {
  name: 'termreportCachedLanguage',
  lookup: (): string | undefined => {
    const cached = readCachedLanguage();
    if (!cached) return undefined;
    if (Date.now() - cached.savedAt >= LANGUAGE_CACHE_TTL_MS) {
      try {
        window.localStorage.removeItem(LANGUAGE_STORAGE_KEY);
      } catch {
        // private mode
      }
      return undefined;
    }
    return cached.language;
  },
  cacheUserLanguage: (language: string): void => {
    if (typeof window === 'undefined') return;

    const cached = readCachedLanguage();
    if (
      cached
      && cached.language === language
      && Date.now() - cached.savedAt < LANGUAGE_CACHE_TTL_MS
    ) {
      return;
    }

    try {
      const payload: CachedLanguage = { language, savedAt: Date.now() };
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // private mode
    }
  },
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(queryLanguageDetector);
languageDetector.addDetector(cachedLanguageDetector);

// 번역 리소스: 필요한 만큼 확장하세요
const resources = {
  ko: {
    translation: {
      tabs: {
        today: '오늘',
        week: '주간',
        interval: '리듬',
        discover: '발견',
        aria_label: '상단 탭',
      },
      customC2: {
        brand: '쑥쑥찰칵 가족 구독권',
        aria: { back: '선물 안내로 돌아가기' },
        meta: {
          landingTitle: '쑥쑥찰칵 구독권 선물 — 연간 30% 할인',
          giftTitle: '구독권 선물하기 — 1년 69,000원',
        },
        landing: {
          kicker: '할머니 할아버지를 위한 선물',
          title: '손주 영상,\n끊김 없이\n길게 보세요',
          lead: '이 선물 하나면 온 가족이 광고 없이,\n최대 10분 영상도 편하게 볼 수 있어요.',
          imageAlt: '선물 상자에서 가족 사진들이 나오는 그림',
          imageCaption: '가족 8명까지 함께',
          imageCaptionCount: '우리 가족 {{count}}명',
          benefitsLabel: '선물에 담긴 특별한 혜택들',
          benefitsBubble: '어떤 점이 좋은지\n하나씩 알려드릴게요.',
          benefitFamilyTitle: '밀린 사진 한 번에 우르르',
          benefitFamilyBody: '쌓아둔 사진도 한 번에 올려서 온 가족이 같이 봐요.',
          benefitVideoTitle: '손주 영상 끊김 없이 길게',
          benefitVideoBody: '최대 10분 영상도 끊기지 않고 볼 수 있어요.',
          benefitAdTitle: '광고 없이 편안하게',
          benefitAdBody: '광고가 나오지 않아서 온 가족이 편하게 봐요.',
          priceLabel: '지금 신청하시면 드리는 가격',
          price: '69,000원',
          originalPrice: '정상가 99,000원',
          discount: '30% 할인',
          priceNote: '가족 8명 기준 1인당 월 719원꼴이에요.\n바로 결제하거나 전화 상담을 받을 수 있어요.',
          priceNoteCount: '우리 가족 {{count}}명 기준 1인당 월 {{amount}}원꼴이에요.\n바로 결제하거나 전화 상담을 받을 수 있어요.',
          ctaHint: '결제는 카드나 토스로 간편하게 할 수 있어요.',
          cta: '지금 선물 신청하기',
          next: '다음',
          prev: '이전으로',
        },
        gift: {
          back: '뒤로가기',
          kicker: '자녀 가족에게 드리는 선물',
          title: '원하시는 방법을\n골라주세요',
          lead: '바로 결제하셔도 되고,\n전화 상담을 먼저 받으셔도 돼요.',
          methodDirectTitle: '지금 바로 결제할게요',
          methodDirectHint: '카드나 토스로 이 자리에서 바로 결제할 수 있어요.',
          methodConsultTitle: '전화 상담을 받고 싶어요',
          methodConsultHint: '평일 오전 10시 ~ 오후 6시 사이에 전화드려요.',
          consultKicker: '전화 상담 신청',
          consultTitle: '연락처를\n남겨주세요',
          consultLead: '성함과 전화번호만 적어주세요.\n저희가 전화드릴게요.',
          nameLabel: '성함',
          namePlaceholder: '예) 김영자',
          phoneLabel: '전화번호',
          phonePlaceholder: '예) 010-1234-5678',
          privacyLabel: '전화 상담을 위한 개인정보 수집에 동의해요',
          privacyHint: '적어주신 정보는 상담 전화에만 사용해요.',
          privacyLink: '개인정보처리방침',
          submitting: '신청 중이에요...',
          submit: '전화 상담 신청하기',
          submitFailed: '신청이 접수되지 않았어요. 잠시 후 다시 눌러주세요.',
        },
        success: {
          kicker: '상담 신청 완료',
          title: '신청이\n잘 접수됐어요',
          lead: '입력하신 전화번호로 상담원이 전화드릴게요.\n평일 오전 10시 ~ 오후 6시 사이에 연락드려요.',
          home: '처음으로 돌아가기',
        },
        payment: {
          kicker: '안전한 토스 결제',
          title: '결제 수단을\n골라주세요',
          productName: '쑥쑥찰칵 가족 구독권 1년',
          orderName: '쑥쑥찰칵 가족 구독권 1년',
          preparing: '결제 화면을 준비하고 있어요...',
          loadFailed: '결제 화면을 열지 못했어요. 잠시 후 다시 시도해주세요.',
          activationHint: '결제가 끝나면 5분~10분 안에 구독이 자동으로 켜져요.',
          pay: '결제하기',
          contactKicker: '결제 전 연락처',
          contactTitle: '연락처를\n남겨주세요',
          contactLead: '성함과 전화번호만 적어주세요.\n결제 확인과 구독 안내에 사용해요.',
          contactSubmit: '다음, 결제하기',
          contactSubmitting: '저장 중이에요...',
          contactFailed: '연락처가 저장되지 않았어요. 잠시 후 다시 눌러주세요.',
          privacyLabel: '결제를 위한 개인정보 수집에 동의해요',
          privacyHint: '적어주신 정보는 결제 확인과 구독 안내에만 사용해요.',
        },
        appRequired: {
          title: '앱에서 열어주세요',
          lead: '쑥쑥찰칵 앱에서 로그인하면\n우리 가족 구독권 선물을 진행할 수 있어요.',
        },
        paymentSuccess: {
          kicker: '결제 완료',
          confirming: '결제를 확인하고 있어요...',
          title: '결제가\n잘 끝났어요',
          activation: '5분에서 10분 안에\n구독이 자동으로 켜져요.\n따로 하실 일은 없어요.',
          amountLabel: '결제 금액',
          amountValue: '{{amount}}원',
          methodLabel: '결제 방법',
          receipt: '영수증 보기',
          errorTitle: '결제 확인이\n필요해요',
          errorLead: '결제 확인 중 문제가 생겼어요.\n이 화면을 다시 열어보거나 고객센터로 문의해주세요.',
          home: '처음으로 돌아가기',
        },
        paymentFail: {
          kicker: '결제 실패',
          title: '결제가\n되지 않았어요',
          lead: '결제가 완료되지 않았어요.\n다시 한번 시도해주세요.',
          retry: '다시 결제하기',
          home: '처음으로 돌아가기',
        },
        billingTest: {
          kicker: '테스트 결제 · 토스 테스트 키',
          title: '올린 아이 사진으로\n매달 포토북',
          lead: '앱에 올려 둔 아이 사진으로 포토북을 자동 제작해\n매달 보내 드려요. 첫 달은 바로 만들어집니다.',
          autoBadge: '자동 제작',
          heroAlt: '올린 아이 사진이 표지에 들어간 포토북',
          price: '29,900원',
          priceUnit: '매달',
          stepPhotos: '올린 아이 사진을 모아요',
          stepMake: '이번 달 포토북을 자동으로 만들어요',
          stepMonthly: '다음 달에도 같은 카드로 자동 결제돼요',
          emailLabel: '이메일',
          emailPlaceholder: '예) name@email.com',
          register: '매달 받아보기 · 29,900원',
          chargeAgain: '다음 달 결제 테스트하기',
          lastCharge: '이번 달 포토북 {{amount}}원이 결제됐어요.',
          confirming: '카드를 등록하고 이번 달 포토북 29,900원을 결제하고 있어요...',
          chargeFailed: '포토북 결제에 실패했어요.',
          doneTitle: '이번 달 포토북이\n시작됐어요',
          doneLead: '올린 사진으로 이번 달 책을 만들고,\n다음 달부터는 같은 카드로 자동 결제됩니다.',
          loadFailed: '결제창을 열지 못했어요. 잠시 후 다시 시도해주세요.',
          home: '상품 화면으로',
          legalCompany: '주식회사 제제미미 대표이사 박미영',
          legalBizNo: '사업자등록번호: 434-81-01803',
          legalMailOrder: '통신판매: 제2025-서울영등포-2692호',
          legalAddress: '주소 : 서울특별시 영등포구 의사당대로 83, 오투타워 서울핀테크랩 4,5,6,8,17,19층(여의도동)',
          legalPhoneLabel: '전화번호:',
        },
        validation: {
          name: '성함을 2글자 이상 적어주세요.',
          phone: '전화번호를 다시 확인해주세요.',
          email: '이메일을 다시 확인해주세요.',
          privacy: '개인정보 수집에 동의해주세요.',
        },
      },
      cheese: {
        title: '치즈',
        aria: {
          back: '이전 화면으로 돌아가기',
          guide: '치즈 이용 안내',
          charlie: '치즈 현황을 안내하는 찰리',
        },
        balance: {
          label: '내 치즈',
          expiration: '받은 날부터 60일',
          history: '내역 보기',
        },
        today: {
          title: '오늘 받을 치즈',
          hint: '놓치기 전에 받아요',
        },
        reward: {
          upload: '사진 업로드',
          record: '기록하기',
          invite: '친구 초대',
          completed: '오늘 완료',
          daily: '매일 적립',
          up_to: '최대 +{{amount}}',
        },
        action: {
          family_piggy_bank: '가족과 치즈 모으기',
          claim: '받기',
          completed: '완료',
          record: '기록',
          invite: '초대',
          upload: '업로드',
          coming_soon: '준비중',
          retry: '다시 시도',
        },
        collect: {
          title: '치즈 모으기',
          daily: '매일 기록',
          invite: '가족 초대',
          mission: '미션',
        },
        shop: {
          eyebrow: 'GOODS SHOP',
          title: '포토북 / 인화 하러가기',
        },
        error: {
          app_required: '앱에서 열면 내 치즈를 확인할 수 있어요.',
          partial: '일부 치즈 정보를 불러오지 못했어요.',
          claim: '치즈를 받지 못했어요. 잠시 후 다시 시도해주세요.',
        },
        notice: {
          title: '유의사항을 꼭 확인해주세요!',
          items: {
            reward_expiration: '치즈패밀리와 업로드 복권 리워드로 받은 치즈의 유효기간은 60일입니다.',
            history: '발급된 치즈는 마이페이지 > 치즈 내역 보기에서 확인하실 수 있습니다.',
            abuse: '부정한 방법으로 친구 초대가 이뤄진 경우 치즈가 회수되며 치즈패밀리 참여가 제한될 수 있습니다.',
            change: '본 리워드는 당사 사정에 따라 사전 고지 없이 변동될 수 있습니다.',
          },
        },
      },
      invite_friend: {
        back: '뒤로',
        title: '친구 초대',
        hero_eyebrow: '함께 모으면 더 커져요',
        hero_title: '친구를 초대하고\n치즈를 함께 모아요',
        reward_aria: '친구 초대 치즈 혜택',
        inviter_label: '초대한 나는',
        inviter_reward: '+100 치즈',
        inviter_limit: '최대 10번',
        invitee_label: '초대받은 친구는',
        invitee_reward: '+200 치즈',
        invitee_limit: '최초 1회',
        my_code_title: '내 추천코드',
        my_code_desc: '코드를 보내면 친구가 바로 참여할 수 있어요.',
        my_code_copied: '추천코드를 복사했어요.',
        copy: '복사',
        share: '친구에게 공유하기',
        share_message: '쑥쑥찰칵에서 함께 사진을 기록해요. 추천코드: {{code}}',
        share_copied: '공유할 문구를 복사했어요.',
        phone_title: '휴대폰 인증이 필요해요',
        phone_desc: '안전한 치즈 지급을 위해 휴대폰 번호를 먼저 확인해 주세요.',
        phone_action: '휴대폰 인증하기',
        received_title: '받은 추천코드가 있나요?',
        received_desc: '친구에게 받은 6자리 코드를 입력해 주세요.',
        code_placeholder: '추천코드 6자리',
        marketing_consent: '친구 초대 혜택과 이벤트 안내를 위한 마케팅 정보 수신에 동의해요.',
        register_action: '추천코드 등록하기',
        registering: '등록 중',
        registered_title: '추천코드 등록 완료',
        registered_desc: '친구와 함께 치즈를 받았어요. 이제 사진을 함께 기록해 보세요.',
        registered_toast: '추천코드가 등록됐어요.',
        market_title: '추천코드를 등록할 수 없어요',
        market_desc: '굿즈샵 구매 이력이 있는 계정은 초대받은 친구 혜택 대상에서 제외돼요.',
        app_required_title: '앱에서 확인할 수 있어요',
        app_required_desc: '쑥쑥찰칵 앱에서 열면 내 추천코드가 보여요.',
        notice_title: '알아두세요',
        notice_expiry: '초대로 받은 치즈의 유효기간은 지급일로부터 90일이에요.',
        notice_limit: '친구를 최대 10명까지 초대하고 치즈를 받을 수 있어요.',
        notice_eligibility: '초대받은 혜택은 굿즈샵 구매 이력이 없는 계정에 최초 1회 제공돼요.',
        retry: '다시 시도',
        error: {
          load: '추천코드 정보를 불러오지 못했어요.',
          invalid_code: '추천코드 6자리를 다시 확인해 주세요.',
          marketing_required: '혜택을 받으려면 마케팅 정보 수신에 동의해 주세요.',
          register: '추천코드를 등록하지 못했어요. 잠시 후 다시 시도해 주세요.',
          copy: '추천코드를 복사하지 못했어요.',
          share: '추천코드를 공유하지 못했어요.',
        },
      },
      verify_account: {
        back: '뒤로',
        title: '휴대폰 인증',
        hero_eyebrow: '안전한 가족 연결',
        hero_title: '휴대폰 번호를\n한 번만 확인해요',
        hero_desc: '확인된 번호로 초대 혜택과 치즈를 안전하게 연결해요.',
        steps_aria: '휴대폰 인증 진행 단계',
        step_phone: '번호 입력',
        step_code: '인증번호',
        step_done: '인증 완료',
        phone_title: '휴대폰 번호를 알려주세요',
        phone_desc: '문자로 6자리 인증번호를 보내드려요.',
        phone_label: '휴대폰 번호',
        phone_placeholder: '숫자만 입력',
        send_code: '인증번호 받기',
        change_phone: '번호 변경',
        code_title: '문자로 받은 번호를 입력해 주세요',
        code_desc: '{{phone}}으로 보낸 6자리 번호를 확인해 주세요.',
        code_label: '인증번호 6자리',
        code_placeholder: '인증번호 입력',
        verify_action: '인증 완료하기',
        resend: '인증번호 다시 받기',
        done_title: '휴대폰 인증을 완료했어요',
        done_desc: '이제 가족 초대 혜택과 치즈를 안전하게 받을 수 있어요.',
        done_action: '친구 초대로 돌아가기',
        app_required_title: '앱에서 인증할 수 있어요',
        app_required_desc: '쑥쑥찰칵 앱에서 열면 계정과 휴대폰 번호를 안전하게 연결할 수 있어요.',
        privacy_note: '입력한 번호는 본인 확인과 초대 혜택 제공을 위해서만 사용해요.',
        retry: '다시 시도',
        error: {
          load: '계정 정보를 불러오지 못했어요.',
          phone: '휴대폰 번호를 다시 확인해 주세요.',
          send: '인증번호를 보내지 못했어요. 잠시 후 다시 시도해 주세요.',
          code: '인증번호 6자리를 다시 확인해 주세요.',
          expired: '인증 시간이 지났어요. 인증번호를 다시 받아 주세요.',
          complete: '휴대폰 인증을 완료하지 못했어요. 잠시 후 다시 시도해 주세요.',
        },
      },
      report: {
        back: '뒤로',
        title: '업로드 제보',
        eyebrow: '영상 업로드가 어려우셨나요?',
        hero_title: '올라가지 않은 영상을\n보내주시면 살펴볼게요',
        hero_desc: '원본 영상만 골라 주시면, 기기와 계정 정보는 알아서 함께 전달돼요. 100MB까지 보낼 수 있어요.',
        pick_video: '영상 선택하기',
        pick_hint: 'mp4, mov 등 · 최대 100MB',
        file_selected: '영상이 선택됐어요',
        comment_label: '코멘트 (선택)',
        comment_placeholder: '어떤 상황에서 업로드가 안 됐는지 간단히 적어 주세요.',
        submit: '보내기',
        submitting: '보내는 중',
        done_title: '잘 받았어요',
        done_desc: '확인해서 더 편하게 올릴 수 있도록 살펴볼게요. 기다려 주셔서 감사해요.',
        done_action: '닫기',
        app_required_title: '앱에서만 제보할 수 있어요',
        app_required_desc: '쑥쑥찰칵 앱에서 열어주시면 영상을 보낼 수 있어요.',
        privacy_note: '영상 정보 확인용으로만 살펴보며, 계정·기기 등 내부 정보는 따로 확인하지 않아요. 업로드된 내용은 일주일 후 폐기돼요.',
        error: {
          size: '영상은 100MB 이하만 보낼 수 있어요.',
          type: '영상 파일만 선택할 수 있어요.',
          submit: '보내지 못했어요. 잠시 후 다시 시도해 주세요.',
        },
      },
      unit: {
        ml: 'ml',
        min: '분',
        hour: '시간',
        times: '회',
      },
      week: {
        this_week: '이번 주',
        no_data: '데이터가 없습니다.',
        download_failed: '다운로드에 실패했습니다.',
        excel_generate_failed: '파일을 만들지 못했어요.',
        excel_share_failed: '파일을 보내지 못했어요.',
        excel_filename_prefix: '육아기록_',
        share_title: '육아 기록',
        share_text: '{{range}} 육아 기록입니다.',
        default_baby_name: '우리아기',
        share_excel_title: '내보내기',
        excel_sheet_title: '내보내기',
        excel_sheet_desc: '최대 6개월까지 한번에 선택이 가능해요.',
        excel_range_label: '기간',
        excel_preset_week: '보고 있는 주',
        excel_preset_day: '보고 있는 날',
        excel_preset_1m: '최근 1개월',
        excel_preset_3m: '최근 3개월',
        excel_preset_6m: '최근 6개월',
        excel_from: '시작일',
        excel_to: '종료일',
        excel_range_too_long: '기간은 최대 6개월까지 선택할 수 있어요.',
        excel_export: '내보내기',
        excel_fetching: '기록을 불러오는 중...',
        excel_open_browser: '브라우저에서 저장',
        excel_open_browser_hint: '공유 창이 없으면 브라우저에서 저장할 수 있어요.',
        excel_share: '공유',
        excel_share_hint: '파일이 준비됐어요. 공유를 눌러 주세요.',
        excel_sheet_records: '기록',
        excel_sheet_daily: '일별',
        excel_col_date: '날짜',
        excel_col_start: '시작',
        excel_col_end: '종료',
        excel_col_category: '종류',
        excel_col_volume: '용량(ml)',
        excel_col_duration: '시간(분)',
        excel_col_memo: '메모',
        excel_col_feeding_count: '수유(회)',
        excel_col_feeding_volume: '수유량(ml)',
        excel_col_sleep_min: '수면(분)',
        excel_col_nappy_count: '기저귀(회)',
        excel_col_other_count: '그 외(회)',
        pattern_analysis_title: '패턴 분석',
        pattern_analysis_desc: '최근 7일 기록을 기준으로 수면·수유·기저귀 패턴을 살펴봐요.',
        pattern_analysis_loading: '패턴을 분석하고 있어요...',
        pattern_analysis_loading_desc: '기록이 많으면 잠시 걸릴 수 있어요.',
        pattern_analysis_failed: '패턴 분석에 실패했습니다.',
        pattern_analysis_invalid_response: '패턴 분석 서버 응답을 읽지 못했습니다. 앱을 최신 상태로 다시 열어주세요. ({{status}}, {{contentType}})',
        pattern_analysis_ai_model: 'AI 패턴 분석',
        pattern_analysis_basic_model: '기본 통계 분석',
        pattern_analysis_llm_fallback: 'AI 평가 응답 형식이 맞지 않아 기본 통계 분석 결과로 보여드려요.',
        pattern_analysis_min_days: '패턴 분석에는 기록이 있는 날짜가 최소 {{count}}일 필요해요. 현재 {{current}}일 기록이 있어요.',
        pattern_analysis_no_items: '분석할 기록이 없습니다.',
        pattern_analysis_retry: '다시 분석',
        pattern_analysis_model: '분석 방식',
        pattern_analysis_quality: '데이터 품질',
        pattern_analysis_overall: '전반평',
        pattern_analysis_category_reviews: '카테고리별 평',
        pattern_analysis_category_patterns: '카테고리별 패턴',
        pattern_analysis_category_insights: '카테고리별 핵심 패턴',
        pattern_analysis_feeding_timeline: '수유 시간대',
        pattern_analysis_feeding_intervals: '수유텀',
        pattern_analysis_daily_routine: '일일 루틴',
        pattern_analysis_daily_flow: '하루 흐름',
        pattern_analysis_count: '횟수',
        pattern_analysis_avg_amount: '평균량',
        pattern_analysis_avg_duration: '평균 지속',
        pattern_analysis_avg_interval: '평균 텀',
        pattern_analysis_points: '확인 포인트',
        pattern_analysis_flexible_time: '유동적',
        pattern_analysis_record_days: '{{current}}/{{total}}일',
        pattern_analysis_days_seen: '{{count}}일 관찰',
        pattern_analysis_occurrences: '{{count}}회',
        pattern_analysis_avg_duration_value: '평균 {{duration}}',
        pattern_analysis_feeding_group: '수유',
        pattern_analysis_food_group: '이유식/간식',
        pattern_analysis_play_group: '놀이',
        pattern_analysis_care_group: '건강/관리',
        pattern_analysis_review: '평가',
        pattern_analysis_pattern: '패턴',
        pattern_analysis_feeding_time: '수유',
        pattern_analysis_interval: '간격',
        pattern_analysis_routine: '루틴',
        pattern_analysis_empty: '표시할 분석 결과가 없습니다.',
        summary: '주간 요약',
        daily_avg_basis: '하루 평균 기준',
        weekly_total: '주간 {{value}}',
        per_day: '하루 {{value}}',
        sleep_night: '밤',
        sleep_nap: '낮',
        summary_empty: '이번 주는 기록이 없어요',
      },
      interval: {
        empty_title: '간격 데이터가 없어요',
        empty_desc: '선택한 카테고리에 표시할 이벤트가 아직 없어요.',
        loading_more: '이전 주 데이터를 불러오는 중...',
        overlap_label: '{{time}} 겹침',
        interval_label: '{{time}} 간격',
        elapsed_label: '{{time}} 경과',
        overlap_marker: '겹침',
        interval_marker: '간격',
        elapsed_marker: '경과',
        now_label: '지금',
        last_record_label: '마지막 기록',
        since_last_record: '마지막 기록 후',
        day_today: '오늘',
        day_yesterday: '어제',
        gap_mode: {
          label: '간격 기준',
          end_to_start: '끝 → 시작',
          start_to_start: '시작 → 시작',
          end_to_end: '끝 → 끝',
          end_to_start_desc: '이전 끝 시간부터 다음 시작까지',
          start_to_start_desc: '시작 시간끼리의 주기',
          end_to_end_desc: '끝 시간끼리의 간격',
        },
      },
      growth_banner: {
        aria_label: '성장 기록 안내',
        default_baby_name: '우리아기',
        empty_title: '{{name}} 키·몸무게가 궁금하다면 👀',
        empty_desc: '첫 기록하고 성장 흐름 그래프 확인하기',
        empty_cta: '{{name}} 첫 기록 남기기',
        stale_title: '기록한 지 {{days}}일이 지났어요',
        stale_desc: '업데이트하고 이번 달 성장 확인하기',
        recent_title: '{{name}} 성장 기록이 업데이트됐어요',
        recent_desc: '이번 달 성장 그래프 확인하기',
        delta_title_weight: '{{name}} {{amount}}kg 컸어요',
        delta_title_height: '{{name}} {{amount}}cm 컸어요',
        delta_title_head: '{{name}} 머리둘레 {{amount}}cm 자랐어요',
        delta_desc_generic: '성장 흐름을 계속 확인해보세요',
        percentile_short: {
          check: '백분위 {{percentile}} · 확인이 필요해요 🙏🏻',
          small: '백분위 {{percentile}} · 작은 편이에요 🌱',
          average: '백분위 {{percentile}} · 평균 범위예요 💕',
          big: '백분위 {{percentile}} · 큰 편이에요 💪🏻',
          very_big: '백분위 {{percentile}} · 많이 큰 편이에요 💪🏻',
        },
      },
      import_file: {
        title: '기록 가져오기',
        badge: '기록 이사',
        headline: '어서 이사오세요',
        subtitle: '다른 수유텀 앱에서 내보낸 파일을 선택하면 기록을 옮길 준비를 시작해요.',
        status_waiting: '대기 중',
        status_parsing: '분석 중',
        status_moving: '이사 중',
        status_importing: '저장 중',
        status_imported: '이사 완료',
        status_ready: '준비 완료',
        status_error: '확인 필요',
        step_select: '파일 선택',
        step_review: '변환 확인',
        step_import: '가져오기',
        breakdown_title: '카테고리별 정리',
        source_app: '다른 앱',
        move_label: '기록 이동 경로',
        from_label: '기존 기록',
        from_value: '다른 앱',
        to_label: '새 보관함',
        to_value: '쑥쑥찰칵 수유텀',
        supported_label: '지원 파일 형식',
        unknown_extension: '파일',
        selected_desc: '{{size}} · {{extension}}',
        drop_title: '이사할 파일 선택',
        drop_active: '여기에 놓아주세요',
        drop_desc: 'TXT, CSV, ZIP 파일을 받을 수 있어요.',
        target_baby_title: '기록을 가져올 아기',
        target_baby_desc: '{{babyName}}에게 기록을 가져와요.',
        target_baby_select_label: '기록을 가져올 아기 선택',
        target_baby_loading: '내 아기 정보를 불러오고 있어요.',
        target_baby_error: '아기 정보를 불러오지 못했어요. 앱에서 다시 열면 자동으로 확인해요.',
        target_baby_empty: '아기 정보를 확인하면 어느 아기에게 가져올지 표시해요.',
        target_baby_no_carer: '수유기록 작성 권한이 있는 아기만 표시해요. 가져올 수 있는 아기가 없어요.',
        target_baby_unknown: '이름 없는 아기',
        target_baby_fallback: 'ID {{babyIdx}}번 아기',
        moving_file: '기록 옮기는 중',
        moving_title: '이사 중이에요',
        moving_desc: '파일 안의 기록을 정리하고 있어요. 잠시만 기다려 주세요.',
        change_file: '다른 파일 선택',
        remove_file: '파일 지우기',
        cta_idle: '파일 선택 후 계속',
        cta_ready: '가져오기',
        cta_importing: '가져오는 중',
        cta_done: '이사 완료',
        unsupported_title: '지원하지 않는 파일 형식이에요',
        unsupported_desc: '아직 자동 변환하지 못하는 파일 형식이에요. TXT, CSV, ZIP 파일을 선택해 주세요.',
        too_large_title: '파일이 너무 커요',
        too_large_desc: '50MB 이하 파일만 선택할 수 있어요.',
        received_title: '이사가 완료되었습니다',
        received_summary: '총 {{count}}개 이동',
        received_desc: '선택한 아기의 수유기록에 저장했어요. 수유텀 탭에서 새로고침하면 방금 가져온 기록을 확인할 수 있어요.',
        received_action: '확인',
        import_progress_title: '{{current}}/{{total}}개 저장 중',
        import_progress_desc: '한 번에 하나씩 저장해요. 성공 {{completed}}개 · 실패 {{failed}}개',
        import_failed_title: '일부 기록을 가져오지 못했어요',
        import_failed_desc: '{{total}}개 중 {{failed}}개 저장에 실패했어요. 이미 저장된 기록이 있을 수 있어 재시도 전 확인이 필요해요.',
        import_missing_auth: '사용자 인증 정보를 확인하지 못해 저장할 수 없어요. 앱에서 다시 열어 주세요.',
        import_missing_baby: '기록을 저장할 아기를 먼저 선택해 주세요.',
        parse_error_title: '파일을 함께 확인해 볼게요',
        parse_failed: '파일을 읽는 중 문제가 생겼어요. 다시 내보낸 파일을 선택하거나 아래에서 문의해 주세요.',
        encoding_error: '파일의 글자를 정확히 읽기 어려워 잠시 멈췄어요. 원래 앱에서 다시 내보내 보시고, 같은 상황이 계속되면 편하게 문의해 주세요.',
        parse_empty: '가져올 수 있는 날짜·시각이 포함된 기록을 찾지 못했어요. 파일을 확인하시거나 아래에서 이동 방법을 문의해 주세요.',
        summary_only_csv_error: '이 파일에는 하루 총 수유량·횟수 등 소중한 기록이 담겨 있어요. 다만 개별 시각이 없어, 현재는 수유 기록으로 바로 옮기기 어려워요. 정확한 수유 간격을 위해 임의의 시간은 넣지 않아요.',
        summary_only_csv_next_step: '원래 앱에서 날짜·시각이 포함된 상세 기록을 내보낼 수 있다면 그 파일을 선택해 주세요. 상세 기록을 제공하지 않는 앱이라면, 아래에서 가능한 이동 방법을 함께 확인해 볼게요.',
        cta_file_unavailable: '파일 확인이 필요해요',
        zip_no_parseable_files: 'ZIP 안에서 가져올 수 있는 TXT 또는 CSV 파일을 찾지 못해 오류가 발생했어요.',
        zip_unsupported_compression: '이 ZIP 압축 방식은 아직 지원하지 않아 오류가 발생했어요. 기본 ZIP으로 다시 압축해 주세요.',
        error_contact_title: '기록 이동을 도와드릴게요',
        error_contact_desc: '기록 이동에 도움이 필요하시면 마이페이지 > 고객센터로 문의해 주세요.',
        unsupported_records_title: '일부 기록은 메모에 남겨놨어요',
        unsupported_records_desc: '자동 변환하지 못한 {{count}}개 항목은 메모로 보존했어요.',
        skipped_records_title: '확실하지 않은 기록은 건너뛰었어요',
        skipped_records_desc: '잘못 저장될 수 있는 {{count}}개 기록은 가져오지 않았어요. 아래에서 원본 내용을 확인할 수 있어요.',
        summary_only_csv_title: '날짜별 합계가 담긴 파일이에요',
        summary_only_csv_notice: '개별 시각이 없는 일별 합계 CSV {{count}}개는 이번 가져오기 대상에서 제외했어요.',
        warning_line: '{{line}}번째 줄',
        warning_show_more: '건너뛴 기록 {{count}}개 더 보기',
        warning_reason_summary_only_csv: '개별 시각이 없는 일별 합계 CSV',
        warning_reason_unsupported_record: '지원하지 않거나 내용을 확실히 해석할 수 없는 기록',
        warning_reason_invalid_datetime: '날짜 또는 시각을 확인할 수 없음',
        warning_reason_incomplete_sleep: '수면 시작·종료 또는 지속시간을 확인할 수 없음',
        warning_reason_inconsistent_sleep: '수면 시각과 기록된 지속시간이 맞지 않음',
        warning_reason_unknown: '안전하게 변환할 수 없는 기록',
        preview_title: '변환 미리보기',
        preview_desc: '가져온 파일을 V2 insert item 형태로 변환한 결과예요.',
        preview_source_label: '원본',
        preview_result_label: '가져올 기록',
        parsed_items: '항목',
        parsed_dates: '날짜',
        memo_fallbacks: '메모 처리',
        missing_baby_idx: 'baby_idx를 찾지 못했어요. 저장 단계에서는 실제 아기 식별자를 넣어야 합니다.',
        meridiem_am: '오전',
        meridiem_pm: '오후',
        left_side: '왼쪽',
        right_side: '오른쪽',
        category_alert: '알림',
        sleep_type_nap: '낮잠',
        sleep_type_night: '밤잠',
        purpose_infant_checkup: '영유아 검진',
        purpose_vaccination: '예방접종',
        purpose_medical_visit_issue: '진료',
        json_payload_title: '저장용 JSON payload 보기',
        privacy_title: '기록은 조심해서 다룰게요',
        privacy_desc: '선택한 파일은 import 확인을 위해서만 사용됩니다.',
      },
      common: {
        loading_alt: '로딩',
        loading: '로딩 중...',
        page_load_failed: '화면을 불러오지 못했어요. 다시 시도해 주세요.',
        reload_page: '다시 불러오기',
        today: '오늘',
        app_title: '수유텀',
        app_only_title: '앱에서 접속해주세요',
        app_only_desc: '해당 서비스는 앱에서만 이용 가능합니다.',
        close: '닫기',
        week_ago: '주 전',
        month_ago: '달 전',
        all: '전체',
        me: '나',
      },
      growth_report: {
        title: '성장 리포트 보기',
        desc: '키/몸무게 등 성장 분석, 백분위 비교',
        open_aria: '성장 리포트 보기',
      },
      growth_page: {
        title: '성장 리포트',
        back: '뒤로가기',
        default_baby_name: '우리 아기',
        age_days: '태어난 지 {{count}}일째',
        age_months: '{{count}}개월째',
        dday_label: 'D+{{count}}',
        dday_before_label: 'D-{{count}}',
        status: '쑥쑥 크는 중',
        measurement_action: '측정 기록',
        measurement_base: '{{date}} 측정 기준',
        metric_tabs_label: '성장 지표',
        metric_empty: '아직 측정 기록이 없어요',
        recorded_base: '{{date}} 기록 기준',
        delta_sentence: {
          weight_up: '지난 기록보다 몸무게가 {{value}} 늘었어요',
          weight_down: '지난 기록보다 몸무게가 {{value}} 줄었어요',
          height_up: '지난 기록보다 키가 {{value}} 자랐어요',
          height_down: '지난 기록보다 키가 {{value}} 작게 측정됐어요',
          head_up: '지난 기록보다 머리둘레가 {{value}} 자랐어요',
          head_down: '지난 기록보다 머리둘레가 {{value}} 작게 측정됐어요',
        },
        delta_since_date: {
          weight_up: '{{date}}보다 {{value}} 늘었어요',
          weight_down: '{{date}}보다 {{value}} 줄었어요',
          height_up: '{{date}}보다 {{value}} 자랐어요',
          height_down: '{{date}}보다 {{value}} 작게 측정됐어요',
          head_up: '{{date}}보다 {{value}} 자랐어요',
          head_down: '{{date}}보다 {{value}} 작게 측정됐어요',
        },
        milestones: {
          entry: '순간 남기기',
          entry_aria: '순간 남기기',
          empty_title: '기억나는 순간이 있나요?',
          empty_description: '떠오르는 순간을 하나 남겨보세요.',
          suggest_row_label: '{{month}}개월 · 요즘 남기기 좋은 순간',
          write_own: '직접 적기',
          past_hint: '지나간 순간도 괜찮아요',
          example_badge: '예시',
          example_desc: '남긴 순간은 이렇게 쌓여요. 첫 순간을 남겨보세요',
          sheet_title: '순간 남기기',
          sheet_edit_title: '순간 수정하기',
          title_label: '어떤 순간이었나요?',
          title_placeholder: '예: 처음 뒤집은 날',
          date_label: '날짜',
          photo_label: '그날의 사진',
          visibility_label: '공개 범위',
          visibility_private: '나만',
          visibility_parents: '부모만',
          visibility_maternal: '엄마 가족',
          visibility_paternal: '아빠 가족',
          visibility_all: '전체 공개',
          visibility_badge: {
            private: '나만',
            parents: '부모만',
            maternal: '엄마 가족',
            paternal: '아빠 가족',
            kin: '양가 가족',
          },
          photo_attach_label: '이 날 사진 붙이기 (선택)',
          photo_change: '사진 바꾸기',
          photo_none_title: '이 날은 사진이 없어요',
          photo_none_desc: '글로 남겨도 충분해요',
          when_label: '언제였나요?',
          who_label: '누가 볼 수 있나요?',
          save_cta: '이 순간 남기기',
          required: '어떤 순간이었는지 적어주세요',
          saved: '소중한 순간을 남겼어요',
          save_failed: '저장하지 못했어요. 잠시 후 다시 시도해 주세요.',
          deleted: '순간 기록을 삭제했어요',
          delete_failed: '삭제하지 못했어요. 잠시 후 다시 시도해 주세요.',
          delete_confirm: '이 순간 기록을 삭제할까요?',
          edit_aria: '{{title}} 수정',
        },
        milestone_presets: {
          first_outing: '첫 외출',
          first_smile: '첫 미소',
          first_babble: '첫 옹알이',
          first_rollover: '첫 뒤집기',
          first_solid_food: '첫 이유식',
          first_sit: '혼자 앉기',
          first_tooth: '첫 이',
          first_crawl: '기어다니기',
          first_pull_to_stand: '잡고 서기',
          first_word: '첫 단어',
          first_step: '첫 걸음',
          first_run: '첫 달리기',
        },
        update: '기록',
        moments_title: '우리의 순간',
        records_count: '지금까지 {{count}}번의 순간을 남겼어요',
        moments_empty_hint: '자라는 순간을 여기에 모아요',
        timeline_latest: '최근',
        timeline_group_count: '{{count}}개',
        timeline_delta_prev: '지난번보다 {{delta}}',
        view_profile_photo: '프로필 사진 보기',
        view_record_media: '사진·영상 보기',
        media_viewer_aria: '미디어 보기',
        media_swipe_hint: '위아래로 넘겨보기',
        slideshow_revisit_count: '사진·영상 {{count}}개로 다시 보기',
        slideshow_play: '슬라이드쇼로 보기',
        slideshow_aria: '성장 슬라이드쇼',
        slideshow_pause: '일시정지',
        slideshow_resume: '재생',
        slideshow_prev: '이전 장면',
        slideshow_next: '다음 장면',
        slideshow_summary_title: '{{days}}일 동안 이만큼 자랐어요',
        slideshow_summary_title_short: '지금까지 이만큼 자랐어요',
        slideshow_first: '처음',
        slideshow_latest: '최근',
        slideshow_replay: '다시 보기',
        slideshow_export: '성장 필름 만들기',
        slideshow_export_title: '성장 필름으로 간직하기',
        slideshow_export_subtitle: '지금까지의 순간들을 한 편의 필름으로 엮어드려요',
        slideshow_export_auto_note: '많은 순간 중 {{count}}장면을 골라 담아뒀어요. 탭해서 원하는 장면으로 바꿀 수 있어요.',
        slideshow_export_limit_note: '필름에는 최대 {{count}}장면까지 담을 수 있어요',
        slideshow_export_selected: '{{selected}} / {{max}} 장면을 담았어요',
        slideshow_export_start: '{{count}}개의 순간으로 필름 만들기',
        slideshow_export_min: '간직하고 싶은 순간을 2개 이상 골라주세요',
        slideshow_export_rendering: '소중한 순간들을 엮는 중…',
        slideshow_export_cancel: '다음에 만들기',
        slideshow_export_done: '성장 필름을 소중히 담아뒀어요',
        slideshow_export_failed: '필름을 완성하지 못했어요. 잠시 후 다시 시도해 주세요.',
        slideshow_music: '배경음악',
        slideshow_music_title: '배경음악',
        slideshow_music_off: '음악 없음',
        slideshow_music_note: '성장 필름에도 함께 흐르게 담아드려요',
        slideshow_entry: '성장 스토리',
        slideshow_entry_aria: '성장 스토리 슬라이드쇼 보기',
        slideshow_moments_count: '{{count}}개의 순간이 모였어요',
        slideshow_locked_toast: '사진이 담긴 기록이 {{count}}개 모이면 성장 스토리가 열려요',
        slideshow_card_desc: '{{count}}개의 순간이 모였어요 · 탭해서 감상하기',
        slideshow_card_locked_desc: '사진이 담긴 기록이 {{min}}개 모이면 열려요',
        slideshow_teaser_title: '사진 기록 {{count}}개가 더 모이면 성장 필름이 열려요',
        slideshow_teaser_desc: '사진과 함께 기록하면 아이의 성장을 한 편의 필름으로 엮어드려요',
        first_extra_title: '태어난 순간도 함께 남길까요?',
        first_extra_desc: '태어났을 때와 예정일을 더하면 곡선이 더 정확해져요',
        first_extra_due_hint: '예정일을 입력하면 성장 곡선을 더 정확히 볼 수 있어요. (선택)',
        first_extra_due_placeholder: '입력 안 함',
        first_extra_birth_label: '태어났을 때',
        first_extra_birth_is_today: '오늘 기록한 날짜가 태어난 날이에요.',
        step_skip: '건너뛰기',
        step_next: '다음',
        step_back: '이전',
        update_soon: '성장 기록 남기기는 앱에서 준비 중이에요.',
        editor_title: '성장 기록하기',
        editor_edit_title: '성장 기록 수정',
        editor_measured_date: '측정일',
        editor_dday_inline: '{{dday}}일',
        editor_change_date: '변경',
        editor_save: '저장',
        editor_delete: '삭제',
        editor_delete_confirm: '이 날짜의 성장 기록을 삭제할까요?',
        editor_required: '몸무게, 키, 머리둘레 중 하나 이상 입력해 주세요.',
        editor_date_before_birth: '생일 이전 날짜는 선택할 수 없어요.',
        editor_date_future: '미래 날짜는 선택할 수 없어요.',
        editor_range_invalid: '입력값 범위를 다시 확인해 주세요. 몸무게는 kg, 키와 머리둘레는 cm 단위예요.',
        editor_range_weight: '몸무게는 kg 단위로 0보다 크고 {{max}}kg 미만이어야 해요.',
        editor_range_height: '키는 cm 단위로 {{min}}~{{max}}cm 사이여야 해요.',
        editor_range_head: '머리둘레는 cm 단위로 {{min}}~{{max}}cm 사이여야 해요.',
        editor_bmi_invalid: '키와 몸무게 값을 다시 확인해 주세요. 키는 cm 단위로 입력해야 해요.',
        editor_saved: '성장 기록을 저장했어요.',
        editor_saved_with_extra_warning: '성장 기록은 저장했어요. 예정일 또는 태어났을 때 정보는 다시 확인해 주세요.',
        editor_deleted: '성장 기록을 삭제했어요.',
        photo_label: '사진',
        photo_optional: '선택',
        photo_none_option: '사진 없이',
        photo_load_more: '더 보기',
        photo_add: '사진 추가',
        photo_add_desc: '측정 순간을 함께 남겨요',
        photo_attached: '함께 기록할 사진',
        photo_change: '사진 변경',
        photo_remove: '사진 삭제',
        photo_sheet_title: '사진 선택',
        photo_library_title: '앱 사진첩',
        photo_library_pending: '연결 예정',
        photo_representative: '그날 대표사진',
        photo_uploaded_title: '그날 사진',
        photo_from_day: '그날 사진',
        photo_count: '{{count}}장',
        photo_select_aria: '사진 선택',
        photo_tip: '사진·영상도 함께 남길 수 있어요',
        photo_loading: '사진 불러오는 중',
        photo_load_failed: '사진을 불러오지 못했어요. 다시 시도해 주세요.',
        photo_retry: '다시 불러오기',
        photo_none_for_date: '이 날짜에 업로드된 사진이 없어요.',
        load_failed: '성장 기록을 불러오지 못했어요.',
        save_failed: '성장 기록을 저장하지 못했어요.',
        delete_failed: '성장 기록을 삭제하지 못했어요.',
        profile_incomplete: '아기 생일 또는 성별 정보가 없어 성장 기록을 계산할 수 없어요.',
        birthday_required_title: '아기 생일 정보가 필요해요',
        birthday_required_desc: '마이페이지에 아기 생일을 등록하면 성장 기록과 백분위 계산을 볼 수 있어요.',
        birthday_chip: '{{date}}',
        due_date_chip: '예정 {{date}}',
        profile_settings_aria: '성장 기준 정보 설정',
        profile_settings_title: '성장 기준 정보',
        profile_settings_desc: '예정일을 입력하면 성장 분석을 더 정확하게 맞출 수 있어요.',
        precision_hint: '예정일을 입력하면 성장 분석을 더 정확하게 맞출 수 있어요.',
        precision_hint_saved: '예정일 {{date}} 기준 정보를 저장해두었어요.',
        due_date_label: '예정일',
        due_date_empty: '예정일을 아직 입력하지 않았어요.',
        due_date_clear: '비우기',
        profile_settings_saved: '예정일 정보를 저장했어요.',
        profile_settings_save_failed: '예정일 정보를 저장하지 못했어요.',
        empty_records: '아직 저장된 성장 기록이 없어요.',
        summary_empty_title: '몸무게, 키, 머리둘레를 기록해 보세요',
        empty_title: '아직 성장 기록이 없어요',
        empty_desc: '몸무게, 키, 머리둘레 중 알고 있는 값 하나만 입력해도 성장 흐름을 볼 수 있어요.',
        empty_cta: '성장 기록 시작하기',
        empty_collage_title: '이 소중한 순간들, 키와 몸무게도 함께',
        empty_collage_desc: '사진과 함께 성장 기록을 남기면 우리 아기의 성장 흐름을 한눈에 볼 수 있어요.',
        empty_eyebrow: '오늘부터, 성장 이야기',
        empty_hero_title: '{{name}}의 성장 이야기를 시작해요',
        empty_hero_desc: '키·몸무게·머리둘레, 하나만 기록해도 이야기가 시작돼요.',
        edit_record_aria: '{{date}} 성장 기록 수정',
        create_report: '성장보고서 만들기',
        create_report_aria: '성장보고서 만들기',
        create_report_soon: '성장보고서 만들기는 앱에서 준비 중이에요.',
        report_builder: {
          entry_desc: '이번 달 사진과 기록으로 한 장을 만들어요',
          title: '성장보고서 만들기',
          intro_heading: '{{name}}의 요즘을\n한 장으로 남겨볼까요?',
          intro_desc: '사진 한 장과 요즘 기록만 고르면 AI가 손글씨 느낌으로 그려요.',
          intro_sample: '다른 가족이 만든 예시',
          intro_sample_alt: '성장보고서 예시 이미지',
          intro_meta: '4단계 · 1분이면 끝나요',
          start: '시작하기',
          back: '이전 단계',
          next: '다음',
          step_of: '{{step}} / {{total}} · {{label}}',
          step_period: '기간',
          step_photo: '사진',
          step_records: '기록',
          step_finish: '마무리',
          period_question: '어느 때를 담을까요?',
          period_hint: '탭하면 바로 다음으로 넘어가요.',
          preset_this_month: '이번 달',
          preset_last_month: '지난달',
          preset_three_months: '최근 3개월',
          preset_custom: '직접 고르기',
          preset_custom_desc: '시작과 끝 날짜를 정해요',
          start_date: '시작일',
          end_date: '종료일',
          period_error: '오늘까지, 1년 이내의 기간을 선택해 주세요.',
          photo_question: '가운데에 넣을 사진 한 장',
          photo_hint: '이 기간 사진 {{count}}장 · 최근 성장기록 사진을 골라뒀어요.',
          photo_hint_empty: '이 기간에 붙인 사진이 없어요. 휴대폰 사진 한 장으로 만들 수 있어요.',
          upload_tile: '내 사진 올리기',
          local_photo_label: '올린 사진',
          records_question: '이 기록들을 담을게요',
          records_hint: '×로 빼고, “답 N”을 눌러 다른 가족 답으로 바꿔요.',
          records_count: '{{count}} / {{max}} 담았어요',
          answers_count: '답 {{count}}',
          records_layout_hint: '사진 양옆에 손글씨로 적혀요. 뺀 칸은 그냥 비워둬요.',
          option_sheet_title: '{{label}} · 답 고르기',
          option_sheet_remove: '이 기록 빼기',
          add_record: '{{label}} 추가',
          remove_record: '{{label}} 빼기',
          records_empty: '이 기간에는 담을 기록이 아직 없어요. 사진만으로도 만들 수 있어요.',
          finish_question: '제목이랑 한마디',
          finish_hint: '한마디는 비워도 괜찮아요.',
          title_label: '제목 · 사진 위에 손글씨로',
          note_label: '엄마 아빠의 한마디 · 선택',
          note_placeholder: '예: 매일 새로운 모습을 보여줘서 고마워',
          summary: '{{period}} · 사진 1장 · 기록 {{count}}개',
          summary_hint: '고칠 게 있으면 이전 단계로 돌아가요',
          create: '이대로 만들기',
          check_status: '완성 여부 확인하기',
          default_title: '{{name}}의 성장기록',
          ai_notice: '선택한 사진과 기록은 이미지 생성을 위해 AI에 전송돼요.',
          not_ready: '성장보고서 만들기를 준비하고 있어요. 기록 선택은 미리 확인할 수 있어요.',
          retry: '다시 불러오기',
          generating_title: '{{name}}의 이야기를 그리고 있어요',
          generating_desc: '사진과 기록을 한 장에 정성껏 담고 있어요. 잠시만 기다려 주세요.',
          complete_title: '성장보고서 완성',
          complete_desc: '{{name}}의 소중한 한 달을 한 장에 담았어요.',
          complete_desc_period: '{{name}}의 소중한 성장기록을 한 장에 담았어요.',
          review_result: '사진과 글씨를 확인한 뒤 저장해 주세요.',
          result_alt: '{{name}}의 성장보고서',
          save: '사진 저장',
          saving: '저장 중…',
          share: '공유하기',
          sharing: '공유 중…',
          saved: '사진을 저장했어요.',
          delivery_failed: '사진을 전달하지 못했어요. 다시 시도해 주세요.',
          filename: '성장보고서',
          select_photo_date: '{{date}} 사진 선택',
          error_photo_type: 'JPG, PNG, WebP 사진을 선택해 주세요.',
          error_photo_size: '사진이 너무 커서 준비하지 못했어요. 다른 사진을 선택해 주세요.',
          error_timeout: '완성에 시간이 더 필요해요. 잠시 후 다시 시도해 주세요.',
          error_permission: '이 아기의 성장보고서를 만들 권한이 없어요.',
          error_daily_limit: '오늘 만들 수 있는 성장보고서를 모두 만들었어요. 내일 다시 이용해 주세요.',
          error_busy: '이미 만들고 있는 성장보고서가 있어요. 잠시 후 다시 확인해 주세요.',
          error_provider_limit: '지금은 생성 요청이 많아요. 잠시 후 다시 시도해 주세요.',
          error_generic: '성장보고서를 만들지 못했어요. 선택한 내용은 그대로 있으니 다시 시도해 주세요.',
        },
        birth: '출생',
        month_short: '{{count}}개월',
        current_value: '현재 {{value}}',
        growth_since_birth: '태어난 날보다 {{value}} 컸어요',
        growth_since_first_record: '첫 기록보다 {{value}} 컸어요',
        growth_since_previous_record: '이전 기록 대비 {{value}}',
        legend_median: '또래 평균',
        legend_range: '정상 범위',
        compare_title: '또래 비교',
        compare_subtitle: '또래 100명과 비교한 우리 아기의 위치예요',
        corrected_age_caption: '예정일을 반영한 교정 나이 기준이에요 ({{date}})',
        corrected_age_caption_plain: '예정일을 반영한 교정 나이 기준이에요',
        profilebook: {
          eyebrow: '우리 아기 100문 100답',
          intro_title: '함께 적는\n{{name}}의 요즘 모습',
          intro_description: '좋아하는 것부터 작은 습관까지,\n우리의 답으로 적어요.',
          intro_hint: '질문을 골라 답을 남기고, 카드에서 지난 답을 볼 수 있어요.',
          skip: '먼저 둘러보기',
          start: '{{name}}의 요즘 적어보기',
          title: '{{name}}의 요즘 모습',
          heading: '함께 적는\n{{name}}의 요즘 모습',
          question_progress: '{{total}}개 중 {{answered}}개 적었어요',
          subtitle_empty: '좋아하는 것부터 작은 습관까지, 우리의 답으로 적어요.',
          subtitle_filled: '좋아하는 것부터 작은 습관까지, 우리의 답으로 적어요.',
          intro: '안내',
          intro_link: '요즘 모습 안내',
          empty_heading: '어떤 모습부터 채워볼까요?',
          empty_hint: '마음 가는 질문 하나만 골라보세요.',
          cards_hint: '카드를 누르면 그동안 남긴 답을 볼 수 있어요.',
          first_answer: '첫 답 남기기',
          first_answer_aria: '{{question}} 첫 답 남기기',
          add_question: '질문 추가',
          filters_label: '질문 종류',
          question_likes: '요즘 가장 좋아하는 것은?',
          question_dislikes: '요즘 조금 싫어하는 것은?',
          question_hobby: '요즘 푹 빠진 놀이는?',
          question_talent: '요즘 새롭게 잘하는 것은?',
          history_title: '질문 기록',
          compose_new: '새 답 남기기',
          compose_edit: '이 답 수정',
          compose_question: '질문 만들기',
          compose_save: '내 답 보태기',
          profile_of: '{{name}}의 요즘',
          author_note: '{{author}}의 답',
          author_preserve_note: '{{author}}의 답 · 이전 답도 남아요',
          preserve_note: '전에 남긴 답도 그대로 간직해요.',
          save_complete: '요즘 모습에 답을 남겼어요.',
          custom_category: '우리만의 질문',
        },
        now_profile: {
          back_to_profile: '{{name}}의 요즘',
          back_to_moments: '우리의 순간',
          timeline_badge: '요즘 모습',
          timeline_open_aria: '{{question}}: {{answer}} · 답변 기록 보기',
          question_history_description: '가족이 남긴 답변을 모아봤어요.',
          answer_count: '답변 {{count}}개',
          newest_first: '최신순',
          show_all_answers: '모든 답변 보기',
          edit_answer: '답변 수정',
          open_question_history: '{{question}} 답변 기록 보기',
          author_me_only: '작성자: 나',
          delete_failed: '삭제하지 못했어요. 답변은 그대로 남아 있으니 다시 시도해주세요.',
          delete_conflict_error: '답변이 바뀌었어요. 최신 내용을 확인한 뒤 다시 삭제해주세요.',

          cancel_delete_record: '취소',
          question_records: '이 질문의 기록',
          filter_question: '질문',
          all_questions: '모든 질문',
          history_count: '답변 {{count}}개 · 최신순',
          reset_filters: '전체 기록 보기',
          edit_record_short: '수정',
          delete_record_short: '삭제',
          delete_record_only_hint: '이 답변만 삭제돼요. 질문과 다른 답변은 남아요.',
          deleting_record: '삭제 중…',
          confirm_delete_record: '답변 삭제',

          all_records: '전체 기록',
          overview_title: '{{name}}의 요즘 모습',
          history_heading: '{{name}}에게 남긴 이야기',
          filter_author: '작성자별 기록 보기',
          filter_all: '전체',
          filter_mine: '내가 쓴 답',
          filter_family: '가족이 쓴 답',
          history_empty: '아직 이곳에 표시할 기록이 없어요.',
          mine_answer: '내가 쓴 답',
          family_answer: '가족이 쓴 답',
          unknown_answer: '작성자 정보 없음',
          mom_answer: '엄마가 쓴 답',
          dad_answer: '아빠가 쓴 답',
          add_my_answer: '나도 답 남기기',
          answer_menu: '답변 더보기',
          create_my_answer_title: '내 답 남기기',
          edit_my_answer_title: '내 답 수정',
          answer_heading_my: '내가 본 {{baby}}의 모습',
          answer_description_my: '가족과 함께 아기의 요즘을 나눠요.',
          me_short: '나',
          author_me: '작성자: 나 · {{author}}',
          save_my_answer: '내 답 저장',
          save_latest_hint: '새 답이 요즘 모습에 표시돼요.',
          save_history_hint: '이 날짜의 답이 전체 기록에 추가돼요.',
          save_preserves_history_hint: '이전 답은 전체 기록에 그대로 남아요.',

          close: '닫기',
          likes_label: '좋아하는 것',
          dislikes_label: '싫어하는 것',
          hobby_label: '취미',
          talent_label: '특기',
          add_question: '+ 질문 하나 더',
          question_placeholder: '질문을 적어요',
          answer_placeholder: '눌러서 적어요',
          history_title: '시간의 이력',
          view_records: '기록 {{count}}개 보기',
          records_title: '답변 기록',
          records_hint: '날짜와 작성자를 확인하고, 보고 싶은 기록을 선택하세요.',
          viewing_earlier: '이전 기록을 보고 있어요',
          back_to_latest: '최신 답 보기',
          latest_record: '최신 기록',
          remove_question: '질문 지우기',
          open_aria: '{{name}}의 요즘 모습 보기',
          entry_hint: '사진을 누르면\n{{name}}의 요즘 모습이 열려요.',
          overview_hint: '질문마다 가장 최근에 남긴 답이에요.',
          loading: '요즘 모습을 불러오고 있어요.',
          load_failed: '요즘 모습을 불러오지 못했어요. 다시 연결해주세요.',
          retry: '다시 불러오기',
          save_failed: '저장하지 못했어요. 적은 내용은 그대로 있으니 다시 시도해주세요.',
          conflict_error: '다른 화면에서 내용이 바뀌었어요. 적은 내용은 남겨뒀어요. 닫은 뒤 최신 답을 확인해주세요.',
          permission_error: '이 내용을 바꿀 권한이 없어요. 내가 본 모습은 새 답으로 남길 수 있어요.',
          legacy_available: '이 기기에 이전 기록이 있어요',
          legacy_import_hint: '이 기기에 저장된 답변을 가족과 함께 볼 수 있게 가져와요.',
          import_legacy: '이전 기록 가져오기',
          legacy_author: '이전 기록',
          author_mom: '엄마',
          author_dad: '아빠',
          author_family: '가족',
          author_with_role: '{{role}} · {{name}}',
          question_by: '{{name}}가 만든 질문',
          question_author_suffix: '가 만든 질문',
          current_answer: '지금',
          new_answer_short: '새 답',
          first_answer_hint: '우리 아기를 알려주세요',
          choose_answer: '누가 본 모습일까요?',
          saving: '저장 중…',
          empty_answer: '첫 답을 기다려요',
          new_answer: '새 답 남기기',
          first_answer: '첫 답 적기',
          first_record_hint: '첫 기록이에요',
          more_records: '+{{count}}개 더',
          question_menu: '질문 메뉴',
          rename_question: '질문 이름 바꾸기',
          new_answer_title: '새 답 남기기',
          edit_answer_title: '이 답 수정',
          new_question_title: '질문 하나 더',
          rename_question_title: '질문 이름 바꾸기',
          answer_label: '답변',
          question_label: '질문',
          answer_required_error: '답을 한마디 적어주세요.',
          question_required_error: '질문을 한마디 적어주세요.',
          character_limit_error: '공백 포함 {{limit}}자까지 적을 수 있어요.',
          two_line_error: '말풍선에서 두 줄 안에 보이도록 조금만 줄여주세요.',
          save_answer_action: '이 답 남기기',
          answer_editor_heading: '{{name}}의 요즘을 담아요',
          question_editor_heading: '{{name}}가 더 궁금해요',
          answer_editor_subtitle: '아기 대신, 한마디를 남겨요.',
          question_editor_subtitle: '우리만의 질문을 만들어봐요.',
          custom_question_label: '나만의 질문',
          two_line_hint: '공백 포함 · 최대 2줄',
          character_count: '{{count}} / {{limit}}',
          author_writes_as: '{{author}}로 남겨요',
          question_examples_title: '이런 질문은 어때요?',
          question_examples_hint: '고른 뒤 바꿔도 좋아요',
          question_example_play: '어떤 놀이를 좋아할까?',
          question_example_sleep: '잠들기 전 뭘 할까?',
          question_example_smile: '언제 제일 많이 웃을까?',
          observed_at_label: '언제의 모습인가요?',
          observed_at_hint: '오늘 적더라도 그 모습을 보였던 날짜로 남겨요.',
          add_record_action: '새 기록 추가',
          save_edit_action: '수정한 답 저장',
          add_question_action: '이 질문에 답하기',
          save_question_action: '질문 저장',
          edit_record: '이 답 수정',
          delete_record: '이 답 삭제',
          delete_record_confirm: '이 기록 하나만 삭제할까요?',
          delete_question_confirm: '“{{question}}” 질문과 모든 기록을 삭제할까요?',
          duplicate_question_error: '이미 있는 질문이에요. 다른 이름을 적어주세요.',
          date_required_error: '날짜를 선택해주세요.',
          future_date_error: '미래 날짜는 기록할 수 없어요.',
          before_birthday_error: '생일보다 이전 날짜는 선택할 수 없어요.',
          discard_changes_confirm: '작성 중인 내용을 버리고 닫을까요?',
          storage_failed: '이 기기에서 답을 저장하지 못했어요. 화면을 닫기 전에 다시 확인해주세요.',
          storage_recovery_warning: '이 기기의 이전 기록을 읽지 못했어요. 원본은 그대로 두고, 새 답은 가족과 함께 보는 기록에 저장할게요.',
        },
        band: {
          small: '작은 편',
          average: '평균 범위',
          big: '큰 편',
        },
        peer_comparison_label: '또래와 비교',
        percentile_scale_end: '100백분위',
        percentile_summary: {
          check: '{{percentile}}백분위 · 확인 필요',
          small: '{{percentile}}백분위 · 작은 편',
          average: '{{percentile}}백분위 · 평균 범위',
          big: '{{percentile}}백분위 · 큰 편',
          very_big: '{{percentile}}백분위 · 많이 큰 편',
        },
        percentile_description: {
          check: '성장 상태를 한 번 확인해 주세요',
          small: '같은 월령 또래보다 작은 편이에요',
          average: '같은 월령 또래의 평균 범위에 있어요',
          big: '같은 월령 또래보다 큰 편이에요',
          very_big: '같은 월령 또래보다 많이 큰 편이에요',
        },
        percentile_peer_label: '또래 100명 중',
        percentile_pill: {
          check: '{{percentile}}번째 · 확인 필요',
          small: '{{percentile}}번째 · 작은 편',
          average: '{{percentile}}번째 · 평균 범위',
          big: '{{percentile}}번째 · 큰 편',
          very_big: '{{percentile}}번째 · 많이 큰 편',
        },
        percentile_comment: {
          unknown: '성장 기록을 저장하면\n또래 비교를 보여드릴게요.',
          check: '전체 백분위중 {{percentile}}번째,\n성장 확인이 필요해요. 🙏🏻',
          small: '전체 백분위중 {{percentile}}번째,\n또래보다 작은 편이에요. 🌱',
          average: '전체 백분위중 {{percentile}}번째,\n또래 평균 범위예요 💕',
          big: '전체 백분위중 {{percentile}}번째,\n또래보다 큰 편이에요. 💪🏻',
          very_big: '전체 백분위중 {{percentile}}번째,\n또래보다 많이 큰 편이에요. 💪🏻',
        },
        records_title: '측정기록',
        date: '날짜',
        metrics: {
          weight: '몸무게',
          height: '키',
          head: '머리둘레',
        },
      },
      care_visual_settings: {
        title: '표시 방식 설정',
        subtitle: '색상은 유지하고 조각 구분 방식만 바꿉니다.',
        default: {
          title: '기본 표시',
          desc: '현재처럼 색상 중심으로 표시합니다.',
        },
        distinct: {
          title: '구분 강화',
          desc: '패턴과 마커를 표시해 비슷한 색의 조각을 더 쉽게 구분합니다.',
        },
        marker_categories: {
          title: '어떤 카테고리에 마커를 표시할까요?',
          desc: '선택한 카테고리의 조각에 작은 마커를 추가합니다.',
        },
      },
      category: {
        mothers_milk: '모유',
        formula: '분유',
        baby_food: '이유식',
        nappy: '배변',
        sleep: '수면',
        pumping: '유축',
        stored_mothers_milk: '유축수유',
        hospital: '병원',
        bath: '목욕',
        memo: '메모',
        temperature: '체온',
        medication: '투약',
        snack: '간식',
        tummy_time: '터미타임',
        play: '놀이',
      },
      today: {
        no_baby_idx: '아기 식별 정보가 없습니다. 앱에서 다시 접속해 주세요.',
        quick_actions: '바로가기',
        share_excel_aria: '내보내기',
        tap_to_show_details: '탭하여 상세 정보 보기',
        tap_to_hide_details: '다시 탭하면 크게 보기',
        previous_day_aria: '이전 날짜',
        next_day_aria: '다음 날짜',
        pattern: {
          title: '하루의 흐름',
          no_data: '기록이 쌓이면 하루의 흐름을 요약해 드려요.',
          sleep_prefix: '수면 {{sleep}} · ',
          sleep_only: '총 수면 {{sleep}}이에요.',
          sleep_split_only: '총 수면 {{sleep}}이 {{count}}번에 나뉘어 기록됐어요.',
          sleep_record_only: '수면 기록 {{count}}건이 있어요.',
          activity_count_only: '{{prefix}}돌봄 기록이 {{count}}건 있어요.',
          activity_sparse: '{{prefix}}{{period}}에 돌봄 기록이 {{count}}건 있어요.',
          activity_peak: '{{prefix}}{{period}}에 돌봄 기록이 가장 많아요.',
          activity_double_peak: '{{prefix}}{{first}}·{{second}} 시간대에 돌봄 기록이 많아요.',
          activity_spread: '{{prefix}}{{periods}} 시간대에 돌봄 기록이 골고루 있어요.',
          activity_across: '{{prefix}}돌봄 기록이 {{periods}} 시간대에 나뉘어 있어요.',
          period: {
            dawn: '새벽',
            morning: '오전',
            afternoon: '오후',
            evening: '저녁',
          },
        },
        summary: {
          title: '리듬 요약',
          subtitle: '간격과 양을 한눈에',
          no_data: '기록 없음',
          no_interval: '-',
          interval: {
            sleep: '깨어 있던 시간',
            feeding: '수유 텀',
            nappy: '배변 간격',
            pumping: '유축 간격',
          },
          sleep: '수면',
          feeding: '수유·식사',
          nappy: '배변',
          pumping: '유축',
          total_sleep: '총 수면',
          sessions: '횟수',
          total_intake: '총 섭취',
          total_feeding: '총 수유',
          nappy_total: '기저귀 교체',
          nappy_detail: '대·소변',
          total_pump: '총 유축',
          avg_pump: '평균/회',
        },
        history: {
          no_records_for_date: '{{date}} 기록이 없어요',
          load_previous: '이전 날짜 불러오기',
          yesterday: '어제',
          today: '오늘',
          tomorrow_first_record: '내일 첫 기록',
        },
      },
      compare: {
        toggle: '비교',
        comparing: '비교 중',
        yesterday: '어제',
        week_ago: '일주일 전',
        month_ago: '한 달 전',
        select_date: '날짜 선택',
        exit: '비교 종료',
      },
      stats: {
        compare_title: '일간 통계 비교',
        times: '회',
        hour: '시간',
        minute: '분',
        volume: '용량',
        duration: '시간',
        increase: '증가',
        decrease: '감소',
      },
      nappy: {
        poo: '대변',
        fee: '소변',
      },
      discover: {
        toast_on: '✨ 업데이트 소식을 가장 먼저 알려드릴게요!',
        toast_off: '알림이 해제되었어요',
        toast_save_failed: '서버 저장에 실패했어요',
        badge_ai_insight: 'AI 기반 육아 인사이트',
        hero_title_line1: '쑥쑥찰칵 AI와 함께',
        hero_title_line2: '우리 아이 리듬을 발견해요',
        hero_desc_line1: '수면·수유를 기록하면 AI가 리듬을 분석해',
        hero_desc_line2: '타이밍 알림을 자동으로 보내드려요. 아래에서 바로 써보고, 준비 중인 기능도 미리 만나보세요.',
        btn_notify_on: '알림 설정 완료',
        btn_notify_off: '출시 알림 받기',
        notify_confirm: '이 기능이 출시되면 바로 알려드릴게요',
        section_available: '지금 바로 써보세요',
        section_coming: '곧 만나실 수 있어요',
        section_coming_time: '2026년 하반기 예정',
        section_coming_badge: '준비 중',
        tag_coming_soon: 'Coming Soon',
        section_more_title: '쑥쑥찰칵과 함께 오롯이 성장',
        section_more_desc_line1: '매일의 기록이 모여 우리 아기만의',
        section_more_desc_line2: '특별한 성장 이야기가 만들어집니다',
        cards: {
          sleep_insight: {
            title: '수유텀 패턴 분석',
            desc: '수유 시간과 간격의 패턴을 파악해 리듬을 찾아드려요',
            details: '머신러닝 기반 분석'
          },
          feeding_rhythm: {
            title: '맞춤형 수유 가이드',
            desc: '우리 아기의 수유 패턴을 이해하고 함께 성장해요',
            details: '개인화된 인사이트'
          },
          growth_insight: {
            title: '성장 발달 인사이트',
            desc: '우리 아기의 성장 패턴과 발달 단계를 분석해요',
            details: '데이터 기반 분석'
          }
        }
      },
      story: {
        meta: {
          home_title: '쑥쑥이의 하루 - 우리 아이의 하루',
          record_title: '촬영하기 - 쑥쑥이의 하루',
        },
        home: {
          brand: '쑥쑥찰칵',
          logo_alt: '로고',
          hero_title_line1: '📹 우리 아이의 하루를',
          hero_title_line2: '5초 영상으로 기록해요',
          hero_desc: '가로 영상을 올려주시거나 직접 가로로 촬영해보세요!',
          split_count_suffix: '분할',
        },
        landing: {
          intro: {
            aria_label: '쑥쑥찰칵 소개 영상',
            dismiss_hint: '더블탭하면 닫히고 다시 보이지 않아요',
          },
        },
        common: {
          back: '뒤로',
          capture: '촬영',
          upload: '업로드',
          close: '닫기',
        },
        capture: {
          ready: '촬영 준비',
          recording: '촬영중... 5초',
          guide_title: '이 안에 맞춰주세요',
          guide_description: '밝은 영역이 최종 영상에 들어가요',
          switch_camera: '카메라 전환',
        },
        draft: {
          saving: '임시저장 중...',
          saved: '임시저장됨',
          retention_note: '최대 {{days}}일 보관',
          save_failed: '임시저장에 실패했어요',
          load_failed: '임시저장본을 불러오지 못했어요',
          retry: '다시 시도',
        },
        crop: {
          title: '보이는 영역 맞추기',
          description: '드래그로 위치를 잡고 줌을 조절해요',
          apply: '적용',
          adjust: '위치 조정',
          zoom: '확대',
          reset: '가운데로',
          visible_area: '최종 표시 영역',
        },
        frame: {
          title: '프레임',
        },
        frame_style: {
          none: '없음',
          white: '화이트',
          rainbow: '레인보우',
          pastel: '파스텔',
        },
        play_mode: {
          title: '재생 방식',
          simultaneous: '동시재생',
          sequential: '순서재생',
          sequential_reveal: '차례등장',
        },
        preview: {
          title: '미리보기',
          open: '미리보기 & 다운로드',
          download: '영상 다운로드',
          exporting: '내보내는 중...',
          created_by: 'created by',
          filename_prefix: '쑥쑥이의하루_',
        },
        export: {
          loading: {
            preparing: {
              title: '영상을 준비하고 있어요',
              subtitle: '폰트와 미디어를 불러오는 중이에요',
            },
            rendering: {
              title: '영상을 만드는 중이에요',
              subtitle: '거의 다 됐어요 · {{percent}}%',
            },
            saving: {
              title: '영상을 저장하는 중이에요',
              subtitle: '잠시 후 갤러리에서 확인할 수 있어요',
            },
            hint: '화면을 닫지 말고 잠시만 기다려 주세요',
          },
        },
        debug: {
          title: '개발 로그',
          clear: '지우기',
          media_recorder: 'MediaRecorder',
          capture_stream: 'captureStream',
          native_bridge: '네이티브 브리지',
          preview_slot: '미리보기 슬롯 {{index}}',
          export_slot: '내보내기 슬롯 {{index}}',
          empty: '아직 기록이 없습니다.',
        },
        errors: {
          template_not_found: '템플릿을 찾을 수 없어요',
          catalog_load_failed: '서버 오류로 템플릿을 불러오지 못했어요',
          catalog_load_description: '잠시 후 다시 시도해주세요. 계속 실패하면 서버 상태를 확인해야 해요.',
          catalog_load_retry: '다시 시도',
          camera_blocked: '카메라 권한이 차단되어 있어요. 브라우저 설정에서 허용해주세요.',
          camera_allow: '카메라 권한을 허용해주세요.',
          camera_missing: '카메라를 찾을 수 없어요.',
          camera_busy: '카메라가 다른 앱에서 사용 중이에요.',
          camera_failed: '카메라 접근에 실패했어요.',
          file_type_unsupported: '영상 파일만 선택할 수 있어요.',
          file_read_failed: '선택한 파일을 불러오지 못했어요. 다시 시도해주세요.',
          image_export_failed: '이미지 내보내기에 실패했어요',
          export_failed: '내보내기에 실패했어요. 다시 시도해주세요.',
        },
        subscription: {
          badge_alt: '구독 전용',
          recorder_banner_title: '구독 후 해당 템플릿을 이용할 수 있어요',
          recorder_banner_description: '신규 회원 첫 구독시 2주간 무료체험!',
          recorder_banner_label: '구독 안내 보기',
          gate_title: '구독 전용 템플릿이에요',
          gate_description: '이 템플릿은 구독자만 사용할 수 있어요. 구독하고 더 많은 스토리를 만들어 보세요.',
          gate_cancel: '다음에',
          gate_cta: '구독 보러가기',
        },
      },
      baby_management: {
        back: '뒤로',
        title: '아기 관리',
        description: '등록된 아기 프로필을 확인해 주세요.',
        profile_list: '아기 프로필 목록',
        loading: '아기 프로필을 불러오고 있어요',
        fetch_failed: '아기 프로필을 불러오지 못했어요',
        no_baby: '아기 정보를 찾을 수 없어요',
        no_profiles: '표시할 아기 프로필이 없어요',
        unnamed_baby: '이름 없는 아기',
        unknown_family: '가족 정보 없음',
        family_name: '소속 가족 · {{name}}',
        family_label: '소속 가족',
        born_on: '출생 {{date}}',
        due_on: '출산 예정 {{date}}',
        height: '키',
        weight: '몸무게',
        head_circumference: '머리둘레',
        current_baby: '현재 아기',
        profile_alt: '{{name}} 프로필 이미지',
        retry: '다시 시도',
        gender_boy: '남아',
        gender_girl: '여아',
        gender_unknown: '아직 몰라요',
        dday_label: '디데이',
        age_label: '나이',
        age_year: '{{years}}년',
        age_years_months_days: '{{years}}년 {{months}}개월 {{days}}일',
        age_months_days: '{{months}}개월 {{days}}일',
        age_months: '{{months}}개월',
        age_days: '{{days}}일',
        age_pregnancy: '{{weeks}}주차 {{days}}일',
        view_detail: '자세히 보기',
        open_detail: '{{name}} 자세히 보기',
        detail_title: '{{name}} 정보',
        close_detail: '상세 닫기',
        basic_info: '기본 정보',
        growth_info: '성장 기록',
        name_label: '이름',
        gender_label: '성별',
        birthday_label: '생일',
        due_date_label: '출산 예정일',
        due_date_short_label: '예정일',
        not_set: '미입력',
      },
      permission: {
        back: '뒤로',
        title: '가족 권한/닉네임 관리',
        invite_family: '가족 초대',
        invite_banner_title: '가족을 초대해보세요.',
        invite_banner_desc: '함께 사진을 올리고 볼 수 있어요.',
        family_management_title: '가족 관리',
        family_select_desc: '관리할 가족을 선택해 주세요',
        current_family: '현재 가족',
        family_info_title: '가족 정보',
        family_name_label: '가족 이름',
        family_name_edit_title: '가족 이름 변경',
        family_name_edit_desc: '가족에게 표시할 이름을 입력해 주세요.',
        family_name_fallback_desc: '아직 가족 이름이 없어 아기 이름을 임시로 사용하고 있어요.',
        family_name_max_length: '최대 10자까지 입력할 수 있어요.',
        family_name_save_failed: '가족 이름을 저장하지 못했어요.',
        family_settings_save_failed: '가족 설정을 저장하지 못했어요.',
        comment_visibility_label: '댓글 공개 범위',
        comment_visibility_title: '댓글 공개 범위',
        comment_visibility_desc: '사진에 댓글을 작성할 수 있는 가족 그룹을 선택해 주세요.',
        comment_group_mom: '엄마 가족',
        comment_group_dad: '아빠 가족',
        comment_group_online: '랜선 가족',
        comment_group_none: '댓글 사용 안 함',
        calendar_start_label: '캘린더 시작일',
        calendar_start_title: '캘린더 시작일',
        calendar_start_desc: '출생일을 캘린더에서 어떻게 표시할지 선택해 주세요.',
        calendar_start_d0: '출생일 D+0',
        calendar_start_d1: '출생일 D+1',
        calendar_start_d0_desc: '병원 등에서 일반적으로 사용하는 시작일이에요.',
        calendar_start_d1_desc: '첫돌이 365일이 되는 시작일이에요.',
        family_members_title: '가족 구성원',
        member_info_title: '구성원 정보',
        basic_info_title: '기본 정보',
        nickname_label_short: '닉네임',
        family_group_label: '가족 그룹',
        family_group_title: '가족 그룹 선택',
        family_group_parents: '부모',
        family_group_paternal: '아빠 가족',
        family_group_maternal: '엄마 가족',
        family_group_online: '랜선 가족',
        relationship_label: '관계',
        relationship_title: '관계 선택',
        permissions_title: '권한',
        detail_carer_title_short: '돌봄 기록',
        save_changes: '변경사항 저장',
        apply: '적용',
        loading_title: '가족 정보를 불러오고 있어요',
        loading_desc: '권한과 닉네임을 확인 중이에요. 잠시만 기다려주세요.',
        view_switch_aria: '가족 보기 방식',
        view_list: '목록',
        view_family_map: '가족도',
        family_map_maternal_side: '엄마 쪽',
        family_map_paternal_side: '아빠 쪽',
        family_map_other_family: '마음으로 이어진 가족',
        family_map_center: '우리 아기',
        family_map_baby: '아기',
        family_map_open_member: '{{name}} 구성원 보기',
        baby_family: '{{name}}의 가족',
        fetch_failed: '가족 정보를 불러오지 못했어요',
        save_failed: '권한 저장에 실패했어요',
        nickname_change: '닉네임 변경',
        nickname_saving: '저장 중...',
        nickname_modal_desc: '가족 안에서 표시되는 닉네임이나 호칭을 입력해주세요.',
        nickname_label: '닉네임/호칭',
        nickname_max_length: '최대 32자까지 입력할 수 있어요.',
        nickname_too_long: '닉네임은 32자 이하여야 해요.',
        nickname_cancel: '취소',
        nickname_save: '저장',
        nickname_required: '닉네임을 입력해주세요.',
        nickname_save_failed: '닉네임 저장에 실패했어요',
        no_more_history: '더 이상 뒤로 갈 곳이 없습니다.',
        confirm_leader_assign: '한번 관리자로 지정하면 본인만 해제할 수 있습니다. 지정하시겠습니까?',
        no_baby_id: '아기 정보를 찾을 수 없어요',
        no_families: '표시할 가족이 없어요',
        no_members: '표시할 구성원이 없어요',
        member_count: '구성원 {{count}}명',
        chip_uploader: '업로드',
        chip_carer: '수유기록',
        chip_downloader: '다운로드',
        leader: '관리자',
        me: '나',
        profile_alt: '{{name}} 프로필 이미지',
        relationship_mom: '엄마',
        relationship_dad: '아빠',
        relationship_maternal_grandfather: '할아버지',
        relationship_maternal_grandmother: '할머니',
        relationship_paternal_grandfather: '할아버지',
        relationship_paternal_grandmother: '할머니',
        relationship_maternal_relative: '엄마쪽 친척',
        relationship_paternal_relative: '아빠쪽 친척',
        relationship_grandfather: '할아버지',
        relationship_grandmother: '할머니',
        relationship_relative: '친척',
        relationship_other_family: '랜선 가족',
        detail_upload_title: '업로드',
        detail_upload_desc: '사진,영상을 올릴 수 있어요.',
        detail_carer_title: '수유기록 조회/작성',
        detail_carer_desc: '수유기록을 보고 입력할 수 있어요.',
        detail_download_title: '다운로드',
        detail_download_desc: '사진을 기기에 저장할 수 있어요.',
        detail_comment_title: '댓글',
        detail_comment_desc: '사진에 댓글을 남길 수 있어요.',
        detail_leader_add_title: '관리자로 추가하기',
        detail_leader_remove_title: '관리자에서 제외하기',
        detail_leader_desc: '가족 구성원을 초대하고 메뉴별 권한을 관리해요.',
        detail_leader_locked_desc: '부모 그룹은 관리자 권한이 고정됩니다.',
      },
      subscription_benefits: {
        back: '뒤로',
        title: '내 구독 혜택',
        refresh: '새로고침',
        loading: '구독 혜택을 불러오는 중이에요',
        status_label: '구독 상태',
        status_active_title: '구독 혜택이 활성화되어 있어요',
        status_active_desc: '현재 계정에서 구독 전용 기능을 사용할 수 있어요.',
        status_inactive_title: '활성 구독 혜택이 없어요',
        status_inactive_desc: '구독 전용 기능은 구독 후 사용할 수 있어요.',
        status_unknown_title: '구독 상태를 확인 중이에요',
        status_unknown_desc: '앱에서 가족 정보를 받으면 구독 혜택을 확인할 수 있어요.',
        family_waiting_title: '가족 정보를 기다리고 있어요',
        family_waiting_desc: '구독 권한 확인에는 앱에서 전달하는 가족 정보가 필요해요.',
        partial_error_title: '일부 정보를 불러오지 못했어요',
        partial_error_desc: '잠시 후 새로고침하면 최신 혜택 상태를 다시 확인할 수 있어요.',
        benefits_label: '혜택',
        benefits_title: '현재 확인 가능한 혜택',
        active_count: '{{count}}/{{total}} 활성',
        enabled: '활성',
        disabled: '비활성',
        benefit_story_title: '3초 스토리 구독 템플릿',
        benefit_story_desc: '구독 전용 템플릿 사용과 워터마크 없는 내보내기를 사용할 수 있어요.',
        benefit_ads_title: '광고 없는 사용',
        benefit_ads_desc: '광고 제거 권한이 활성화되어 있는지 확인해요.',
        benefit_special_video_title: '스페셜 영상',
        benefit_special_video_desc: '스페셜 영상 기능 권한이 활성화되어 있는지 확인해요.',
        benefit_search_title: '검색 기능',
        benefit_search_desc: '검색 기능 권한이 활성화되어 있는지 확인해요.',
        account_label: '계정 정보',
        signup_date: '가입일',
        permission_source: '권한 정보',
        source_loaded: '확인됨',
        source_waiting: '대기 중',
        unknown: '확인 불가',
        status_subscribed: '구독중',
        status_cancellation_scheduled: '구독 해지 예정',
        status_not_subscribed: '미구독',
        status_checking: '확인중',
        next_billing_date: '다음 결제 예정일은 {{date}} 입니다.',
        benefit_available_until: '{{date}}까지 혜택을 이용할 수 있어요.',
        next_billing_unknown: '다음 결제 예정일을 확인 중입니다.',
        benefit_period_unknown: '혜택 이용 기간을 확인할 수 없어요.',
        select_family: '가족 선택',
        family_label: '{{names}}의 가족',
        receiver_family: '구독 혜택 받는 가족: {{family}}',
        current_user_subscribed: '회원님이 구독 중이에요',
        other_family_subscribed: '다른 가족이 구독 중이에요',
        subscription_owner_checking: '구독 상태를 확인 중이에요',
        all_family_active_title: '모든 가족이 혜택을 받고있어요.',
        only_me: '나 혼자 혜택을 받고 있어요.',
        only_me_upgrade_desc: '다른 가족 모두 혜택을 받고 싶다면,\n구독을 업그레이드 해주셔야해요.',
        upgrade_inquiry: '업그레이드 문의하기',
        other_family_receiving_title: '다른 가족이\n구독 혜택을 받고 있어요.',
        other_family_receiving_desc: '구독 혜택은 한 가족만 받을 수 있어요.\n지금 보고 있는 가족은 구독 혜택을 받고 있지 않아요.',
        all_family_ending_title: '모든 가족이 받고 있던 혜택이\n곧 종료 됩니다.',
        all_family_active_desc: '우리 가족 모두가 아래 구독 혜택을 받고있어요!',
        all_family_active_desc_with_count: '{{family}} {{count}}명 모두가\n아래 구독 혜택을 받고있어요!',
        keep_all_subscription_benefits: '모든 구독 혜택 유지하기',
        subscription_issue_title: '구독 정보를 확인하지 못했어요.',
        subscription_issue_desc: '구독 중인데 혜택이 보이지 않으면 고객센터로 문의해주세요.',
        contact_support: '문제가 있어요',
        manage_subscription: '구독 관리하기',
        explore_other_subscriptions: '다른 구독 알아보기',
        cancel_subscription: '구독 해지하기',
        debug_status_title: '구독중 판단 근거',
        debug_status_copy: '복사하기',
        debug_status_copied: '복사됨',
        debug_status_copy_failed: '복사 실패',
        debug_status_close: '닫기',
        terms_title: '구독 안내',
        legal_links_label: '약관 및 개인정보 안내',
        terms_of_use: '이용약관',
        privacy_policy: '개인정보 취급방침',
        config_error_title: '혜택 정보를 불러오지 못했어요',
        config_error_desc: '잠시 후 다시 시도하면 최신 구독 혜택을 확인할 수 있어요.',
        retry: '다시 시도',
      },
      family_piggy_bank: {
        back: '뒤로',
        title: '가족과 치즈 모으기',
        intro_title: '우리 가족 저금통',
        intro_desc: '가족과 함께 치즈를 차곡차곡 모아보세요.',
        intro_skip: '건너뛰기',
        vault_label: '우리 가족 저금통',
        level_name_1: '아기 곳간이',
        level_name_2: '포근포근 곳간이',
        level_name_3: '든든한 곳간이',
        level_name_4: '반짝반짝 곳간이',
        level_name_5: '햇살 가득 곳간이',
        mascot_idle_1: '우리 가족 치즈가 차곡차곡!',
        mascot_idle_2: '함께 모으니 더 든든해요',
        mascot_idle_3: '저금통을 톡 눌러보세요',
        mascot_idle_4: '오늘도 치즈를 모아볼까요?',
        mascot_react_1: '냠냠! 정말 맛있어요 💛',
        mascot_react_2: '아이 든든해~ 고마워요!',
        mascot_react_3: '몸이 점점 따뜻해져요~',
        mascot_pet_1: '에헤헤, 간지러워요!',
        mascot_pet_2: '포근포근~ 기분 좋아요',
        mascot_pet_3: '히히, 또 놀러 와 주세요!',
        pet_aria: '곳간이 쓰다듬기',
        total_tag: '우리 가족이 모아준 치즈 🧀 {{count}}개',
        family_together: '가족 {{count}}명이 함께 모으는 중',
        available_cheese: '내 치즈',
        cheese_count: '{{count}}개',
        available_cheese_label: '내 치즈 {{count}}개',
        balance_retry: '다시 불러오기',
        feed_button: '저금통에 치즈 넣기',
        deposit_open: '치즈 저금하기',
        deposit_sheet_eyebrow: 'FAMILY VAULT',
        deposit_sheet_title: '얼마나 저금할까요?',
        deposit_sheet_desc: '내가 가진 치즈 안에서 원하는 만큼 골라보세요.',
        deposit_close: '닫기',
        deposit_amount_label: '저금할 치즈',
        deposit_amount_aria: '저금할 치즈 수량',
        deposit_slider_aria: '저금할 치즈 수량 조절',
        deposit_preset_quarter: '25%',
        deposit_preset_half: '절반',
        deposit_preset_all: '전부',
        deposit_available: '보유 {{count}}개',
        deposit_remaining: '남는 치즈 {{count}}개',
        deposit_confirm: '{{count}}개 저금하기',
        deposit_expiration_preview: '지금 저금하면 {{date}}까지 사용할 수 있어요.',
        cheese_unit: '개',
        earn_link: '치즈 더 모으기',
        earn_title: '치즈 모으는 방법',
        earn_intro: '사진을 올리고 가족을 초대해 보세요.',
        earn_method_1_title: '사진 업로드',
        earn_method_1_desc: '오늘의 사진을 올리고 치즈를 받아요',
        earn_method_2_title: '가족 초대',
        earn_method_2_desc: '가족과 함께하면 치즈가 더 빨리 모여요',
        earn_method_3_title: '미션',
        earn_method_3_desc: '새로운 미션을 준비하고 있어요',
        earn_close: '확인',
        deposit_empty: '치즈가 없어요. 더 모아볼까요?',
        deposit_title: '가족 저금통 적립',
        deposit_processing: '치즈를 넣고 있어요...',
        deposit_failed: '치즈를 넣지 못했어요. 다시 시도해 주세요.',
        deposit_retry: '치즈 정보를 확인하지 못했어요. 잠시 후 다시 시도해 주세요.',
        deposit_pending: '치즈 차감 결과를 확인 중이에요. 중복 차감을 막기 위해 잠시 후 내 치즈를 확인해 주세요.',
        deposit_balance_changed: '보유 치즈가 변경됐어요. 다시 확인해 주세요.',
        deposit_success: '{{count}}개를 넣었어요 · {{date}}까지 사용할 수 있어요.',
        deposit_success_mixed: '만료 치즈 {{expiringCount}}개와 무기한 치즈 {{nonExpiringCount}}개를 넣었어요.',
        deposit_success_non_expiring: '무기한 치즈 {{count}}개를 넣었어요.',
        expiration_policy: '획득한 치즈는 넣은 날부터 6개월, 구매 치즈는 만료 없이 사용할 수 있어요.',
        next_expiration: '가장 가까운 만료일 {{date}}',
        expired: '만료',
        family_member: '가족',
        history_open: '저금 내역',
        history_title: '치즈 저금 내역',
        history_description: '가족이 저금한 치즈와 만료일을 건별로 확인할 수 있어요.',
        history_count: '최근 {{count}}건',
        history_amount_label: '저금한 치즈',
        history_amount: '{{count}}개',
        history_depositor_label: '저금한 사람',
        history_deposited_at_label: '저금일',
        history_expires_at_label: '만료일',
        history_available: '사용 가능',
        history_no_expiration: '없음',
        history_empty: '아직 모은 치즈가 없어요.',
        me_name: '나',
        member_mom: '엄마',
        member_dad: '아빠',
        member_grandma: '할머니',
        use_cta: '포토북 / 인화 하러가기',
        use_toast: '치즈 상점을 열심히 준비하고 있어요! 🧀',
        memory_title: '최근에 모은 치즈',
        activity_deposit: '{{name}}님이 {{count}}개를 모았어요',
        activity_deposit_me: '내가 {{count}}개를 모았어요',
        time_just_now: '방금 전',
        time_hours_ago: '{{count}}시간 전',
        time_yesterday: '어제',
        demo_reset: '데모 데이터 초기화',
        demo_reset_done: '데모 데이터를 초기화했어요',
      },
      intro: {
        title: '수유텀 통계가',
        subtitle: '완전 새로워졌어요!',
        desc: '쑥쑥찰칵의 새로운 수유텀을 만나보세요',
        cta: '알아보기'
      },
      tour: {
        skip: '건너뛰기',
        next: '다음',
        done: '완료',
        steps: {
          tabs: {
            title: '탭 소개',
            desc: '일별·주별 통계와 리듬 흐름까지 한눈에! AI 수유텀 패턴 분석 기능도 새롭게 준비했어요'
          },
          today: {
            title: '오늘 탭',
            desc: '타임테이블을 터치해보세요. 시간대별 상세 내역을 확인할 수 있어요'
          },
          week: {
            title: '주간 탭',
            desc: '한 주의 패턴을 한눈에! 우리 아이의 흐름을 쉽게 파악하세요'
          },
          week_excel: {
            title: '주간 기록 내려받기',
            desc: '내보내기 버튼 한 번이면 끝! 주간 기록을 표로 저장하세요'
          },
          discover: {
            title: '발견 탭',
            desc: '수면·수유를 기록하면 AI가 리듬을 분석해 타이밍 알림을 자동으로 보내드려요. 출시된 기능과 준비 중인 기능을 한곳에서 만나보세요.'
          }
        }
      },
      beta: {
        badge: '🧪 Beta Tester 모집 중',
        app_name: '쑥쑥찰칵의 새로운 AI 기능',
        title: '꿀잠',
        title_highlight: ' 타이밍',
        subtitle: 'AI가 찾아주는 우리 아기 최적의 잠잘 시간',
        intro_lead_line1: '"지금 재워야 하나...?"',
        intro_lead_line2: '더 이상 고민하지 마세요',
        intro_body_line1: '아기마다 다른 수면 리듬이 있어요.',
        intro_body_line2: '꿀잠 타이밍이 우리 아기의 기록을 분석해서',
        intro_body_line3: '잠들기 좋은 시간에 알림을 보내드려요 🔔',
        phone_time: '오후 1:24',
        notif_app: '쑥쑥찰칵',
        notif_when: '지금',
        notif_title: '🍯 꿀잠 타이밍이에요!',
        notif_body_line1: '지금이 재우기 좋은 시간이에요.',
        notif_body_line2: '약 10분 후면 꿀잠 타이밍이에요.',
        phone_extra: '오전 수면 기록 완료',
        phone_caption: '실제 알림 미리보기',
        how_title: '✨ 이렇게 동작해요',
        how_subtitle: '평소처럼 기록하면, 나머지는 AI가 알아서',
        step1_title: '평소처럼 기록하기',
        step1_desc: '쑥쑥찰칵에서 수유, 수면, 배변을 기록해 주세요',
        step2_title: 'AI가 패턴 분석',
        step2_desc: '기록을 바탕으로 우리 아기만의 수면 리듬을 파악해요',
        step3_title: '꿀잠 알림 도착 🔔',
        step3_desc: '재우기 좋은 타이밍에 푸시 알림을 보내드려요',
        tag_feeding: '🍼 수유',
        tag_sleep: '💤 수면',
        tag_diaper: '🧷 배변',
        benefits_title: '🎁 베타 테스터 혜택',
        benefits_subtitle: '가장 먼저 경험하고, 함께 만들어가요',
        benefit1_title: '누구보다 먼저 사용 + 무료',
        benefit1_desc: '베타 기간 동안 꿀잠 타이밍 알림을 무료로 먼저 만나보세요',
        benefit2_title: '개발팀과 직접 소통',
        benefit2_desc: '의견이 바로 반영돼요. "이런 기능 있으면 좋겠다"를 직접 전해주세요',
        cta_button: '🍯 꿀잠 타이밍 베타 신청하기',
        cta_note: '12개월 이하 아기 부모님 대상',
        auto_notice_title: '수면을 기록하면 자동으로 알림 대상이 돼요',
        auto_notice_desc: '별도 신청 없이, 아기의 수면을 기록하면 꿀잠 타이밍 알림을 자동으로 보내드려요.',
        registered_notice_title: '이미 꿀잠 타이밍 알림 대상이에요',
        registered_notice_desc: '수면을 기록하면 잠들기 좋은 시간에 꿀잠 타이밍 알림을 보내드릴게요.',
        faq_title: '자주 묻는 질문',
        faq1_q: 'Q. 기록을 많이 해야 하나요?',
        faq1_a: '아니요! 처음에는 비슷한 또래 아기들의 패턴을 기반으로 알림을 보내드리고, 기록이 쌓이면 자동으로 우리 아기에게 맞는 개인화 알림으로 발전해요.',
        faq2_q: 'Q. 베타 기간 중 비용이 있나요?',
        faq2_a: '베타 테스트 기간 동안은 완전 무료예요. 여러분의 소중한 피드백이 가장 큰 도움이 됩니다 🙏',
        status_pending: '신청이 접수되었어요',
        status_approved: '베타테스트가 승인되었어요! 🎉',
        status_already: '이미 신청하셨어요',
        status_full: '신청이 마감되었어요',
        apply_failed: '신청에 실패했어요. 다시 시도해 주세요',
        apply_success: '베타테스트 신청이 완료되었어요! 🎉',
        apply_success_title: '베타 신청이 완료되었어요!',
        apply_success_desc: '곧 꿀잠 타이밍 알림을 받으실 수 있어요',
        apply_success_confirm: '확인',
        already_applied_title: '이미 신청이 완료되었어요',
        already_applied_desc: '이 아기는 이미 꿀잠 타이밍 베타에 신청되었어요. 승인 후 알림을 보내드릴게요!',
        family_applied_title: '가족이 이미 신청했어요',
        family_applied_desc: '같은 아기로 다른 가족 구성원이 이미 베타 신청을 완료했어요.',
        status_cancelled: '베타 참여가 취소되었어요',
        cancelled_title: '베타 참여가 취소되었어요',
        cancelled_desc: '베타 테스트 참여가 취소되었어요. 문의사항이 있으시면 고객센터로 연락해 주세요.',
        footer: '© 2026 쑥쑥찰칵',
        back: '뒤로',
        no_eligible_babies: '신청 가능한 아기가 없습니다.',
        select_baby_title: '아기를 선택해주세요',
        select_baby_desc: '베타 서비스를 신청할 아기를 선택해주세요.',
        sleep_banner_title: '꿀잠 타이밍',
        sleep_banner_subtitle: '우리 아기 잠들기 좋은 시간을 확인해보세요',
      },
      letter: {
        title: '대표 미미의 편지',
        nav_title: '미미의 편지',
        close: '닫기',
        kicker: '쑥쑥찰칵을 만든 사람으로부터',
        hero_title: '한 아이의 오늘은,\n누군가의 평생이 되니까요.',
        hero_subtitle: '당신이 정성껏 남겨 온 시간을\n오래 지키겠다는 마음을 담아,\n직접 편지를 씁니다.',
        open_cue: '편지를 열어보세요',
        edition_default: '쑥쑥찰칵 감사 편지',
        issue: 'FOR YOUR FAMILY',
        to_line: '아이의 오늘을\n믿고 맡겨 주신 당신께',
        greeting: '안녕하세요.\n쑥쑥찰칵을 만들고 있는 미미입니다.',
        p1: '저도 아이를 키우는 부모예요. 정신없이 하루를 보내고 아이가 잠든 뒤, 휴대폰 속 오늘의 사진을 한참 들여다보던 밤이 많았어요.',
        p2: '쑥쑥찰칵을 믿고, 아이의 소중한 날들을 이곳에 남겨 주셔서 진심으로 고맙습니다. 바쁜 하루 속에서도 사진 한 장, 영상 하나를 골라 남기는 그 마음이 얼마나 큰지 알고 있어요.',
        p3: '처음 웃던 표정, 품에 안겼던 작은 몸, 가족이 함께 웃었던 순간까지. 지금은 익숙한 하루가 언젠가 다시 꺼내 보고 싶은 추억이 되겠지요. 그때도 오늘의 마음을 만날 수 있도록, 맡겨 주신 기록을 소중히 지키겠습니다.',
        p4: '보내 주신 믿음에 좋은 서비스로 답하겠습니다. 기록하는 순간은 더 편하게, 다시 꺼내 보는 시간은 더 따뜻하게 느끼실 수 있도록 하나씩 살피고 꾸준히 개선하겠습니다.',
        photo_caption: '작은 손 안에 담긴 오늘을\n오래 기억할 수 있도록',
        promise_label: '미미의 약속',
        promise_heading: '빠르게보다 바르게,\n많이보다 오래.',
        promise: '가족의 가장 소중한 기록을 다루는 일에 익숙함이 생기지 않도록 매일 처음의 마음으로 살피겠습니다.',
        closing: '고맙습니다.\n아이의 오늘 곁에서, 오래 잘 만들겠습니다.',
        sign_role: '쑥쑥찰칵을 만든 사람',
        sign_name: '대표 미미 드림',
        postscript: '오늘도 소중한 하루를\n남겨 주셔서 고맙습니다.',
        feedback: {
          label: '편지에 마음 전하기',
          support: '응원해요',
          touched: '마음이 전해졌어요',
          write: '익명으로 한마디 남기기',
          fold: '다음에 남길게요',
          placeholder: '남기고 싶은 말이 있다면, 편하게 적어주세요.',
          privacy: '익명으로 전달되며, 팀에서만 읽을 수 있어요.',
          count: '{{current}} / {{limit}}',
          send: '마음 보내기',
          sending: '전하고 있어요…',
          reaction_thanks: '마음이 전해졌어요. 고맙습니다.',
          message_thanks: '전해주신 마음, 소중히 읽을게요. 고맙습니다.',
          error: '아직 전하지 못했어요. 잠시 후 다시 눌러주세요.',
          already_sent: '이 편지에는 이미 한마디를 남겨주셨어요. 지금 적으신 글은 추가로 전송되지 않았어요.',
        },
        opening_alt: '오래된 사진과 봉투, 만년필이 놓인 따뜻한 기록 풍경',
        hands_alt: '어른의 손가락을 꼭 잡은 아기의 작은 손',
        closing_alt: '리본과 밀랍으로 봉인한 편지와 만년필',
        variant: {
          family_hd: {
            label: '패밀리 고화질 구독 감사 편지',
            p1: '저도 아이를 키우는 부모예요. 정신없이 하루를 보내고 아이가 잠든 뒤, 휴대폰 속 오늘의 사진을 한참 들여다보던 밤이 많았어요.',
            p2: '그 마음으로 만든 쑥쑥찰칵을 믿고, 패밀리 고화질 구독으로 함께해 주셔서 진심으로 고맙습니다.',
            p3: '이제 오늘의 목소리와 표정이 FHD 고화질로 더 선명하게 남습니다. 몇 해 뒤 다시 꺼내 보아도 그날의 온도가 흐려지지 않도록요.',
            p4: '그래서 더 천천히 살피고, 더 오래 안심할 수 있는 서비스를 만들겠습니다. 당신이 기록에 마음을 쓰는 동안, 보이지 않는 곳의 걱정은 저희가 먼저 하겠습니다.',
          },
          family_hd_trial: {
            label: '패밀리 고화질 체험 감사 편지',
            p1: '저도 아이를 키우는 부모예요. 정신없이 하루를 보내고 아이가 잠든 뒤, 휴대폰 속 오늘의 사진을 한참 들여다보던 밤이 많았어요.',
            p2: '패밀리 고화질을 처음 경험해 보기로 해 주셔서 고맙습니다. 아이의 오늘을 함께 지킬 기회를 주셔서 반가워요.',
            p3: '체험하는 동안 오늘의 목소리와 표정을 FHD 고화질로 남겨 보세요. 몇 해 뒤 다시 보아도 그날의 온도가 선명하도록요.',
            p4: '짧은 체험이 오래 믿을 수 있는 인연의 시작이 되도록, 보이지 않는 곳까지 정성껏 살피겠습니다.',
          },
          family: {
            label: '패밀리 구독 감사 편지',
            p1: '저도 아이를 키우는 부모예요. 정신없이 하루를 보내고 아이가 잠든 뒤, 휴대폰 속 오늘의 사진을 한참 들여다보던 밤이 많았어요.',
            p2: '그 마음으로 만든 쑥쑥찰칵을 믿고, 패밀리 구독으로 함께해 주셔서 진심으로 고맙습니다.',
            p3: '긴 영상도, 사진이 많은 날도 서두르지 않고 남겨 주세요. 대단한 사건이 없어도 아이의 평범한 하루는 충분히 기록할 가치가 있으니까요.',
            p4: '그래서 더 천천히 살피고, 더 오래 안심할 수 있는 서비스를 만들겠습니다. 당신이 기록에 마음을 쓰는 동안, 보이지 않는 곳의 걱정은 저희가 먼저 하겠습니다.',
          },
          family_trial: {
            label: '패밀리 체험 감사 편지',
            p1: '저도 아이를 키우는 부모예요. 정신없이 하루를 보내고 아이가 잠든 뒤, 휴대폰 속 오늘의 사진을 한참 들여다보던 밤이 많았어요.',
            p2: '패밀리 구독을 처음 경험해 보기로 해 주셔서 고맙습니다. 아이의 오늘을 함께 지킬 기회를 주셔서 반가워요.',
            p3: '체험하는 동안 긴 영상도, 사진이 많은 날도 여유롭게 남겨 보세요. 평범한 하루까지 빠짐없이 쌓이는 기쁨을 느끼실 수 있기를 바라요.',
            p4: '짧은 체험이 오래 믿을 수 있는 인연의 시작이 되도록, 보이지 않는 곳까지 정성껏 살피겠습니다.',
          },
          adfree: {
            label: '광고 없이 구독 감사 편지',
            p1: '저도 아이를 키우는 부모예요. 정신없이 하루를 보내고 아이가 잠든 뒤, 휴대폰 속 오늘의 사진을 한참 들여다보던 밤이 많았어요.',
            p2: '그 마음으로 만든 쑥쑥찰칵을 믿고, 광고 없이 구독으로 함께해 주셔서 진심으로 고맙습니다.',
            p3: '이제 다른 것에 시선을 빼앗기지 않고 아이의 기록에만 머물러 주세요. 가족의 추억을 꺼내는 시간이 조금 더 고요하고 온전하기를 바랍니다.',
            p4: '그래서 더 천천히 살피고, 더 오래 안심할 수 있는 서비스를 만들겠습니다. 당신이 기록에 마음을 쓰는 동안, 보이지 않는 곳의 걱정은 저희가 먼저 하겠습니다.',
          },
          adfree_trial: {
            label: '광고 없이 체험 감사 편지',
            p1: '저도 아이를 키우는 부모예요. 정신없이 하루를 보내고 아이가 잠든 뒤, 휴대폰 속 오늘의 사진을 한참 들여다보던 밤이 많았어요.',
            p2: '광고 없는 화면을 처음 경험해 보기로 해 주셔서 고맙습니다. 아이의 오늘을 함께 지킬 기회를 주셔서 반가워요.',
            p3: '체험하는 동안 다른 것에 시선을 빼앗기지 않고 아이의 기록에만 머물러 보세요. 추억을 꺼내는 시간이 조금 더 고요해지기를 바랍니다.',
            p4: '짧은 체험이 오래 믿을 수 있는 인연의 시작이 되도록, 보이지 않는 곳까지 정성껏 살피겠습니다.',
          },
        },
      },
      belly_clock: {
        banner_title: '배꼽시계',
        banner_subtitle: '우리 아이 수유 타이밍을 미리 알려드려요',
        back: '뒤로',
        title: '배꼽시계',
        hero_title: '우리 아이만의 수유 리듬을 읽어요',
        hero_desc: '수유를 기록하면 AI가 간격과 리듬을 분석해 다음 수유 타이밍 알림을 자동으로 보내드려요.',
        how_title: '어떻게 작동하나요',
        step1_title: '기록',
        step1_desc: '매일 수유 시간을 기록하면',
        step2_title: 'AI 분석',
        step2_desc: 'AI가 우리 아이의 수유 간격과 리듬을 분석해요',
        step3_title: '미리 알림',
        step3_desc: '다음 수유 타이밍을 30분 전에 살짝 알려드려요',
        benefits_title: '이런 점이 좋아요',
        benefit1_title: '수유 텀 놓치지 않기',
        benefit1_desc: '바빠도 다음 수유 시간을 잊지 않아요',
        benefit2_title: '외출·낮잠 계획',
        benefit2_desc: '다음 수유 타이밍에 맞춰 하루를 계획해요',
        benefit3_title: '밤중 수유 대비',
        benefit3_desc: '밤중 수유 리듬을 미리 가늠해 대비해요',
        trust_note: '아이마다 수유 리듬은 달라요.\n배꼽시계는 우리 아이의 최근 패턴을 반영해\n타이밍을 제안해요.',
        notice_title: '수유 알림을 받으려면',
        notice_desc: '모유·분유 수유는 시작 시각과 함께\n수유량 또는 수유 지속 시간을 기록해 주세요.\n시작 시각만 기록된 경우\n다음 수유 타이밍을 예측할 수 없어요.\n이유식·간식 기록은 알림에 영향을 주지 않아요.\n수유 간격이 날마다 크게 다르면\n알림이 빗나가거나 오지 않을 수 있어요.',
      },
      sleep: {
        status_title: '꿀잠 타이밍',
        awake_time: '깨어있는 시간',
        readiness: '꿀잠 타이밍',
        recommended_time: '권장 수면 시각',
        confidence: '예측 정확도',
        confidence_high: '기록이 충분해요',
        confidence_low: '기록을 모으고 있어요',
        weekly_pattern: '주간 수면 패턴',
         no_session: '아직 분석 중인 기록이 없어요',
         no_session_desc: '수면을 기록하면 AI가 리듬을 분석해 꿀잠 타이밍 알림을 자동으로 보내드려요.',
         no_active_session: '지금은 잠자는 중이에요',
         no_active_session_desc: '아기가 깨어나면 꿀잠 타이밍을 분석해드릴게요',
         sleeping_duration: '{{time}}째 수면 중',
         awake_no_session: '깨어 있어요',
         awake_no_session_desc: '수면 시간 예측 정보가 아직 없어요',
        load_failed: '꿀잠 타이밍을 불러오지 못했어요',
        load_failed_desc: '잠시 후 다시 시도해 주세요.',
        retry: '다시 시도',
        guide_how_title: '어떻게 작동하나요',
        guide_step1_title: '기록',
        guide_step1_desc: '평소처럼 수면 시간을 기록하면',
        guide_step2_title: 'AI 분석',
        guide_step2_desc: 'AI가 우리 아기의 수면 리듬을 분석해요',
        guide_step3_title: '미리 알림',
        guide_step3_desc: '잠들기 좋은 타이밍에 알림을 보내드려요',
        guide_benefits_title: '이런 점이 좋아요',
        guide_benefit1_title: '재울 타이밍 놓치지 않기',
        guide_benefit1_desc: '잠들기 좋은 시간에 맞춰 알려드려요',
        guide_benefit2_title: '하루 리듬 계획',
        guide_benefit2_desc: '낮잠·밤잠 타이밍에 맞춰 하루를 계획해요',
        guide_benefit3_title: '기록할수록 더 정확해져요',
        guide_benefit3_desc: '수면 기록이 쌓일수록 우리 아기에 맞게 맞춰져요',
        guide_trust_note: '아기마다 수면 리듬은 달라요. 꿀잠 타이밍은 우리 아기의 최근 패턴을 반영해 타이밍을 제안해요.',
        minutes: '분',
        score: '점',
        wake_window: '활동 시간',
        back: '뒤로',
        go_to_status: '꿀잠 타이밍 보기',
        hours: '시간',
        days_unit: '일',
        remaining: '약 {{minutes}} 뒤 권장 수면 시각',
        overtime: '{{minutes}} 초과',
        prediction_range: '예측 범위',
        personalized: '개인화',
        age_based: '월령 기반',
        analysis_basis: 'AI 분석 기반',
        analysis_improving: '기록이 쌓일수록 더 정확해져요',
        status_active: '아직 활발해요',
        status_little_tired: '살짝 피곤해요',
        status_drowsy: '슬슬 졸려요',
        status_sleep_now: '지금 재우면 좋아요!',
        status_overtime: '잠잘 시간을 넘겼어요',
        awake_time_elapsed: '깨어난 지 {{time}}',
        baby_default_name: '우리아기',
        baby_dday: '생후 {{days}}일',
      },
      books: {
        badge: 'photo book',
        theme: {
          badge: '사진책 만들기',
          title: '어느 시간을\n한 권에 담을까요?',
          intro: '담고 싶은 기간을 직접 고른 뒤 사진을 선택해요.',
          subtitle: '생후 {{months}}개월 기준으로 추천을 준비했어요.',
          previewLabel: '최근 사진으로 미리 본 사진책',
          previewTitle: '우리 아기 사진책',
          previewSubtitle: '소중한 시간을 한 권에',
          periodPrimaryTitle: '기간 고르기',
          periodPrimaryDesc: '연도 · 월 · 월령 · 첫 100일',
          browseBadge: '기간 선택',
          browseTitle: '어느 기간을 담을까요?',
          browseDesc: '원하는 기준을 고른 뒤 정확한 기간을 선택해요.',
          browseBack: '처음으로 돌아가기',
          browseActionTitle: '다른 기록 고르기',
          browseActionDesc: '연도별 · 월별 · 월령별 · 기념일',
          periodBadge: '기간 선택',
          periodBack: '다른 기록으로 돌아가기',
          periodGuideTitle: '기간 기준',
          milestoneGuideTitle: '성장 기준',
          recommendedBadge: '지금 추천',
          recommendedShort: '추천',
          recommendedAgeBadge: '지금 추천 · 생후 {{months}}개월',
          recommendedAction: '이 기록 선택',
          yearlyTitle: '연간 기록',
          yearlyMeta: '{{count}}개 연도',
          yearlyShort: '연도별',
          yearlyBrowseDesc: '한 해의 변화를 시간순으로 모아요.',
          yearlyPickerDesc: '책으로 만들 연도를 골라주세요.',
          monthlyTitle: '월간 기록',
          monthlyMeta: '{{count}}개월',
          monthlyShort: '월별',
          monthlyBrowseDesc: '사진이 많은 한 달을 가볍게 담아요.',
          monthlyPickerDesc: '책으로 만들 달을 골라주세요.',
          ageMonthTitle: '월령별 기록',
          ageMonthMeta: '{{count}}개 월령',
          ageMonthShort: '월령별',
          ageMonthBrowseDesc: '개월차별 성장 모습을 비교하기 좋아요.',
          ageMonthPickerDesc: '책으로 만들 월령을 골라주세요.',
          choiceCount: '{{count}}개',
          directTitle: '직접 사진을 고를래요',
          directDesc: '기간을 정하지 않고 지금처럼 직접 선택해요.',
          directPrimaryTitle: '기간 없이 사진 직접 고르기',
          directPrimaryDesc: '원하는 사진부터 바로 선택해요.',
          preparingSuggestions: '생일 기준 추천을 확인 중이에요. 사진은 바로 고를 수 있어요.',
          noBirthday: '아기 생일 정보를 확인하지 못했어요. 사진을 직접 골라 시작할 수 있어요.',
          selected: '선택한 주제',
          dateRange: '{{range}} 사진',
          back: '주제 선택으로 돌아가기',
          choice: {
            yearlyTitle: '{{year}}년 성장 기록',
            yearlySubtitle: '{{year}}년의 순간을 한 권에 담아요.',
            yearlyReason: '한 해의 변화를 시간순으로 돌아보기 좋아요.',
            monthlyTitle: '{{year}}년 {{month}}월 기록',
            monthlySubtitle: '한 달의 일상과 변화를 모아 담아요.',
            monthlyReason: '사진이 많은 달을 가볍게 한 권으로 만들기 좋아요.',
            ageMonthTitle: '{{month}}개월차 기록',
            ageMonthSubtitle: '{{month}}개월차의 하루를 모아 담아요.',
            ageMonthProgressSubtitle: '{{month}}개월차, 오늘까지의 기록이에요.',
            ageMonthReason: '월령에 따라 달라지는 모습을 비교하기 좋아요.',
            first100Title: '우리 아기의 첫 100일',
            first100ProgressTitle: '100일을 향해',
            first100Subtitle: '태어난 날부터 백일까지의 기록이에요.',
            first100ProgressSubtitle: '태어난 날부터 오늘까지 담아요.',
            first100Reason: '처음 100일의 변화를 한 흐름으로 남기기 좋아요.',
            firstYearTitle: '첫돌까지의 기록',
            firstYearProgressTitle: '첫돌을 향해',
            firstYearSubtitle: '태어난 날부터 첫 생일까지 담아요.',
            firstYearProgressSubtitle: '태어난 날부터 오늘까지의 성장 기록이에요.',
            firstYearReason: '첫해의 성장 흐름을 한 권으로 돌아보기 좋아요.',
            fromBirthTitle: '태어난 날부터 지금까지',
            fromBirthSubtitle: '처음 만난 날부터 오늘까지 모두 담아요.',
            fromBirthReason: '지금까지의 시간을 한눈에 돌아볼 수 있어요.',
          },
        },
        autoSetup: {
          close: '닫기',
          back: '이전으로',
          step: '{{current}} / 2',
          periodLabel: '기간 선택 완료',
          densityTitle: '사진을 얼마나 담을까요?',
          densityDesc: '기간 전체를 살펴보고 선택한 정도에 맞춰 좋은 순간을 골라드려요.',
          recommended: '추천',
          density: {
            highlightsTitle: '핵심 순간만',
            highlightsDesc: '비슷한 사진은 줄이고 대표 장면 위주로 담아요.',
            balancedTitle: '알맞게',
            balancedDesc: '중요한 순간과 일상을 균형 있게 담아요.',
            richTitle: '풍성하게',
            richDesc: '같은 날의 다양한 표정과 장면도 넉넉히 담아요.',
            estimate: '약 {{count}}장',
          },
          next: '다음',
          manual: '사진을 직접 고를래요',
          layoutStepLabel: '구성 취향',
          layoutTitle: '어떤 느낌으로 만들까요?',
          layoutDesc: '페이지마다 사진과 기록을 살펴보고 선택한 방향에 맞춰 구성해요.',
          layout: {
            autoTitle: '사진에 맞게',
            autoDesc: '사진 비율과 기록을 보고 페이지마다 어울리게 구성해요.',
            photoForwardTitle: '사진을 크게',
            photoForwardDesc: '한 장면을 시원하게 보여주는 페이지를 많이 만들어요.',
            storyTitle: '기록과 함께',
            storyDesc: '사진 기록이 있는 장면은 글이 자연스럽게 보이도록 구성해요.',
            collageTitle: '여러 장면씩',
            collageDesc: '가까운 시간의 사진을 묶어 리듬감 있게 구성해요.',
          },
          loadingCandidates: '기간 사진 {{count}}장을 확인하고 있어요.',
          candidatesReady: '{{count}}장 확인 완료 · 약 {{target}}장 선택 예정',
          noPhotos: '선택한 기간에 사진이 없어요.',
          loadFailed: '기간의 사진을 모두 불러오지 못했어요.',
          create: '사진을 골라 책 구성하기',
          creatingTitle: '사진책의 첫 구성을 만들고 있어요',
          creatingDesc: '비슷한 순간은 정리하고, 기간의 흐름과 사진 기록이 자연스럽게 이어지도록 구성해요.',
          stageCollected: '기간 사진 확인 완료',
          stageSelecting: '대표 장면 고르기',
          stagePreparing: '페이지 구성 준비',
          cancel: '취소',
          errorTitle: '자동 구성을 마치지 못했어요',
          failed: '잠시 후 다시 시도해 주세요.',
          retry: '다시 시도',
          selected: '기간 전체에서 {{count}}장의 순간을 골랐어요.',
        },
        pick: {
          title: '사진책에 담을\n순간을 골라볼까요?',
          addTitle: '사진을 더 골라\n다시 구성해볼까요?',
          addProgress: '{{count}}장 더 고르면 다시 추천하고 배치해요.',
          addReady: '추가 선택이 끝났어요. 다시 추천하고 배치할 수 있어요.',
          cancelAdd: '추가 선택 취소',
        },
        photos: {
          source: '최근 업로드 사진',
          previewSource: '최근 사진 미리보기',
          reload: '사진 다시 불러오기',
          recentRange: '이번 달',
          monthRange: '{{month}} 사진',
          autoPick: '권장 사진 {{count}}장 자동 선택',
          autoAdd: '부족한 사진 자동 추가',
          autoPickDesc: '촬영일순으로 배치하고 나중에 뺄 수 있어요.',
          selectAllLoaded: '로드된 사진 {{count}}장 전체 선택',
          allLoadedSelected: '로드된 사진 모두 선택됨',
          maxSelected: '최대 {{count}}장 선택됨',
          autoLoadMore: '다음 사진 자동 불러오기',
          loadingMore: '사진을 불러오는 중...',
          loadMore: '사진 더 보기',
          loadMoreRetry: '사진 더 보기 다시 시도',
          ddayBadge: 'D+{{count}}',
          recent: '최근',
          pickNumber: '{{number}}번째 사진 선택',
          emptyTitle: '가져올 사진이 없어요.',
          emptyDesc: '앱에 저장된 최근 사진을 찾지 못했어요.',
        },
        album: {
          missingBridge: '앱 사진첩을 연결하지 못했어요.',
          loadFailed: '사진을 불러오지 못했어요.',
          loadMoreFailed: '사진을 더 불러오지 못했어요.',
        },
        tray: {
          hint: '사진책에 담을 사진을 모아두는 중',
          empty: '사진을 탭하면 여기에 쌓여요',
          expandedTitle: '고른 사진 {{count}}장',
          collapse: '접기',
          remove: '선택에서 빼기',
          open: '고른 사진 보기',
          count: '{{count}}장 골랐어요',
        },
        draft: {
          saving: '저장 중',
          saved: '자동 저장됨',
          saveFailed: '저장하지 못했어요',
          continueTitle: '만들던 사진책 이어서 편집',
          continueMeta: '{{title}} · {{photos}}장 · {{pages}}쪽',
        },
        edit: {
          undo: '실행 취소',
          redo: '다시 실행',
        },
        action: {
          reset: '고른 사진을 모두 비웠어요.',
          resetLabel: '선택 초기화',
          autoSelected: '최근 순간에서 {{count}}장을 자동으로 골랐어요.',
          allLoadedSelected: '로드된 사진 {{count}}장을 모두 선택했어요.',
          photoLimitReached: '사진은 최대 {{count}}장까지 선택할 수 있어요.',
          needMorePhotos: '현재 {{count}}장이에요. {{needed}}장까지 더 골라 주세요.',
          addedToPage: '선택한 사진을 원하는 페이지에 추가했어요.',
          removedFromBook: '사진책에서 사진을 뺐어요.',
          photoPositionSaved: '사진 위치를 저장했어요.',
          photoPositionReset: '사진 위치를 자동 배치로 되돌렸어요.',
          keepExistingForPageAdd: '페이지에 새 사진을 추가하는 동안 기존 사진은 유지해 주세요.',
          aiPolishFailed: '기존 편집은 그대로 두었어요. AI 추천만 다시 시도해 주세요.',
          pageDeleted: '페이지를 삭제했어요.',
          titleSuggestionApplied: '제안한 책 제목을 적용했어요.',
          coverPhotoChanged: '표지 사진을 바꿨어요.',
          coverStyleChanged: '내지는 그대로 두고 표지 스타일을 바꿨어요.',
          undo: '이전 편집으로 돌아갔어요.',
          redo: '편집을 다시 적용했어요.',
        },
        cta: {
          pickAtLeastOne: '사진을 골라주세요',
          pickPhotoProgress: '사진 {{current}} / {{needed}}장',
          next: '{{count}}장으로 다음',
          previewNow: '{{count}}장으로 미리보기',
          recompose: '{{count}}장으로 다시 구성',
          planning: '사진책 배치를 정리하는 중...',
          aiRequired: 'AI 배치가 필요해요',
          layoutRequired: '배치를 먼저 준비해 주세요',
          creating: '인쇄용 PDF를 만드는 중...',
          createDraft: '완성본 확인',
        },
        defaultTitle: '우리 아기 사진책',
        create: {
          failed: '인쇄용 PDF를 만들지 못했어요.',
          missingPhoto: '배치에 사용한 사진을 찾지 못했어요. 다시 배치해 주세요.',
          pageCountMismatch: '인쇄 페이지 수가 맞지 않아요. 다시 시도해 주세요.',
          progress: {
            preparing: '인쇄 규격을 준비하는 중',
            rendering: '사진을 페이지에 배치하는 중',
            uploading: '인쇄 페이지를 저장하는 중',
            finalizing: '최종 PDF를 만드는 중',
            count: '{{current}} / {{total}} 페이지',
          },
        },
        subject: {
          loadingTitle: '사진 속 얼굴을 확인하는 중',
          loadingDetail: '얼굴과 사람 위치를 살펴보고 잘리지 않는 배치를 준비하고 있어요.',
          inlineLoading: '얼굴이 잘리지 않도록 사진을 확인하고 있어요. {{current}} / {{total}}',
        },
        template: {
          back: '사진 선택으로 돌아가기',
          badge: 'layout',
          title: '사진책 구성을 확인해주세요',
          subtitle: '{{count}}장 · {{range}}',
          noDate: '날짜 정보 없음',
          recommendedProduct: '추천 상품',
          bookSettings: '책 제목과 크기',
          settingsSummary: '{{spec}} · {{photos}}장',
          coverIncluded: '{{name}} 표지',
          photoCount: '{{count}}장',
          advanced: '책 크기와 상세 정보',
          bookSpec: '책 크기',
          bookTitle: '책 제목',
          bookTitlePlaceholder: '우리 아기 사진책',
          spec: '판형',
          range: '기간',
          minimumPages: '최소 {{count}}p',
          needPhotosTitle: '{{count}}장만 더 고르면 구성이 더 알차져요.',
          needPhotos: '이 판형의 권장 사진은 {{count}}장이에요. 현재 {{current}}장을 골랐어요.',
          addPhotos: '사진 더 고르기',
          loading: '템플릿을 불러오는 중...',
          errorTitle: '템플릿을 불러오지 못했어요.',
          retry: '다시 불러오기',
          loadFailed: '템플릿을 불러오지 못했어요.',
          authRequired: '앱 인증 후 사진책 템플릿을 불러올 수 있어요.',
          missingConfig: 'Sweetbook API 키 설정이 필요해요.',
          empty: '사용할 수 있는 사진책 템플릿을 찾지 못했어요.',
          previewNote: '지금 보이는 배치와 같은 좌표로 인쇄 페이지를 만들어요. 최종화 후 실제 PDF도 다시 확인할 수 있어요.',
          recommendation: {
            loadingTitle: '사진 구성을 살펴보는 중',
            loadingDetail: '사진 방향, 촬영 시간과 장면 구성을 보고 알맞은 책 크기를 고르고 있어요.',
            inlineLoading: '사진에 어울리는 책 크기를 고르는 중이에요.',
            failed: '책 크기를 자동으로 고르지 못했어요.',
            fallback: '자동 추천을 완료하지 못해 기본 크기를 적용했어요.',
            manual: '직접 선택한 책 크기예요.',
            mixedOrientation: '세로·가로 사진이 섞여 있어 다양한 콜라주에 잘 맞아요.',
            recordRich: '사진을 여유 있게 보여주기 편한 크기예요.',
            largeCollection: '사진이 많아 여러 장면을 넉넉하게 담기 좋아요.',
            landscapeRich: '가로 사진을 크게 보여주기 좋은 넉넉한 판형이에요.',
            portraitRich: '세로 사진 비중과 잘 어울리는 판형이에요.',
            lowBlankPages: '선택한 사진 수로 빈 페이지를 가장 적게 만들 수 있어요.',
            balanced: '사진 수와 페이지 구성에 가장 균형이 좋은 크기예요.',
          },
        },
        previewDraft: {
          title: '사진책 배치 결과',
          subtitle: '선택한 사진을 바탕으로 완성한 페이지 구성이에요.',
          editorialLabel: 'AI 편집 방향',
          titleSuggestion: '제안 제목',
          applyTitle: '적용',
          manualEditsSafe: '다시 추천해도 직접 수정한 부분은 유지돼요.',
          addPhotos: '사진 더 추가',
          loadingTitle: '사진책을 구성하고 있어요',
          loadingPrepareDetail: '사진과 책 규격을 함께 살펴보고 있어요.',
          loadingSubjectDetail: '사진을 살펴보고 얼굴이 잘리지 않는 구성을 준비하고 있어요. {{current}} / {{total}}',
          loadingDetail: '사진의 촬영 시간, 방향과 기록을 살펴보고 알맞은 페이지 구성을 만드는 중이에요.',
          loadingPreviewDetail: '완성될 페이지를 마지막으로 확인하고 있어요.',
          errorTitle: '배치 결과를 만들지 못했어요',
          planFailed: '자동 배치를 만들지 못했어요.',
          planUnavailable: '배치에 사용할 페이지 구성을 찾지 못했어요.',
          cover: '표지',
          frontCover: '앞표지',
          coverShort: '표지',
          pageLabel: '내지 {{current}} / {{total}}',
          previous: '이전 페이지',
          next: '다음 페이지',
          estimatedPages: '예상 {{count}}p',
          compactSummary: '{{photos}}장 · {{pages}}쪽 구성 · 인쇄 {{printPages}}p',
          layoutPages: '사진 배치 {{count}}쪽',
          photoCoverage: '선택 {{selected}}장 · 배치 {{used}}장',
          printPages: '인쇄 규격 {{count}}p',
          blankPageNotice: '사진은 {{layoutCount}}쪽에 배치했어요. 실제 인쇄본은 이 판형의 규격에 맞춰 {{printCount}}p가 되며, 남는 내지는 빈 페이지로 채워져요.',
          autoLayout: '자동 배치 최대 {{count}}장/쪽',
          aiLayout: 'AI 자동 배치',
          fallbackLayout: '기본 배치',
          aiFallback: 'AI 추천을 사용할 수 없어 검증된 기본 배치를 보여주고 있어요.',
          retryAi: 'AI로 다시 다듬기',
          sceneCount: '{{count}}개 장면',
          collageCount: '콜라주 {{count}}쪽',
          recordCount: '기록 {{count}}개 반영',
          lowQuality: '해상도가 낮은 사진 {{count}}장은 인쇄 품질이 떨어질 수 있어요.',
          authRequired: '앱 인증 후 미리보기를 만들 수 있어요.',
          loadFailed: '미리보기 정보를 불러오지 못했어요.',
          editor: {
            open: '이 페이지 편집',
            tapPage: '이 페이지 상세 편집',
            tapHint: '페이지를 탭하면 사진, 글, 꾸미기를 자세히 수정할 수 있어요.',
            close: '편집 닫기',
            done: '완료',
            coverEditorTitle: '표지 편집',
            pageEditorTitle: '{{page}}쪽 편집',
            tabsLabel: '페이지 편집 도구',
            tabs: {
              photos: '사진',
              text: '글',
              style: '꾸미기',
            },
            photoTabHint: '이 페이지에 들어갈 사진과 순서, 보이는 위치를 조정해요.',
            textTabHint: '이 페이지에만 표시할 날짜와 이야기를 다듬어요.',
            styleTabHint: '사진 배치와 종이 배경을 이 페이지에 맞게 골라요.',
            coverDetails: '표지 내용',
            bookTitle: '책 제목',
            noDateRange: '사진 날짜가 표지에 표시돼요.',
            coverPhoto: '표지 사진',
            changeCover: '표지 사진 변경',
            changeCoverDesc: '책의 첫인상이 될 사진을 골라주세요.',
            coverSafe: '얼굴이 안전하게 보여요',
            coverCaution: '표지 잘림을 확인해 주세요',
            coverUnsafe: '얼굴이 잘릴 수 있어요',
            coverUnknown: '표지 화면에서 잘림을 확인해 주세요',
            useRecommendedCover: '얼굴이 잘 보이는 추천 사진 사용',
            coverStyle: '표지 스타일',
            coverStyleHint: '내지 구성은 그대로 두고 표지만 바꿔요.',
            recommendedShort: '추천',
            safeShort: '안전',
            cropRiskShort: '잘림',
            checkShort: '확인',
            coverPhotoOption: '표지 사진 {{number}} · {{status}}',
            pagePhotos: '이 페이지 사진 {{count}}장',
            addToPage: '사진 추가',
            fitFaces: '얼굴 맞춤',
            refinePage: '이 페이지 추천',
            heroPhoto: '대표 사진',
            photoNumber: '사진 {{number}}',
            heroShort: '대표',
            editedShort: '편집됨',
            dragPhoto: '사진 {{number}} 순서 이동',
            editPhoto: '편집',
            replaceShort: '교체',
            deleteShort: '삭제',
            setHero: '대표 사진으로 지정',
            moveEarlier: '앞으로 이동',
            moveLater: '뒤로 이동',
            adjustPhoto: '사진 위치 조정',
            position: '위치',
            replacePhoto: '사진 교체',
            removePhoto: '사진책에서 삭제',
            chooseNewPhoto: '앨범에서 새 사진 추가',
            pageText: '페이지 글',
            date: '날짜',
            pageTitle: '제목',
            comment: '코멘트',
            commentPlaceholder: '이 순간에 남기고 싶은 이야기를 적어주세요.',
            focusDate: '날짜 작성',
            focusTitle: '제목 작성',
            focusComment: '코멘트 작성',
            closeWriting: '글 작성 닫기',
            layout: '사진 배치',
            decorate: '페이지 꾸미기',
            backgroundHint: '사진과 글은 그대로 두고 종이 배경색만 바꿔요.',
            pickCover: '표지 사진 선택',
            pickAdd: '현재 페이지로 가져올 사진',
            pickReplace: '교체할 사진 선택',
            moveNotice: '다른 페이지의 사진을 선택하면 이 페이지로 이동해요.',
            swapNotice: '다른 페이지 사진과 자리를 맞바꿔요.',
            coverNotice: '내지 배치는 바뀌지 않아요.',
            closePicker: '사진 선택 닫기',
            fromPage: '{{page}}쪽',
            noMoveCandidates: '가져올 다른 사진이 없어요.',
            addFirstPage: '첫 내지 추가',
            addPageFull: '페이지 추가',
            movePageEarlier: '페이지를 앞으로 이동',
            earlier: '앞으로',
            addPage: '추가',
            deletePageShort: '삭제',
            movePageLater: '페이지를 뒤로 이동',
            later: '뒤로',
            deletePageWithPhotos: '이 페이지와 사진 {{count}}장을 책에서 함께 뺄까요?',
            deleteBlankPage: '이 빈 페이지를 삭제할까요?',
            cancel: '취소',
            confirmDeletePage: '페이지 삭제',
            backgroundOptions: {
              original: '기본',
              cream: '크림',
              blush: '핑크',
              sky: '하늘',
              sage: '그린',
              lilac: '라일락',
            },
            layoutOptions: {
              aspectAware: '사진 비율 맞춤',
              fullBleed: '가득 채우기',
              singleFramed: '여백 있게',
              heroPair: '큰 사진 + 작은 사진',
              splitVertical: '좌우 나누기',
              splitHorizontal: '위아래 나누기',
              trioFocus: '대표 사진 강조',
              trioRow: '나란히 배치',
              quadGrid: '2 x 2 격자',
              storyGrid: '기록형 격자',
              collageFive: '대표 사진 콜라주',
              collageSix: '3 x 2 콜라주',
              evenGrid: '고른 격자',
            },
          },
        },
        result: {
          badge: 'print PDF',
          title: '인쇄용 PDF가 완성됐어요',
          preparingTitle: '인쇄용 PDF를 만드는 중이에요',
          subtitle: '아직 주문하거나 결제하지 않았어요. 수정한 뒤 새 PDF로 다시 만들 수 있어요.',
          pageCount: '페이지',
          estimate: '예상 가격',
          cover: '표지',
          content: '내지 구성',
          automaticContent: 'AI 자동 구성 · {{count}}페이지',
          editLayout: '수정해서 다시 만들기',
          startOver: '이 결과 지우기',
          deleting: '결과를 삭제하는 중...',
          deleteFailed: '결과를 삭제하지 못했어요.',
        },
        preview: {
          cover: '표지',
          contents: '내지',
          open: '열기',
          refresh: 'PDF 상태 새로고침',
          coverTitle: '표지 PDF 미리보기',
          contentsTitle: '내지 PDF 미리보기',
          notReadyTitle: 'PDF를 준비하는 중이에요',
          notReadyDesc: '준비가 끝나면 자동으로 미리보기를 열어드릴게요.',
          note: 'WebView에서 PDF가 보이지 않으면 열기 버튼으로 확인하세요.',
          failed: 'PDF 생성에 실패했어요. 배치를 확인한 뒤 다시 만들어 주세요.',
          refreshFailed: 'PDF 상태를 확인하지 못했어요.',
        },
      },
      printv2: {
        ui: {
          pickTitle: '사진 고르기',
          quantityValue_one: '{{count}}장',
          quantityValue_other: '{{count}}장',
          productSummary: '{{size}} · {{min}}장부터',
          priceSummary: '장당 {{amount}}',
          removePhoto: '사진 빼기',
          editThisPhoto: '이 사진 꾸미기',
          edited: '편집됨',
          previewOpen: '{{number}}번째 완성본 크게 보기',
          previewTitle: '완성본 {{current}} / {{total}}',
          previewClose: '완성본 닫기',
          previewLoading: '완성본을 준비하고 있어요',
          zoomIn: '눌러서 확대',
          zoomOut: '눌러서 축소',
          requestNotice: '현재는 결제 없이 주문을 접수해요.',
          quantityLabel: '{{number}}번째 사진 인화 수량',
          decreaseQuantity: '{{number}}번째 사진 수량 줄이기',
          increaseQuantity: '{{number}}번째 사진 수량 늘리기',
          quantityValue: '{{count}}장',
          selectedCount: '사진 {{photos}}개 · 인화 {{prints}}장',
          minimumRemaining: '{{count}}장 더 필요해요',
          designTitle: '디자인',
          designAppliesAll: '모든 사진에 같이 적용돼요',
          designChange: '변경',
          designDone: '완료',
          removeAllCaptions: '모든 사진 문구 지우기 ({{count}}장)',
          removeAllCaptionsConfirm: '사진 {{count}}장의 문구를 모두 지울까요?',
          removeAllCaptionsAction: '모두 지우기',
          removeAllCaptionsDone: '{{count}}장의 문구를 지웠어요',
          currentPhoto: '{{current}} / {{total}} 사진',
          photoList: '사진 목록',
          previousPhoto: '이전 사진',
          nextPhoto: '다음 사진',
          cropAction: '사진 위치',
          captionAction: '문구',
          shippingLater: '배송비는 주소를 입력하면 나와요.',
          toDecorate: '사진 꾸미기',
          returnToDecorate: '꾸미기로 돌아가기',
          toAddress: '배송지 입력하기',
          reviewPreviewHint: '사진 위치와 문구를 확인해 주세요.',
          previewMore: '{{count}}장 더 보기',
          previewFailed: '미리보기를 불러오지 못했어요',
          previewRetry: '다시 불러오기',
          editPhoto: '{{number}}번째 사진 수정',
          backToStart: '처음으로',
          addressTitle: '배송지 입력',
        },
        draft: {
          saving: '저장 중…',
          saved: '저장됨',
          failedRetry: '저장 못 함 · 다시 시도',
          conflict: '다른 기기에서 수정됐어요',
          deleted: '삭제된 임시저장',
          rejected: '저장할 수 없어요',
          failedDesc: '자동 저장이 멈췄어요. 편집 내용은 이 기기에 남아 있어요.',
          retry: '다시 시도',
          conflictDesc: '최신 내용을 불러오거나 지금 편집을 사본으로 저장하세요.',
          reloadLatest: '최신 내용 불러오기',
          saveCopy: '사본으로 저장',
          deletedDesc: '다른 기기에서 삭제됐어요. 지금 편집은 그대로 있어요.',
          tooLargeDesc: '내용이 많아 저장하지 못했어요. 사진을 줄여 주세요.',
          rejectedDesc: '저장할 수 없는 내용이 있어요. 편집을 바꾸면 다시 저장돼요.',
          defaultTitle: '{{month}}월 사진인화',
          reloaded: '최신 내용을 불러왔어요.',
        },
        recommend: {
          action: '{{count}}장 추천받기',
          loading: '고르는 중',
          undo: '추천 되돌리기',
          none: '추천할 만한 사진을 찾지 못했어요.',
          failed: '추천하지 못했어요. 다시 시도해 주세요.',
        },
        printCheck: {
          face: '얼굴이 {{edges}} 가장자리에서 잘릴 수 있어요',
          lowResolution: '화질이 낮아 흐리게 인화될 수 있어요',
          both: '얼굴이 잘리고 화질도 흐릴 수 있어요',
          summary: '인화 전 확인이 필요한 사진 {{count}}장',
          adjust: '위치 조정',
          review: '확인',
          next: '다음 사진 보기',
          marker: '인화 확인 필요',
          orderFace: '얼굴이 잘릴 수 있는 사진 {{count}}장',
          orderLowResolution: '화질이 낮은 사진 {{count}}장',
        },
        photoViewer: {
          open: '{{label}} 사진 크게 보기',
          close: '크게 보기 닫기',
          select: '이 사진 고르기',
          deselect: '선택 해제',
          loading: '사진을 불러오고 있어요',
          failed: '사진을 불러오지 못했어요',
        },
        collections: {
          entry: '즐겨찾기',
          sourceLabel: '사진 출처',
          regularPhotos: '전체 사진',
          nextPageHint: '여기엔 사진이 없어요. 다음 사진을 불러와 보세요.',
          noPhotos: '이 즐겨찾기에는 인화할 사진이 없어요.',
          loadMore: '다음 사진 불러오기',
          title: '즐겨찾기 선택',
          loading: '즐겨찾기를 불러오는 중이에요',
          loginRequired: '앱에서 로그인하면 즐겨찾기를 볼 수 있어요.',
          retry: '다시 시도',
          loadFailed: '즐겨찾기를 불러오지 못했어요.',
          empty: '즐겨찾기가 없어요.',
          favorites: '즐겨찾기',
          itemCount: '{{count}}개',
        },
        draftAction: {
          title: '임시저장',
          close: '닫기',
          keepEditing: '계속 편집하기',
          viewDrafts: '임시저장 목록 보기',
          loginShort: '로그인 필요',
          needsAttention: '저장 안 됨',
          loginRequired: '앱에서 로그인하면 임시저장할 수 있어요.',
          unsaved: '아직 저장되지 않았어요. 잠시 후 다시 시도해 주세요.',
        },
        draftManager: {
          pending: '저장 대기',
          nameLabel: '임시저장 이름',
          nameInputLabel: '임시저장 이름 입력',
          nameRequired: '이름을 1~80자로 입력해 주세요.',
          cancelRename: '취소',
          confirmRename: '이름 저장',
          rename: '이름 변경',
          renameButton: '이름 변경',
          copyName: '{{title}} 사본',
          failed: '처리하지 못했어요. 잠시 후 다시 시도해 주세요.',
          emptyTitle: '임시저장한 인화가 없어요',
          editing: '편집 중',
          manageLabel: '{{title}} 관리',
          manage: '관리',
          close: '닫기',
          justNow: '방금',
          duplicate: '사본 만들기',
          delete: '삭제',
          deleteTitle: '“{{title}}”을 삭제할까요?',
          deleteDesc: '삭제하면 되돌릴 수 없어요. 원본 사진과 접수된 주문은 그대로예요.',
          cancel: '취소',
          deleteConfirm: '삭제',
          renameLabel: '새 이름',
          copyLabel: '사본 이름',
          renameConfirm: '이름 저장',
          copyConfirm: '사본 저장',
          busy: '처리 중이에요. 잠시만 기다려 주세요.',
          unsaved: '지금 편집을 아직 저장하지 못했어요. 잠시 후 다시 시도해 주세요.',
          changed: '다른 기기에서 바뀌었어요. 최신 내용을 확인한 뒤 다시 시도해 주세요.',
          actionFailed: '변경하지 못했어요. 잠시 후 다시 시도해 주세요.',
          deletedDone: '“{{title}}”을 삭제했어요.',
          renamedDone: '“{{title}}”로 이름을 바꿨어요.',
          copiedDone: '“{{title}}” 사본을 만들었어요.',
          continueEditing: '편집 중인 인화',
        },
        shop: {
          pricePending: '가격 확인 중',
          retryQuote: '다시 시도',
          authRequired: '앱에서 로그인한 뒤 다시 시도해 주세요.',
          quoteFailed: '가격을 불러오지 못했어요.',
          uploading: '주문서를 만들고 있어요',
          uncertain: '주문서를 확인하고 있어요. 중복 주문을 막으려면 잠시 기다려 주세요. 문의할 때는 아래 요청번호를 알려주세요.',
          failed: '주문서를 열지 못했어요. 다시 시도해 주세요.',
          storageUnavailable: '주문 정보를 저장하지 못했어요. 잠시 후 다시 시도해 주세요.',
          requestId: '요청번호: {{id}}',
          estimate: '배송비와 할인은 주문서에서 확인할 수 있어요.',
          tooLarge: '파일이 너무 커요. 사진이나 수량을 줄여 나눠서 주문해 주세요.',
          checkout: '주문서 작성',
        },
        drafts: {
          title: '임시저장',
          loadFailed: '임시저장을 불러오지 못했어요 · 다시 시도',
          collapse: '접기',
          showAll: '전체 보기',
          counts: '사진 {{photoCount}}개 · 인화 {{printCount}}장',
          savedAgo: '{{time}} 저장',
          loadMore: '더 보기',
          loadMoreFailed: '임시저장을 더 불러오지 못했어요.',
          openMissing: '삭제된 임시저장이라 목록에서 뺐어요.',
          openFailed: '임시저장을 열지 못했어요. 잠시 후 다시 시도해 주세요.',
        },
        start: {
          newTitle: '새로 만들기',
          unsavedTitle: '저장되지 않은 편집이 있어요',
          unsavedDesc: '새로 만들면 지금 편집은 사라져요.',
          unsavedBack: '편집으로 돌아가기',
          unsavedDiscard: '버리고 새로 만들기',
          pickPhotos: '사진 고르기',
        },
        orderReview: {
          title: '완성본 확인',
        },
        result: {
          cleanupTitle: '“{{title}}” 임시저장을 남겨둘까요?',
          cleanupFallbackTitle: '이 인화',
          cleanupDesc: '남겨두면 같은 사진으로 다시 주문할 수 있어요.',
          cleanupFailed: '삭제하지 못했어요. 잠시 후 다시 시도해 주세요.',
          cleanupRemove: '삭제',
          cleanupKeep: '남겨두기',
          cleanupDone: '임시저장에서 삭제했어요',
        },
      },
      prints: {
        pick: {
          title: '어떤 순간을\n인화할까요?',
          periodBadge: '사진 고르기',
        },
        steps: {
          label: '진행 단계',
          pick: '고르기',
          review: '확인',
          decorate: '꾸미기',
          order: '주문',
        },
        resume: {
          title: '만들던 인화가 있어요',
          counts: '사진 {{photoCount}}개 · 인화 {{printCount}}장',
          discard: '새로 만들기',
          continue: '이어서 만들기',
          restored: '임시저장을 불러왔어요.',
        },
        review: {
          badge: '선택 확인',
          title: '고른 사진을 확인해 주세요',
          counts: '사진 {{photoCount}}개 · 인화 {{printCount}}장',
          minimumLeft: '최소 수량까지 {{count}}장 남았어요',
          minimumReady: '주문 가능한 수량을 채웠어요',
          copiesHint: '다음 화면에서 사진별 인화 장수를 조절할 수 있어요.',
          remove: '{{number}}번째 사진 빼기',
          empty: '고른 사진이 없어요.',
          addMore: '사진 더 고르기',
        },
        orderReview: {
          badge: '마지막 확인',
          title: '완성본을 확인해 주세요',
          desc: '배송지를 적기 전에 수량과 꾸미기 설정을 한 번 더 확인해요.',
          preview: '선택한 사진 미리보기',
          quantity: '수량',
          counts: '사진 {{photoCount}}개 · 인화 {{printCount}}장',
          finish: '표면',
          frame: '프레임',
          frameValue: '{{number}}번',
          caption: '문구',
          captionCount: '{{count}}장',
          morePhotos: '+{{count}}장',
          sampleHint: '대표 {{count}}장으로 인화 디자인을 확인해요',
          sameDesign: '같은 디자인으로 인화돼요',
          previewItem: '인화 완성본 미리보기',
          longCaptionWarning: '문구가 20자를 넘는 사진이 {{count}}장 있어요',
          unitPrice: '단가',
          amount: '예상 금액',
        },
        periodFlow: {
          start: {
            badge: '사진 인화',
            title: '사진을 어떻게 찾아볼까요?',
            desc: '보고 싶은 사진 범위를 골라 주세요.',
            autoTitle: '기간으로 찾아보기',
            autoDesc: '개월·월·연도로 사진을 좁혀요.',
            manualTitle: '최근 사진 바로 보기',
            manualDesc: '최근 사진부터 바로 골라요.',
            notice: '어느 쪽이든 보관함에 담은 사진은 계속 남아 있어요.',
            back: '처음 선택으로 돌아가기',
          },
          badge: '기간 선택',
          title: '어느 기간을 볼까요?',
          intro: '사진을 찾을 기간 하나만 골라주세요.',
          previewLabel: '최근 사진으로 미리 본 폴라로이드',
          recentThreeMonthsTitle: '최근 3개월',
          recentThreeMonthsDesc: '요즘의 표정과 변화를 골고루 담아요.',
          recommendedBadge: '최근 사진 추천',
          minimumHint: '사진이 충분하다면 {{count}}장 이상 골라둘게요',
          findByPeriod: '다른 기간 보기',
          directBadge: '직접 선택',
          directTitle: '사진을 직접 고를게요',
          directDesc: '사진첩을 보며 마음에 드는 사진을 직접 선택해요.',
          backToSelection: '사진 고르기로 돌아가기',
          loading: '사진을 고르는 중…',
          loadingDesc: '사진을 확인하며 비슷한 장면을 정리하고 있어요.',
          loadingCount: '사진 {{count}}장을 확인하고 있어요',
          collectingCount: '사진 {{count}}장을 모아 살펴보는 중',
          loadingBridge: '사진첩을 연결하고 있어요',
          errorTitle: '좋은 순간을 고르지 못했어요',
          error: '사진을 추천하지 못했어요. 잠시 후 다시 시도해 주세요.',
          noPhotos: '선택한 기간에 사진이 없어요.',
          retry: '다시 시도',
          changePeriod: '기간 변경',
          resultBadge: '자동 선택 결과',
          reviewTitle: '골라둔 사진을\n확인해볼까요?',
          noSuitableReviewTitle: '사진을 함께\n골라볼까요?',
          result: '{{total}}장 중 {{selected}}장을 골라뒀어요',
          resultDesc: '비슷한 장면은 덜고, 기간 전체에서 골고루 담았어요.',
          insufficient: '이 기간의 사진 {{count}}장을 모두 담았어요.',
          insufficientDesc: '최소 {{count}}장까지 사진을 더 고르거나 기간을 넓혀주세요.',
          analysisUnavailable: '사진을 끝까지 확인하지 못했어요. 잠시 후 다시 시도해 주세요.',
          resultAll: '이 기간의 사진 {{count}}장을 모두 선택했어요.',
          resultAllDesc: '아래에서 사진을 자유롭게 추가하거나 뺄 수 있어요.',
          resultAllLimited: '먼저 {{count}}장을 선택했어요.',
          resultAllLimitedDesc: '아래에서 선택한 사진을 확인하고 조정해 주세요.',
          resultNoSuitable: '추천할 사진을 고르지 못했어요.',
          resultHighlights: '{{selected}}장을 골라뒀어요.',
          resultHighlightsInsufficient: '좋은 사진 {{selected}}장을 골랐어요.',
          resultNoAddition: '현재 {{total}}장을 선택했어요.',
          resultWithExisting: '{{added}}장을 더 골라 총 {{total}}장이 됐어요.',
          resultHighlightsDesc: '비슷한 장면은 한 장씩 덜고, 기간 전체에서 골고루 골랐어요.',
          resultHighlightsWithDiaryDesc: '사진 기록이 있는 {{count}}장을 우선하고, 비슷한 장면은 정리했어요.',
          resultHighlightsInsufficientDesc: '최소 수량까지 {{count}}장 더 필요해요. 아래에서 직접 고르거나 다른 기간을 열어보세요.',
          suitableCount: '추천 사진 {{count}}장',
          excludedCount: '직접 확인할 사진 {{count}}장',
          diarySelectedCount: '사진 기록 {{count}}장',
          similarScenesReduced: '비슷한 장면 정리',
          autoFillRemaining: '{{count}}장 추천받기',
          directFillRemaining: '아래에서 {{count}}장 더 고르기',
          autoPickMinimum: '{{count}}장 추천받기',
          backToManualSelection: '직접 고르기로 돌아가기',
          cancelRecommend: '그만두고 직접 고르기',
          recommendAssistDesc: '고른 사진은 그대로 두고 부족한 {{count}}장만 추천해요.',
          recommendAssistAction: '추천받기',
          recommendationPhotoBadge: '추천',
          recommendationPreviewBadge: '추천 확인',
          recommendationPreviewTitle: '{{count}}장을 추천해요',
          recommendationPreviewDesc: '지금 고른 사진은 그대로 두고, 아래 사진만 추가할게요.',
          recommendationDismiss: '직접 더 고르기',
          recommendationApply: '추천 사진 추가',
          recommendationApplied: '추천 사진 {{count}}장을 추가했어요.',
          recommendationDismissed: '추천은 적용하지 않았어요.',
          recommendationUndone: '추천으로 추가한 사진을 뺐어요.',
          undoRecommendation: '추천만 취소',
          widenPeriod: '다른 기간 보기',
          reviewAlbumTitle: '전체 사진에서 더 고르기',
          reviewAlbumHint: '노란 숫자는 선택 순서예요. 사진을 누르면 추가하거나 뺄 수 있어요.',
          addPhotoLabel: '{{label}} 사진 추가',
          removePhotoLabel: '{{label}} 사진 제외',
          mode: {
            title: '사진을 어떻게 고를까요?',
            desc: '추천을 받거나 직접 살펴보며 고를 수 있어요.',
            counting: '사진 세어보는 중',
            photoCount: '{{count}}장+',
            recommended: '추천',
            highlightsTitle: '예쁜 순간만 쏙',
            highlightsDesc: '사람이 잘 보이고 인화했을 때 예쁠 사진을 먼저 추천해요.',
            allTitle: '이 기간 사진 모두 선택',
            allDesc: '이 기간에서 최대 200장까지 선택해요.',
            autoTitle: '{{count}}장 추천받기',
            autoFillTitle: '{{count}}장까지 추천받기',
            continueWithSelection: '현재 선택으로 계속하기',
            continueWithSelectionDesc: '선택한 사진을 확인하고 필요한 부분만 다듬어요.',
            autoDesc: '기록, 표정과 화질을 살펴 사진을 먼저 골라드려요.',
            quantityCurrent: '자동 선택 {{count}}장',
            quantityChange: '수량 변경',
            quantityLabel: '자동 선택 수량',
            quantityBasic: '기본',
            quantityComfortable: '넉넉하게',
            quantityMany: '많이',
            manualTitle: '직접 고르기',
            manualDesc: '사진을 넘겨보며 마음에 드는 것만 직접 선택해요.',
            notice: '자동으로 고른 뒤에도 사진을 자유롭게 더하거나 뺄 수 있어요.',
            autoLoading: '좋은 순간을 고르는 중…',
            autoLoadingDesc: '기록과 표정을 살펴보고 비슷한 장면은 한 장씩 정리하고 있어요.',
            allLoading: '사진을 선택하는 중…',
            allLoadingDesc: '이 기간의 사진을 선택하고 있어요.',
            highlightsLoading: '예쁜 순간을 찾는 중…',
            highlightsLoadingDesc: '기록이 적힌 사진은 먼저 챙기고, 나머지 사진에서 예쁜 순간을 빠르게 찾고 있어요.',
            analysisProgress: '{{completed}} / {{total}}장 넘겨보는 중',
            changeMode: '다른 선택 방법',
          },
          browse: {
            title: '다른 시기도 이어 볼까요?',
            hint: '이미 고른 사진은 그대로 남아 있어요.',
            shortLabel: '다른 시기',
            loadMore: '사진 더 보기',
            loadingMore: '사진을 더 불러오는 중…',
            loadMoreHint: '아래로 내리면 다음 사진을 자동으로 불러와요.',
          },
          picker: {
            badge: '추억 찾기',
            desc: '기간을 고르면 그 시기의 사진으로 바로 이동해요.',
            back: '기간 종류로 돌아가기',
            yearlyTitle: '연도 선택',
            monthlyTitle: '월 선택',
            ageMonthTitle: '월령 선택',
          },
          options: {
            special: '특별한 시기',
            monthly: '월별',
            monthlyDesc: '한 달씩 가볍게',
            ageMonth: '월령별',
            ageMonthDesc: '개월차 성장 기록',
            yearly: '연도별',
            yearlyDesc: '한 해의 변화를 골고루',
            first100: '첫 100일',
            firstYear: '첫돌까지',
            fromBirth: '태어난 날부터',
          },
          period: {
            yearlyTitle: '{{year}}년',
            yearlyDesc: '한 해의 변화를 시간순으로 골라요.',
            monthlyTitle: '{{year}}년 {{month}}월',
            monthlyDesc: '한 달의 일상에서 좋은 순간을 골라요.',
            ageMonthTitle: '{{month}}개월차',
            ageMonthDesc: '이 시기의 표정과 변화를 모아요.',
            first100Desc: '처음 만난 날부터 백일까지 담아요.',
            firstYearDesc: '첫해의 성장 변화를 골고루 담아요.',
            fromBirthDesc: '처음 만난 날부터 지금까지 돌아봐요.',
          },
        },
        photos: {
          source: '최근 업로드 사진',
          previewSource: '최근 사진 미리보기',
          loadingPeriod: '{{period}} 사진을 불러오는 중',
          pickHint: '사진을 눌러 선택해 주세요',
          reload: '사진 다시 불러오기',
          viewLabel: '사진 보기 방식',
          gridView: '사진',
          recordsView: '기록',
          noRecord: '기록 없음',
          recordOverLimit: '문구 20자 초과',
          period: '사진 기간',
          recentRange: '최근',
          monthRange: '{{month}}부터의 사진',
          olderMonth: '이전 달 사진 보기',
          newerMonth: '다음 달 사진 보기',
          autoLoadMore: '다음 사진 자동 불러오기',
          loadingMore: '사진을 불러오는 중...',
          ddayBadge: 'D+{{count}}',
          recent: '최근',
          pickNumber: '{{number}}번째 사진 선택',
          emptyTitle: '사진이 없어요',
          emptyDesc: '앱에 저장된 최근 사진을 찾지 못했어요.',
        },
        album: {
          missingBridge: '앱 사진첩을 연결하지 못했어요.',
          loadFailed: '사진을 불러오지 못했어요.',
          loadMoreFailed: '사진을 더 불러오지 못했어요.',
          requestFailed: '사진을 불러오지 못했어요. (오류 {{status}})',
        },
        tray: {
          empty: '사진을 누르면 여기에 추가돼요',
          expandedTitle: '선택한 사진 {{count}}장',
          collapse: '접기',
          remove: '선택에서 빼기',
          open: '선택한 사진 보기',
          count: '{{count}}장 선택했어요',
          floatingCount: '사진 보관함 · {{count}}장',
          floatingCountDetailed: '사진 {{photoCount}}개 · 인화 {{printCount}}장',
          hint: '눌러서 선택 확인',
          minHint: '{{count}}장부터 주문할 수 있어요',
        },
        decorate: {
          title: '사진을 꾸며볼까요?',
          hint: '사진 속 날짜·기록·스탬프를 직접 눌러보세요',
          hintBase: '옵션을 고르면 바로 미리보기에 반영돼요',
          recordsLoaded: '사진 기록 {{count}}개도 함께 불러왔어요.',
          longRecordMarker: '문구 20자 초과',
          longRecordCurrent: '이 사진 문구는 앞 20자만 인화돼요',
          longRecordSummary: '문구가 20자를 넘는 사진 {{count}}장',
          longRecordLimit: '인화에는 앞 20자만 들어가요.',
          longRecordOriginalSafe: '원본 기록은 그대로 유지돼요.',
          nextLongRecord: '다음 긴 문구 보기',
          nextLongRecordShort: '다음 기록',
          reviewLongRecords: '확인',
          editDiary: '인화 문구 수정',
          addDiary: '문구 추가',
          editDiaryShort: '문구 수정',
          restoreDiaryShort: '문구 다시 넣기',
          diarySaved: '문구를 바꿨어요.',
          diaryRemoved: '문구를 뺐어요.',
          diaryRestored: '원래 기록으로 되돌렸어요.',
          editDate: '날짜 표시 변경',
        },
        diaryEditor: {
          title: '문구 편집',
          safeHint: '원래 기록은 바뀌지 않아요.',
          close: '문구 편집 닫기',
          original: '원래 기록',
          originalCount: '{{count}}자',
          noOriginal: '기록이 없어요.',
          printText: '인화 문구',
          placeholder: '사진과 함께 인화할 문구',
          limit: '최대 20자까지 인화돼요.',
          useOriginal: '기록으로 되돌리기',
          remove: '문구 빼기',
          cancel: '취소',
          apply: '적용',
        },
        chip: {
          frame: '프레임',
          font: '글씨체',
          date: '날짜 문구',
          stamp: '스탬프',
          finish: '표면',
          dateBase: '날짜 표기',
        },
        crop: {
          title: '사진 위치 조정',
          hint: '드래그로 위치를, 두 손가락이나 슬라이더로 크기를 조절해요',
          adjust: '사진 위치 조정',
          zoom: '확대 배율',
          reset: '초기화',
          done: '적용',
          close: '닫기',
        },
        design: {
          lang: { none: '표시 안 함', both: '디데이·날짜', dday: '디데이만', date: '날짜만' },
        },
        polaroid: {
          lang: {
            kor: '한국어',
            eng: 'English',
            jpn: '日本語',
            korSample: '태어난 지 154일  2026.06.03',
            engSample: '2026.06.03  D+154',
            jpnSample: '生まれてから154日  2026.06.03',
          },
          font: {
            writer: '손글씨',
            gothic: '고딕체',
            pen: '펜글씨',
            lineSeedJp: 'LINE Seed JP',
            bizUdpMincho: 'BIZ UDPMincho',
            kiwiMaru: 'Kiwi Maru',
            japaneseSample: 'すくすく育つ毎日',
          },
          stamp: {
            month: '개월',
            ageMonths: '나이+개월',
            year: '나이',
            none: '없음',
            monthSample: '5개월',
            ageMonthsSample: '1歳3ヶ月',
            yearSample: '1歳',
            select: '{{number}}번째 스탬프',
          },
          frame: {
            select: '{{number}}번째 프레임',
            fallback: '기본 프레임으로 보여주고 있어요.',
          },
        },
        finish: {
          matteLabel: '무광',
          glareLabel: '유광',
          matteDesc: '손자국이 덜 보여요',
          glareDesc: '색감이 선명해요',
        },
        quantity: { decrease: '수량 줄이기', increase: '수량 늘리기' },
        unit: { sheet: '장' },
        action: {
          minimumFilled: '최소 주문 장수에 맞췄어요.',
          reset: '모두 비웠어요.',
          saved: '인화 구성을 저장했어요.',
          needPhoto: '사진을 먼저 골라 주세요.',
          resetLabel: '선택 비우기',
          resetPrompt: '고른 사진과 인화 설정을 모두 비울까요?',
          resetCancel: '취소',
          resetConfirm: '모두 비우기',
        },
        config: { fallback: '인화 설정을 불러오지 못해 기본 3x4 설정을 보여드려요.' },
        cta: {
          prev: '이전',
          pickPhoto: '사진을 골라 주세요',
          toDecorate: '{{count}}장 선택 완료 · 다음',
          toReview: '사진 {{photoCount}}개 · 인화 {{printCount}}장 확인',
          editPhotos: '{{count}}개 사진 편집하기',
          needMore: '{{left}}장 더 담으면 주문할 수 있어요',
          belowMinHint: '최소 {{min}}장부터 주문할 수 있어요 · 지금 {{current}}장',
          request: '앱에서 인화 주문',
          requestCount: '{{count}}장 인화 주문',
          toAddress: '{{count}}장 · {{amount}} · 배송지 입력',
          toOrderReview: '완성본 확인',
          toAddressDetailed: '{{count}}장 · {{amount}} · 배송지 입력',
        },
        video: {
          badge: 'MEMORY MOVIE',
          button: '영상으로 뽑기',
          buttonDesc: '인트로·아웃트로가 있는 추억 영상 · 최대 {{count}}장',
          previewLabel: '영상에 담을 폴라로이드 미리보기',
          creatingTitle: '추억 영상을 만드는 중…',
          creatingDesc: '폴라로이드를 한 장씩 넘기며 이어 붙이고 있어요.',
          progress: '{{percent}}%',
          cancel: '그만 만들기',
          saved: '추억 영상을 저장했어요.',
          failed: '영상을 만들지 못했어요. 잠시 후 다시 시도해 주세요.',
          unsupported: '이 기기에서는 영상 저장을 지원하지 않아요.',
          introTitle: '우리의 작은 순간들',
          introSubtitle: '{{count}}장의 폴라로이드로 꺼내보는 추억',
          outroTitle: '오늘의 순간을 오래오래',
          outroSubtitle: '함께한 시간을 한 장씩 담았어요.',
        },
        faceReview: {
          checking: '얼굴 영역 확인 중...',
          title: '얼굴이 잘릴 수 있어요',
          desc: '얼굴이 인화 영역 가장자리에 걸린 사진이에요. 위치를 다시 맞추거나 그대로 진행하세요.',
          faceCrop: '얼굴이 인화 영역의 {{edges}} 가장자리에 걸려요',
          photoOrder: '선택한 사진 중 {{number}}번째',
          faceCount: '감지된 얼굴 {{count}}개',
          adjustOne: '이 사진 다시 맞추기',
          adjust: '다시 맞추기',
          continue: '확인하고 주문서 작성',
          toast: '인화 영역을 다시 맞춰주세요.',
          detectFailed: '얼굴 확인에 실패했어요.',
          detectFailedHint: '다시 확인하거나 확인 없이 진행할 수 있어요.',
          retryCta: '확인 실패 · 다시 시도',
          retry: '다시 확인',
          skip: '확인 없이 진행',
          skipped: '얼굴 자동 확인을 건너뛰었어요. 완성본을 직접 확인해 주세요.',
          edge: {
            left: '왼쪽',
            right: '오른쪽',
            top: '위쪽',
            bottom: '아래쪽',
            outside: '바깥쪽',
          },
        },
        footer: {
          selected: '선택 {{photoCount}}장 · 총 {{printCount}}장',
          minimumLeft: '{{left}}장만 더!',
          ready: '주문 준비 완료',
          progress: '{{printCount}} / {{count}}장',
        },
        address: {
          title: '어디로 보내드릴까요?',
          subtitle: '일본 내 주소로 배송해드려요',
          name: '받는 분 (氏名)',
          namePlaceholder: '山田 花子',
          kana: '후리가나 (フリガナ)',
          kanaPlaceholder: 'ヤマダ ハナコ',
          postal: '우편번호 (〒)',
          postalHint: '7자리를 입력하면 주소가 자동으로 채워져요',
          postalNotFound: '이 우편번호로 주소를 찾지 못했어요. 직접 입력해 주세요.',
          postalFailed: '주소 자동완성에 실패했어요. 직접 입력해 주세요.',
          prefecture: '도도부현',
          prefectureSelect: '선택',
          city: '시구정촌 (市区町村)',
          cityPlaceholder: '千代田区千代田',
          address1: '번지 (番地)',
          address1Placeholder: '1-1-1',
          address2: '건물명·호수 (선택)',
          address2Placeholder: 'マンション名 101号室',
          phone: '전화번호',
          email: '이메일 (선택)',
          requiredField: '필수 입력 항목이에요',
          postalInvalid: '우편번호 7자리를 입력해 주세요.',
          phoneInvalid: '전화번호는 숫자 10~11자리로 입력해 주세요.',
          emailInvalid: '이메일 형식을 확인해 주세요.',
        },
        pay: {
          cta: '주문하기',
          needAddress: '배송지를 입력해 주세요',
          preparing: '주문하는 중…',
          uploading: '사진 올리는 중…',
          uploadTitle: '주문을 보내는 중이에요',
          uploadHint: '화면을 닫지 말고 잠시만 기다려 주세요.',
          uploadProgress: '{{done}}/{{total}}장',
          failed: '주문에 실패했어요. 잠시 후 다시 시도해 주세요.',
          uploadFailed: '주문은 접수됐지만 파일 업로드에 실패했어요. 주문번호 {{orderNum}}로 문의해 주세요.',
          summaryCount: '사진 {{count}}장',
          summaryCountDetailed: '사진 {{photoCount}}개 · 인화 {{printCount}}장',
          summaryUnit: '장당 {{unit}}',
          total: '예상 금액',
          productSubtotal: '상품 금액',
          shippingFee: '배송비',
          totalWithShipping: '합계',
          paymentFeeNotice: '카드 테스트는 엔화 합계를 같은 숫자의 원화 테스트 금액으로 써요. 실제로 청구되지 않아요.',
          selectRegion: '배송 지역을 선택해 주세요',
          testCheckout: '토스 카드 테스트 · {{amount}}',
          cardTest: '카드 테스트',
          withoutPayment: '결제 없이 진행하기',
          testNotConfigured: '테스트 결제가 준비되지 않아 주문하지 않았어요.',
          requestCheckout: '주문 접수',
          testNotice: '카드 테스트는 실제로 청구되지 않아요.',
          tossTestAmount: '테스트 결제 금액: {{amount}}',
          tossOrderName: '사진 인화 테스트',
          testSuccessDesc: '카드 테스트 결제가 완료됐어요. 실제 청구나 인화 제작·배송은 진행되지 않습니다.',
          retryConfirmation: '결제 다시 확인',
          shippingLater: '배송지는 일본 국내 지역별 요금이 적용돼요',
          shippingEstimate: '지역별 배송비 {{min}}〜{{max}}',
        },
        store: {
          productLabel: '인화 상품',
          productKind: '사진 인화',
          productName: '폴라로이드 3×4',
          unitSuffix: '1장 · 세금 포함',
          minLabel: '최소 수량',
          minValue: '{{count}}장부터',
          billingLabel: '요금',
          billingValue: '장당 결제 · 구독 아님',
          shippingLabel: '배송비',
          shippingValue: '일본 국내 지역별 {{min}}〜{{max}}',
        },
        legal: {
          navLabel: '상점 정보',
          scta: '특정상거래법 기반 표기',
          contact: '문의',
          contactLine: '이메일 {{email}}',
          companyLine: '{{shop}} · {{company}} · {{email}}',
          back: '인화 화면으로 돌아가기',
        },
        contact: {
          title: '문의 안내',
          supportTitle: '일본어로 문의하실 수 있습니다',
          supportDescription: 'すくすくぱしゃ 사진 인화 주문에 관한 문의를 이메일과 전화로 받고 있습니다.',
          emailLabel: '고객지원 이메일',
          phoneLabel: '일본어 고객지원 전화번호',
          prepareTitle: '주문 문의 시 보내 주세요',
          prepareDescription: '주문번호(또는 주문 시 이메일 주소)\n문의 내용',
          defectNote: '파손·인쇄 불량·오배송·수량 부족에 관한 문의는 문제를 확인할 수 있는 상품과 포장 사진도 함께 보내 주세요.',
          internationalCallNote: '전화번호는 한국 번호입니다. 이용 중인 통신사에 따라 국제전화 요금이 발생할 수 있습니다.',
        },
        scta: {
          sellerTitle: '사업자 정보',
          shopName: '상점명',
          seller: '판매업자',
          representative: '운영 책임자',
          address: '소재지',
          addressValue: '〒07325 서울특별시 영등포구 의사당대로 83, O2타워 서울핀테크랩 4·5·6·8·17·19F',
          phone: '일본어 고객지원 전화번호',
          email: '고객지원 이메일',
          industry: '업종',
          industryValue: '모바일 애플리케이션 / 소프트웨어',
          business: '사업 내용',
          businessValue: '가족용 사진·아이 성장 기록 앱 운영. 보호자와 가족이 사진·영상·육아 추억을 안전하게 저장, 정리, 공유할 수 있는 서비스를 제공합니다.',
          storeUrl: '스토어 URL',
          productTitle: '상품·서비스 가격',
          product: '상품·서비스 종류',
          productValue: '커스텀 사진 인화(폴라로이드 등)',
          price: '가격대',
          priceValue: '가격은 스토어 페이지의 각 상품에 표시된 대로입니다. 인화는 장당 결제입니다.',
          otherFees: '상품 대금 외 필요 요금',
          otherFeesValue: '배송비는 판매자가 정한 지역별 요금으로, 일본 국내(홋카이도·오키나와 제외) 3,000엔, 홋카이도 4,000엔, 오키나와 5,000엔입니다. 도서 지역은 소속 도도부현 요금을 적용합니다. 이 배송비는 운송사 공시 운임이 아닙니다. 소비세는 상품 가격에 포함되어 있습니다. 토스 카드 테스트 결제로 진행합니다. 엔화 합계와 같은 숫자를 원화(KRW) 테스트 금액으로 사용하며, 환율 변환이 아닙니다. 실제 청구는 없습니다.',
          delivery: '배송/서비스 제공',
          deliveryValue: '주문 후 제작을 시작하는 커스텀 상품으로, 주문 후 7〜14영업일 이내에 발송합니다.',
          methods: '결제 방법',
          methodsValue: '토스페이먼츠 카드 테스트 결제',
          timing: '지급 시기',
          timingValue: '카드 인증 후 테스트 결제를 승인합니다. 실제 청구는 발생하지 않습니다.',
          returnTitle: '반품·취소 정책',
          returnValue: '수주 생산 커스텀 인화 상품이므로 고객 사정에 의한 주문 후 취소·반품·교환은 받지 않습니다. 파손·인쇄 불량·오배송·수량 부족이 있는 경우 상품 도착 후 7일 이내에 {{email}}로 주문번호(또는 주문 시 이메일), 문제 내용, 확인 가능한 상품·포장 사진을 보내 주세요. 확인 후 무상 재제작·교환 또는 누락 수량을 추가 발송하며, 이때 필요한 반송·재배송비는 판매자가 부담합니다. 교환이 어려운 경우 해당 상품 대금을 환불하고, 전량 반품 시 최초 배송비도 환불합니다. 반송은 먼저 고객지원 안내를 받은 뒤 진행해 주세요.',
          supportLanguage: '지원 언어',
          supportLanguageValue: '이메일과 전화로 일본어 문의가 가능합니다. 한국 전화번호로 국제전화 요금이 발생할 수 있습니다.',
        },
        result: {
          checking: '결제 결과를 확인하고 있어요...',
          requestedTitle: '주문이 접수됐어요!',
          requestedDesc: '주문을 확인한 뒤 안내드릴게요.',
          successTitle: '결제가 완료됐어요!',
          successDesc: '인화해서 보내드릴게요.',
          pendingTitle: '입금을 기다리고 있어요',
          pendingDesc: '입금이 확인되면 인화를 시작해요. 기한 안에 결제해 주세요.',
          cancelledTitle: '결제가 완료되지 않았어요',
          cancelledDesc: '고른 사진은 그대로 있으니 다시 시도할 수 있어요.',
          errorTitle: '결제 결과를 확인하지 못했어요',
          errorDesc: '잠시 후 다시 확인해 주세요. 문제가 계속되면 문의해 주세요.',
          orderNum: '주문번호',
          count: '인화 수량',
          countValue: '{{count}}장',
          amount: '결제 금액',
          retry: '다시 시도하기',
          done: '확인',
        },
      },
      announce: {
        title: '🍯 꿀잠 타이밍 베타테스트 안내',
        badge: 'Beta Test',
        feature_title: '✨ 꿀잠 타이밍이란?',
        feature_desc1: '아기의 수면 기록을 분석하여 잠들기 좋은 시간에 알림을 보내드려요.',
        feature_desc2: '기록이 쌓일수록 우리 아기에게 맞는 개인화된 알림으로 발전해요.',
        feature_desc3: '비슷한 또래 아기들의 패턴을 참고하여 처음부터 도움을 드려요.',
        record_title: '📝 수면 기록을 꼼꼼히 남겨주세요',
        record_desc: '수면 기록을 꼼꼼히 등록해 주시면 더 정확한 알림을 받으실 수 있어요.',
        notify_title: '🔔 베타 알림 안내',
        notify_desc: '수면을 기록하면 별도 신청 없이 자동으로 꿀잠 타이밍 알림 대상이 됩니다.',
        note_individual: '꿀잠 타이밍은 날씨 예보처럼 참고해 주세요. 아기의 컨디션, 성장 속도, 환경 등에 따라 실제 수면 타이밍은 달라질 수 있어요. 아기의 수면 신호도 함께 살펴봐 주세요.',
        note_under60: '생후 60일 미만의 아기는 생체시계가 형성되는 시기로, 수면 패턴이 불규칙할 수 있어 예측 정확도가 낮을 수 있습니다.',
        note_medical: '본 서비스는 일반적인 수면 정보를 제공하며, 의료적 조언을 대체하지 않습니다. 아기의 수면에 대한 우려가 있으시면 소아과 전문의와 상담해 주세요.',
        note_guide: '꿀잠 타이밍은 부모님의 판단을 도와드리는 보조 도구입니다.',
        button_label: '🍯 베타테스트 안내 확인',
        close: '닫기',
      },
    },
  },
  en: {
    translation: {
      tabs: {
        today: 'Today',
        week: 'Week',
        interval: 'Rhythm',
        discover: 'Discover',
        aria_label: 'Top tabs',
      },
      customC2: {
        brand: 'Ssukssuk ChalKak family subscription',
        aria: { back: 'Back to gift details' },
        meta: {
          landingTitle: 'A one-year subscription gift for your family',
          giftTitle: 'Apply for a family subscription gift',
        },
        landing: {
          kicker: 'A gift for grandparents',
          title: "Enjoy your\ngrandchild's videos\nwithout interruption",
          lead: 'This one gift lets the whole family enjoy\nvideos up to 10 minutes, with no ads.',
          imageAlt: 'An illustration of family photos popping out of a gift box',
          imageCaption: 'Share with up to 8 family members',
          imageCaptionCount: '{{count}} family members',
          benefitsLabel: 'What this gift includes',
          benefitsBubble: 'Let me walk you through\nthe benefits, one by one.',
          benefitFamilyTitle: 'Upload piled-up photos at once',
          benefitFamilyBody: 'Post all the saved-up photos in one go and enjoy them together.',
          benefitVideoTitle: 'Long videos, no cut-offs',
          benefitVideoBody: 'Watch videos up to 10 minutes without interruption.',
          benefitAdTitle: 'No ads at all',
          benefitAdBody: 'Nothing gets in the way while the whole family watches.',
          priceLabel: 'Special price when you apply now',
          price: 'KRW 69,000',
          originalPrice: 'Regularly KRW 99,000',
          discount: '30% off',
          priceNote: 'That is about KRW 719 per person per month for 8 family members.\nYou can pay right away or ask for a phone call.',
          priceNoteCount: 'That is about KRW {{amount}} per person per month for {{count}} family members.\nYou can pay right away or ask for a phone call.',
          ctaHint: 'You can pay easily by card or Toss.',
          cta: 'Send this gift now',
          next: 'Next',
          prev: 'Go back',
        },
        gift: {
          back: 'Back',
          kicker: "A gift for your child's family",
          title: 'How would you\nlike to proceed?',
          lead: 'You can pay right away,\nor talk with us on the phone first.',
          methodDirectTitle: 'I will pay right away',
          methodDirectHint: 'Pay by card or Toss right here.',
          methodConsultTitle: 'I would like a phone call',
          methodConsultHint: 'We call on weekdays between 10 AM and 6 PM.',
          consultKicker: 'Request a phone call',
          consultTitle: 'Leave your\ncontact details',
          consultLead: 'Just your name and phone number.\nWe will call you.',
          nameLabel: 'Your name',
          namePlaceholder: 'e.g. Youngja Kim',
          phoneLabel: 'Your phone number',
          phonePlaceholder: 'e.g. 010-1234-5678',
          privacyLabel: 'I agree to the collection of personal information for the phone call',
          privacyHint: 'We use the information only to contact you for the consultation.',
          privacyLink: 'Privacy Policy',
          submitting: 'Submitting...',
          submit: 'Request a phone call',
          submitFailed: 'We could not submit your request. Please try again shortly.',
        },
        success: {
          kicker: 'Request received',
          title: 'Your request has\nbeen received',
          lead: 'We will call you at the number you entered.\nWe call on weekdays between 10 AM and 6 PM.',
          home: 'Back to the first page',
        },
        payment: {
          kicker: 'Secure payment by Toss',
          title: 'Choose your\npayment method',
          productName: 'Ssukssuk ChalKak family subscription, 1 year',
          orderName: 'Ssukssuk ChalKak family subscription, 1 year',
          preparing: 'Preparing the payment screen...',
          loadFailed: 'We could not open the payment screen. Please try again shortly.',
          activationHint: 'After payment, the subscription turns on automatically within 5 to 10 minutes.',
          pay: 'Pay now',
          contactKicker: 'Contact details first',
          contactTitle: 'Leave your\ncontact details',
          contactLead: 'Just your name and phone number.\nWe use them to confirm payment and turn on the subscription.',
          contactSubmit: 'Next, pay',
          contactSubmitting: 'Saving...',
          contactFailed: 'We could not save your contact details. Please try again shortly.',
          privacyLabel: 'I agree to the collection of personal information for payment',
          privacyHint: 'We use the information only to confirm payment and set up the subscription.',
        },
        appRequired: {
          title: 'Please open this in the app',
          lead: 'Sign in with Babyfolio to continue\nthe family subscription gift.',
        },
        paymentSuccess: {
          kicker: 'Payment complete',
          confirming: 'Confirming your payment...',
          title: 'Your payment\nis complete',
          activation: 'The subscription will turn on\nautomatically within 5 to 10 minutes.\nThere is nothing more to do.',
          amountLabel: 'Amount paid',
          amountValue: 'KRW {{amount}}',
          methodLabel: 'Payment method',
          receipt: 'View receipt',
          errorTitle: 'We need to check\nyour payment',
          errorLead: 'Something went wrong while confirming the payment.\nPlease reopen this screen or contact customer support.',
          home: 'Back to the first page',
        },
        paymentFail: {
          kicker: 'Payment failed',
          title: 'The payment did\nnot go through',
          lead: 'The payment was not completed.\nPlease try again.',
          retry: 'Try paying again',
          home: 'Back to the first page',
        },
        billingTest: {
          kicker: 'Test checkout · Toss test keys',
          title: 'A monthly photobook\nfrom uploaded baby photos',
          lead: 'We automatically make a photobook from the baby photos\nalready in the app, and send one every month. The first book starts now.',
          autoBadge: 'Auto-made',
          heroAlt: 'A photobook with a baby photo on the cover',
          price: '29,900 won',
          priceUnit: 'every month',
          stepPhotos: 'We gather the baby photos you uploaded',
          stepMake: 'This month’s photobook is made automatically',
          stepMonthly: 'Next month is charged on the same card',
          emailLabel: 'Email',
          emailPlaceholder: 'e.g. name@email.com',
          register: 'Get it monthly · 29,900 won',
          chargeAgain: 'Test next month’s charge',
          lastCharge: 'This month’s photobook was charged {{amount}} won.',
          confirming: 'Registering the card and charging 29,900 won for this month’s photobook...',
          chargeFailed: 'The photobook payment failed.',
          doneTitle: 'This month’s photobook\nhas started',
          doneLead: 'We’ll make this month’s book from uploaded photos,\nand charge the same card automatically next month.',
          loadFailed: 'Could not open the payment window. Please try again shortly.',
          home: 'Back to the product',
          legalCompany: '주식회사 제제미미 대표이사 박미영',
          legalBizNo: '사업자등록번호: 434-81-01803',
          legalMailOrder: '통신판매: 제2025-서울영등포-2692호',
          legalAddress: '주소 : 서울특별시 영등포구 의사당대로 83, 오투타워 서울핀테크랩 4,5,6,8,17,19층(여의도동)',
          legalPhoneLabel: '전화번호:',
        },
        validation: {
          name: 'Please enter at least two characters for your name.',
          phone: 'Please check your phone number.',
          email: 'Please check your email address.',
          privacy: 'Please agree to the collection of personal information.',
        },
      },
      cheese: {
        title: 'Cheese',
        aria: {
          back: 'Go back',
          guide: 'Cheese guide',
          charlie: 'Charlie showing your cheese balance',
        },
        balance: {
          label: 'My cheese',
          expiration: 'Valid for 60 days',
          history: 'View history',
        },
        today: {
          title: "Today's cheese",
          hint: 'Claim before it is gone',
        },
        reward: {
          upload: 'Upload a photo',
          record: 'Add a record',
          invite: 'Invite friends',
          completed: 'Done today',
          daily: 'Earn daily',
          up_to: 'Up to +{{amount}}',
        },
        action: {
          family_piggy_bank: 'Collect cheese with family',
          claim: 'Claim',
          completed: 'Done',
          record: 'Record',
          invite: 'Invite',
          upload: 'Upload',
          coming_soon: 'Soon',
          retry: 'Retry',
        },
        collect: {
          title: 'Collect cheese',
          daily: 'Daily record',
          invite: 'Invite family',
          mission: 'Missions',
        },
        shop: {
          eyebrow: 'GOODS SHOP',
          title: 'Shop photobooks / Prints',
        },
        error: {
          app_required: 'Open this page in the app to see your cheese.',
          partial: 'Some cheese information could not be loaded.',
          claim: 'Could not claim cheese. Please try again shortly.',
        },
        notice: {
          title: 'Please review these important notes.',
          items: {
            reward_expiration: 'Cheese received through Cheese Family and upload lottery rewards is valid for 60 days.',
            history: 'You can review issued cheese under My Page > Cheese History.',
            abuse: 'Fraudulent family invitations may result in cheese being reclaimed and restrictions on Cheese Family participation.',
            change: 'These rewards may change without prior notice due to company circumstances.',
          },
        },
      },
      invite_friend: {
        back: 'Back',
        title: 'Invite Friends',
        hero_eyebrow: 'More when we collect together',
        hero_title: 'Invite your friends\nand collect cheese together',
        reward_aria: 'Friend invitation cheese rewards',
        inviter_label: 'You receive',
        inviter_reward: '+100 cheese',
        inviter_limit: 'Up to 10 times',
        invitee_label: 'Your friend receives',
        invitee_reward: '+200 cheese',
        invitee_limit: 'First time only',
        my_code_title: 'My referral code',
        my_code_desc: 'Send this code so your friend can join right away.',
        my_code_copied: 'Referral code copied.',
        copy: 'Copy',
        share: 'Share with friends',
        share_message: 'Let’s record photos together on Babyfolio. Referral code: {{code}}',
        share_copied: 'Share message copied.',
        phone_title: 'Phone verification required',
        phone_desc: 'Please verify your phone number so cheese can be rewarded safely.',
        phone_action: 'Verify phone',
        received_title: 'Received a referral code?',
        received_desc: 'Enter the 6-character code from your friend.',
        code_placeholder: '6-character code',
        marketing_consent: 'I agree to receive marketing information about invitation rewards and events.',
        register_action: 'Register referral code',
        registering: 'Registering',
        registered_title: 'Referral code registered',
        registered_desc: 'You and your friend received cheese. Start recording photos together.',
        registered_toast: 'Referral code registered.',
        market_title: 'This code cannot be registered',
        market_desc: 'Accounts with a Goods Shop purchase history are not eligible for invitee rewards.',
        app_required_title: 'Available in the app',
        app_required_desc: 'Open this page in Babyfolio to see your referral code.',
        notice_title: 'Please note',
        notice_expiry: 'Cheese earned from invitations expires 90 days after it is issued.',
        notice_limit: 'Invite up to 10 friends and receive cheese.',
        notice_eligibility: 'Invitee rewards are provided once to accounts with no Goods Shop purchase history.',
        retry: 'Try again',
        error: {
          load: 'Could not load referral code information.',
          invalid_code: 'Please check the 6-character referral code.',
          marketing_required: 'Agree to marketing information to receive the reward.',
          register: 'Could not register the referral code. Please try again shortly.',
          copy: 'Could not copy the referral code.',
          share: 'Could not share the referral code.',
        },
      },
      verify_account: {
        back: 'Back',
        title: 'Phone verification',
        hero_eyebrow: 'A secure family connection',
        hero_title: 'Verify your phone\njust once',
        hero_desc: 'A verified number keeps invitation rewards and cheese connected safely.',
        steps_aria: 'Phone verification progress',
        step_phone: 'Phone number',
        step_code: 'Verification code',
        step_done: 'Complete',
        phone_title: 'Enter your phone number',
        phone_desc: 'We will text you a 6-digit verification code.',
        phone_label: 'Phone number',
        phone_placeholder: 'Numbers only',
        send_code: 'Send code',
        change_phone: 'Change',
        code_title: 'Enter the code from the text',
        code_desc: 'Check the 6-digit code sent to {{phone}}.',
        code_label: '6-digit code',
        code_placeholder: 'Enter code',
        verify_action: 'Complete verification',
        resend: 'Send a new code',
        done_title: 'Phone verified',
        done_desc: 'You can now receive family invitation rewards and cheese safely.',
        done_action: 'Return to friend invitation',
        app_required_title: 'Verify in the app',
        app_required_desc: 'Open Babyfolio to connect your account and phone number securely.',
        privacy_note: 'We use this number only to verify your identity and provide invitation rewards.',
        retry: 'Try again',
        error: {
          load: 'Could not load your account information.',
          phone: 'Please check your phone number.',
          send: 'Could not send the code. Please try again shortly.',
          code: 'Please check the 6-digit verification code.',
          expired: 'The verification time expired. Request a new code.',
          complete: 'Could not complete verification. Please try again shortly.',
        },
      },
      report: {
        back: 'Back',
        title: 'Upload report',
        eyebrow: 'Having trouble uploading a video?',
        hero_title: 'Send us the video\nthat didn’t upload',
        hero_desc: 'Just choose the original video — device and account details are included automatically. You can send up to 100MB.',
        pick_video: 'Choose a video',
        pick_hint: 'mp4, mov, and more · max 100MB',
        file_selected: 'Video selected',
        comment_label: 'Comment (optional)',
        comment_placeholder: 'Briefly tell us when the upload failed.',
        submit: 'Send',
        submitting: 'Sending',
        done_title: 'Thanks, we got it',
        done_desc: 'We’ll take a look so uploading feels easier next time. Thank you for your patience.',
        done_action: 'Close',
        app_required_title: 'Please open this in the app',
        app_required_desc: 'You can send the video only from the Babyfolio app.',
        privacy_note: 'We only review the video details themselves, and we don’t separately inspect account or device information. Uploaded content is discarded after one week.',
        error: {
          size: 'Videos must be 100MB or smaller.',
          type: 'Please choose a video file.',
          submit: 'Couldn’t send it just now. Please try again in a moment.',
        },
      },
      // --- 번역 추가 시작 ---
      unit: {
        ml: 'ml',
        min: 'min',
        hour: 'hr',
        times: 'x',
      },
      week: {
        this_week: 'This week',
        no_data: 'No data available.',
        download_failed: 'Download failed.',
        excel_generate_failed: 'Couldn’t create the file.',
        excel_share_failed: 'Couldn’t share the file.',
        excel_filename_prefix: 'ParentingLog_',
        share_title: 'Parenting log',
        share_text: 'Parenting log for {{range}}.',
        default_baby_name: 'My Baby',
        share_excel_title: 'Export',
        excel_sheet_title: 'Export',
        excel_sheet_desc: 'You can select up to 6 months at once.',
        excel_range_label: 'Date range',
        excel_preset_week: 'This week',
        excel_preset_day: 'This day',
        excel_preset_1m: 'Last month',
        excel_preset_3m: 'Last 3 months',
        excel_preset_6m: 'Last 6 months',
        excel_from: 'Start',
        excel_to: 'End',
        excel_range_too_long: 'You can select up to 6 months.',
        excel_export: 'Export',
        excel_fetching: 'Loading records...',
        excel_open_browser: 'Save in browser',
        excel_open_browser_hint: 'If sharing isn’t available, you can save it in the browser.',
        excel_share: 'Share',
        excel_share_hint: 'The file is ready. Tap Share.',
        excel_sheet_records: 'Records',
        excel_sheet_daily: 'Daily',
        excel_col_date: 'Date',
        excel_col_start: 'Start',
        excel_col_end: 'End',
        excel_col_category: 'Type',
        excel_col_volume: 'Amount (ml)',
        excel_col_duration: 'Duration (min)',
        excel_col_memo: 'Note',
        excel_col_feeding_count: 'Feeds',
        excel_col_feeding_volume: 'Feed volume (ml)',
        excel_col_sleep_min: 'Sleep (min)',
        excel_col_nappy_count: 'Diapers',
        excel_col_other_count: 'Other',
        pattern_analysis_title: 'Pattern analysis',
        pattern_analysis_desc: 'Review sleep, feeding, and diaper patterns from the latest 7 days of records.',
        pattern_analysis_loading: 'Analyzing patterns...',
        pattern_analysis_loading_desc: 'This may take a moment when there are many records.',
        pattern_analysis_failed: 'Pattern analysis failed.',
        pattern_analysis_invalid_response: 'Could not read the pattern analysis server response. Please reopen the latest app version. ({{status}}, {{contentType}})',
        pattern_analysis_ai_model: 'AI pattern analysis',
        pattern_analysis_basic_model: 'Basic statistical analysis',
        pattern_analysis_llm_fallback: 'The AI review response did not match the expected format, so basic statistical analysis is shown instead.',
        pattern_analysis_min_days: 'Pattern analysis needs at least {{count}} days with records. You currently have {{current}} days.',
        pattern_analysis_no_items: 'There are no records to analyze.',
        pattern_analysis_retry: 'Analyze again',
        pattern_analysis_model: 'Analysis type',
        pattern_analysis_quality: 'Data quality',
        pattern_analysis_overall: 'Overall review',
        pattern_analysis_category_reviews: 'Category reviews',
        pattern_analysis_category_patterns: 'Category patterns',
        pattern_analysis_category_insights: 'Key patterns by category',
        pattern_analysis_feeding_timeline: 'Feeding timeline',
        pattern_analysis_feeding_intervals: 'Feeding intervals',
        pattern_analysis_daily_routine: 'Daily routine',
        pattern_analysis_daily_flow: 'Daily flow',
        pattern_analysis_count: 'Count',
        pattern_analysis_avg_amount: 'Avg amount',
        pattern_analysis_avg_duration: 'Avg duration',
        pattern_analysis_avg_interval: 'Avg interval',
        pattern_analysis_points: 'Watch points',
        pattern_analysis_flexible_time: 'Flexible',
        pattern_analysis_record_days: '{{current}}/{{total}} days',
        pattern_analysis_days_seen: 'Seen on {{count}} days',
        pattern_analysis_occurrences: '{{count}} times',
        pattern_analysis_avg_duration_value: 'Avg {{duration}}',
        pattern_analysis_feeding_group: 'Feeding',
        pattern_analysis_food_group: 'Food/snacks',
        pattern_analysis_play_group: 'Play',
        pattern_analysis_care_group: 'Health/care',
        pattern_analysis_review: 'Review',
        pattern_analysis_pattern: 'Pattern',
        pattern_analysis_feeding_time: 'Feeding',
        pattern_analysis_interval: 'Interval',
        pattern_analysis_routine: 'Routine',
        pattern_analysis_empty: 'No analysis result to display.',
        summary: 'Weekly summary',
        daily_avg_basis: 'Daily average',
        weekly_total: '{{value}} this week',
        per_day: '{{value}} a day',
        sleep_night: 'Night',
        sleep_nap: 'Nap',
        summary_empty: 'No records this week',
      },
      interval: {
        empty_title: 'No interval data yet',
        empty_desc: 'There are no events matching the selected filters yet.',
        loading_more: 'Loading previous week...',
        overlap_label: '{{time}} overlap',
        interval_label: '{{time}} gap',
        elapsed_label: '{{time}} elapsed',
        overlap_marker: 'Overlap',
        interval_marker: 'Gap',
        elapsed_marker: 'Elapsed',
        now_label: 'Now',
        last_record_label: 'Last record',
        since_last_record: 'Since last record',
        day_today: 'Today',
        day_yesterday: 'Yesterday',
        gap_mode: {
          label: 'Measure gap',
          end_to_start: 'End → Start',
          start_to_start: 'Start → Start',
          end_to_end: 'End → End',
          end_to_start_desc: 'From previous end to next start',
          start_to_start_desc: 'Cycle between starts',
          end_to_end_desc: 'Gap between ends',
        },
      },
      growth_banner: {
        aria_label: 'Growth records guide',
        default_baby_name: 'Baby',
        empty_title: "Curious about {{name}}'s height and weight? 👀",
        empty_desc: 'Add the first record and check growth trend charts',
        empty_cta: "Add {{name}}'s first record",
        stale_title: "It's been {{days}} days since the last record",
        stale_desc: "Update it and check this month's growth",
        recent_title: "{{name}}'s growth record is up to date",
        recent_desc: "Check this month's growth chart",
        delta_title_weight: '{{name}} gained {{amount}}kg',
        delta_title_height: '{{name}} grew {{amount}}cm',
        delta_title_head: "{{name}}'s head grew {{amount}}cm",
        delta_desc_generic: 'Keep checking the growth trend',
        percentile_short: {
          check: 'P{{percentile}} · Check growth 🙏🏻',
          small: 'P{{percentile}} · Smaller side 🌱',
          average: 'P{{percentile}} · Average range 💕',
          big: 'P{{percentile}} · Bigger side 💪🏻',
          very_big: 'P{{percentile}} · Much bigger side 💪🏻',
        },
      },
      import_file: {
        title: 'Import Records',
        badge: 'Record move',
        headline: 'Welcome aboard',
        subtitle: 'Choose an exported file from another baby tracker to prepare the import.',
        status_waiting: 'Waiting',
        status_parsing: 'Parsing',
        status_moving: 'Moving',
        status_importing: 'Saving',
        status_imported: 'Imported',
        status_ready: 'Ready',
        status_error: 'Check needed',
        step_select: 'Select file',
        step_review: 'Review',
        step_import: 'Import',
        breakdown_title: 'By category',
        source_app: 'Other app',
        move_label: 'Record move path',
        from_label: 'Current records',
        from_value: 'Other app',
        to_label: 'New home',
        to_value: 'Babyfolio',
        supported_label: 'Supported file formats',
        unknown_extension: 'File',
        selected_desc: '{{size}} · {{extension}}',
        drop_title: 'Choose a file to move',
        drop_active: 'Drop it here',
        drop_desc: 'TXT, CSV, and ZIP files are supported.',
        target_baby_title: 'Baby to import records for',
        target_baby_desc: 'Records will be imported for {{babyName}}.',
        target_baby_select_label: 'Select baby to import records for',
        target_baby_loading: 'Loading your baby information.',
        target_baby_error: 'Could not load baby information. Reopen this page from the app to check automatically.',
        target_baby_empty: 'Once baby information is available, this shows which baby the records will be imported for.',
        target_baby_no_carer: 'Only babies with care log write permission are shown. No importable baby is available.',
        target_baby_unknown: 'Unnamed baby',
        target_baby_fallback: 'Baby ID {{babyIdx}}',
        moving_file: 'Moving records',
        moving_title: 'Moving records',
        moving_desc: 'We are organizing the records in this file. Please wait a moment.',
        change_file: 'Choose another file',
        remove_file: 'Remove file',
        cta_idle: 'Choose a file to continue',
        cta_ready: 'Import',
        cta_importing: 'Importing',
        cta_done: 'Imported',
        unsupported_title: 'This file type is not supported',
        unsupported_desc: 'This file format cannot be converted automatically yet. Please choose TXT, CSV, or ZIP.',
        too_large_title: 'The file is too large',
        too_large_desc: 'Please choose a file under 50MB.',
        received_title: 'Import complete',
        received_summary: '{{count}} records moved',
        received_desc: 'The records were saved to the selected baby. Refresh the feeding interval tab to see the newly imported records.',
        received_action: 'OK',
        import_progress_title: 'Saving {{current}}/{{total}}',
        import_progress_desc: 'Saving one record at a time. Done {{completed}} · Failed {{failed}}',
        import_failed_title: 'Some records could not be imported',
        import_failed_desc: '{{failed}} of {{total}} records failed to save. Some records may already be saved, so please review before retrying.',
        import_missing_auth: 'Could not verify user authentication. Reopen this page from the app.',
        import_missing_baby: 'Select the baby to save records for first.',
        parse_error_title: 'Let us help check your file',
        parse_failed: 'There was a problem reading the file. Please try a fresh export or contact us below.',
        encoding_error: 'We paused because the file text was difficult to read reliably. Please try exporting it again from the original app, or contact us if this continues.',
        parse_empty: 'We did not find importable records with dates and times. Please check the file or ask us about transfer options below.',
        summary_only_csv_error: 'This file contains your daily totals, such as feeding amounts and counts. Individual times are not included, so we cannot currently transfer it directly into feeding records. We do not assign estimated times, to keep feeding intervals accurate.',
        summary_only_csv_next_step: 'If your previous app offers detailed records with dates and times, please select that export. If it only provides daily totals, contact us below so we can look into possible transfer options together.',
        cta_file_unavailable: 'Please review this file',
        zip_no_parseable_files: 'An error occurred because no importable TXT or CSV file was found inside the ZIP.',
        zip_unsupported_compression: 'An error occurred because this ZIP compression method is not supported yet. Please recompress it as a standard ZIP.',
        error_contact_title: 'Let us help with your records',
        error_contact_desc: 'For help moving your records, please contact us through My Page > Customer Support.',
        unsupported_records_title: 'Some records were kept as memos',
        unsupported_records_desc: '{{count}} items could not be converted automatically and were kept as memos.',
        skipped_records_title: 'Uncertain records were skipped',
        skipped_records_desc: '{{count}} records that could be saved incorrectly were not imported. You can review their source text below.',
        summary_only_csv_title: 'This file contains daily totals',
        summary_only_csv_notice: '{{count}} daily-summary CSV file(s) without individual times were left out of this import.',
        warning_line: 'Line {{line}}',
        warning_show_more: 'Show {{count}} more skipped records',
        warning_reason_summary_only_csv: 'Daily totals without individual record times',
        warning_reason_unsupported_record: 'Unsupported record or content that cannot be interpreted reliably',
        warning_reason_invalid_datetime: 'Date or time could not be verified',
        warning_reason_incomplete_sleep: 'Sleep start, end, or duration could not be verified',
        warning_reason_inconsistent_sleep: 'Sleep times do not match the recorded duration',
        warning_reason_unknown: 'Record could not be converted safely',
        preview_title: 'Conversion preview',
        preview_desc: 'Imported file converted into V2 insert items.',
        preview_source_label: 'Original',
        preview_result_label: 'Record to import',
        parsed_items: 'Items',
        parsed_dates: 'Dates',
        memo_fallbacks: 'Memo',
        missing_baby_idx: 'baby_idx was not found. The real baby identifier must be injected at save time.',
        meridiem_am: 'AM',
        meridiem_pm: 'PM',
        left_side: 'Left',
        right_side: 'Right',
        category_alert: 'Reminder',
        sleep_type_nap: 'Nap',
        sleep_type_night: 'Night sleep',
        purpose_infant_checkup: 'Infant checkup',
        purpose_vaccination: 'Vaccination',
        purpose_medical_visit_issue: 'Clinic visit',
        json_payload_title: 'View save JSON payload',
        privacy_title: 'Your records stay handled with care',
        privacy_desc: 'The selected file is used only for import review.',
      },
      // --- 번역 추가 끝 ---
      common: {
        loading_alt: 'Loading',
        loading: 'Loading...',
        page_load_failed: 'Unable to load this page. Please try again.',
        reload_page: 'Reload page',
        today: 'Today',
        app_title: 'Baby tracker',
        app_only_title: 'Please open in the app',
        app_only_desc: 'This service is only available in the app.',
        close: 'Close',
        week_ago: ' week ago',
        month_ago: ' month ago',
        all: 'All',
        me: 'Me',
      },
      growth_report: {
        title: 'View growth report',
        desc: 'Height, weight, growth trends, and percentiles',
        open_aria: 'View growth report',
      },
      growth_page: {
        title: 'Growth report',
        back: 'Back',
        default_baby_name: 'My baby',
        age_days: '{{count}} days old',
        age_months: '{{count}} months old',
        dday_label: 'D+{{count}}',
        dday_before_label: 'D-{{count}}',
        status: 'Growing steadily',
        measurement_action: 'Measure',
        measurement_base: 'Measured on {{date}}',
        metric_tabs_label: 'Growth metrics',
        metric_empty: 'No measurements yet',
        recorded_base: 'Based on {{date}} record',
        delta_sentence: {
          weight_up: 'Gained {{value}} since the last record',
          weight_down: 'Down {{value}} from the last record',
          height_up: 'Grew {{value}} taller since the last record',
          height_down: 'Measured {{value}} shorter than the last record',
          head_up: 'Head circumference grew {{value}} since the last record',
          head_down: 'Head measured {{value}} smaller than the last record',
        },
        delta_since_date: {
          weight_up: 'Up {{value}} since {{date}}',
          weight_down: 'Down {{value}} since {{date}}',
          height_up: 'Grew {{value}} since {{date}}',
          height_down: 'Measured {{value}} shorter than on {{date}}',
          head_up: 'Head circumference grew {{value}} since {{date}}',
          head_down: 'Head measured {{value}} smaller than on {{date}}',
        },
        milestones: {
          entry: 'Keep a moment',
          entry_aria: 'Keep a moment',
          empty_title: 'Is there a moment you remember?',
          empty_description: 'Save a moment that comes to mind.',
          suggest_row_label: 'Month {{month}} · moments worth keeping these days',
          write_own: 'Write your own',
          past_hint: 'Past moments are welcome too',
          example_badge: 'Example',
          example_desc: 'Moments you keep stack up here. Keep your first one',
          sheet_title: 'Keep a moment',
          sheet_edit_title: 'Edit this moment',
          title_label: 'What was the moment?',
          title_placeholder: 'e.g. First time rolling over',
          date_label: 'Date',
          photo_label: 'Photo from that day',
          visibility_label: 'Who can see this',
          visibility_private: 'Only me',
          visibility_parents: 'Parents',
          visibility_maternal: "Mom's family",
          visibility_paternal: "Dad's family",
          visibility_all: 'Everyone',
          visibility_badge: {
            private: 'Only me',
            parents: 'Parents',
            maternal: "Mom's family",
            paternal: "Dad's family",
            kin: 'Both families',
          },
          photo_attach_label: 'Attach a photo from this day (optional)',
          photo_change: 'Change photo',
          photo_none_title: 'No photos from this day',
          photo_none_desc: 'Words alone are enough',
          when_label: 'When was it?',
          who_label: 'Who can see this?',
          save_cta: 'Keep this moment',
          required: 'Please describe the moment',
          saved: 'A precious moment saved',
          save_failed: 'Could not save. Please try again later.',
          deleted: 'Moment deleted',
          delete_failed: 'Could not delete. Please try again later.',
          delete_confirm: 'Delete this moment?',
          edit_aria: 'Edit {{title}}',
        },
        milestone_presets: {
          first_outing: 'First outing',
          first_smile: 'First smile',
          first_babble: 'First babble',
          first_rollover: 'First rollover',
          first_solid_food: 'First solid food',
          first_sit: 'Sitting up alone',
          first_tooth: 'First tooth',
          first_crawl: 'Crawling',
          first_pull_to_stand: 'Pulling up to stand',
          first_word: 'First word',
          first_step: 'First steps',
          first_run: 'First run',
        },
        update: 'Record',
        moments_title: 'Our moments',
        records_count: '{{count}} moments recorded so far',
        moments_empty_hint: 'Growing moments gather here',
        timeline_latest: 'Latest',
        timeline_group_count: '{{count}} entries',
        timeline_delta_prev: '{{delta}} since last',
        view_profile_photo: 'View profile photo',
        view_record_media: 'View photo/video',
        media_viewer_aria: 'Media viewer',
        media_swipe_hint: 'Swipe up/down',
        slideshow_revisit_count: 'Revisit {{count}} photos and videos',
        slideshow_play: 'Play slideshow',
        slideshow_aria: 'Growth slideshow',
        slideshow_pause: 'Pause',
        slideshow_resume: 'Play',
        slideshow_prev: 'Previous',
        slideshow_next: 'Next',
        slideshow_summary_title: 'Look how much they grew in {{days}} days',
        slideshow_summary_title_short: 'Look how much they have grown',
        slideshow_first: 'First',
        slideshow_latest: 'Latest',
        slideshow_replay: 'Replay',
        slideshow_export: 'Create a growth film',
        slideshow_export_title: 'Keep it as a growth film',
        slideshow_export_subtitle: 'We will weave these moments into one little film',
        slideshow_export_auto_note: 'We picked {{count}} moments for you. Tap to swap in the scenes you love.',
        slideshow_export_limit_note: 'A film can hold up to {{count}} moments',
        slideshow_export_selected: '{{selected}} / {{max}} moments in your film',
        slideshow_export_start: 'Weave {{count}} moments into a film',
        slideshow_export_min: 'Choose at least 2 moments to keep',
        slideshow_export_rendering: 'Weaving your little moments together…',
        slideshow_export_cancel: 'Maybe later',
        slideshow_export_done: 'Your growth film is safely saved',
        slideshow_export_failed: 'We couldn’t finish the film. Please try again later.',
        slideshow_music: 'Music',
        slideshow_music_title: 'Background music',
        slideshow_music_off: 'No music',
        slideshow_music_note: 'It will play through your growth film too',
        slideshow_entry: 'Growth story',
        slideshow_entry_aria: 'Watch the growth story slideshow',
        slideshow_moments_count: '{{count}} moments gathered',
        slideshow_locked_toast: 'The growth story unlocks after {{count}} records with photos',
        slideshow_card_desc: '{{count}} moments gathered · tap to watch',
        slideshow_card_locked_desc: 'Unlocks with {{min}} records with photos',
        slideshow_teaser_title: 'Add {{count}} more photo memories to unlock a growth film',
        slideshow_teaser_desc: 'Record with photos and we will weave them into a little growth film',
        first_extra_title: 'Add the moment of birth too?',
        first_extra_desc: 'Birth info and due date make the curve more accurate',
        first_extra_due_hint: 'Adding the due date helps make the growth curve more accurate. (optional)',
        first_extra_due_placeholder: 'Not set',
        first_extra_birth_label: 'At birth',
        first_extra_birth_is_today: "The date you're recording is the birth date.",
        step_skip: 'Skip',
        step_next: 'Next',
        step_back: 'Back',
        update_soon: 'Growth logging is being prepared in the app.',
        editor_title: 'Log growth',
        editor_edit_title: 'Edit growth record',
        editor_measured_date: 'Measurement date',
        editor_dday_inline: '{{dday}}',
        editor_change_date: 'Change',
        editor_save: 'Save',
        editor_delete: 'Delete',
        editor_delete_confirm: 'Delete this growth record for this date?',
        editor_required: 'Enter at least one of weight, height, or head circumference.',
        editor_date_before_birth: 'Dates before the birthday cannot be selected.',
        editor_date_future: 'Future dates cannot be selected.',
        editor_range_invalid: 'Please check the value range. Weight uses kg; height and head circumference use cm.',
        editor_range_weight: 'Weight should be in kg, greater than 0 and less than {{max}}kg.',
        editor_range_height: 'Height should be in cm, between {{min}} and {{max}}cm.',
        editor_range_head: 'Head circumference should be in cm, between {{min}} and {{max}}cm.',
        editor_bmi_invalid: 'Please check height and weight again. Height should be entered in cm.',
        editor_saved: 'Growth record saved.',
        editor_saved_with_extra_warning: 'Growth record saved. Please check the due date or birth measurements again.',
        editor_deleted: 'Growth record deleted.',
        photo_label: 'Photo',
        photo_optional: 'Optional',
        photo_none_option: 'No photo',
        photo_load_more: 'More',
        photo_add: 'Add photo',
        photo_add_desc: 'Save the measurement moment together',
        photo_attached: 'Photo to save together',
        photo_change: 'Change photo',
        photo_remove: 'Remove photo',
        photo_sheet_title: 'Select photo',
        photo_library_title: 'App photos',
        photo_library_pending: 'Coming soon',
        photo_representative: 'Photo from that day',
        photo_uploaded_title: 'Photos from that day',
        photo_from_day: 'Photo from that day',
        photo_count: '{{count}} photos',
        photo_select_aria: 'Select photo',
        photo_tip: 'You can add a photo or video here too',
        photo_loading: 'Loading photos',
        photo_load_failed: 'Could not load photos. Please try again.',
        photo_retry: 'Reload photos',
        photo_none_for_date: 'No uploaded photos for this date.',
        load_failed: 'Could not load growth records.',
        save_failed: 'Could not save the growth record.',
        delete_failed: 'Could not delete the growth record.',
        profile_incomplete: 'Baby birthday or sex is missing, so growth percentiles cannot be calculated.',
        birthday_required_title: 'Baby birthday is needed',
        birthday_required_desc: 'Add the baby birthday in My Page to view growth records and percentile calculations.',
        birthday_chip: '{{date}}',
        due_date_chip: 'Due {{date}}',
        profile_settings_aria: 'Set growth reference information',
        profile_settings_title: 'Growth reference information',
        profile_settings_desc: 'Add the due date to make growth analysis more accurate.',
        precision_hint: 'Add the due date to make growth analysis more accurate.',
        precision_hint_saved: 'Due date {{date}} has been saved as reference information.',
        due_date_label: 'Due date',
        due_date_empty: 'No due date entered yet.',
        due_date_clear: 'Clear',
        profile_settings_saved: 'Due date saved.',
        profile_settings_save_failed: 'Could not save the due date.',
        empty_records: 'No growth records saved yet.',
        summary_empty_title: 'Add weight, height, or head circumference',
        empty_title: 'No growth records yet',
        empty_desc: 'Enter any one value you know: weight, height, or head circumference.',
        empty_cta: 'Start tracking',
        empty_collage_title: 'These precious moments \u2014 capture height and weight too',
        empty_collage_desc: 'Log growth alongside photos to see your baby\u2019s growth journey at a glance.',
        empty_eyebrow: 'Your growth story',
        empty_hero_title: "Start {{name}}'s growth story",
        empty_hero_desc: 'Weight, height, or head — one entry and the story begins.',
        edit_record_aria: 'Edit growth record for {{date}}',
        create_report: 'Create report',
        create_report_aria: 'Create growth report',
        create_report_soon: 'Growth report creation is being prepared in the app.',
        report_builder: {
          entry_desc: 'Turn this month’s photos and notes into one keepsake',
          title: 'Create growth report',
          intro_heading: 'Shall we keep {{name}}’s\nrecent days in one image?',
          intro_desc: 'Pick one photo and a few recent records, and AI draws them in a handwritten style.',
          intro_sample: 'Example made by another family',
          intro_sample_alt: 'Example growth report',
          intro_meta: '4 steps · about a minute',
          start: 'Get started',
          back: 'Previous step',
          next: 'Next',
          step_of: '{{step}} / {{total}} · {{label}}',
          step_period: 'Period',
          step_photo: 'Photo',
          step_records: 'Records',
          step_finish: 'Finish',
          period_question: 'Which time should we capture?',
          period_hint: 'Tap one to move on.',
          preset_this_month: 'This month',
          preset_last_month: 'Last month',
          preset_three_months: 'Last 3 months',
          preset_custom: 'Choose dates',
          preset_custom_desc: 'Set a start and end date',
          start_date: 'Start date',
          end_date: 'End date',
          period_error: 'Choose a period up to today and no longer than one year.',
          photo_question: 'One photo for the center',
          photo_hint: '{{count}} photos in this period · the latest growth-record photo is preselected.',
          photo_hint_empty: 'No attached photos in this period. You can use one photo from your phone.',
          upload_tile: 'Upload my photo',
          local_photo_label: 'Uploaded photo',
          records_question: 'We’ll include these records',
          records_hint: 'Tap × to remove one, or the answer count to switch to another family member’s answer.',
          records_count: '{{count}} / {{max}} included',
          answers_count: '{{count}} answers',
          records_layout_hint: 'They’re handwritten around the photo. Removed slots simply stay empty.',
          option_sheet_title: '{{label}} · choose an answer',
          option_sheet_remove: 'Remove this record',
          add_record: 'Add {{label}}',
          remove_record: 'Remove {{label}}',
          records_empty: 'There are no records in this period yet. You can still create it with a photo.',
          finish_question: 'Title and a note',
          finish_hint: 'The note is optional.',
          title_label: 'Title · handwritten above the photo',
          note_label: 'A note from the family · optional',
          note_placeholder: 'e.g. Thank you for showing us something new every day',
          summary: '{{period}} · 1 photo · {{count}} records',
          summary_hint: 'Go back to a previous step to change anything',
          create: 'Create with these',
          check_status: 'Check if it is ready',
          default_title: '{{name}}’s growth story',
          ai_notice: 'Your selected photo and records are sent to AI to create the image.',
          not_ready: 'Growth reports are being prepared. You can preview the record selection now.',
          retry: 'Try again',
          generating_title: 'Drawing {{name}}’s story',
          generating_desc: 'We are carefully arranging the photo and records into one image. This may take a moment.',
          complete_title: 'Growth report ready',
          complete_desc: 'A precious month with {{name}}, all in one image.',
          complete_desc_period: 'A precious chapter of {{name}}’s growth, all in one image.',
          review_result: 'Please check the photo and text before saving.',
          result_alt: '{{name}}’s growth report',
          save: 'Save image',
          saving: 'Saving…',
          share: 'Share',
          sharing: 'Sharing…',
          saved: 'Image saved.',
          delivery_failed: 'Could not deliver the image. Please try again.',
          filename: 'growth-report',
          select_photo_date: 'Select photo from {{date}}',
          error_photo_type: 'Choose a JPG, PNG, or WebP image.',
          error_photo_size: 'This photo is too large to prepare. Please choose another one.',
          error_timeout: 'The report needs more time. Please try again in a moment.',
          error_permission: 'You do not have permission to create a report for this baby.',
          error_daily_limit: 'You have made all available reports for today. Please come back tomorrow.',
          error_busy: 'Another growth report is already being created. Check again in a moment.',
          error_provider_limit: 'There are many requests right now. Please try again shortly.',
          error_generic: 'Could not create the report. Your choices are still here, so you can try again.',
        },
        birth: 'Birth',
        month_short: '{{count}}mo',
        current_value: 'Current {{value}}',
        growth_since_birth: 'Up {{value}} since birth',
        growth_since_first_record: 'Up {{value}} since the first record',
        growth_since_previous_record: 'Change since previous record: {{value}}',
        legend_median: 'Peer average',
        legend_range: 'Typical range',
        compare_title: 'Peer comparison',
        compare_subtitle: 'Where your baby stands among 100 peers',
        corrected_age_caption: 'Based on corrected age ({{date}})',
        corrected_age_caption_plain: 'Based on corrected age (adjusted for due date)',
        profilebook: {
          eyebrow: 'Our Baby: 100 Questions',
          intro_title: '{{name}} Lately,\nTogether',
          intro_description: 'From favorite things to little habits,\nlet’s write it together, one answer at a time.',
          intro_hint: 'Choose a question to answer. Tap an answered card to see past answers.',
          skip: 'Take a look first',
          start: 'Write about {{name}} lately',
          title: '{{name}} Lately',
          heading: '{{name}} Lately,\nTogether',
          question_progress: '{{answered}} of {{total}} questions filled in',
          subtitle_empty: 'From favorite things to little habits, told through our answers.',
          subtitle_filled: 'From favorite things to little habits, told through our answers.',
          intro: 'Guide',
          intro_link: 'About this page',
          empty_heading: 'Where should we begin?',
          empty_hint: 'Pick just one question you like.',
          cards_hint: 'Tap a card to see all the answers shared so far.',
          first_answer: 'Add first answer',
          first_answer_aria: 'Add the first answer to {{question}}',
          add_question: 'Add question',
          filters_label: 'Question types',
          question_likes: 'What do they love most lately?',
          question_dislikes: 'What do they dislike lately?',
          question_hobby: 'What play are they into lately?',
          question_talent: 'What are they newly good at?',
          history_title: 'Question history',
          compose_new: 'Add a new answer',
          compose_edit: 'Edit this answer',
          compose_question: 'Create a question',
          compose_save: 'Add my answer',
          profile_of: '{{name}} Lately',
          author_note: 'By {{author}}',
          author_preserve_note: 'By {{author}} · Earlier answers stay',
          preserve_note: 'Earlier answers stay here, too.',
          save_complete: 'Your answer was saved.',
          custom_category: 'Our own question',
        },
        now_profile: {
          back_to_profile: '{{name}} Lately',
          back_to_moments: 'Our moments',
          timeline_badge: 'Lately',
          timeline_open_aria: '{{question}}: {{answer}} · View answer history',
          question_history_description: 'Answers shared by your family.',
          answer_count_one: '{{count}} answer',
          answer_count_other: '{{count}} answers',
          newest_first: 'Newest first',
          show_all_answers: 'Show all answers',
          edit_answer: 'Edit answer',
          open_question_history: 'View answers for {{question}}',
          author_me_only: 'Author: me',
          delete_failed: 'Could not delete this answer. It is still saved. Please try again.',
          delete_conflict_error: 'This answer has changed. Review the latest version before deleting.',

          cancel_delete_record: 'Cancel',
          question_records: 'History for this question',
          filter_question: 'Question',
          all_questions: 'All questions',
          history_count_one: '{{count}} answer · Newest first',
          history_count_other: '{{count}} answers · Newest first',
          reset_filters: 'Show all records',
          edit_record_short: 'Edit',
          delete_record_short: 'Delete',
          delete_record_only_hint: 'Only this answer will be deleted. The question and other answers stay.',
          deleting_record: 'Deleting…',
          confirm_delete_record: 'Delete answer',

          all_records: 'All records',
          overview_title: '{{name}} lately',
          history_heading: 'Stories about {{name}}',
          filter_author: 'Filter records by author',
          filter_all: 'All',
          filter_mine: 'My answers',
          filter_family: 'Family answers',
          history_empty: 'No records to show here yet.',
          mine_answer: 'Written by me',
          family_answer: 'Written by family',
          unknown_answer: 'Author unknown',
          mom_answer: 'Written by Mom',
          dad_answer: 'Written by Dad',
          add_my_answer: 'Add my answer',
          answer_menu: 'Answer options',
          create_my_answer_title: 'Add my answer',
          edit_my_answer_title: 'Edit my answer',
          answer_heading_my: '{{baby}} through my eyes',
          answer_description_my: 'Share everyday moments with your family.',
          me_short: 'Me',
          author_me: 'Author: me · {{author}}',
          save_my_answer: 'Save my answer',
          save_latest_hint: 'Your new answer will appear on this page.',
          save_history_hint: 'This dated answer will be added to All records.',
          save_preserves_history_hint: 'Previous answers stay in All records.',

          close: 'Close',
          likes_label: 'Likes',
          dislikes_label: 'Dislikes',
          hobby_label: 'Hobby',
          talent_label: 'Talent',
          add_question: '+ Add a question',
          question_placeholder: 'Write a question',
          answer_placeholder: 'Tap to write',
          history_title: 'Answer history',
          view_records_one: 'View {{count}} record',
          view_records_other: 'View {{count}} records',
          records_title: 'Answer history',
          records_hint: 'Check the date and author, then choose a record to view.',
          viewing_earlier: 'Viewing an earlier record',
          back_to_latest: 'View latest answer',
          latest_record: 'Latest record',
          remove_question: 'Remove question',
          open_aria: 'Open {{name}} Lately',
          entry_hint: 'Tap the photo to open\n{{name}} Lately.',
          overview_hint: 'The most recent answer to each question.',
          loading: 'Loading answers.',
          load_failed: 'Could not load answers. Please reconnect and try again.',
          retry: 'Try again',
          save_failed: 'Could not save. Your text is still here, so please try again.',
          conflict_error: 'This changed on another screen. Your text is still here. Close and check the latest answer.',
          permission_error: 'You do not have permission to change this. You can add what you noticed as a new answer.',
          legacy_available: 'Earlier records are on this device',
          legacy_import_hint: 'Import answers saved on this device to share them with your family.',
          import_legacy: 'Import earlier records',
          legacy_author: 'Earlier record',
          author_mom: 'Mom',
          author_dad: 'Dad',
          author_family: 'Family',
          author_with_role: '{{role}} · {{name}}',
          question_by: 'Question by {{name}}',
          question_author_suffix: '\'s question',
          current_answer: 'Now',
          new_answer_short: 'New answer',
          first_answer_hint: 'Tell us about your baby',
          choose_answer: 'Who noticed this?',
          saving: 'Saving…',
          empty_answer: 'Waiting for the first answer',
          new_answer: 'Add a new answer',
          first_answer: 'Write the first answer',
          first_record_hint: 'First record',
          more_records: '+{{count}} more',
          question_menu: 'Question menu',
          rename_question: 'Rename question',
          new_answer_title: 'Add a new answer',
          edit_answer_title: 'Edit this answer',
          new_question_title: 'Add another question',
          rename_question_title: 'Rename question',
          answer_label: 'Answer',
          question_label: 'Question',
          answer_required_error: 'Write a short answer.',
          question_required_error: 'Write a short question.',
          character_limit_error: 'You can enter up to {{limit}} characters, including spaces.',
          two_line_error: 'Make it a little shorter so it fits within two lines in the speech bubble.',
          save_answer_action: 'Save this answer',
          answer_editor_heading: 'Capture {{name}} today',
          question_editor_heading: 'More to discover about {{name}}',
          answer_editor_subtitle: 'Leave a few words for your baby.',
          question_editor_subtitle: 'Create a question just for your family.',
          custom_question_label: 'Your own question',
          two_line_hint: 'Including spaces · Up to 2 lines',
          character_count: '{{count}} / {{limit}}',
          author_writes_as: 'Post as {{author}}',
          question_examples_title: 'How about one of these?',
          question_examples_hint: 'Choose one, then make it yours',
          question_example_play: 'Favorite way to play?',
          question_example_sleep: 'Before-bed routine?',
          question_example_smile: 'What makes you smile?',
          observed_at_label: 'When was this true?',
          observed_at_hint: 'You can add an earlier moment even when recording it today.',
          add_record_action: 'Add new record',
          save_edit_action: 'Save edited answer',
          add_question_action: 'Answer this question',
          save_question_action: 'Save question',
          edit_record: 'Edit this answer',
          delete_record: 'Delete this answer',
          delete_record_confirm: 'Delete only this record?',
          delete_question_confirm: 'Delete “{{question}}” and all of its records?',
          duplicate_question_error: 'This question already exists. Try another name.',
          date_required_error: 'Choose a date.',
          future_date_error: 'Future dates cannot be recorded.',
          before_birthday_error: 'Choose a date on or after the birthday.',
          discard_changes_confirm: 'Discard your changes and close?',
          storage_failed: 'This device could not save your answer. Please check again before closing.',
          storage_recovery_warning: 'We could not read this device’s earlier records. The original stays untouched, and new answers will be saved to your shared family records.',
        },
        band: {
          small: 'Smaller side',
          average: 'Average range',
          big: 'Larger side',
        },
        peer_comparison_label: 'Compared with peers',
        percentile_scale_end: '100th percentile',
        percentile_summary: {
          check: 'P{{percentile}} · Check growth',
          small: 'P{{percentile}} · Smaller side',
          average: 'P{{percentile}} · Average range',
          big: 'P{{percentile}} · Bigger side',
          very_big: 'P{{percentile}} · Much bigger',
        },
        percentile_description: {
          check: 'This measurement may need a growth check.',
          small: 'Smaller than peers of the same age.',
          average: 'Within the average range for peers of the same age.',
          big: 'Bigger than peers of the same age.',
          very_big: 'Much bigger than peers of the same age.',
        },
        percentile_peer_label: 'Among 100 peers',
        percentile_pill: {
          check: 'P{{percentile}} · Check-up',
          small: 'P{{percentile}} · Smaller side',
          average: 'P{{percentile}} · Average range',
          big: 'P{{percentile}} · Bigger side',
          very_big: 'P{{percentile}} · Much bigger',
        },
        percentile_comment: {
          unknown: 'Save a growth record and\nwe will show the peer comparison.',
          check: 'Percentile {{percentile}} overall —\na growth check-up is recommended. 🙏🏻',
          small: 'Percentile {{percentile}} overall —\nsmaller than peers. 🌱',
          average: 'Percentile {{percentile}} overall —\nwithin the peer average range 💕',
          big: 'Percentile {{percentile}} overall —\nbigger than peers. 💪🏻',
          very_big: 'Percentile {{percentile}} overall —\nmuch bigger than peers. 💪🏻',
        },
        records_title: 'Measurements',
        date: 'Date',
        metrics: {
          weight: 'Weight',
          height: 'Height',
          head: 'Head',
        },
      },
      care_visual_settings: {
        title: 'Display settings',
        subtitle: 'Keep category colors and change only how slices are distinguished.',
        default: {
          title: 'Default display',
          desc: 'Show categories primarily by color.',
        },
        distinct: {
          title: 'Enhanced distinction',
          desc: 'Show patterns and markers so similarly colored slices are easier to tell apart.',
        },
        marker_categories: {
          title: 'Which categories should show markers?',
          desc: 'Add small markers to slices from the selected categories.',
        },
      },
      category: {
        mothers_milk: 'Breast milk',
        formula: 'Formula',
        baby_food: 'Baby food',
        nappy: 'Diaper',
        sleep: 'Sleep',
        pumping: 'Pumping',
        stored_mothers_milk: 'Bottle-fed breast milk',
        hospital: 'Hospital',
        bath: 'Bath',
        memo: 'Memo',
        temperature: 'Temperature',
        medication: 'Medication',
        snack: 'Snack',
        tummy_time: 'Tummy time',
        play: 'Play',
      },
      today: {
        no_baby_idx: 'Baby identifier not found. Please reopen from the app.',
        quick_actions: 'Shortcuts',
        share_excel_aria: 'Export',
        tap_to_show_details: 'Tap to show details',
        tap_to_hide_details: 'Tap again to enlarge',
        previous_day_aria: 'Previous day',
        next_day_aria: 'Next day',
        pattern: {
          title: 'Daily pattern',
          no_data: 'Your daily pattern will appear as records build up.',
          sleep_prefix: '{{sleep}} of sleep · ',
          sleep_only: '{{sleep}} of sleep in total.',
          sleep_split_only: '{{sleep}} of sleep was recorded across {{count}} sessions.',
          sleep_record_only: 'Sleep records logged: {{count}}.',
          activity_count_only: '{{prefix}}Care records: {{count}}.',
          activity_sparse: '{{prefix}}Care records in the {{period}}: {{count}}.',
          activity_peak: '{{prefix}}Care records were most frequent in the {{period}}.',
          activity_double_peak: '{{prefix}}Care records clustered in the {{first}} and {{second}}.',
          activity_spread: '{{prefix}}Care records were spread evenly across {{periods}}.',
          activity_across: '{{prefix}}Care records were logged across {{periods}}.',
          period: {
            dawn: 'early morning',
            morning: 'morning',
            afternoon: 'afternoon',
            evening: 'evening',
          },
        },
        summary: {
          title: 'Rhythm summary',
          subtitle: 'Intervals and amounts at a glance',
          no_data: 'No records',
          no_interval: '-',
          interval: {
            sleep: 'Awake for',
            feeding: 'Feeding gap',
            nappy: 'Diaper gap',
            pumping: 'Pumping gap',
          },
          sleep: 'Sleep',
          feeding: 'Feeding & meals',
          nappy: 'Diaper',
          pumping: 'Pumping',
          total_sleep: 'Total sleep',
          sessions: 'Sessions',
          total_intake: 'Total intake',
          total_feeding: 'Total feeding',
          nappy_total: 'Diaper changes',
          nappy_detail: 'Poop / Pee',
          total_pump: 'Total pumped',
          avg_pump: 'Avg / session',
        },
        history: {
          no_records_for_date: 'No records yet for {{date}}',
          load_previous: 'Load previous day\'s records',
          yesterday: 'Yesterday',
          today: 'Today',
          tomorrow_first_record: 'First record tomorrow',
        },
      },
      compare: {
        toggle: 'Compare',
        comparing: 'Comparing',
        yesterday: 'Yesterday',
        week_ago: '1 week ago',
        month_ago: '1 month ago',
        select_date: 'Choose date',
        exit: 'End comparison',
      },
      stats: {
        compare_title: 'Compare daily records',
        times: 'x',
        hour: 'hr',
        minute: 'min',
        volume: 'Volume',
        duration: 'Duration',
        increase: 'increase',
        decrease: 'decrease',
      },
      nappy: {
        poo: 'Poop',
        fee: 'Pee',
      },
      discover: {
        toast_on: '✨ You will be the first to get updates!',
        toast_off: 'Notifications turned off',
        toast_save_failed: 'Failed to save to server',
        badge_ai_insight: 'AI-powered parenting insight',
        hero_title_line1: 'With Babyfolio AI',
        hero_title_line2: "discover your baby's rhythm",
        hero_desc_line1: 'Log sleep or feeding and AI analyzes the rhythm,',
        hero_desc_line2: "then sends timing alerts automatically. Try what's live below, and preview what's coming next.",
        btn_notify_on: 'Notifications enabled',
        btn_notify_off: 'Get notified on launch',
        notify_confirm: 'We will let you know as soon as this launches',
        section_available: 'Available now',
        section_coming: 'Coming soon',
        section_coming_time: 'Planned for H1 2025',
        section_coming_badge: 'In progress',
        tag_coming_soon: 'Coming Soon',
        section_more_title: 'Grow together with Babyfolio',
        section_more_desc_line1: 'Daily records come together to build',
        section_more_desc_line2: 'a special growth story just for your baby',
        cards: {
          sleep_insight: {
            title: 'Feeding interval pattern analysis',
            desc: 'We find the rhythm from feeding time and intervals',
            details: 'Machine learning-based analysis'
          },
          feeding_rhythm: {
            title: 'Personalized feeding guide',
            desc: 'Understand your baby’s feeding pattern and grow together',
            details: 'Personalized insights'
          },
          growth_insight: {
            title: 'Growth and development insights',
            desc: 'Analyze your baby’s growth and development patterns',
            details: 'Data-driven analysis'
          }
        }
      },
        story: {
          meta: {
            home_title: 'Babyfolio - Your baby\'s day',
            record_title: 'Record - Babyfolio',
          },
          home: {
            brand: 'Babyfolio',
            logo_alt: 'Logo',
            hero_title_line1: '📹 Capture your baby\'s day',
            hero_title_line2: 'as a 5-second video',
            hero_desc: 'Upload a landscape video or record one in landscape.',
            split_count_suffix: ' slots',
          },
        landing: {
          intro: {
            aria_label: 'Babyfolio intro video',
            dismiss_hint: 'Double-tap to close — you won\'t see this again',
          },
        },
        common: {
          back: 'Back',
          capture: 'Capture',
          upload: 'Upload',
          close: 'Close',
        },
        capture: {
          ready: 'Ready to record',
          recording: 'Recording... 5 sec',
          guide_title: 'Keep it inside this area',
          guide_description: 'The bright area will be used in the final story',
          switch_camera: 'Switch camera',
        },
        draft: {
          saving: 'Saving draft…',
          saved: 'Draft saved',
          retention_note: 'Kept for up to {{days}} days',
          save_failed: 'Failed to save draft',
          load_failed: "Couldn't load your saved draft",
          retry: 'Retry',
        },
        crop: {
          title: 'Adjust visible area',
          description: 'Drag to position, then adjust zoom',
          apply: 'Apply',
          adjust: 'Adjust',
          zoom: 'Zoom',
          reset: 'Center',
          visible_area: 'Final visible area',
        },
        frame: {
          title: 'Frame',
        },
        frame_style: {
          none: 'None',
          white: 'White',
          rainbow: 'Rainbow',
          pastel: 'Pastel',
        },
        play_mode: {
          title: 'Playback',
          simultaneous: 'Together',
          sequential: 'In order',
          sequential_reveal: 'Reveal',
        },
        preview: {
          title: 'Preview',
          open: 'Preview & download',
          download: 'Download video',
          exporting: 'Exporting...',
          created_by: 'created by',
          filename_prefix: 'babyfolio_day_',
        },
        export: {
          loading: {
            preparing: {
              title: 'Getting things ready',
              subtitle: 'Loading fonts and media',
            },
            rendering: {
              title: 'Creating your video',
              subtitle: 'Almost there · {{percent}}%',
            },
            saving: {
              title: 'Saving your video',
              subtitle: 'It will be in your gallery shortly',
            },
            hint: 'Please keep this screen open',
          },
        },
        debug: {
          title: 'Debug Log',
          clear: 'Clear',
          media_recorder: 'MediaRecorder',
          capture_stream: 'captureStream',
          native_bridge: 'Native bridge',
          preview_slot: 'Preview slot {{index}}',
          export_slot: 'Export slot {{index}}',
          empty: 'No events yet.',
        },
        errors: {
          template_not_found: 'Template not found.',
          catalog_load_failed: 'Failed to load story templates due to a server error',
          catalog_load_description: 'Please try again in a moment. If this keeps failing, the server needs to be checked.',
          catalog_load_retry: 'Try again',
          camera_blocked: 'Camera permission is blocked. Please allow it in your browser settings.',
          camera_allow: 'Please allow camera access.',
          camera_missing: 'No camera was found.',
          camera_busy: 'The camera is being used by another app.',
          camera_failed: 'Failed to access the camera.',
          file_type_unsupported: 'Please choose a video file.',
          file_read_failed: 'Failed to read the selected file. Please try again.',
          image_export_failed: 'Failed to export the image.',
          export_failed: 'Export failed. Please try again.',
        },
        subscription: {
          badge_alt: 'Subscription only',
          recorder_banner_title: 'Subscribe to use this template',
          recorder_banner_description: 'New members get a 2-week free trial on the first subscription!',
          recorder_banner_label: 'View subscription offer',
          gate_title: 'Subscription-only template',
          gate_description: 'This template is available to subscribers only. Subscribe to make more stories.',
          gate_cancel: 'Not now',
          gate_cta: 'Subscribe',
        },
      },
      baby_management: {
        back: 'Back',
        title: 'Baby Management',
        description: 'Review the registered baby profiles.',
        profile_list: 'Baby profile list',
        loading: 'Loading baby profiles',
        fetch_failed: 'Failed to load baby profiles',
        no_baby: 'Baby information is not available',
        no_profiles: 'No baby profiles to display',
        unnamed_baby: 'Unnamed baby',
        unknown_family: 'Family information unavailable',
        family_name: 'Family · {{name}}',
        family_label: 'Family',
        born_on: 'Born on {{date}}',
        due_on: 'Due {{date}}',
        height: 'Height',
        weight: 'Weight',
        head_circumference: 'Head',
        current_baby: 'Current baby',
        profile_alt: '{{name}} profile image',
        retry: 'Try again',
        gender_boy: 'Boy',
        gender_girl: 'Girl',
        gender_unknown: 'Not sure yet',
        dday_label: 'D-day',
        age_label: 'Age',
        age_year: '{{years}}y',
        age_years_months_days: '{{years}}y {{months}}m {{days}}d',
        age_months_days: '{{months}}m {{days}}d',
        age_months: '{{months}} mo',
        age_days: '{{days}} d',
        age_pregnancy: 'Week {{weeks}}, Day {{days}}',
        view_detail: 'View details',
        open_detail: 'View {{name}} details',
        detail_title: '{{name}}',
        close_detail: 'Close details',
        basic_info: 'Basic info',
        growth_info: 'Growth record',
        name_label: 'Name',
        gender_label: 'Gender',
        birthday_label: 'Birthday',
        due_date_label: 'Due date',
        due_date_short_label: 'Due',
        not_set: 'Not set',
      },
      permission: {
        back: 'Back',
        title: 'Family Permissions & Nicknames',
        invite_family: 'Invite family',
        invite_banner_title: 'Invite your family.',
        invite_banner_desc: 'Upload and view photos together.',
        family_management_title: 'Family Management',
        family_select_desc: 'Choose a family to manage',
        current_family: 'Current family',
        family_info_title: 'Family Information',
        family_name_label: 'Family name',
        family_name_edit_title: 'Change family name',
        family_name_edit_desc: 'Enter the name shown to your family.',
        family_name_fallback_desc: "A family name hasn't been set yet, so the baby's name is being used temporarily.",
        family_name_max_length: 'You can enter up to 10 characters.',
        family_name_save_failed: 'Could not save the family name.',
        family_settings_save_failed: 'Could not save the family settings.',
        comment_visibility_label: 'Comment access',
        comment_visibility_title: 'Comment access',
        comment_visibility_desc: 'Choose which family groups can leave comments on photos.',
        comment_group_mom: "Mom's family",
        comment_group_dad: "Dad's family",
        comment_group_online: 'Online family',
        comment_group_none: 'Comments off',
        calendar_start_label: 'Calendar start day',
        calendar_start_title: 'Calendar start day',
        calendar_start_desc: 'Choose how the birth date is counted in the calendar.',
        calendar_start_d0: 'Birth date D+0',
        calendar_start_d1: 'Birth date D+1',
        calendar_start_d0_desc: 'The start day commonly used by hospitals and clinics.',
        calendar_start_d1_desc: 'The first birthday lands on day 365.',
        family_members_title: 'Family Members',
        member_info_title: 'Member Information',
        basic_info_title: 'Basic Information',
        nickname_label_short: 'Nickname',
        family_group_label: 'Family group',
        family_group_title: 'Choose family group',
        family_group_parents: 'Parents',
        family_group_paternal: "Dad's family",
        family_group_maternal: "Mom's family",
        family_group_online: 'Online family',
        relationship_label: 'Relationship',
        relationship_title: 'Choose relationship',
        permissions_title: 'Permissions',
        detail_carer_title_short: 'Care records',
        save_changes: 'Save changes',
        apply: 'Apply',
        loading_title: 'Loading family information',
        loading_desc: 'Checking permissions and nicknames. Please wait.',
        view_switch_aria: 'Family view mode',
        view_list: 'List',
        view_family_map: 'Family map',
        family_map_maternal_side: "Mom's side",
        family_map_paternal_side: "Dad's side",
        family_map_other_family: 'Family by heart',
        family_map_center: 'Our baby',
        family_map_baby: 'Baby',
        family_map_open_member: 'View {{name}}',
        baby_family: "{{name}}'s Family",
        fetch_failed: 'Failed to load family information',
        save_failed: 'Failed to save permissions',
        nickname_change: 'Change nickname',
        nickname_saving: 'Saving...',
        nickname_modal_desc: 'Enter the nickname or title shown within this family.',
        nickname_label: 'Nickname/title',
        nickname_max_length: 'You can enter up to 32 characters.',
        nickname_too_long: 'The nickname must be 32 characters or fewer.',
        nickname_cancel: 'Cancel',
        nickname_save: 'Save',
        nickname_required: 'Please enter a nickname.',
        nickname_save_failed: 'Failed to save the nickname',
        no_more_history: 'There is no previous page to go back to.',
        confirm_leader_assign: 'Once assigned as an admin, only that member can remove it. Do you want to continue?',
        no_baby_id: 'Baby information is not available',
        no_families: 'No families to display',
        no_members: 'No members to display',
        member_count: '{{count}} members',
        chip_uploader: 'Upload',
        chip_carer: 'Care Log',
        chip_downloader: 'Download',
        leader: 'Admin',
        me: 'Me',
        profile_alt: '{{name}} profile image',
        relationship_mom: 'Mom',
        relationship_dad: 'Dad',
        relationship_maternal_grandfather: 'Maternal grandfather',
        relationship_maternal_grandmother: 'Maternal grandmother',
        relationship_paternal_grandfather: 'Paternal grandfather',
        relationship_paternal_grandmother: 'Paternal grandmother',
        relationship_maternal_relative: 'Maternal relative',
        relationship_paternal_relative: 'Paternal relative',
        relationship_grandfather: 'Grandfather',
        relationship_grandmother: 'Grandmother',
        relationship_relative: 'Relative',
        relationship_other_family: 'Other family',
        detail_upload_title: 'Upload',
        detail_upload_desc: 'Can upload photos and videos.',
        detail_carer_title: 'Care Log Access/Edit',
        detail_carer_desc: 'Can view and write care logs.',
        detail_download_title: 'Download',
        detail_download_desc: 'Can save photos to the device.',
        detail_comment_title: 'Comments',
        detail_comment_desc: 'Can leave comments on photos.',
        detail_leader_add_title: 'Add as Admin',
        detail_leader_remove_title: 'Remove Admin',
        detail_leader_desc: 'Can invite family members and manage menu permissions.',
        detail_leader_locked_desc: 'Parent groups always keep admin privileges.',
      },
      subscription_benefits: {
        back: 'Back',
        title: 'My Subscription Benefits',
        refresh: 'Refresh',
        loading: 'Loading subscription benefits',
        status_label: 'Subscription status',
        status_active_title: 'Subscription benefits are active',
        status_active_desc: 'Subscription-only features are available on this account.',
        status_inactive_title: 'No active subscription benefits',
        status_inactive_desc: 'Subscription-only features become available after subscribing.',
        status_unknown_title: 'Checking subscription status',
        status_unknown_desc: 'Benefits can be checked after the app provides family information.',
        family_waiting_title: 'Waiting for family information',
        family_waiting_desc: 'Subscription permission checks need family information from the app.',
        partial_error_title: 'Some information could not be loaded',
        partial_error_desc: 'Refresh in a moment to check the latest benefit status again.',
        benefits_label: 'Benefits',
        benefits_title: 'Currently available benefits',
        active_count: '{{count}}/{{total}} active',
        enabled: 'Active',
        disabled: 'Inactive',
        benefit_story_title: '3-sec Story subscription templates',
        benefit_story_desc: 'Use subscription-only templates and export without the watermark.',
        benefit_ads_title: 'Ad-free use',
        benefit_ads_desc: 'Checks whether the ad-removal permission is active.',
        benefit_special_video_title: 'Special video',
        benefit_special_video_desc: 'Checks whether the special video permission is active.',
        benefit_search_title: 'Search',
        benefit_search_desc: 'Checks whether the search permission is active.',
        account_label: 'Account information',
        signup_date: 'Signup date',
        permission_source: 'Permission data',
        source_loaded: 'Loaded',
        source_waiting: 'Waiting',
        unknown: 'Unknown',
        status_subscribed: 'Subscribed',
        status_cancellation_scheduled: 'Cancellation scheduled',
        status_not_subscribed: 'Not subscribed',
        status_checking: 'Checking',
        next_billing_date: 'Your next billing date is {{date}}.',
        benefit_available_until: 'You can use your benefits until {{date}}.',
        next_billing_unknown: 'Checking your next billing date.',
        benefit_period_unknown: 'Your benefit availability period could not be verified.',
        select_family: 'Select family',
        family_label: "{{names}}'s Family",
        receiver_family: 'Family receiving benefits: {{family}}',
        current_user_subscribed: 'You are subscribed.',
        other_family_subscribed: 'Another family member is subscribed.',
        subscription_owner_checking: 'Checking subscription status.',
        all_family_active_title: 'Every family member is receiving benefits.',
        only_me: 'Only you are receiving benefits.',
        only_me_upgrade_desc: 'To let every family member receive benefits,\nplease upgrade your subscription.',
        upgrade_inquiry: 'Ask about upgrade',
        other_family_receiving_title: 'Another family is\nreceiving subscription benefits.',
        other_family_receiving_desc: 'Only one family can receive subscription benefits.\nThe family you are viewing is not receiving them.',
        all_family_ending_title: 'The benefits your family has been receiving\nwill end soon.',
        all_family_active_desc: 'Everyone in your family is receiving the subscription benefits below.',
        all_family_active_desc_with_count: 'All {{count}} members of {{family}}\nare receiving the subscription benefits below.',
        keep_all_subscription_benefits: 'Keep all subscription benefits',
        subscription_issue_title: 'We could not confirm subscription information.',
        subscription_issue_desc: 'If you are subscribed but benefits are not shown, please contact support.',
        contact_support: 'Something is wrong',
        manage_subscription: 'Manage subscription',
        explore_other_subscriptions: 'Explore other plans',
        cancel_subscription: 'Cancel subscription',
        debug_status_title: 'Subscription decision details',
        debug_status_copy: 'Copy',
        debug_status_copied: 'Copied',
        debug_status_copy_failed: 'Copy failed',
        debug_status_close: 'Close',
        terms_title: 'Subscription terms',
        legal_links_label: 'Terms and privacy information',
        terms_of_use: 'Terms of Use',
        privacy_policy: 'Privacy Policy',
        config_error_title: 'Could not load benefit information',
        config_error_desc: 'Try again shortly to check the latest subscription benefits.',
        retry: 'Try again',
      },
      family_piggy_bank: {
        back: 'Back',
        title: 'Collect Cheese Together',
        intro_title: 'Our Family Piggy Bank',
        intro_desc: 'Collect cheese together with your family, one piece at a time.',
        intro_skip: 'Skip',
        vault_label: 'Our family piggy bank',
        level_name_1: 'Baby Pantry Pal',
        level_name_2: 'Cozy Pantry Pal',
        level_name_3: 'Hearty Pantry Pal',
        level_name_4: 'Sparkling Pantry Pal',
        level_name_5: 'Sunny Pantry Pal',
        mascot_idle_1: 'Our cheese is stacking up!',
        mascot_idle_2: 'Saving together feels great',
        mascot_idle_3: 'Give the piggy bank a tap',
        mascot_idle_4: 'Shall we collect more today?',
        mascot_react_1: 'Yum! So delicious 💛',
        mascot_react_2: 'I feel so full~ thank you!',
        mascot_react_3: 'I feel warmer and warmer~',
        mascot_pet_1: 'Hehe, that tickles!',
        mascot_pet_2: 'So cozy~ I love it',
        mascot_pet_3: 'Hehe, come see me again!',
        pet_aria: 'Pat the pantry pal',
        total_tag: 'Cheese from our family 🧀 {{count}}',
        family_together: '{{count}} family members are collecting together',
        available_cheese: 'My cheese',
        cheese_count: '{{count}}',
        available_cheese_label: 'My cheese {{count}}',
        balance_retry: 'Try again',
        feed_button: 'Add cheese to the bank',
        deposit_open: 'Save cheese',
        deposit_sheet_eyebrow: 'FAMILY VAULT',
        deposit_sheet_title: 'How much will you save?',
        deposit_sheet_desc: 'Choose any amount up to your available cheese.',
        deposit_close: 'Close',
        deposit_amount_label: 'Cheese to save',
        deposit_amount_aria: 'Number of cheese to save',
        deposit_slider_aria: 'Adjust the number of cheese to save',
        deposit_preset_quarter: '25%',
        deposit_preset_half: 'Half',
        deposit_preset_all: 'All',
        deposit_available: 'Available {{count}}',
        deposit_remaining: '{{count}} remaining',
        deposit_confirm: 'Save {{count}}',
        deposit_expiration_preview: 'Save now and use it through {{date}}.',
        cheese_unit: '',
        earn_link: 'Collect more cheese',
        earn_title: 'How to collect cheese',
        earn_intro: 'Upload a photo or invite your family.',
        earn_method_1_title: 'Upload photos',
        earn_method_1_desc: 'Upload today’s photo and receive cheese',
        earn_method_2_title: 'Invite family',
        earn_method_2_desc: 'Collect faster when your family joins',
        earn_method_3_title: 'Missions',
        earn_method_3_desc: 'New missions are coming soon',
        earn_close: 'Done',
        deposit_empty: 'No cheese yet. Time to collect more!',
        deposit_title: 'Family piggy bank deposit',
        deposit_processing: 'Adding cheese...',
        deposit_failed: 'Could not add cheese. Please try again.',
        deposit_retry: 'Could not verify your cheese. Please try again shortly.',
        deposit_pending: 'We are checking the deduction result. Please check your cheese shortly to prevent a duplicate deduction.',
        deposit_balance_changed: 'Your cheese balance changed. Please check it and try again.',
        deposit_success: 'Added {{count}} · available through {{date}}.',
        deposit_success_mixed: 'Added {{expiringCount}} expiring and {{nonExpiringCount}} non-expiring cheese.',
        deposit_success_non_expiring: 'Added {{count}} non-expiring cheese.',
        expiration_policy: 'Earned cheese lasts six months after deposit; purchased cheese does not expire.',
        next_expiration: 'Next expiration {{date}}',
        expired: 'Expired',
        family_member: 'Family',
        history_open: 'Savings history',
        history_title: 'Cheese savings history',
        history_description: 'Review each family deposit and its expiration date.',
        history_count: '{{count}} recent deposits',
        history_amount_label: 'Cheese saved',
        history_amount: '{{count}}',
        history_depositor_label: 'Saved by',
        history_deposited_at_label: 'Saved on',
        history_expires_at_label: 'Expires on',
        history_available: 'Available',
        history_no_expiration: 'None',
        history_empty: 'No cheese has been added yet.',
        me_name: 'Me',
        member_mom: 'Mom',
        member_dad: 'Dad',
        member_grandma: 'Grandma',
        use_cta: 'Make a photobook / prints',
        use_toast: 'The cheese shop is coming soon! 🧀',
        memory_title: 'Recent cheese',
        activity_deposit: '{{name}} collected {{count}}',
        activity_deposit_me: 'I collected {{count}}',
        time_just_now: 'Just now',
        time_hours_ago: '{{count}} hours ago',
        time_yesterday: 'Yesterday',
        demo_reset: 'Reset demo data',
        demo_reset_done: 'Demo data has been reset',
      },
      intro: {
        subtitle: 'have been completely revamped!',
        desc: 'Meet the new feeding interval on Babyfolio',
        cta: 'Learn more'
      },
      tour: {
        skip: 'Skip',
        next: 'Next',
        done: 'Done',
        steps: {
          tabs: {
            title: 'Tabs overview',
            desc: 'See daily, weekly, and rhythm flows at a glance! AI feeding rhythm analysis is also on the way'
          },
          today: {
            title: 'Today tab',
            desc: 'Tap the timetable to view detailed logs by time'
          },
          week: {
            title: 'Week tab',
            desc: 'See the weekly pattern at a glance! Understand your baby’s rhythm'
          },
          week_excel: {
            title: 'Download weekly logs',
            desc: 'One tap to export weekly logs as a table'
          },
          discover: {
            title: 'Discover tab',
            desc: 'Log sleep or feeding and AI analyzes the rhythm, then sends timing alerts automatically. Explore live features and what’s coming next in one place.'
          }
        }
      },
      beta: {
        badge: '🧪 Beta Tester Recruitment',
        app_name: "Babyfolio's New AI Feature",
        title: 'Sleep',
        title_highlight: ' Forecast',
        subtitle: "AI finds your baby's optimal sleep time",
        intro_lead_line1: '"Should I put them to sleep now...?"',
        intro_lead_line2: "Don't worry anymore",
        intro_body_line1: 'Every baby has a different sleep rhythm.',
        intro_body_line2: 'Sleep Forecast analyzes your records',
        intro_body_line3: 'and sends alerts at the best time to sleep 🔔',
        phone_time: '1:24 PM',
        notif_app: 'Babyfolio',
        notif_when: 'now',
        notif_title: "🍯 It's Sleep Forecast Time!",
        notif_body_line1: "Now is a good time to put your baby to sleep.",
        notif_body_line2: "About 10 minutes until Sleep Forecast time.",
        phone_extra: 'Morning sleep recorded',
        phone_caption: 'Actual notification preview',
        how_title: '✨ How It Works',
        how_subtitle: 'Just record as usual, AI handles the rest',
        step1_title: 'Record as usual',
        step1_desc: 'Log feeding, sleep, and diaper changes',
        step2_title: 'AI analyzes patterns',
        step2_desc: "Identifies your baby's unique sleep rhythm from records",
        step3_title: 'Sleep alert arrives 🔔',
        step3_desc: 'Get push notifications at the best time to sleep',
        tag_feeding: '🍼 Feeding',
        tag_sleep: '💤 Sleep',
        tag_diaper: '🧷 Diaper',
        benefits_title: '🎁 Beta Tester Benefits',
        benefits_subtitle: 'Be the first to experience and help shape it',
        benefit1_title: 'First access + Free',
        benefit1_desc: 'Try Sleep Forecast alerts for free during the beta period',
        benefit2_title: 'Direct communication with the team',
        benefit2_desc: 'Your feedback gets applied right away',
        cta_button: '🍯 Sign Up for Sleep Forecast Beta',
        cta_note: 'For parents of babies under 12 months',
        auto_notice_title: 'Record sleep to get alerts automatically',
        auto_notice_desc: "No sign-up needed — just record your baby's sleep and we'll send Sleep Forecast alerts automatically.",
        registered_notice_title: "You're all set for Sleep Forecast alerts",
        registered_notice_desc: "Keep recording sleep and we'll send Sleep Forecast alerts at the best time to sleep.",
        faq_title: 'Frequently Asked Questions',
        faq1_q: 'Q. Do I need to record a lot?',
        faq1_a: "No! At first, we send alerts based on patterns of similar-aged babies. As records build up, notifications automatically evolve to match your baby.",
        faq2_q: 'Q. Is there a cost during the beta?',
        faq2_a: "It's completely free during the beta test period. Your valuable feedback is the biggest help 🙏",
        status_pending: 'Your application has been received',
        status_approved: 'You are approved for beta testing! 🎉',
        status_already: "You've already applied",
        status_full: 'Registration is full',
        apply_failed: 'Application failed. Please try again',
        apply_success: 'Beta test application complete! 🎉',
        apply_success_title: 'Beta registration complete!',
        apply_success_desc: "You'll receive Sleep Forecast alerts soon",
        apply_success_confirm: 'OK',
        already_applied_title: 'Already registered',
        already_applied_desc: 'This baby is already signed up for Sleep Forecast beta. We\'ll notify you after approval!',
        family_applied_title: 'A family member already applied',
        family_applied_desc: 'Another family member has already registered this baby for the beta.',
        status_cancelled: 'Beta participation cancelled',
        cancelled_title: 'Beta participation cancelled',
        cancelled_desc: 'Your beta test participation has been cancelled. Please contact support if you have any questions.',
        footer: '© 2026 Babyfolio',
        back: 'Back',
        no_eligible_babies: 'No eligible babies.',
        select_baby_title: 'Select a baby',
        select_baby_desc: 'Select the baby for beta registration.',
        sleep_banner_title: 'Sleep Forecast',
        sleep_banner_subtitle: 'Check the best time for your baby to sleep',
      },
      letter: {
        title: 'A letter from Mimi',
        nav_title: 'Mimi’s letter',
        close: 'Close',
        kicker: 'FROM THE PERSON BEHIND BABYFOLIO',
        hero_title: 'A child’s today\nbecomes someone’s forever.',
        hero_subtitle: 'I am writing this myself, with a promise to care for the days you have so lovingly kept.',
        open_cue: 'OPEN THE LETTER',
        edition_default: 'A note of thanks',
        issue: 'FOR YOUR FAMILY',
        to_line: 'To you, who trusted us with your child’s today',
        greeting: 'Hello.\nI’m Mimi, the person behind Babyfolio.',
        p1: 'I’m a parent, too. After a day that passed in a blur and my child was finally asleep, I often found myself looking through the photos from that ordinary day for a long while.',
        p2: 'Thank you for trusting Babyfolio with your child’s precious days. I know how much love goes into choosing a photo or a video to keep, even in the middle of a busy day.',
        p3: 'A first smile, a tiny body in your arms, a laugh shared by the whole family. The days that feel so familiar now may become the memories you most want to revisit. We will care for the moments you have entrusted to us, so you can return to the feeling of today.',
        p4: 'We will honor your trust by continuing to improve Babyfolio. We will pay attention to the details, making it easier to keep your memories and more meaningful to revisit them.',
        photo_caption: 'So the day held in this little hand can stay close for years to come',
        promise_label: 'MIMI’S PROMISE',
        promise_heading: 'Right before fast,\nand lasting before more.',
        promise: 'We will never grow casual about caring for a family’s most precious memories.',
        closing: 'Thank you.\nI’ll keep building carefully, beside your child’s today.',
        sign_role: 'The person behind Babyfolio',
        sign_name: 'Mimi, Founder & CEO',
        postscript: 'Thank you for keeping another precious day with us.',
        feedback: {
          label: 'Respond to the letter',
          support: 'Cheering you on',
          touched: 'This touched me',
          write: 'Leave an anonymous note',
          fold: 'Maybe another time',
          placeholder: 'If there’s something you’d like to say, feel free to leave a note.',
          privacy: 'Your note is anonymous and only our team can read it.',
          count: '{{current}} / {{limit}}',
          send: 'Send my note',
          sending: 'Sending…',
          reaction_thanks: 'Your kindness reached us. Thank you.',
          message_thanks: 'Thank you. We’ll read your note with care.',
          error: 'Your response hasn’t reached us yet. Please try again in a moment.',
          already_sent: 'You’ve already left a note for this letter. This new text hasn’t been sent.',
        },
        opening_alt: 'A warm still life of old photos, an envelope, and a fountain pen',
        hands_alt: 'A baby’s small hand holding an adult finger',
        closing_alt: 'A letter sealed with ribbon and wax beside a fountain pen',
        variant: {
          family_hd: {
            label: 'A thank-you note for Family HD',
            p1: 'I’m a parent, too. After a day that passed in a blur and my child was finally asleep, I often found myself looking through the photos from that ordinary day for a long while.',
            p2: 'Thank you for trusting the Babyfolio I built from that feeling, and for joining us with Family HD.',
            p3: 'Now today’s voice and expressions remain clearer in full HD, so the warmth of this day will still be there when you return to it years from now.',
            p4: 'We will look more carefully and build something you can trust for a very long time. While you put your heart into remembering, we will worry about what happens behind the scenes.',
          },
          family_hd_trial: {
            label: 'A welcome note for Family HD trial',
            p1: 'I’m a parent, too. After a day that passed in a blur and my child was finally asleep, I often found myself looking through the photos from that ordinary day for a long while.',
            p2: 'Thank you for choosing to experience Family HD. I’m grateful for the chance to help care for your child’s today.',
            p3: 'During your trial, keep today’s voice and expressions in full HD, so the warmth of this day stays clear when you return to it years from now.',
            p4: 'We will care for every detail, so this short trial can become the beginning of trust that lasts.',
          },
          family: {
            label: 'A thank-you note for Family',
            p1: 'I’m a parent, too. After a day that passed in a blur and my child was finally asleep, I often found myself looking through the photos from that ordinary day for a long while.',
            p2: 'Thank you for trusting the Babyfolio I built from that feeling, and for joining us with Family.',
            p3: 'Keep the long videos and the photo-filled days without rushing. Even when nothing remarkable happens, an ordinary day with your child is worth remembering.',
            p4: 'We will look more carefully and build something you can trust for a very long time. While you put your heart into remembering, we will worry about what happens behind the scenes.',
          },
          family_trial: {
            label: 'A welcome note for Family trial',
            p1: 'I’m a parent, too. After a day that passed in a blur and my child was finally asleep, I often found myself looking through the photos from that ordinary day for a long while.',
            p2: 'Thank you for choosing to experience Family. I’m grateful for the chance to help care for your child’s today.',
            p3: 'During your trial, keep long videos and photo-filled days with room to spare. I hope you feel the joy of seeing even ordinary days gather into a story.',
            p4: 'We will care for every detail, so this short trial can become the beginning of trust that lasts.',
          },
          adfree: {
            label: 'A thank-you note for Ad-Free',
            p1: 'I’m a parent, too. After a day that passed in a blur and my child was finally asleep, I often found myself looking through the photos from that ordinary day for a long while.',
            p2: 'Thank you for trusting the Babyfolio I built from that feeling, and for joining us with Ad-Free.',
            p3: 'Stay with your child’s memories without anything pulling your attention away. I hope revisiting your family’s days now feels a little quieter and more complete.',
            p4: 'We will look more carefully and build something you can trust for a very long time. While you put your heart into remembering, we will worry about what happens behind the scenes.',
          },
          adfree_trial: {
            label: 'A welcome note for Ad-Free trial',
            p1: 'I’m a parent, too. After a day that passed in a blur and my child was finally asleep, I often found myself looking through the photos from that ordinary day for a long while.',
            p2: 'Thank you for choosing to experience Ad-Free. I’m grateful for the chance to help care for your child’s today.',
            p3: 'During your trial, stay with your child’s memories without anything pulling your attention away. I hope that time feels a little quieter.',
            p4: 'We will care for every detail, so this short trial can become the beginning of trust that lasts.',
          },
        },
      },
      belly_clock: {
        banner_title: 'Tummy Forecast',
        banner_subtitle: "Know your baby's next feeding time in advance",
        back: 'Back',
        title: 'Tummy Forecast',
        hero_title: "Read your baby's own feeding rhythm",
        hero_desc: "Log feedings and AI analyzes the intervals and rhythm, then sends the next feeding alert automatically.",
        how_title: 'How it works',
        step1_title: 'Record',
        step1_desc: 'Log each feeding time',
        step2_title: 'AI analysis',
        step2_desc: "AI analyzes your baby's feeding intervals and rhythm",
        step3_title: 'Early reminder',
        step3_desc: 'Get a gentle heads-up 30 minutes before the next feeding',
        benefits_title: 'Why it helps',
        benefit1_title: 'Never miss a feeding',
        benefit1_desc: 'Stay on top of the next feeding, even on busy days',
        benefit2_title: 'Plan outings & naps',
        benefit2_desc: 'Plan your day around the next feeding time',
        benefit3_title: 'Prep for night feeds',
        benefit3_desc: 'Anticipate the night feeding rhythm in advance',
        trust_note: "Every baby's feeding rhythm is different.\nTummy Forecast suggests timing based on your baby's recent patterns.",
        notice_title: 'To receive feeding reminders',
        notice_desc: "For breast milk and formula feeds, please record the amount or duration along with the start time.\nIf only the start time is recorded, we can't predict the next feeding time.\nSolid food and snack records don't affect alerts.\nIf feeding intervals vary a lot from day to day, alerts may be off or may not arrive.",
      },
      sleep: {
        status_title: 'Sleep Forecast',
        awake_time: 'Time Awake',
        readiness: 'Sleep Forecast',
        recommended_time: 'Recommended Sleep Time',
        confidence: 'Prediction Accuracy',
        confidence_high: 'Enough records collected',
        confidence_low: 'Still collecting records',
        weekly_pattern: 'Weekly Sleep Pattern',
         no_session: 'No sleep analysis yet',
         no_session_desc: 'Log sleep and AI analyzes the rhythm, then sends Sleep Forecast alerts automatically.',
         no_active_session: 'Baby is sleeping now',
         no_active_session_desc: 'Sleep Forecast analysis will start when your baby wakes up',
         sleeping_duration: 'Sleeping for {{time}}',
         awake_no_session: 'Baby is awake',
         awake_no_session_desc: 'Sleep prediction is not yet available',
        load_failed: "Couldn't load Sleep Forecast",
        load_failed_desc: 'Please try again in a moment.',
        retry: 'Try again',
        guide_how_title: 'How it works',
        guide_step1_title: 'Record',
        guide_step1_desc: 'Log sleep times as usual',
        guide_step2_title: 'AI analysis',
        guide_step2_desc: "AI learns your baby's sleep rhythm",
        guide_step3_title: 'Early reminder',
        guide_step3_desc: 'Get an alert at a good time for sleep',
        guide_benefits_title: 'Why it helps',
        guide_benefit1_title: 'Catch the right sleep window',
        guide_benefit1_desc: 'Know when it’s a good time to put baby down',
        guide_benefit2_title: 'Plan the day',
        guide_benefit2_desc: 'Plan naps and bedtime around the rhythm',
        guide_benefit3_title: 'Gets better with records',
        guide_benefit3_desc: 'More sleep logs make the timing more personal',
        guide_trust_note: "Every baby's sleep rhythm is different. Sleep Forecast suggests timing based on your baby's recent patterns.",
        minutes: 'min',
        score: 'pts',
        wake_window: 'Wake Window',
        back: 'Back',
        go_to_status: 'View Sleep Forecast',
        hours: 'h',
        days_unit: ' days',
        remaining: 'About {{minutes}} until recommended sleep',
        overtime: '{{minutes}} overtime',
        prediction_range: 'Prediction Range',
        personalized: 'Personalized',
        age_based: 'Age-based',
        analysis_basis: 'AI Analysis Basis',
        analysis_improving: 'Gets more accurate with more data',
        status_active: 'Still active',
        status_little_tired: 'Getting a bit tired',
        status_drowsy: 'Getting drowsy',
        status_sleep_now: 'Good time to sleep!',
        status_overtime: 'Past sleep time',
        awake_time_elapsed: '{{time}} awake',
        baby_default_name: 'Baby',
        baby_dday: 'Day {{days}}',
      },
      books: {
        badge: 'photo book',
        theme: {
          badge: 'Create a photo book',
          title: 'Which moments belong\nin this book?',
          intro: 'Choose the period you want, then select the photos.',
          subtitle: 'We prepared a recommendation for age {{months}} months.',
          previewLabel: 'Photo book preview using recent photos',
          previewTitle: 'Our Baby Photo Book',
          previewSubtitle: 'Precious moments in one book',
          periodPrimaryTitle: 'Choose a period',
          periodPrimaryDesc: 'Year · month · age month · first 100 days',
          browseBadge: 'Choose a period',
          browseTitle: 'Which period will you include?',
          browseDesc: 'Choose a grouping, then select the exact period.',
          browseBack: 'Back to the beginning',
          browseActionTitle: 'Choose another record',
          browseActionDesc: 'Year · month · age month · milestone',
          periodBadge: 'Choose a period',
          periodBack: 'Back to other records',
          periodGuideTitle: 'Calendar period',
          milestoneGuideTitle: 'Growth milestone',
          recommendedBadge: 'Recommended now',
          recommendedShort: 'Recommended',
          recommendedAgeBadge: 'Recommended · age {{months}} months',
          recommendedAction: 'Choose this record',
          yearlyTitle: 'Yearly record',
          yearlyMeta: '{{count}} years',
          yearlyShort: 'By year',
          yearlyBrowseDesc: 'Collect a year of changes in chronological order.',
          yearlyPickerDesc: 'Choose the year for this book.',
          monthlyTitle: 'Monthly record',
          monthlyMeta: '{{count}} months',
          monthlyShort: 'By month',
          monthlyBrowseDesc: 'Make a lighter book from one photo-filled month.',
          monthlyPickerDesc: 'Choose the calendar month for this book.',
          ageMonthTitle: 'Age-by-month record',
          ageMonthMeta: '{{count}} age ranges',
          ageMonthShort: 'By age',
          ageMonthBrowseDesc: 'Compare growth from one age month to another.',
          ageMonthPickerDesc: 'Choose the age-by-month period for this book.',
          choiceCount: '{{count}}',
          directTitle: "I'll choose photos myself",
          directDesc: 'Skip the date range and choose freely as before.',
          directPrimaryTitle: 'Choose photos without a period',
          directPrimaryDesc: 'Start directly with the photos you want.',
          preparingSuggestions: 'Checking a birthday-based recommendation. You can choose photos now.',
          noBirthday: "We couldn't find the birthday. You can still choose photos yourself.",
          selected: 'Selected theme',
          dateRange: '{{range}} photos',
          back: 'Back to theme selection',
          choice: {
            yearlyTitle: '{{year}} growth record',
            yearlySubtitle: 'Collect the moments from {{year}} in one book.',
            yearlyReason: 'A clear way to review a full year in order.',
            monthlyTitle: '{{month}}/{{year}} record',
            monthlySubtitle: 'Collect one calendar month of everyday changes.',
            monthlyReason: 'A light book for a month with plenty of photos.',
            ageMonthTitle: 'Month {{month}} record',
            ageMonthSubtitle: 'Collect the days from age month {{month}}.',
            ageMonthProgressSubtitle: 'Age month {{month}}, through today.',
            ageMonthReason: 'Easy to compare changes from one age month to another.',
            first100Title: "Baby's first 100 days",
            first100ProgressTitle: 'On the way to 100 days',
            first100Subtitle: 'From birth through day 100.',
            first100ProgressSubtitle: 'From birth through today.',
            first100Reason: 'Preserve the first 100 days as one continuous story.',
            firstYearTitle: 'The first year',
            firstYearProgressTitle: 'On the way to the first birthday',
            firstYearSubtitle: 'From birth through the first birthday.',
            firstYearProgressSubtitle: 'The growth record from birth through today.',
            firstYearReason: 'Review the flow of the first year in one book.',
            fromBirthTitle: 'From birth through today',
            fromBirthSubtitle: 'Collect every period from the first day through today.',
            fromBirthReason: 'Review the whole story so far at a glance.',
          },
        },
        autoSetup: {
          close: 'Close',
          back: 'Back',
          step: '{{current}} / 2',
          periodLabel: 'Period selected',
          densityTitle: 'How many moments should we include?',
          densityDesc: 'We will review the full period and choose the best moments at the level you prefer.',
          recommended: 'Recommended',
          density: {
            highlightsTitle: 'Highlights only',
            highlightsDesc: 'Reduce similar photos and focus on the defining moments.',
            balancedTitle: 'Balanced',
            balancedDesc: 'Mix important milestones with everyday moments.',
            richTitle: 'More moments',
            richDesc: 'Keep more expressions and scenes, even from the same day.',
            estimate: 'About {{count}}',
          },
          next: 'Next',
          manual: 'Choose photos myself',
          layoutStepLabel: 'Layout preference',
          layoutTitle: 'How should the book feel?',
          layoutDesc: 'Each page will follow your preference while adapting to the photos and notes.',
          layout: {
            autoTitle: 'Fit each photo',
            autoDesc: 'Adapt each page to the photo proportions and available notes.',
            photoForwardTitle: 'Larger photos',
            photoForwardDesc: 'Use more pages that give one scene plenty of space.',
            storyTitle: 'Photos with notes',
            storyDesc: 'Give written memories a natural place beside their photos.',
            collageTitle: 'More scenes per page',
            collageDesc: 'Group photos taken close together into rhythmic collages.',
          },
          loadingCandidates: 'Checking {{count}} photos from this period.',
          candidatesReady: '{{count}} checked · about {{target}} will be selected',
          noPhotos: 'There are no photos in the selected period.',
          loadFailed: 'We could not load all photos from this period.',
          create: 'Select photos and compose book',
          creatingTitle: 'Creating the first version of your book',
          creatingDesc: 'We are reducing similar moments and arranging the timeline, photos, and notes into a natural story.',
          stageCollected: 'Period photos ready',
          stageSelecting: 'Choosing representative moments',
          stagePreparing: 'Preparing page composition',
          cancel: 'Cancel',
          errorTitle: 'We could not finish the automatic composition',
          failed: 'Please try again in a moment.',
          retry: 'Try again',
          selected: 'Selected {{count}} moments from the full period.',
        },
        pick: {
          title: 'Which moments\nfor your photo book?',
          addTitle: 'Choose more photos\nand compose it again',
          addProgress: 'Choose {{count}} more, then we will recommend and compose again.',
          addReady: 'Your additions are ready. You can recommend and compose again.',
          cancelAdd: 'Cancel adding photos',
        },
        photos: {
          source: 'Recently uploaded photos',
          previewSource: 'Recent photo preview',
          reload: 'Reload photos',
          recentRange: 'This month',
          monthRange: '{{month}} photos',
          autoPick: 'Auto-select {{count}} recommended photos',
          autoAdd: 'Add missing photos automatically',
          autoPickDesc: 'We will arrange them by date. You can remove any photo later.',
          selectAllLoaded: 'Select all {{count}} loaded photos',
          allLoadedSelected: 'All loaded photos selected',
          maxSelected: 'Maximum {{count}} photos selected',
          autoLoadMore: 'Automatically load the next photos',
          loadingMore: 'Loading photos...',
          loadMore: 'Load more photos',
          loadMoreRetry: 'Retry loading photos',
          ddayBadge: 'D+{{count}}',
          recent: 'Recent',
          pickNumber: 'Select photo {{number}}',
          emptyTitle: 'No photos to show.',
          emptyDesc: "We couldn't find recent photos saved in the app.",
        },
        album: {
          missingBridge: "Couldn't connect to the app photo album.",
          loadFailed: "Couldn't load photos.",
          loadMoreFailed: "Couldn't load more photos.",
        },
        tray: {
          hint: 'Collecting photos for your book',
          empty: 'Tap photos to add them here',
          expandedTitle: '{{count}} photos selected',
          collapse: 'Collapse',
          remove: 'Remove from selection',
          open: 'View selected photos',
          count: '{{count}} photos selected',
        },
        draft: {
          saving: 'Saving',
          saved: 'Autosaved',
          saveFailed: 'Could not save',
          continueTitle: 'Continue editing your photo book',
          continueMeta: '{{title}} · {{photos}} photos · {{pages}} pages',
        },
        edit: {
          undo: 'Undo',
          redo: 'Redo',
        },
        action: {
          reset: 'Cleared all selected photos.',
          resetLabel: 'Clear selection',
          autoSelected: 'Selected {{count}} recent moments automatically.',
          allLoadedSelected: 'Selected all {{count}} loaded photos.',
          photoLimitReached: 'You can select up to {{count}} photos.',
          needMorePhotos: '{{count}} selected. Please choose {{needed}} photos.',
          addedToPage: 'Added the selected photos to the requested page.',
          removedFromBook: 'Removed the photo from this book.',
          photoPositionSaved: 'Saved the photo position.',
          photoPositionReset: 'Restored automatic photo positioning.',
          keepExistingForPageAdd: 'Keep the existing selection while adding photos to a page.',
          aiPolishFailed: 'Your edits are unchanged. Try the AI suggestion again when ready.',
          pageDeleted: 'Page deleted.',
          titleSuggestionApplied: 'Applied the suggested book title.',
          coverPhotoChanged: 'Cover photo changed.',
          coverStyleChanged: 'Changed the cover style without changing the content pages.',
          undo: 'Returned to the previous edit.',
          redo: 'Reapplied the edit.',
        },
        cta: {
          pickAtLeastOne: 'Choose at least one photo',
          pickPhotoProgress: '{{current}} / {{needed}} photos',
          next: 'Next with {{count}} photos',
          previewNow: 'Preview with {{count}} photos',
          recompose: 'Recompose with {{count}} photos',
          planning: 'Composing the photo book layout...',
          aiRequired: 'AI layout required',
          layoutRequired: 'Prepare the layout first',
          creating: 'Creating print-ready PDF...',
          createDraft: 'Review final version',
        },
        defaultTitle: 'Our Baby Photo Book',
        create: {
          failed: 'Could not create the print-ready PDF.',
          missingPhoto: 'A photo used in the layout is missing. Regenerate the layout.',
          pageCountMismatch: 'The print page count does not match. Try again.',
          progress: {
            preparing: 'Preparing the print format',
            rendering: 'Placing photos on pages',
            uploading: 'Saving print pages',
            finalizing: 'Creating the final PDF',
            count: '{{current}} / {{total}} pages',
          },
        },
        subject: {
          loadingTitle: 'Checking faces in your photos',
          loadingDetail: 'Finding faces and people so the layout keeps them comfortably in frame.',
          inlineLoading: 'Checking photos to keep faces in frame. {{current}} / {{total}}',
        },
        template: {
          back: 'Back to photo selection',
          badge: 'layout',
          title: 'Review your photo book layout',
          subtitle: '{{count}} photos · {{range}}',
          noDate: 'No date information',
          recommendedProduct: 'Recommended product',
          bookSettings: 'Book title and size',
          settingsSummary: '{{spec}} · {{photos}} photos',
          coverIncluded: '{{name}} cover',
          photoCount: '{{count}} photos',
          advanced: 'Book size and details',
          bookSpec: 'Book size',
          bookTitle: 'Book title',
          bookTitlePlaceholder: 'Our Baby Photo Book',
          spec: 'Format',
          range: 'Period',
          minimumPages: 'Minimum {{count}}p',
          needPhotosTitle: 'Choose {{count}} more photos for a fuller layout.',
          needPhotos: 'This format works best with {{count}} photos. You currently selected {{current}}.',
          addPhotos: 'Choose more photos',
          loading: 'Loading templates...',
          errorTitle: 'Could not load templates.',
          retry: 'Try again',
          loadFailed: 'Could not load templates.',
          authRequired: 'App authentication is required to load photo book templates.',
          missingConfig: 'A Sweetbook API key is required.',
          empty: 'No compatible photo book template is available.',
          previewNote: 'Print pages use the same coordinates shown here. You can review the final PDF after generation.',
          recommendation: {
            loadingTitle: 'Reviewing photo composition',
            loadingDetail: 'Choosing a book size from photo orientation, capture times, and scene composition.',
            inlineLoading: 'Choosing the book size that fits your photos.',
            failed: 'Could not choose a book size automatically.',
            fallback: 'Automatic selection was unavailable, so the default size is applied.',
            manual: 'You selected this book size.',
            mixedOrientation: 'The mix of portrait and landscape photos fits flexible collage layouts.',
            recordRich: 'This size gives the photos comfortable breathing room.',
            largeCollection: 'This size has room for a large number of scenes.',
            landscapeRich: 'The larger page gives landscape photos enough room.',
            portraitRich: 'This format suits the high share of portrait photos.',
            lowBlankPages: 'This size minimizes blank pages for the selected photo count.',
            balanced: 'This size best balances the photo count and page composition.',
          },
        },
        previewDraft: {
          title: 'Photo book layout',
          subtitle: 'This page layout was created from your selected photos.',
          editorialLabel: 'AI editorial direction',
          titleSuggestion: 'Suggested title',
          applyTitle: 'Apply',
          manualEditsSafe: 'Your manual edits stay in place when you ask for another suggestion.',
          addPhotos: 'Add more photos',
          loadingTitle: 'Composing your photo book',
          loadingPrepareDetail: 'Reviewing your photos together with the available book formats.',
          loadingSubjectDetail: 'Reviewing each photo to keep faces comfortably in frame. {{current}} / {{total}}',
          loadingDetail: 'Reviewing capture times, orientations, and notes to compose each page.',
          loadingPreviewDetail: 'Checking the finished pages one last time.',
          errorTitle: 'Could not create the layout',
          planFailed: 'Could not create the automatic layout.',
          planUnavailable: 'No compatible page layout was found.',
          cover: 'Cover',
          frontCover: 'Front cover',
          coverShort: 'Cover',
          pageLabel: 'Page {{current}} / {{total}}',
          previous: 'Previous page',
          next: 'Next page',
          estimatedPages: 'About {{count}} pages',
          compactSummary: '{{photos}} photos · {{pages}} pages · {{printPages}}p print',
          layoutPages: '{{count}} photo pages',
          photoCoverage: '{{selected}} selected · {{used}} placed',
          printPages: '{{count}}p print format',
          blankPageNotice: 'Photos were arranged across {{layoutCount}} pages. The printed book will be {{printCount}} pages to meet this format, with the remaining pages left blank.',
          autoLayout: 'Up to {{count}} photos per page',
          aiLayout: 'AI layout',
          fallbackLayout: 'Basic layout',
          aiFallback: 'AI suggestions were unavailable, so the validated basic layout is shown.',
          retryAi: 'Refine with AI',
          sceneCount: '{{count}} scenes',
          collageCount: '{{count}} collage pages',
          recordCount: '{{count}} records included',
          lowQuality: '{{count}} low-resolution photos may print poorly.',
          authRequired: 'App authentication is required for preview.',
          loadFailed: 'Could not load preview details.',
          editor: {
            open: 'Edit this page',
            tapPage: 'Open detailed page editor',
            tapHint: 'Tap the page to edit photos, text, and style in detail.',
            close: 'Close editor',
            done: 'Done',
            coverEditorTitle: 'Edit cover',
            pageEditorTitle: 'Edit page {{page}}',
            tabsLabel: 'Page editing tools',
            tabs: {
              photos: 'Photos',
              text: 'Text',
              style: 'Style',
            },
            photoTabHint: 'Adjust the photos, order, and visible crop for this page.',
            textTabHint: 'Edit the date and story shown only on this page.',
            styleTabHint: 'Choose the photo layout and paper background for this page.',
            coverDetails: 'Cover details',
            bookTitle: 'Book title',
            noDateRange: 'Photo dates will appear on the cover.',
            coverPhoto: 'Cover photo',
            changeCover: 'Change cover photo',
            changeCoverDesc: 'Choose the photo that should introduce the book.',
            coverSafe: 'Faces fit safely',
            coverCaution: 'Check the cover crop',
            coverUnsafe: 'Faces may be cropped',
            coverUnknown: 'Check the crop in the cover preview',
            useRecommendedCover: 'Use the face-safe recommendation',
            coverStyle: 'Cover style',
            coverStyleHint: 'Change only the cover while keeping the content pages.',
            recommendedShort: 'Best',
            safeShort: 'Safe',
            cropRiskShort: 'Crop risk',
            checkShort: 'Check',
            coverPhotoOption: 'Cover photo {{number}} · {{status}}',
            pagePhotos: '{{count}} photos on this page',
            addToPage: 'Add photo',
            fitFaces: 'Fit faces',
            refinePage: 'Suggest this page',
            heroPhoto: 'Featured photo',
            photoNumber: 'Photo {{number}}',
            heroShort: 'Featured',
            editedShort: 'Edited',
            dragPhoto: 'Reorder photo {{number}}',
            editPhoto: 'Edit',
            replaceShort: 'Replace',
            deleteShort: 'Delete',
            setHero: 'Set as featured photo',
            moveEarlier: 'Move earlier',
            moveLater: 'Move later',
            adjustPhoto: 'Adjust photo position',
            position: 'Position',
            replacePhoto: 'Replace photo',
            removePhoto: 'Remove from book',
            chooseNewPhoto: 'Add a new album photo',
            pageText: 'Page text',
            date: 'Date',
            pageTitle: 'Title',
            comment: 'Comment',
            commentPlaceholder: 'Write the story you want to remember from this moment.',
            focusDate: 'Edit date',
            focusTitle: 'Edit title',
            focusComment: 'Edit comment',
            closeWriting: 'Close writing editor',
            layout: 'Photo layout',
            decorate: 'Decorate page',
            backgroundHint: 'Change only the paper color while keeping photos and text in place.',
            pickCover: 'Choose cover photo',
            pickAdd: 'Move a photo to this page',
            pickReplace: 'Choose a replacement photo',
            moveNotice: 'Choosing a photo from another page moves it here.',
            swapNotice: 'The two photos will swap page positions.',
            coverNotice: 'Content page placement will not change.',
            closePicker: 'Close photo picker',
            fromPage: 'Page {{page}}',
            noMoveCandidates: 'There are no other photos to move.',
            addFirstPage: 'Add first content page',
            addPageFull: 'Add page',
            movePageEarlier: 'Move page earlier',
            earlier: 'Earlier',
            addPage: 'Add',
            deletePageShort: 'Delete',
            movePageLater: 'Move page later',
            later: 'Later',
            deletePageWithPhotos: 'Remove this page and its {{count}} photos from the book?',
            deleteBlankPage: 'Delete this blank page?',
            cancel: 'Cancel',
            confirmDeletePage: 'Delete page',
            backgroundOptions: {
              original: 'Default',
              cream: 'Cream',
              blush: 'Pink',
              sky: 'Sky',
              sage: 'Green',
              lilac: 'Lilac',
            },
            layoutOptions: {
              aspectAware: 'Fit photo ratios',
              fullBleed: 'Fill page',
              singleFramed: 'With margin',
              heroPair: 'Large + small',
              splitVertical: 'Side by side',
              splitHorizontal: 'Top and bottom',
              trioFocus: 'Feature one photo',
              trioRow: 'Even row',
              quadGrid: '2 x 2 grid',
              storyGrid: 'Story grid',
              collageFive: 'Featured collage',
              collageSix: '3 x 2 collage',
              evenGrid: 'Even grid',
            },
          },
        },
        result: {
          badge: 'print PDF',
          title: 'Your print-ready PDF is complete',
          preparingTitle: 'Creating your print-ready PDF',
          subtitle: 'No order or payment has been placed. You can edit the book and create a new PDF.',
          pageCount: 'Pages',
          estimate: 'Estimated price',
          cover: 'Cover',
          content: 'Page layout',
          automaticContent: 'AI layout · {{count}} pages',
          editLayout: 'Edit and rebuild',
          startOver: 'Delete this result',
          deleting: 'Deleting this result...',
          deleteFailed: 'Could not delete this result.',
        },
        preview: {
          cover: 'Cover',
          contents: 'Pages',
          open: 'Open',
          refresh: 'Refresh PDF status',
          coverTitle: 'Cover PDF preview',
          contentsTitle: 'Page PDF preview',
          notReadyTitle: 'Preparing the PDF',
          notReadyDesc: 'The preview will open automatically when it is ready.',
          note: 'If the PDF is not visible in the WebView, use Open.',
          failed: 'PDF generation failed. Check the layout and try again.',
          refreshFailed: 'Could not refresh the PDF status.',
        },
      },
      printv2: {
        ui: {
          pickTitle: 'Choose photos',
          quantityValue_one: '{{count}} print',
          quantityValue_other: '{{count}} prints',
          productSummary: '{{size}} · From {{min}} prints',
          priceSummary: '{{amount}} each',
          removePhoto: 'Remove',
          editThisPhoto: 'Edit this photo',
          edited: 'Edited',
          previewOpen: 'View print {{number}} full screen',
          previewTitle: 'Print {{current}} / {{total}}',
          previewClose: 'Close print preview',
          previewLoading: 'Preparing your print',
          zoomIn: 'Tap to zoom in',
          zoomOut: 'Tap to zoom out',
          requestNotice: 'Your order will be submitted without payment.',
          quantityLabel: 'Print quantity for photo {{number}}',
          decreaseQuantity: 'Decrease quantity for photo {{number}}',
          increaseQuantity: 'Increase quantity for photo {{number}}',
          quantityValue: '{{count}} prints',
          selectedCount: '{{photos}} photos · {{prints}} prints',
          minimumRemaining: '{{count}} more needed',
          designTitle: 'Design',
          designAppliesAll: 'Applies to all photos',
          designChange: 'Change',
          designDone: 'Done',
          removeAllCaptions: 'Remove captions from all photos ({{count}})',
          removeAllCaptionsConfirm: 'Remove captions from {{count}} photos?',
          removeAllCaptionsAction: 'Remove all',
          removeAllCaptionsDone: 'Removed captions from {{count}} photos',
          currentPhoto: 'Photo {{current}} / {{total}}',
          photoList: 'Photos',
          previousPhoto: 'Previous photo',
          nextPhoto: 'Next photo',
          cropAction: 'Photo position',
          captionAction: 'Caption',
          shippingLater: 'Shipping is shown after you enter an address.',
          toDecorate: 'Decorate photos',
          returnToDecorate: 'Return to decorating',
          toAddress: 'Enter shipping address',
          reviewPreviewHint: 'Check each photo\'s position and caption.',
          previewMore: 'Show {{count}} more',
          previewFailed: 'Couldn\'t load previews',
          previewRetry: 'Try again',
          editPhoto: 'Edit photo {{number}}',
          backToStart: 'Back to start',
          addressTitle: 'Shipping address',
        },
        draft: {
          saving: 'Saving…',
          saved: 'Saved',
          failedRetry: 'Not saved · Retry',
          conflict: 'Changed on another device',
          deleted: 'Deleted draft',
          rejected: 'Can\'t save',
          failedDesc: 'Autosave paused. Your edits are still on this device.',
          retry: 'Retry',
          conflictDesc: 'Load the latest version or save your edits as a copy.',
          reloadLatest: 'Load latest',
          saveCopy: 'Save as copy',
          deletedDesc: 'Deleted on another device. Your current edits are still here.',
          tooLargeDesc: 'Too much to save. Remove some photos.',
          rejectedDesc: 'Some content can\'t be saved. Change your edits to save again.',
          defaultTitle: 'Photo prints for month {{month}}',
          reloaded: 'Loaded the latest version.',
        },
        recommend: {
          action: 'Suggest {{count}}',
          loading: 'Choosing',
          undo: 'Undo suggestions',
          none: 'Couldn\'t find suitable photos to suggest.',
          failed: 'Couldn\'t suggest photos. Please try again.',
        },
        printCheck: {
          face: 'A face may be cut off at the {{edges}} edge',
          lowResolution: 'Low resolution — this may print blurry',
          both: 'A face may be cut off and it may print blurry',
          summary: '{{count}} photos to check before printing',
          adjust: 'Adjust',
          review: 'Review',
          next: 'Next photo',
          marker: 'Check before printing',
          orderFace: '{{count}} may cut off faces',
          orderLowResolution: '{{count}} low resolution',
        },
        photoViewer: {
          open: 'View {{label}} photo larger',
          close: 'Close photo',
          select: 'Choose this photo',
          deselect: 'Remove from selection',
          loading: 'Loading photo',
          failed: 'Couldn\'t load the photo',
        },
        collections: {
          entry: 'Favorites',
          sourceLabel: 'Photo source',
          regularPhotos: 'All photos',
          nextPageHint: 'No photos here yet. Try loading more.',
          noPhotos: 'No printable photos in this favorites list.',
          loadMore: 'Load next photos',
          title: 'Choose favorites',
          loading: 'Loading favorites…',
          loginRequired: 'Sign in through the app to see your favorites.',
          retry: 'Try again',
          loadFailed: 'Couldn\'t load your favorites.',
          empty: 'No favorites yet.',
          favorites: 'Favorites',
          itemCount: '{{count}} items',
        },
        draftAction: {
          title: 'Save draft',
          close: 'Close',
          keepEditing: 'Keep editing',
          viewDrafts: 'View drafts',
          loginShort: 'Sign in needed',
          needsAttention: 'Not saved',
          loginRequired: 'Sign in through the app to save drafts.',
          unsaved: 'Not saved yet. Please try again in a moment.',
        },
        draftManager: {
          pending: 'Waiting to save',
          nameLabel: 'Draft name',
          nameInputLabel: 'Enter a draft name',
          nameRequired: 'Enter a name of 1–80 characters.',
          cancelRename: 'Cancel',
          confirmRename: 'Save name',
          rename: 'Rename',
          renameButton: 'Rename',
          copyName: '{{title}} copy',
          failed: 'Couldn\'t complete this. Please try again shortly.',
          emptyTitle: 'No drafts yet',
          editing: 'Editing',
          manageLabel: 'Manage {{title}}',
          manage: 'Manage',
          close: 'Close',
          justNow: 'Just now',
          duplicate: 'Make a copy',
          delete: 'Delete',
          deleteTitle: 'Delete “{{title}}”?',
          deleteDesc: 'This can\'t be undone. Original photos and placed orders stay.',
          cancel: 'Cancel',
          deleteConfirm: 'Delete',
          renameLabel: 'New name',
          copyLabel: 'Copy name',
          renameConfirm: 'Save name',
          copyConfirm: 'Save copy',
          busy: 'Working on it. Please wait.',
          unsaved: 'Your current edits aren\'t saved yet. Please try again in a moment.',
          changed: 'It changed on another device. Check the latest version and try again.',
          actionFailed: 'Couldn\'t save the change. Please try again shortly.',
          deletedDone: 'Deleted “{{title}}”.',
          renamedDone: 'Renamed to “{{title}}”.',
          copiedDone: 'Made a copy: “{{title}}”.',
          continueEditing: 'Currently editing',
        },
        shop: {
          pricePending: 'Checking price',
          retryQuote: 'Try again',
          authRequired: 'Sign in through the app and try again.',
          quoteFailed: 'Couldn\'t load the price.',
          uploading: 'Creating your order form',
          uncertain: 'Still confirming your order form. Please wait to avoid a duplicate order. If you contact us, share the request ID below.',
          failed: 'Couldn\'t open the order form. Please try again.',
          storageUnavailable: 'Couldn\'t save your order. Please try again shortly.',
          requestId: 'Request ID: {{id}}',
          estimate: 'Shipping and discounts appear on the order form.',
          tooLarge: 'The files are too large. Reduce photos or copies and split the order.',
          checkout: 'Continue to order',
        },
        drafts: {
          title: 'Drafts',
          loadFailed: 'Couldn\'t load drafts · Retry',
          collapse: 'Collapse',
          showAll: 'Show all',
          counts: '{{photoCount}} photos · {{printCount}} prints',
          savedAgo: 'Saved {{time}}',
          loadMore: 'Load more',
          loadMoreFailed: 'Couldn\'t load more drafts.',
          openMissing: 'This draft was deleted, so it was removed from the list.',
          openFailed: 'Couldn\'t open this draft. Please try again later.',
        },
        start: {
          newTitle: 'Start new',
          unsavedTitle: 'You have unsaved edits',
          unsavedDesc: 'Starting new will discard them.',
          unsavedBack: 'Back to editing',
          unsavedDiscard: 'Discard and start new',
          pickPhotos: 'Choose photos',
        },
        orderReview: {
          title: 'Review your prints',
        },
        result: {
          cleanupTitle: 'Keep the “{{title}}” draft?',
          cleanupFallbackTitle: 'this print',
          cleanupDesc: 'Keep it to reorder the same photos later.',
          cleanupFailed: 'Couldn\'t delete it. Please try again later.',
          cleanupRemove: 'Delete',
          cleanupKeep: 'Keep',
          cleanupDone: 'Removed from drafts',
        },
      },
      prints: {
        pick: {
          title: 'Which moments\nwould you like to print?',
          periodBadge: 'Choose photos',
        },
        steps: {
          label: 'Progress',
          pick: 'Pick',
          review: 'Review',
          decorate: 'Decorate',
          order: 'Order',
        },
        resume: {
          title: 'You have an unfinished print set',
          counts: '{{photoCount}} photos · {{printCount}} prints',
          discard: 'Start over',
          continue: 'Continue editing',
          restored: 'Draft restored.',
        },
        review: {
          badge: 'Review selection',
          title: 'Review your selected photos',
          counts: '{{photoCount}} photos · {{printCount}} prints',
          minimumLeft: '{{count}} more prints to reach the minimum',
          minimumReady: 'You have reached the order minimum',
          copiesHint: 'You can adjust copies for each photo on the next screen.',
          remove: 'Remove photo {{number}}',
          empty: 'No photos are selected.',
          addMore: 'Choose more photos',
        },
        orderReview: {
          badge: 'Final check',
          title: 'Review your finished prints',
          desc: 'Check the quantity and design before entering your shipping address.',
          preview: 'Selected photo preview',
          quantity: 'Quantity',
          counts: '{{photoCount}} photos · {{printCount}} prints',
          finish: 'Finish',
          frame: 'Frame',
          frameValue: 'No. {{number}}',
          caption: 'Captions',
          captionCount: '{{count}} photos',
          morePhotos: '+{{count}} more',
          sampleHint: 'Check the print design with {{count}} sample photos',
          sameDesign: 'Printed in the same design',
          previewItem: 'Final print preview',
          longCaptionWarning: '{{count}} captions are over 20 characters',
          unitPrice: 'Unit price',
          amount: 'Estimated total',
        },
        periodFlow: {
          start: {
            badge: 'Photo prints',
            title: 'How would you like to find photos?',
            desc: 'Choose a date range or browse recent photos.',
            autoTitle: 'Browse by date',
            autoDesc: 'Filter by baby age, month, or year.',
            manualTitle: 'Browse recent photos',
            manualDesc: 'Start with your latest photos.',
            notice: 'Your current selection will stay.',
            back: 'Back',
          },
          badge: 'Choose a period',
          title: 'Choose a time period',
          intro: 'We\'ll show photos from that period.',
          previewLabel: 'Polaroid preview using recent photos',
          recentThreeMonthsTitle: 'Last 3 months',
          recentThreeMonthsDesc: 'A balanced selection of recent expressions and changes.',
          recommendedBadge: 'Recommended from recent photos',
          minimumHint: 'We\'ll suggest at least {{count}} photos when enough are available.',
          findByPeriod: 'Choose another period',
          directBadge: 'Manual selection',
          directTitle: 'Choose photos myself',
          directDesc: 'Browse your album and select the photos you want.',
          backToSelection: 'Back to photos',
          loading: 'Selecting your best photos…',
          loadingDesc: 'Reviewing each photo and filtering similar shots.',
          loadingCount: 'Reviewing {{count}} photos',
          collectingCount: 'Gathering and reviewing {{count}} photos',
          loadingBridge: 'Connecting to your photo album',
          errorTitle: 'We could not select photos',
          error: 'We could not recommend photos. Please try again in a moment.',
          noPhotos: 'There are no photos in this period.',
          retry: 'Try again',
          changePeriod: 'Change period',
          resultBadge: 'Suggested photos',
          reviewTitle: 'Review your\nsuggested photos',
          noSuitableReviewTitle: 'Shall we pick\nthem together?',
          result: 'We picked {{selected}} of {{total}} photos',
          resultDesc: 'We filtered similar shots and selected photos across the full period.',
          insufficient: 'Selected all {{count}} photos from this period.',
          insufficientDesc: 'Select more photos or choose a longer period to reach the {{count}}-print minimum.',
          analysisUnavailable: 'We could not finish reviewing the photos. Please try again.',
          resultAll: 'Selected all {{count}} photos from this period.',
          resultAllDesc: 'You can add or remove photos below.',
          resultAllLimited: 'Selected the first {{count}} photos.',
          resultAllLimitedDesc: 'Review the photos below and adjust your selection.',
          resultNoSuitable: 'We could not find suitable photos to suggest.',
          resultHighlights: 'We picked {{selected}} photos for you.',
          resultHighlightsInsufficient: 'Found {{selected}} suitable photos.',
          resultNoAddition: 'You currently have {{total}} photos selected.',
          resultWithExisting: 'Added {{added}} more for a total of {{total}} selected photos.',
          resultHighlightsDesc: 'We filtered similar shots and selected photos across the full period.',
          resultHighlightsWithDiaryDesc: 'We prioritized {{count}} photos with notes and filtered similar shots.',
          resultHighlightsInsufficientDesc: 'You need {{count}} more to reach the minimum. Pick below or open another period.',
          suitableCount: '{{count}} recommended',
          excludedCount: '{{count}} more to review',
          diarySelectedCount: '{{count}} with notes',
          similarScenesReduced: 'Similar photos filtered',
          autoFillRemaining: 'Suggest {{count}} more',
          directFillRemaining: 'Select {{count}} more below',
          autoPickMinimum: 'Suggest {{count}} photos',
          backToManualSelection: 'Back to manual selection',
          cancelRecommend: 'Stop and pick photos myself',
          recommendAssistDesc: 'Keep your current selection and suggest only the {{count}} photos still needed.',
          recommendAssistAction: 'Get suggestions',
          recommendationPhotoBadge: 'Suggested',
          recommendationPreviewBadge: 'Review suggestions',
          recommendationPreviewTitle: 'We suggest {{count}} photos',
          recommendationPreviewDesc: 'Your current selection will stay. Only the photos below will be added.',
          recommendationDismiss: 'Keep selecting',
          recommendationApply: 'Add suggested photos',
          recommendationApplied: 'Added {{count}} suggested photos.',
          recommendationDismissed: 'No suggested photos were added.',
          recommendationUndone: 'Removed the suggested photos.',
          undoRecommendation: 'Remove suggested photos',
          widenPeriod: 'View another period',
          reviewAlbumTitle: 'Choose more photos',
          reviewAlbumHint: 'Numbers show the selection order. Tap a photo to add or remove it.',
          addPhotoLabel: 'Add photo from {{label}}',
          removePhotoLabel: 'Remove photo from {{label}}',
          mode: {
            title: 'How would you like to choose photos?',
            desc: 'Get suggestions or browse and choose photos yourself.',
            counting: 'Counting the photos',
            photoCount: '{{count}}+ photos',
            recommended: 'Recommended',
            highlightsTitle: 'Best moments',
            highlightsDesc: 'We\'ll suggest photos with clear subjects that should print well.',
            allTitle: 'Select all photos',
            allDesc: 'Select up to 200 photos from this period.',
            autoTitle: 'Suggest {{count}} photos',
            autoFillTitle: 'Suggest up to {{count}} photos',
            continueWithSelection: 'Continue with current picks',
            continueWithSelectionDesc: 'Review your selected photos and refine only what you need.',
            autoDesc: 'We review notes, expressions, and quality to suggest photos first.',
            quantityCurrent: 'Auto-select {{count}}',
            quantityChange: 'Change count',
            quantityLabel: 'Automatic selection count',
            quantityBasic: 'Standard',
            quantityComfortable: 'More',
            quantityMany: 'Most',
            manualTitle: 'Choose photos myself',
            manualDesc: 'Browse and select only the photos you want.',
            notice: 'You can freely add or remove photos after the automatic pick.',
            autoLoading: 'Choosing the best moments…',
            autoLoadingDesc: 'We are reviewing notes and expressions while sorting similar scenes.',
            allLoading: 'Selecting photos…',
            allLoadingDesc: 'Selecting photos from this period.',
            highlightsLoading: 'Finding the best moments…',
            highlightsLoadingDesc: 'Prioritizing photos with notes, then reviewing the rest.',
            analysisProgress: 'Reviewing {{completed}} of {{total}}',
            changeMode: 'Choose another method',
          },
          browse: {
            title: 'Browse another period?',
            hint: 'Your current selection will stay.',
            shortLabel: 'Other periods',
            loadMore: 'Load more photos',
            loadingMore: 'Loading more photos…',
            loadMoreHint: 'Scroll down to load more photos automatically.',
          },
          picker: {
            badge: 'Choose a period',
            desc: 'Select a period to view those photos.',
            back: 'Back to period types',
            yearlyTitle: 'Choose a year',
            monthlyTitle: 'Choose a month',
            ageMonthTitle: 'Choose baby\'s age',
          },
          options: {
            special: 'Special moments',
            monthly: 'By month',
            monthlyDesc: 'Browse one month at a time',
            ageMonth: 'By baby age',
            ageMonthDesc: 'Memories by baby age',
            yearly: 'By year',
            yearlyDesc: 'Changes across a whole year',
            first100: 'First 100 days',
            firstYear: 'Until first birthday',
            fromBirth: 'From birth',
          },
          period: {
            yearlyTitle: '{{year}}',
            yearlyDesc: 'Moments are selected in order across the year.',
            monthlyTitle: '{{month}}/{{year}}',
            monthlyDesc: 'The best moments from one month of everyday life.',
            ageMonthTitle: 'Month {{month}}',
            ageMonthDesc: 'Expressions and changes from this stage.',
            first100Desc: 'From the day you met through day 100.',
            firstYearDesc: 'A balanced look at the first year of growth.',
            fromBirthDesc: 'Look back from the day you met until today.',
          },
        },
        photos: {
          source: 'Recently uploaded photos',
          previewSource: 'Recent photo preview',
          loadingPeriod: 'Loading photos from {{period}}',
          pickHint: 'Tap a photo to select it',
          reload: 'Reload photos',
          viewLabel: 'Photo view',
          gridView: 'Photos',
          recordsView: 'Notes',
          noRecord: 'No note',
          recordOverLimit: 'Over 20 characters',
          period: 'Photo period',
          recentRange: 'Recent',
          monthRange: 'Photos from {{month}}',
          olderMonth: 'Show previous month photos',
          newerMonth: 'Show next month photos',
          autoLoadMore: 'Automatically load the next photos',
          loadingMore: 'Loading photos...',
          ddayBadge: 'D+{{count}}',
          recent: 'Recent',
          pickNumber: 'Select photo {{number}}',
          emptyTitle: 'No photos',
          emptyDesc: 'We could not find recent photos saved in the app.',
        },
        album: {
          missingBridge: 'Could not connect to the app photo album.',
          loadFailed: 'Failed to load photos.',
          loadMoreFailed: 'Failed to load more photos.',
          requestFailed: 'Failed to load photos. (Error {{status}})',
        },
        tray: {
          empty: 'Tap photos to add them here',
          expandedTitle: '{{count}} photos selected',
          collapse: 'Collapse',
          remove: 'Remove from selection',
          open: 'View selected photos',
          count: '{{count}} selected',
          floatingCount: 'Selected photos · {{count}}',
          floatingCountDetailed: '{{photoCount}} photos · {{printCount}} prints',
          hint: 'Tap to review your selection',
          minHint: 'Order from {{count}} prints',
        },
        decorate: {
          title: 'Customize your prints',
          hint: 'Tap the date, note, or stamp on the photo to change it',
          hintBase: 'Options apply to the preview instantly',
          recordsLoaded: 'We found notes for {{count}} photos.',
          longRecordMarker: 'Caption over 20 characters',
          longRecordCurrent: 'Only the first 20 characters will print',
          longRecordSummary: '{{count}} captions over 20 characters',
          longRecordLimit: 'Only the first 20 characters will be printed.',
          longRecordOriginalSafe: 'The original note stays unchanged.',
          nextLongRecord: 'Next long caption',
          nextLongRecordShort: 'Next caption',
          reviewLongRecords: 'Review',
          editDiary: 'Edit print caption',
          addDiary: 'Add caption',
          editDiaryShort: 'Edit caption',
          restoreDiaryShort: 'Restore caption',
          diarySaved: 'Caption updated.',
          diaryRemoved: 'Caption removed.',
          diaryRestored: 'Restored the original note.',
          editDate: 'Change date display',
        },
        diaryEditor: {
          title: 'Edit caption',
          safeHint: 'Your original note won\'t change.',
          close: 'Close caption editor',
          original: 'Original note',
          originalCount: '{{count}} characters',
          noOriginal: 'No note.',
          printText: 'Printed caption',
          placeholder: 'Caption to print',
          limit: 'Up to 20 characters print.',
          useOriginal: 'Use original note',
          remove: 'Remove caption',
          cancel: 'Cancel',
          apply: 'Apply',
        },
        chip: {
          frame: 'Frame',
          font: 'Font',
          date: 'Date line',
          stamp: 'Stamp',
          finish: 'Paper finish',
          dateBase: 'Date label',
        },
        crop: {
          title: 'Adjust photo position',
          hint: 'Drag to move, pinch or use the slider to zoom',
          adjust: 'Adjust photo position',
          zoom: 'Zoom level',
          reset: 'Reset',
          done: 'Apply',
          close: 'Close',
        },
        design: {
          lang: { none: 'None', both: 'Date and baby age', dday: 'Days since birth', date: 'Date only' },
        },
        polaroid: {
          lang: {
            kor: '한국어',
            eng: 'English',
            jpn: '日本語',
            korSample: '태어난 지 154일  2026.06.03',
            engSample: '2026.06.03  D+154',
            jpnSample: '生まれてから154日  2026.06.03',
          },
          font: {
            writer: 'Handwriting',
            gothic: 'Sans serif',
            pen: 'Pen',
            lineSeedJp: 'LINE Seed JP',
            bizUdpMincho: 'BIZ UDPMincho',
            kiwiMaru: 'Kiwi Maru',
            japaneseSample: 'すくすく育つ毎日',
          },
          stamp: {
            month: 'Months',
            ageMonths: 'Age + Months',
            year: 'Age',
            none: 'None',
            monthSample: '5 months',
            ageMonthsSample: '1y 3m',
            yearSample: '1 year',
            select: 'Stamp {{number}}',
          },
          frame: {
            select: 'Frame {{number}}',
            fallback: 'Showing the default frame.',
          },
        },
        finish: {
          matteLabel: 'Matte',
          glareLabel: 'Glossy',
          matteDesc: 'Fewer fingerprints',
          glareDesc: 'Vivid colors',
        },
        quantity: { decrease: 'Decrease quantity', increase: 'Increase quantity' },
        unit: { sheet: ' prints' },
        action: {
          minimumFilled: 'Set quantities to the minimum order.',
          reset: 'Cleared.',
          saved: 'Saved your print setup.',
          needPhoto: 'Please pick photos first.',
          resetLabel: 'Clear selection',
          resetPrompt: 'Clear all selected photos and print settings?',
          resetCancel: 'Cancel',
          resetConfirm: 'Clear all',
        },
        config: { fallback: 'Print settings could not be loaded, so the default 3x4 settings are shown.' },
        cta: {
          prev: 'Back',
          pickPhoto: 'Select photos',
          toDecorate: '{{count}} selected · Next',
          toReview: 'Review {{photoCount}} photos · {{printCount}} prints',
          editPhotos: 'Edit {{count}} photos',
          needMore: 'Add {{left}} more to order',
          belowMinHint: 'Orders start at {{min}} prints · {{current}} now',
          request: 'Order prints in app',
          requestCount: 'Order {{count}} prints',
          toAddress: '{{count}} prints · {{amount}} · Shipping info',
          toOrderReview: 'Review your prints',
          toAddressDetailed: '{{count}} prints · {{amount}} · Shipping info',
        },
        video: {
          badge: 'MEMORY MOVIE',
          button: 'Make a memory movie',
          buttonDesc: 'A nostalgic intro-to-outro movie · up to {{count}} photos',
          previewLabel: 'Polaroids going into the memory movie',
          creatingTitle: 'Making your memory movie…',
          creatingDesc: 'Turning through your Polaroids one by one.',
          progress: '{{percent}}%',
          cancel: 'Stop making it',
          saved: 'Your memory movie has been saved.',
          failed: 'Could not make the movie. Please try again shortly.',
          unsupported: 'Video saving is not supported on this device.',
          introTitle: 'Our little moments',
          introSubtitle: 'Looking back through {{count}} Polaroids',
          outroTitle: 'Keep today close, always',
          outroSubtitle: 'Our time together, held one frame at a time.',
        },
        faceReview: {
          checking: 'Checking face areas...',
          title: 'Faces may get cropped',
          desc: 'Faces are near the edge in these photos. Reposition them or continue as is.',
          faceCrop: 'A face touches the {{edges}} edge of the print area',
          photoOrder: 'Selected photo {{number}}',
          faceCount: 'Faces detected: {{count}}',
          adjustOne: 'Reposition this photo',
          adjust: 'Reposition',
          continue: 'Continue to order',
          toast: 'Please reposition the print area.',
          detectFailed: 'Face check failed.',
          detectFailedHint: 'Check again or continue without it.',
          retryCta: 'Check failed · Tap to retry',
          retry: 'Check again',
          skip: 'Continue without checking',
          skipped: 'Automatic face checking was skipped. Please review the finished set yourself.',
          edge: {
            left: 'left',
            right: 'right',
            top: 'top',
            bottom: 'bottom',
            outside: 'outer',
          },
        },
        footer: {
          selected: '{{photoCount}} photos · {{printCount}} prints total',
          minimumLeft: '{{left}} more to go!',
          ready: 'Ready to order',
          progress: '{{printCount}} / {{count}}',
        },
        address: {
          title: 'Where should we ship them?',
          subtitle: 'We deliver to addresses in Japan',
          name: 'Recipient (氏名)',
          namePlaceholder: '山田 花子',
          kana: 'Furigana (フリガナ)',
          kanaPlaceholder: 'ヤマダ ハナコ',
          postal: 'Postal code (〒)',
          postalHint: 'Enter 7 digits to autofill the address',
          postalNotFound: 'No address found for this postal code. Please fill it in manually.',
          postalFailed: 'Address autofill failed. Please fill it in manually.',
          prefecture: 'Prefecture',
          prefectureSelect: 'Select',
          city: 'City (市区町村)',
          cityPlaceholder: '千代田区千代田',
          address1: 'Street address (番地)',
          address1Placeholder: '1-1-1',
          address2: 'Building / room (optional)',
          address2Placeholder: 'マンション名 101号室',
          phone: 'Phone number',
          email: 'Email (optional)',
          requiredField: 'This field is required',
          postalInvalid: 'Enter a 7-digit postal code.',
          phoneInvalid: 'Enter a 10- or 11-digit phone number.',
          emailInvalid: 'Check the email format.',
        },
        pay: {
          cta: 'Place order',
          needAddress: 'Enter your shipping address',
          preparing: 'Placing order…',
          uploading: 'Uploading photos…',
          uploadTitle: 'Sending your order',
          uploadHint: 'Please keep this screen open.',
          uploadProgress: '{{done}}/{{total}}',
          failed: 'Could not place your order. Please try again shortly.',
          uploadFailed: 'Your order was placed, but the file upload failed. Contact us with order number {{orderNum}}.',
          summaryCount: '{{count}} photos',
          summaryCountDetailed: '{{photoCount}} photos · {{printCount}} prints',
          summaryUnit: '{{unit}} each',
          total: 'Estimated total',
          productSubtotal: 'Product subtotal',
          shippingFee: 'Shipping fee',
          totalWithShipping: 'Total',
          paymentFeeNotice: 'Card test uses the JPY total as a KRW test amount. Nothing is charged.',
          selectRegion: 'Select a shipping region',
          testCheckout: 'Toss card test · {{amount}}',
          cardTest: 'Card test',
          withoutPayment: 'Continue without payment',
          testNotConfigured: 'Test payment isn\'t set up, so no order was placed.',
          requestCheckout: 'Submit order request',
          testNotice: 'Card tests are not charged.',
          tossTestAmount: 'Test payment amount: {{amount}}',
          tossOrderName: 'Photo prints test',
          testSuccessDesc: 'The card test payment is complete. No actual charge, print production, or shipping takes place.',
          retryConfirmation: 'Check payment again',
          shippingLater: 'Regional rates apply to delivery addresses in Japan.',
          shippingEstimate: 'Regional shipping {{min}}–{{max}}',
        },
        store: {
          productLabel: 'Print product',
          productKind: 'Photo prints',
          productName: 'Polaroid 3×4',
          unitSuffix: 'per print · tax included',
          minLabel: 'Minimum order',
          minValue: 'From {{count}} prints',
          billingLabel: 'Billing',
          billingValue: 'Pay per print · not a subscription',
          shippingLabel: 'Shipping',
          shippingValue: '{{min}}–{{max}} by region within Japan',
        },
        legal: {
          navLabel: 'Store information',
          scta: 'Notation based on the Specified Commercial Transactions Act',
          contact: 'Contact',
          contactLine: 'Email {{email}}',
          companyLine: '{{shop}} · {{company}} · {{email}}',
          back: 'Back to prints',
        },
        contact: {
          title: 'Contact',
          supportTitle: 'Support is available in Japanese',
          supportDescription: 'We accept questions about Sukusuku Pasha photo print orders by email and phone.',
          emailLabel: 'Customer support email',
          phoneLabel: 'Japanese customer support phone',
          prepareTitle: 'Please include with your order inquiry',
          prepareDescription: 'Order number (or the email address used for the order)\nDetails of your inquiry',
          defectNote: 'For damage, printing defects, incorrect items, or missing quantities, also attach photos of the affected items and packaging that show the issue.',
          internationalCallNote: 'This is a South Korean phone number. International calling charges may apply depending on your carrier.',
        },
        scta: {
          sellerTitle: 'Seller information',
          shopName: 'Shop name',
          seller: 'Seller',
          representative: 'Person in charge',
          address: 'Address',
          addressValue: 'Seoul Fintech Lab-4, 5, 6, 8, 17, 19F, O2 Tower, 83 Uisadang-daero, Yeongdeungpo-gu, Seoul, 07325, Republic of Korea',
          phone: 'Japanese customer support phone',
          email: 'Customer support email',
          industry: 'Industry',
          industryValue: 'Mobile application / software',
          business: 'Business description',
          businessValue: 'We operate a family photo and child growth record app. Parents and families can safely store, organize, and share photos, videos, and parenting memories.',
          storeUrl: 'Store URL',
          productTitle: 'Product and service prices',
          product: 'Type of product / service',
          productValue: 'Custom photo prints (polaroid and similar formats)',
          price: 'Price range',
          priceValue: 'Prices are as displayed for each product on the store page. Prints are charged per sheet.',
          otherFees: 'Fees other than the product price',
          otherFeesValue: 'Shipping uses regional rates set by the seller: 3,000 yen within Japan excluding Hokkaido and Okinawa, 4,000 yen for Hokkaido, and 5,000 yen for Okinawa. Remote islands use the rate for their prefecture. These are the seller’s shipping fees, not the carrier’s published tariffs. Consumption tax is included in the product price. Checkout uses Toss card test payments. The JPY total is used as the same numeric amount in KRW for testing, without currency conversion. No actual charge is made.',
          delivery: 'Delivery / service provision',
          deliveryValue: 'This is a made-to-order custom product and will be dispatched within 7–14 business days after the order is placed.',
          methods: 'Payment methods',
          methodsValue: 'Toss Payments card test payment',
          timing: 'Payment timing',
          timingValue: 'The test payment is confirmed after card authentication. No actual charge is made.',
          returnTitle: 'Returns and cancellation policy',
          returnValue: 'These are made-to-order custom prints, so cancellations, returns, or exchanges for customer convenience are not accepted after ordering. For damage, printing defects, incorrect items, or missing quantities, email {{email}} within 7 days of delivery with your order number (or order email address), a description of the issue, and photos of the affected items and packaging where available. After checking the issue, we will reprint, replace, or send missing items at no charge and cover the necessary return and replacement shipping costs. If replacement is not possible, we will refund the affected items; the original shipping fee will also be refunded for a full return. Please contact support for instructions before returning items.',
          supportLanguage: 'Support language',
          supportLanguageValue: 'Japanese support is available by email and phone. This is a South Korean number; international calling charges may apply.',
        },
        result: {
          checking: 'Checking your payment...',
          requestedTitle: 'Order received!',
          requestedDesc: 'We\'ll review your order and get back to you.',
          successTitle: 'Payment complete!',
          successDesc: 'We\'ll print and ship your photos.',
          pendingTitle: 'Waiting for your payment',
          pendingDesc: 'Printing starts once payment is confirmed. Please pay by the deadline.',
          cancelledTitle: 'Payment was not completed',
          cancelledDesc: 'Your selected photos are still here, so you can try again.',
          errorTitle: 'Could not verify the payment',
          errorDesc: 'Please check again in a moment. Contact us if the problem continues.',
          orderNum: 'Order number',
          count: 'Prints',
          countValue: '{{count}} prints',
          amount: 'Amount',
          retry: 'Try again',
          done: 'Done',
        },
      },
      announce: {
        title: '🍯 Sleep Forecast Beta Test',
        badge: 'Beta Test',
        feature_title: '✨ What is Sleep Forecast?',
        feature_desc1: 'We analyze your baby\'s sleep records and send alerts at the best time for sleep.',
        feature_desc2: 'As records build up, notifications evolve into personalized alerts tailored to your baby.',
        feature_desc3: 'From the start, we reference patterns of similar-aged babies to help you.',
        record_title: '📝 Keep Logging Sleep Records',
        record_desc: 'Log sleep records consistently and you\'ll receive more accurate alerts.',
        notify_title: '🔔 Beta Notification Info',
        notify_desc: "Just record sleep and you're automatically enrolled for Sleep Forecast alerts — no sign-up needed.",
        note_individual: 'Think of Sleep Forecast like a weather forecast. Actual sleep timing may vary depending on your baby\'s condition, growth pace, and environment. Please also watch for your baby\'s sleep cues.',
        note_under60: 'For babies under 60 days old, the body clock is still developing and sleep patterns may be irregular, so prediction accuracy may be lower.',
        note_medical: 'This service provides general sleep information and does not replace medical advice. If you have concerns about your baby\'s sleep, please consult a pediatrician.',
        note_guide: 'Sleep Forecast is a tool to assist your parental judgment.',
        button_label: '🍯 View Beta Test Info',
        close: 'Close',
      },
    },
  },
  ja: {
    translation: {
      tabs: {
        today: '今日',
        week: '週間',
        interval: 'リズム',
        discover: '発見',
        aria_label: '上部タブ',
      },
      customC2: {
        brand: 'スクスクチャルカッ ファミリープラン',
        aria: { back: 'ギフト案内に戻る' },
        meta: {
          landingTitle: '家族に贈る1年間のサブスクリプション',
          giftTitle: 'ファミリープランのギフト申込み',
        },
        landing: {
          kicker: 'おじいちゃん・おばあちゃんへのギフト',
          title: 'お孫さんの動画を、\n途切れずに\n長く楽しめます',
          lead: 'このギフトひとつで、家族みんなが広告なしで\n最大10分の動画までゆっくり楽しめます。',
          imageAlt: 'ギフトボックスから家族写真が飛び出すイラスト',
          imageCaption: '家族8人まで一緒に',
          imageCaptionCount: '家族{{count}}人',
          benefitsLabel: 'ギフトに含まれる特典',
          benefitsBubble: 'どこが良いのか、\nひとつずつご紹介します。',
          benefitFamilyTitle: 'たまった写真を一気にアップ',
          benefitFamilyBody: 'ためていた写真も一度にアップして、家族みんなで見られます。',
          benefitVideoTitle: 'お孫さんの動画を途切れず長く',
          benefitVideoBody: '最大10分の動画も途切れずに見られます。',
          benefitAdTitle: '広告なしで快適に',
          benefitAdBody: '広告が出ないので、家族みんなでゆっくり見られます。',
          priceLabel: '今お申込みいただいた場合の特別価格',
          price: '69,000ウォン',
          originalPrice: '通常 99,000ウォン',
          discount: '30% OFF',
          priceNote: '家族8人なら1人あたり月719ウォンほどです。\nすぐに決済するか、電話相談をお選びいただけます。',
          priceNoteCount: '家族{{count}}人なら1人あたり月{{amount}}ウォンほどです。\nすぐに決済するか、電話相談をお選びいただけます。',
          ctaHint: 'カードやTossで簡単にお支払いできます。',
          cta: '今すぐギフトを贈る',
          next: '次へ',
          prev: '前に戻る',
        },
        gift: {
          back: '戻る',
          kicker: 'お子さまのご家族へのギフト',
          title: 'ご希望の方法を\nお選びください',
          lead: 'すぐに決済することも、\n先に電話で相談することもできます。',
          methodDirectTitle: '今すぐ決済します',
          methodDirectHint: 'カードやTossでその場で決済できます。',
          methodConsultTitle: '電話で相談したいです',
          methodConsultHint: '平日の午前10時〜午後6時にお電話します。',
          consultKicker: '電話相談の申込み',
          consultTitle: '連絡先を\nご記入ください',
          consultLead: 'お名前と電話番号だけで大丈夫です。\nこちらからお電話します。',
          nameLabel: 'お名前',
          namePlaceholder: '例）キム・ヨンジャ',
          phoneLabel: '電話番号',
          phonePlaceholder: '例）010-1234-5678',
          privacyLabel: '電話相談のための個人情報収集に同意します',
          privacyHint: 'ご記入の情報は相談のご連絡のみに使用します。',
          privacyLink: '個人情報取扱方針',
          submitting: '申込み中...',
          submit: '電話相談を申し込む',
          submitFailed: '申込みを受け付けられませんでした。しばらく後にもう一度お試しください。',
        },
        success: {
          kicker: '相談申込み完了',
          title: 'お申込みを\n受け付けました',
          lead: 'ご記入いただいた番号にお電話します。\n平日の午前10時〜午後6時におかけします。',
          home: '最初の画面に戻る',
        },
        payment: {
          kicker: 'Tossで安全に決済',
          title: 'お支払い方法を\nお選びください',
          productName: 'スクスクチャルカッ ファミリープラン 1年',
          orderName: 'スクスクチャルカッ ファミリープラン 1年',
          preparing: '決済画面を準備しています...',
          loadFailed: '決済画面を開けませんでした。しばらく後にもう一度お試しください。',
          activationHint: '決済後、5〜10分以内にサブスクリプションが自動で有効になります。',
          pay: '決済する',
          contactKicker: '決済前の連絡先',
          contactTitle: '連絡先を\nご記入ください',
          contactLead: 'お名前と電話番号だけで大丈夫です。\n決済確認とサブスクリプション案内に使います。',
          contactSubmit: '次へ、決済する',
          contactSubmitting: '保存しています...',
          contactFailed: '連絡先を保存できませんでした。しばらく後にもう一度お試しください。',
          privacyLabel: '決済のための個人情報収集に同意します',
          privacyHint: 'ご記入の情報は決済確認とサブスクリプション案内のみに使用します。',
        },
        appRequired: {
          title: 'アプリで開いてください',
          lead: 'すくすくぱしゃアプリでログインすると\n家族プランのギフトを進められます。',
        },
        paymentSuccess: {
          kicker: '決済完了',
          confirming: '決済を確認しています...',
          title: '決済が\n完了しました',
          activation: '5〜10分以内に\nサブスクリプションが自動で有効になります。\nほかにしていただくことはありません。',
          amountLabel: '決済金額',
          amountValue: '{{amount}}ウォン',
          methodLabel: '決済方法',
          receipt: '領収書を見る',
          errorTitle: '決済の確認が\n必要です',
          errorLead: '決済の確認中に問題が発生しました。\nこの画面をもう一度開くか、カスタマーセンターへお問い合わせください。',
          home: '最初の画面に戻る',
        },
        paymentFail: {
          kicker: '決済失敗',
          title: '決済が\n完了しませんでした',
          lead: '決済が完了しませんでした。\nもう一度お試しください。',
          retry: 'もう一度決済する',
          home: '最初の画面に戻る',
        },
        billingTest: {
          kicker: 'テスト決済 · トスのテストキー',
          title: 'アップした写真で\n毎月フォトブック',
          lead: 'アプリに上げたお子さんの写真でフォトブックを自動制作し、\n毎月お届けします。初月分はすぐに始まります。',
          autoBadge: '自動制作',
          heroAlt: '表紙にお子さんの写真が入ったフォトブック',
          price: '29,900円',
          priceUnit: '毎月',
          stepPhotos: 'アップした写真を集めます',
          stepMake: '今月のフォトブックを自動で作ります',
          stepMonthly: '来月も同じカードで自動決済されます',
          emailLabel: 'メールアドレス',
          emailPlaceholder: '例）name@email.com',
          register: '毎月受け取る · 29,900円',
          chargeAgain: '来月の決済をテストする',
          lastCharge: '今月のフォトブック {{amount}}円が決済されました。',
          confirming: 'カードを登録して今月のフォトブック 29,900円を決済しています...',
          chargeFailed: 'フォトブックの決済に失敗しました。',
          doneTitle: '今月のフォトブックが\n始まりました',
          doneLead: 'アップした写真で今月の本を作り、\n来月からは同じカードで自動決済します。',
          loadFailed: '決済画面を開けませんでした。しばらくしてから再試行してください。',
          home: '商品画面へ',
          legalCompany: '주식회사 제제미미 대표이사 박미영',
          legalBizNo: '사업자등록번호: 434-81-01803',
          legalMailOrder: '통신판매: 제2025-서울영등포-2692호',
          legalAddress: '주소 : 서울특별시 영등포구 의사당대로 83, 오투타워 서울핀테크랩 4,5,6,8,17,19층(여의도동)',
          legalPhoneLabel: '전화번호:',
        },
        validation: {
          name: 'お名前を2文字以上入力してください。',
          phone: '電話番号をご確認ください。',
          email: 'メールアドレスをご確認ください。',
          privacy: '個人情報の収集に同意してください。',
        },
      },
      cheese: {
        title: 'チーズ',
        aria: {
          back: '前の画面に戻る',
          guide: 'チーズ利用案内',
          charlie: 'チーズ残高を案内するチャーリー',
        },
        balance: {
          label: 'マイチーズ',
          expiration: '受取日から60日間',
          history: '履歴を見る',
        },
        today: {
          title: '今日もらえるチーズ',
          hint: '忘れずに受け取ろう',
        },
        reward: {
          upload: '写真をアップロード',
          record: '記録する',
          invite: '家族を招待',
          completed: '本日完了',
          daily: '毎日貯まる',
          up_to: '最大 +{{amount}}',
        },
        action: {
          family_piggy_bank: '家族とチーズを集める',
          claim: '受け取る',
          completed: '完了',
          record: '記録',
          invite: '招待',
          upload: 'アップロード',
          coming_soon: '準備中',
          retry: '再試行',
        },
        collect: {
          title: 'チーズを集める',
          daily: '毎日記録',
          invite: '友だち招待',
          mission: 'ミッション',
        },
        shop: {
          eyebrow: 'GOODS SHOP',
          title: 'フォトブック / 写真印刷を見に行く',
        },
        error: {
          app_required: 'アプリで開くとマイチーズを確認できます。',
          partial: '一部のチーズ情報を読み込めませんでした。',
          claim: 'チーズを受け取れませんでした。しばらくしてから再度お試しください。',
        },
        notice: {
          title: '注意事項を必ずご確認ください。',
          items: {
            reward_expiration: 'チーズファミリーおよびアップロード抽選特典で受け取ったチーズの有効期限は60日です。',
            history: '付与されたチーズはマイページ > チーズ履歴から確認できます。',
            abuse: '不正な方法で家族招待が行われた場合、チーズの回収およびチーズファミリーへの参加が制限されることがあります。',
            change: '本特典は当社の都合により、事前の告知なく変更される場合があります。',
          },
        },
      },
      invite_friend: {
        back: '戻る',
        title: '友だち招待',
        hero_eyebrow: '一緒に集めるともっとたまる',
        hero_title: '友だちを招待して\nチーズを一緒に集めよう',
        reward_aria: '友だち招待のチーズ特典',
        inviter_label: '招待した人',
        inviter_reward: '+100チーズ',
        inviter_limit: '最大10回',
        invitee_label: '招待された友だち',
        invitee_reward: '+200チーズ',
        invitee_limit: '初回1回のみ',
        my_code_title: '自分の紹介コード',
        my_code_desc: 'コードを送ると友だちがすぐ参加できます。',
        my_code_copied: '紹介コードをコピーしました。',
        copy: 'コピー',
        share: '友だちに共有する',
        share_message: 'すくすくぱしゃで一緒に写真を記録しよう。紹介コード: {{code}}',
        share_copied: '共有メッセージをコピーしました。',
        phone_title: '携帯電話の認証が必要です',
        phone_desc: 'チーズを安全に付与するため、携帯電話番号を先に確認してください。',
        phone_action: '携帯電話を認証',
        received_title: '紹介コードを受け取りましたか？',
        received_desc: '友だちから受け取った6桁のコードを入力してください。',
        code_placeholder: '紹介コード6桁',
        marketing_consent: '招待特典やイベントに関するマーケティング情報の受信に同意します。',
        register_action: '紹介コードを登録',
        registering: '登録中',
        registered_title: '紹介コードの登録完了',
        registered_desc: '友だちと一緒にチーズを受け取りました。写真の記録を始めましょう。',
        registered_toast: '紹介コードを登録しました。',
        market_title: '紹介コードを登録できません',
        market_desc: 'グッズショップで購入履歴のあるアカウントは招待特典の対象外です。',
        app_required_title: 'アプリで確認できます',
        app_required_desc: 'すくすくぱしゃアプリで開くと紹介コードが表示されます。',
        notice_title: 'ご案内',
        notice_expiry: '招待でもらったチーズの有効期限は付与日から90日です。',
        notice_limit: '友だちを最大10人まで招待してチーズを受け取れます。',
        notice_eligibility: '招待特典はグッズショップの購入履歴がないアカウントに初回1回のみ提供されます。',
        retry: 'もう一度試す',
        error: {
          load: '紹介コードの情報を読み込めませんでした。',
          invalid_code: '紹介コード6桁をもう一度確認してください。',
          marketing_required: '特典を受け取るにはマーケティング情報の受信に同意してください。',
          register: '紹介コードを登録できませんでした。しばらくしてからもう一度お試しください。',
          copy: '紹介コードをコピーできませんでした。',
          share: '紹介コードを共有できませんでした。',
        },
      },
      verify_account: {
        back: '戻る',
        title: '携帯電話認証',
        hero_eyebrow: '安全な家族のつながり',
        hero_title: '携帯電話番号を\n一度だけ確認します',
        hero_desc: '確認済みの番号で招待特典とチーズを安全につなげます。',
        steps_aria: '携帯電話認証の進行状況',
        step_phone: '番号入力',
        step_code: '認証番号',
        step_done: '認証完了',
        phone_title: '携帯電話番号を入力してください',
        phone_desc: '6桁の認証番号をSMSで送信します。',
        phone_label: '携帯電話番号',
        phone_placeholder: '数字のみ入力',
        send_code: '認証番号を受け取る',
        change_phone: '番号を変更',
        code_title: 'SMSで届いた番号を入力してください',
        code_desc: '{{phone}}に送信した6桁の番号を確認してください。',
        code_label: '認証番号6桁',
        code_placeholder: '認証番号を入力',
        verify_action: '認証を完了',
        resend: '認証番号を再送信',
        done_title: '携帯電話認証が完了しました',
        done_desc: '家族招待の特典とチーズを安全に受け取れるようになりました。',
        done_action: '友だち招待に戻る',
        app_required_title: 'アプリで認証できます',
        app_required_desc: 'すくすくぱしゃアプリでアカウントと携帯電話番号を安全につなげられます。',
        privacy_note: '入力した番号は本人確認と招待特典の提供にのみ使用します。',
        retry: 'もう一度試す',
        error: {
          load: 'アカウント情報を読み込めませんでした。',
          phone: '携帯電話番号をもう一度確認してください。',
          send: '認証番号を送信できませんでした。しばらくしてからもう一度お試しください。',
          code: '6桁の認証番号をもう一度確認してください。',
          expired: '認証時間が過ぎました。認証番号を再送信してください。',
          complete: '携帯電話認証を完了できませんでした。しばらくしてからもう一度お試しください。',
        },
      },
      report: {
        back: '戻る',
        title: 'アップロード報告',
        eyebrow: '動画のアップロードでお困りですか？',
        hero_title: 'アップロードできなかった動画を\n送っていただけますか',
        hero_desc: '元の動画を選ぶだけで大丈夫です。端末やアカウント情報は自動で一緒に送られます。100MBまで送れます。',
        pick_video: '動画を選ぶ',
        pick_hint: 'mp4、mov など · 最大100MB',
        file_selected: '動画が選択されました',
        comment_label: 'コメント（任意）',
        comment_placeholder: 'アップロードできなかった状況を簡単に書いてください。',
        submit: '送る',
        submitting: '送信中',
        done_title: '受け取りました',
        done_desc: '確認して、もっと快適にアップロードできるよう見直します。ありがとうございます。',
        done_action: '閉じる',
        app_required_title: 'アプリでのみ報告できます',
        app_required_desc: 'すくすくぱしゃアプリで開くと動画を送れます。',
        privacy_note: '動画情報の確認のみに使い、アカウントや端末などの内部情報は別途確認しません。アップロードされた内容は1週間後に破棄されます。',
        error: {
          size: '動画は100MB以下のみ送れます。',
          type: '動画ファイルのみ選択できます。',
          submit: '送れませんでした。しばらくしてからもう一度お試しください。',
        },
      },
      // --- 번역 추가 시작 ---
      unit: {
        ml: 'ml',
        min: '分',
        hour: '時間',
        times: '回',
      },
      week: {
        this_week: '今週',
        no_data: 'データがありません。',
        download_failed: 'ダウンロードに失敗しました。',
        excel_generate_failed: 'ファイルを作れませんでした。',
        excel_share_failed: 'ファイルを共有できませんでした。',
        excel_filename_prefix: '育児記録_',
        share_title: '育児記録',
        share_text: '{{range}}の育児記録です。',
        default_baby_name: '赤ちゃん',
        share_excel_title: '書き出す',
        excel_sheet_title: '書き出す',
        excel_sheet_desc: '最大6か月分まで一度に選べます。',
        excel_range_label: '期間',
        excel_preset_week: '表示中の週',
        excel_preset_day: '表示中の日',
        excel_preset_1m: '最近1か月',
        excel_preset_3m: '最近3か月',
        excel_preset_6m: '最近6か月',
        excel_from: '開始日',
        excel_to: '終了日',
        excel_range_too_long: '期間は最大6か月まで選べます。',
        excel_export: '書き出す',
        excel_fetching: '記録を読み込んでいます...',
        excel_open_browser: 'ブラウザで保存',
        excel_open_browser_hint: '共有できないときはブラウザで保存できます。',
        excel_share: '共有',
        excel_share_hint: 'ファイルの準備ができました。共有を押してください。',
        excel_sheet_records: '記録',
        excel_sheet_daily: '日別',
        excel_col_date: '日付',
        excel_col_start: '開始',
        excel_col_end: '終了',
        excel_col_category: '種類',
        excel_col_volume: '量(ml)',
        excel_col_duration: '時間(分)',
        excel_col_memo: 'メモ',
        excel_col_feeding_count: '授乳(回)',
        excel_col_feeding_volume: '授乳量(ml)',
        excel_col_sleep_min: '睡眠(分)',
        excel_col_nappy_count: 'おむつ(回)',
        excel_col_other_count: 'その他(回)',
        pattern_analysis_title: 'パターン分析',
        pattern_analysis_desc: '直近7日間の記録をもとに、睡眠・授乳・おむつのパターンを確認します。',
        pattern_analysis_loading: 'パターンを分析しています...',
        pattern_analysis_loading_desc: '記録が多い場合は少し時間がかかることがあります。',
        pattern_analysis_failed: 'パターン分析に失敗しました。',
        pattern_analysis_invalid_response: 'パターン分析サーバーの応答を読み取れませんでした。アプリを最新状態で開き直してください。({{status}}, {{contentType}})',
        pattern_analysis_ai_model: 'AIパターン分析',
        pattern_analysis_basic_model: '基本統計分析',
        pattern_analysis_llm_fallback: 'AI評価の応答形式が一致しなかったため、基本統計分析の結果を表示しています。',
        pattern_analysis_min_days: 'パターン分析には記録のある日が最低{{count}}日必要です。現在は{{current}}日分です。',
        pattern_analysis_no_items: '分析できる記録がありません。',
        pattern_analysis_retry: 'もう一度分析',
        pattern_analysis_model: '分析方式',
        pattern_analysis_quality: 'データ品質',
        pattern_analysis_overall: '総合評価',
        pattern_analysis_category_reviews: 'カテゴリ別評価',
        pattern_analysis_category_patterns: 'カテゴリ別パターン',
        pattern_analysis_category_insights: 'カテゴリ別の主要パターン',
        pattern_analysis_feeding_timeline: '授乳の時間帯',
        pattern_analysis_feeding_intervals: '授乳間隔',
        pattern_analysis_daily_routine: '一日のルーティン',
        pattern_analysis_daily_flow: '一日の流れ',
        pattern_analysis_count: '回数',
        pattern_analysis_avg_amount: '平均量',
        pattern_analysis_avg_duration: '平均継続',
        pattern_analysis_avg_interval: '平均間隔',
        pattern_analysis_points: '確認ポイント',
        pattern_analysis_flexible_time: '流動的',
        pattern_analysis_record_days: '{{current}}/{{total}}日',
        pattern_analysis_days_seen: '{{count}}日観察',
        pattern_analysis_occurrences: '{{count}}回',
        pattern_analysis_avg_duration_value: '平均{{duration}}',
        pattern_analysis_feeding_group: '授乳',
        pattern_analysis_food_group: '離乳食/おやつ',
        pattern_analysis_play_group: '遊び',
        pattern_analysis_care_group: '健康/ケア',
        pattern_analysis_review: '評価',
        pattern_analysis_pattern: 'パターン',
        pattern_analysis_feeding_time: '授乳',
        pattern_analysis_interval: '間隔',
        pattern_analysis_routine: 'ルーティン',
        pattern_analysis_empty: '表示できる分析結果がありません。',
        summary: '週間まとめ',
        daily_avg_basis: '1日平均',
        weekly_total: '週 {{value}}',
        per_day: '1日 {{value}}',
        sleep_night: '夜',
        sleep_nap: '昼',
        summary_empty: '今週は記録がありません',
      },
      interval: {
        empty_title: '間隔データがありません',
        empty_desc: '選択したカテゴリに表示できるイベントがまだありません。',
        loading_more: '前の週のデータを読み込み中...',
        overlap_label: '{{time}}重なり',
        interval_label: '{{time}}間隔',
        elapsed_label: '{{time}}経過',
        overlap_marker: '重なり',
        interval_marker: '間隔',
        elapsed_marker: '経過',
        now_label: '今',
        last_record_label: '最後の記録',
        since_last_record: '最後の記録から',
        day_today: '今日',
        day_yesterday: '昨日',
        gap_mode: {
          label: '間隔の基準',
          end_to_start: '終 → 始',
          start_to_start: '始 → 始',
          end_to_end: '終 → 終',
          end_to_start_desc: '前回の終了時刻から次の開始まで',
          start_to_start_desc: '開始時刻同士の周期',
          end_to_end_desc: '終了時刻同士の間隔',
        },
      },
      growth_banner: {
        aria_label: '成長記録の案内',
        default_baby_name: '赤ちゃん',
        empty_title: '{{name}}の身長・体重が気になるなら 👀',
        empty_desc: '最初の記録を残して成長の流れグラフを確認',
        empty_cta: '{{name}}の最初の記録を残す',
        stale_title: '記録してから{{days}}日が経ちました',
        stale_desc: '更新して今月の成長を確認',
        recent_title: '{{name}}の成長記録が更新されました',
        recent_desc: '今月の成長グラフを確認',
        delta_title_weight: '{{name}}は{{amount}}kg大きくなりました',
        delta_title_height: '{{name}}は{{amount}}cm伸びました',
        delta_title_head: '{{name}}の頭囲が{{amount}}cm大きくなりました',
        delta_desc_generic: '成長の流れを続けて確認しましょう',
        percentile_short: {
          check: 'P{{percentile}} · 確認が必要です 🙏🏻',
          small: 'P{{percentile}} · 小さめです 🌱',
          average: 'P{{percentile}} · 平均範囲です 💕',
          big: 'P{{percentile}} · 大きめです 💪🏻',
          very_big: 'P{{percentile}} · かなり大きめです 💪🏻',
        },
      },
      import_file: {
        title: '記録の取り込み',
        badge: '記録のお引越し',
        headline: '育児記録も\nすくすくぱしゃで',
        subtitle: '他のアプリでつけてた授乳の記録もまとめてすくすくぱしゃに移動できます！',
        status_waiting: '待機中',
        status_parsing: '解析中',
        status_moving: '移行中',
        status_importing: '保存中',
        status_imported: '移行完了',
        status_ready: '準備完了',
        status_error: '確認が必要',
        step_select: 'ファイル選択',
        step_review: '内容を確認',
        step_import: '取り込み',
        breakdown_title: 'カテゴリ別',
        source_app: '他のアプリ',
        move_label: '記録の移動先',
        from_label: '元の記録',
        from_value: '他のアプリでつけてた授乳の記録もまとめてすくすくぱしゃに移動できます！',
        to_label: '新しい保存先',
        to_value: '',
        supported_label: '対応ファイル形式',
        unknown_extension: 'ファイル',
        selected_desc: '{{size}} · {{extension}}',
        drop_title: 'ファイルを選択',
        drop_active: 'ここに置いてください',
        drop_desc: 'TXT、CSV、ZIPファイルに対応しています。',
        target_baby_title: '記録するお子さまを選択',
        target_baby_desc: '{{babyName}}のベビーログにデータを移動します',
        target_baby_select_label: '記録するお子さまを選択',
        target_baby_loading: 'お子さま情報を読み込んでいます。',
        target_baby_error: 'お子さま情報を読み込めませんでした。アプリから開き直すと自動で確認します。',
        target_baby_empty: 'お子さま情報を確認できると、どのお子さまに取り込むか表示します。',
        target_baby_no_carer: '育児記録の作成権限があるお子さまだけを表示します。取り込めるお子さまがありません。',
        target_baby_unknown: '名前のないお子さま',
        target_baby_fallback: 'ID {{babyIdx}}のお子さま',
        moving_file: '記録を移行中',
        moving_title: '移行中です',
        moving_desc: 'ファイル内の記録を整理しています。少しだけお待ちください。',
        change_file: '別のファイルを選択',
        remove_file: 'ファイルを削除',
        cta_idle: '次へ',
        cta_ready: '取り込む',
        cta_importing: '取り込み中',
        cta_done: '移行完了',
        unsupported_title: 'ファイル形式の確認をお願いします',
        unsupported_desc: 'こちらの形式は現在、自動取り込みの対象外となっています。TXT・CSV・ZIPのファイルをお試しいただくか、下記よりお気軽にご相談ください。',
        too_large_title: 'ファイルサイズの確認をお願いします',
        too_large_desc: 'お手数ですが、50MB以下のファイルをお選びください。分割などが難しい場合は、下記よりご相談いただけます。',
        received_title: '移行が完了しました',
        received_summary: '合計{{count}}件を移行',
        received_desc: '選択したお子さまの育児記録に保存しました。授乳間隔タブで再読み込みすると、取り込んだ記録を確認できます。',
        received_action: '確認',
        import_progress_title: '{{current}}/{{total}}件を保存中',
        import_progress_desc: '1件ずつ保存しています。成功 {{completed}}件 · 失敗 {{failed}}件',
        import_failed_title: '一部の記録を取り込めませんでした',
        import_failed_desc: '{{total}}件中{{failed}}件の保存に失敗しました。すでに保存された記録がある可能性があるため、再試行前に確認が必要です。',
        import_missing_auth: 'ユーザー認証情報を確認できないため保存できません。アプリから開き直してください。',
        import_missing_baby: '記録を保存するお子さまを先に選択してください。',
        parse_error_title: 'ファイルの内容を一緒に確認させてください',
        parse_failed: 'ファイルの読み込み中に問題が発生しました。お手数ですが、書き出し直したファイルをお試しいただくか、下記よりご相談ください。',
        encoding_error: '文字を正確に読み取ることが難しいため、取り込みを一時停止しています。お手数ですが、元のアプリからもう一度書き出してお試しください。同じ状況が続く場合は、お気軽にご相談ください。',
        parse_empty: '日付と時刻が含まれる取り込み対象の記録を確認できませんでした。ファイルをご確認いただくか、下記より移行方法についてご相談ください。',
        summary_only_csv_error: 'このファイルには、1日の授乳量や回数など、大切な記録が日ごとの合計として保存されています。個別の時刻が含まれていないため、現在はそのまま授乳記録へ移すことが難しい形式です。授乳間隔を正確に保つため、仮の時刻を補っての取り込みは控えています。',
        summary_only_csv_next_step: '元のアプリで日付と時刻を含む詳細な記録を書き出せる場合は、そちらをお選びください。日別の合計のみが提供される場合も、下記よりお気軽にご相談ください。可能な移行方法を一緒に確認いたします。',
        cta_file_unavailable: 'ファイルの確認をお願いします',
        zip_no_parseable_files: 'ZIP内に取り込み対象のTXT・CSVファイルを確認できませんでした。内容をご確認いただくか、下記よりご相談ください。',
        zip_unsupported_compression: 'こちらのZIP圧縮方式は、現在、自動で展開することが難しい形式です。お手数ですが、TXT・CSVを直接お選びいただくか、標準のZIP形式でお試しください。',
        error_contact_title: '大切な記録の移行をお手伝いします',
        error_contact_desc: '記録の移行についてお困りの際は、マイページ > お問い合わせよりお気軽にご相談ください。',
        unsupported_records_title: '一部の記録はメモとして残しました',
        unsupported_records_desc: '自動変換できなかった{{count}}件はメモとして保存しました。',
        skipped_records_title: '確実に変換できない記録はスキップしました',
        skipped_records_desc: '誤って保存される可能性がある{{count}}件は取り込みませんでした。元の内容は以下で確認できます。',
        summary_only_csv_title: '日ごとの合計が記録されたファイルです',
        summary_only_csv_notice: '個別の時刻がない日別集計CSV {{count}}件は、今回の取り込み対象から除いています。',
        warning_line: '{{line}}行目',
        warning_show_more: 'スキップした残り{{count}}件を表示',
        warning_reason_summary_only_csv: '個別の記録時刻がない日別集計CSV',
        warning_reason_unsupported_record: '未対応、または内容を正確に読み取れない記録',
        warning_reason_invalid_datetime: '日付または時刻を確認できません',
        warning_reason_incomplete_sleep: '睡眠の開始・終了、または継続時間を確認できません',
        warning_reason_inconsistent_sleep: '睡眠の時刻と記録された継続時間が一致しません',
        warning_reason_unknown: '安全に変換できない記録',
        preview_title: 'プレビュー',
        preview_desc: '取り込んだファイルをV2 insert item形式に変換した結果です。',
        preview_source_label: '元の記録',
        preview_result_label: '取り込む記録',
        parsed_items: '項目',
        parsed_dates: '日付',
        memo_fallbacks: 'メモ',
        missing_baby_idx: 'baby_idxが見つかりません。保存時には実際の赤ちゃん識別子を入れる必要があります。',
        meridiem_am: '午前',
        meridiem_pm: '午後',
        left_side: '左',
        right_side: '右',
        category_alert: '通知',
        sleep_type_nap: '昼寝',
        sleep_type_night: '夜の睡眠',
        purpose_infant_checkup: '乳幼児健診',
        purpose_vaccination: '予防接種',
        purpose_medical_visit_issue: '診察',
        json_payload_title: '保存用JSON payloadを見る',
        privacy_title: '大切な記録を、丁寧にお預かりします',
        privacy_desc: '選択したファイルは取り込み確認のためだけに使用されます。',
      },
      // --- 번역 추가 끝 ---
      common: {
        loading_alt: '読み込み中',
        loading: '読み込み中...',
        page_load_failed: '画面を読み込めませんでした。もう一度お試しください。',
        reload_page: '再読み込み',
        today: '今日',
        app_title: 'ベビーログ',
        app_only_title: 'アプリからアクセスしてください',
        app_only_desc: '本サービスはアプリからのみご利用いただけます。',
        close: '閉じる',
        week_ago: '週間前',
        month_ago: 'ヶ月前',
        all: 'すべて',
        me: '自分',
      },
      growth_report: {
        title: '成長レポートを見る',
        desc: '身長・体重などの成長分析、パーセンタイル比較',
        open_aria: '成長レポートを見る',
      },
      growth_page: {
        title: '成長レポート',
        back: '戻る',
        default_baby_name: 'お子さま',
        age_days: '生後{{count}}日目',
        age_months: '{{count}}ヶ月目',
        dday_label: 'D+{{count}}',
        dday_before_label: 'D-{{count}}',
        status: 'すくすく成長中',
        measurement_action: '測定記録',
        measurement_base: '{{date}}の測定基準',
        metric_tabs_label: '成長指標',
        metric_empty: '測定記録はまだありません',
        recorded_base: '{{date}}の記録基準',
        delta_sentence: {
          weight_up: '前回の記録より体重が{{value}}増えました',
          weight_down: '前回の記録より体重が{{value}}減りました',
          height_up: '前回の記録より身長が{{value}}伸びました',
          height_down: '前回の記録より身長が{{value}}小さく測定されました',
          head_up: '前回の記録より頭囲が{{value}}大きくなりました',
          head_down: '前回の記録より頭囲が{{value}}小さく測定されました',
        },
        delta_since_date: {
          weight_up: '{{date}}より体重が{{value}}増えました',
          weight_down: '{{date}}より体重が{{value}}減りました',
          height_up: '{{date}}より身長が{{value}}伸びました',
          height_down: '{{date}}より身長が{{value}}低く測定されました',
          head_up: '{{date}}より頭囲が{{value}}大きくなりました',
          head_down: '{{date}}より頭囲が{{value}}小さく測定されました',
        },
        milestones: {
          entry: '瞬間を残す',
          entry_aria: '瞬間を残す',
          empty_title: '思い出に残っている瞬間はありますか？',
          empty_description: '思い浮かぶ瞬間をひとつ残してみましょう。',
          suggest_row_label: '{{month}}か月 · 最近残したい瞬間',
          write_own: '自分で書く',
          past_hint: '過ぎた瞬間でも大丈夫',
          example_badge: '例',
          example_desc: '残した瞬間はこうして積み重なります。最初の瞬間を残してみましょう',
          sheet_title: '瞬間を残す',
          sheet_edit_title: '瞬間を編集',
          title_label: 'どんな瞬間でしたか?',
          title_placeholder: '例: はじめて寝返りした日',
          date_label: '日付',
          photo_label: 'その日の写真',
          visibility_label: '公開範囲',
          visibility_private: '自分だけ',
          visibility_parents: '両親のみ',
          visibility_maternal: 'ママ側の家族',
          visibility_paternal: 'パパ側の家族',
          visibility_all: '全員に公開',
          visibility_badge: {
            private: '自分だけ',
            parents: '両親のみ',
            maternal: 'ママ側',
            paternal: 'パパ側',
            kin: '両方の家族',
          },
          photo_attach_label: 'この日の写真を添える (任意)',
          photo_change: '写真を変える',
          photo_none_title: 'この日の写真はありません',
          photo_none_desc: '言葉だけでも十分です',
          when_label: 'いつでしたか?',
          who_label: '誰が見られますか?',
          save_cta: 'この瞬間を残す',
          required: 'どんな瞬間だったか入力してください',
          saved: '大切な瞬間を残しました',
          save_failed: '保存できませんでした。しばらくしてからもう一度お試しください。',
          deleted: '瞬間の記録を削除しました',
          delete_failed: '削除できませんでした。しばらくしてからもう一度お試しください。',
          delete_confirm: 'この瞬間の記録を削除しますか?',
          edit_aria: '{{title}}を編集',
        },
        milestone_presets: {
          first_outing: 'はじめてのお出かけ',
          first_smile: 'はじめての笑顔',
          first_babble: 'はじめての喃語',
          first_rollover: 'はじめての寝返り',
          first_solid_food: 'はじめての離乳食',
          first_sit: 'ひとりでおすわり',
          first_tooth: 'はじめての歯',
          first_crawl: 'はいはい',
          first_pull_to_stand: 'つかまり立ち',
          first_word: 'はじめての言葉',
          first_step: 'はじめての一歩',
          first_run: 'はじめての駆け足',
        },
        update: '記録',
        moments_title: '私たちの瞬間',
        records_count: 'これまでに{{count}}回の記録を残しました',
        moments_empty_hint: '成長の瞬間をここに集めます',
        timeline_latest: '最近',
        timeline_group_count: '{{count}}件',
        timeline_delta_prev: '前回より{{delta}}',
        view_profile_photo: 'プロフィール写真を見る',
        view_record_media: '写真・動画を見る',
        media_viewer_aria: 'メディアビューア',
        media_swipe_hint: '上下にスワイプ',
        slideshow_revisit_count: '写真・動画{{count}}件をもう一度見る',
        slideshow_play: 'スライドショーで見る',
        slideshow_aria: '成長スライドショー',
        slideshow_pause: '一時停止',
        slideshow_resume: '再生',
        slideshow_prev: '前へ',
        slideshow_next: '次へ',
        slideshow_summary_title: '{{days}}日間でこんなに成長しました',
        slideshow_summary_title_short: 'これまでにこんなに成長しました',
        slideshow_first: '最初',
        slideshow_latest: '最近',
        slideshow_replay: 'もう一度見る',
        slideshow_export: '成長フィルムを作る',
        slideshow_export_title: '成長フィルムにして残す',
        slideshow_export_subtitle: 'これまでの瞬間を一本のフィルムに紡ぎます',
        slideshow_export_auto_note: 'たくさんの瞬間から{{count}}の場面を選んでおきました。タップで好きな場面に入れ替えられます。',
        slideshow_export_limit_note: 'フィルムには最大{{count}}の場面まで残せます',
        slideshow_export_selected: '{{selected}} / {{max}}の場面をフィルムに',
        slideshow_export_start: '{{count}}つの瞬間でフィルムを作る',
        slideshow_export_min: '残したい瞬間を2つ以上選んでください',
        slideshow_export_rendering: '大切な瞬間をつむいでいます…',
        slideshow_export_cancel: 'また今度にする',
        slideshow_export_done: '成長フィルムを大切に保存しました',
        slideshow_export_failed: 'フィルムを完成できませんでした。しばらくしてからもう一度お試しください。',
        slideshow_music: 'BGM',
        slideshow_music_title: 'BGM',
        slideshow_music_off: '音楽なし',
        slideshow_music_note: '成長フィルムにも一緒に流れます',
        slideshow_entry: '成長ストーリー',
        slideshow_entry_aria: '成長ストーリーのスライドショーを見る',
        slideshow_moments_count: '{{count}}個の瞬間が集まりました',
        slideshow_locked_toast: '写真付きの記録が{{count}}件そろうと成長ストーリーが見られます',
        slideshow_card_desc: '{{count}}個の瞬間が集まりました · タップして再生',
        slideshow_card_locked_desc: '写真付きの記録が{{min}}件そろうと見られます',
        slideshow_teaser_title: '写真付きの記録があと{{count}}件で成長フィルムが作れます',
        slideshow_teaser_desc: '写真と一緒に記録すると、成長の記録を一本のフィルムに紡ぎます',
        first_extra_title: '生まれた瞬間も残しますか？',
        first_extra_desc: '出生時と予定日を加えると曲線がより正確になります',
        first_extra_due_hint: '予定日を入力すると、成長曲線をより正確に確認できます。（任意）',
        first_extra_due_placeholder: '未入力',
        first_extra_birth_label: '生まれたとき',
        first_extra_birth_is_today: '記録した日付が誕生日です。',
        step_skip: 'スキップ',
        step_next: '次へ',
        step_back: '戻る',
        update_soon: '成長記録の入力はアプリで準備中です。',
        editor_title: '成長を記録',
        editor_edit_title: '成長記録を編集',
        editor_measured_date: '測定日',
        editor_dday_inline: '{{dday}}日',
        editor_change_date: '変更',
        editor_save: '保存',
        editor_delete: '削除',
        editor_delete_confirm: 'この日の成長記録を削除しますか？',
        editor_required: '体重、身長、頭囲のうち1つ以上を入力してください。',
        editor_date_before_birth: '誕生日より前の日付は選択できません。',
        editor_date_future: '未来の日付は選択できません。',
        editor_range_invalid: '入力値の範囲を確認してください。体重はkg、身長と頭囲はcm単位です。',
        editor_range_weight: '体重はkg単位で、0より大きく{{max}}kg未満で入力してください。',
        editor_range_height: '身長はcm単位で、{{min}}〜{{max}}cmの範囲で入力してください。',
        editor_range_head: '頭囲はcm単位で、{{min}}〜{{max}}cmの範囲で入力してください。',
        editor_bmi_invalid: '身長と体重をもう一度確認してください。身長はcm単位で入力してください。',
        editor_saved: '成長記録を保存しました。',
        editor_saved_with_extra_warning: '成長記録は保存しました。予定日または出生時の情報をもう一度確認してください。',
        editor_deleted: '成長記録を削除しました。',
        photo_label: '写真',
        photo_optional: '任意',
        photo_none_option: '写真なし',
        photo_load_more: 'もっと見る',
        photo_add: '写真を追加',
        photo_add_desc: '測定の瞬間も一緒に残します',
        photo_attached: '一緒に記録する写真',
        photo_change: '写真を変更',
        photo_remove: '写真を削除',
        photo_sheet_title: '写真を選択',
        photo_library_title: 'アプリ写真帳',
        photo_library_pending: '連携予定',
        photo_representative: 'その日の代表写真',
        photo_uploaded_title: 'その日の写真',
        photo_from_day: 'その日の写真',
        photo_count: '{{count}}枚',
        photo_select_aria: '写真を選択',
        photo_tip: 'ここに写真・動画も一緒に残せます',
        photo_loading: '写真を読み込み中',
        photo_load_failed: '写真を読み込めませんでした。もう一度お試しください。',
        photo_retry: '再読み込み',
        photo_none_for_date: 'この日にアップロードされた写真はありません。',
        load_failed: '成長記録を読み込めませんでした。',
        save_failed: '成長記録を保存できませんでした。',
        delete_failed: '成長記録を削除できませんでした。',
        profile_incomplete: 'お子さまの誕生日または性別がないため、成長パーセンタイルを計算できません。',
        birthday_required_title: 'お子さまの誕生日情報が必要です',
        birthday_required_desc: 'マイページにお子さまの誕生日を登録すると、成長記録とパーセンタイル計算を確認できます。',
        birthday_chip: '{{date}}',
        due_date_chip: '予定 {{date}}',
        profile_settings_aria: '成長基準情報を設定',
        profile_settings_title: '成長基準情報',
        profile_settings_desc: '予定日を入力すると、成長分析をより正確にできます。',
        precision_hint: '予定日を入力すると、成長分析をより正確にできます。',
        precision_hint_saved: '予定日{{date}}の基準情報を保存しました。',
        due_date_label: '予定日',
        due_date_empty: '予定日はまだ入力されていません。',
        due_date_clear: '空にする',
        profile_settings_saved: '予定日情報を保存しました。',
        profile_settings_save_failed: '予定日情報を保存できませんでした。',
        empty_records: '保存された成長記録はまだありません。',
        summary_empty_title: '体重・身長・頭囲を記録しましょう',
        empty_title: '成長記録はまだありません',
        empty_desc: '体重、身長、頭囲のうち分かる値を1つ入力するだけで成長の流れを確認できます。',
        empty_cta: '成長を記録する',
        empty_collage_title: 'この大切な瞬間に、身長と体重も一緒に',
        empty_collage_desc: '写真と一緒に成長記録を残すと、赤ちゃんの成長の流れがひと目で分かります。',
        empty_eyebrow: '今日から、成長のストーリー',
        empty_hero_title: '{{name}}の成長ストーリーを始めましょう',
        empty_hero_desc: '体重・身長・頭囲、どれか1つの記録から物語が始まります。',
        edit_record_aria: '{{date}}の成長記録を編集',
        create_report: '成長レポートを作成',
        create_report_aria: '成長レポートを作成',
        create_report_soon: '成長レポートの作成はアプリで準備中です。',
        report_builder: {
          entry_desc: '今月の写真と記録を一枚にまとめます',
          title: '成長レポートを作成',
          intro_heading: '{{name}}の最近を\n一枚に残しませんか？',
          intro_desc: '写真1枚と最近の記録を選ぶだけで、AIが手書き風に描きます。',
          intro_sample: 'ほかの家族が作った例',
          intro_sample_alt: '成長レポートの例',
          intro_meta: '4ステップ · 約1分',
          start: 'はじめる',
          back: '前のステップ',
          next: '次へ',
          step_of: '{{step}} / {{total}} · {{label}}',
          step_period: '期間',
          step_photo: '写真',
          step_records: '記録',
          step_finish: '仕上げ',
          period_question: 'どの時期を残しますか？',
          period_hint: 'タップするとすぐ次へ進みます。',
          preset_this_month: '今月',
          preset_last_month: '先月',
          preset_three_months: '最近3か月',
          preset_custom: '日付を指定',
          preset_custom_desc: '開始日と終了日を決めます',
          start_date: '開始日',
          end_date: '終了日',
          period_error: '今日までの1年以内の期間を選択してください。',
          photo_question: '中央に入れる写真を1枚',
          photo_hint: 'この期間の写真{{count}}枚 · 最新の成長記録の写真を選んでおきました。',
          photo_hint_empty: 'この期間に添えた写真がありません。スマートフォンの写真1枚で作成できます。',
          upload_tile: '写真をアップロード',
          local_photo_label: 'アップロードした写真',
          records_question: 'この記録を入れます',
          records_hint: '×で外し、「答え N」をタップすると別の家族の答えに変えられます。',
          records_count: '{{count}} / {{max}} 件',
          answers_count: '答え {{count}}',
          records_layout_hint: '写真の両側に手書き風で入ります。外した枠はそのまま空きになります。',
          option_sheet_title: '{{label}} · 答えを選ぶ',
          option_sheet_remove: 'この記録を外す',
          add_record: '{{label}}を追加',
          remove_record: '{{label}}を外す',
          records_empty: 'この期間に入れる記録はまだありません。写真だけでも作成できます。',
          finish_question: 'タイトルとひとこと',
          finish_hint: 'ひとことは空でも大丈夫です。',
          title_label: 'タイトル · 写真の上に手書き風で',
          note_label: '家族からのひとこと · 任意',
          note_placeholder: '例：毎日新しい姿を見せてくれてありがとう',
          summary: '{{period}} · 写真1枚 · 記録{{count}}件',
          summary_hint: '直したいところがあれば前のステップに戻れます',
          create: 'このまま作る',
          check_status: '完成したか確認する',
          default_title: '{{name}}の成長記録',
          ai_notice: '選択した写真と記録は、画像作成のためAIに送信されます。',
          not_ready: '成長レポートは準備中です。記録の選択は先に確認できます。',
          retry: 'もう一度読み込む',
          generating_title: '{{name}}の物語を描いています',
          generating_desc: '写真と記録を一枚に丁寧にまとめています。少しお待ちください。',
          complete_title: '成長レポート完成',
          complete_desc: '{{name}}との大切なひと月を一枚にまとめました。',
          complete_desc_period: '{{name}}の大切な成長記録を一枚にまとめました。',
          review_result: '写真と文字を確認してから保存してください。',
          result_alt: '{{name}}の成長レポート',
          save: '写真を保存',
          saving: '保存中…',
          share: '共有',
          sharing: '共有中…',
          saved: '写真を保存しました。',
          delivery_failed: '写真を渡せませんでした。もう一度お試しください。',
          filename: '成長レポート',
          select_photo_date: '{{date}}の写真を選択',
          error_photo_type: 'JPG、PNG、WebPの写真を選択してください。',
          error_photo_size: '写真が大きすぎて準備できませんでした。別の写真を選択してください。',
          error_timeout: '完成まで時間が必要です。少し後でもう一度お試しください。',
          error_permission: 'このお子さまの成長レポートを作成する権限がありません。',
          error_daily_limit: '本日作成できる成長レポートの上限に達しました。明日またお試しください。',
          error_busy: 'すでに別の成長レポートを作成中です。少し後で確認してください。',
          error_provider_limit: 'ただいま作成リクエストが混み合っています。少し後でもう一度お試しください。',
          error_generic: '成長レポートを作成できませんでした。選択内容は残っているので、もう一度お試しください。',
        },
        birth: '出生',
        month_short: '{{count}}ヶ月',
        current_value: '現在 {{value}}',
        growth_since_birth: '生まれた日から{{value}}大きくなりました',
        growth_since_first_record: '最初の記録から{{value}}大きくなりました',
        growth_since_previous_record: '前回の記録比 {{value}}',
        legend_median: '同月齢の平均',
        legend_range: '標準範囲',
        compare_title: '同月齢との比較',
        compare_subtitle: '同月齢100人の中でのお子さまの位置です',
        corrected_age_caption: '出産予定日を反映した修正月齢基準です（{{date}}）',
        corrected_age_caption_plain: '出産予定日を反映した修正月齢基準です',
        profilebook: {
          eyebrow: 'わが子の100問100答',
          intro_title: 'みんなで書く\n{{name}}の最近の姿',
          intro_description: '好きなものから小さな習慣まで、\n一緒に見守るみんなで少しずつ綴ります。',
          intro_hint: '質問を選んで答えを書きましょう。回答済みのカードで以前の答えも見られます。',
          skip: '先に見てみる',
          start: '{{name}}の最近を書く',
          title: '{{name}}の最近の姿',
          heading: 'みんなで書く\n{{name}}の最近の姿',
          question_progress: '{{total}}問中{{answered}}問に答えました',
          subtitle_empty: '好きなものから小さな習慣まで、みんなの答えで綴ります。',
          subtitle_filled: '好きなものから小さな習慣まで、みんなの答えで綴ります。',
          intro: '案内',
          intro_link: '最近の姿について',
          empty_heading: 'どんな姿から綴りますか？',
          empty_hint: '気になる質問をひとつ選んでください。',
          cards_hint: 'カードを押すと、これまでの答えを見られます。',
          first_answer: '最初の答えを書く',
          first_answer_aria: '{{question}}に最初の答えを書く',
          add_question: '質問を追加',
          filters_label: '質問の種類',
          question_likes: '最近いちばん好きなものは？',
          question_dislikes: '最近ちょっと苦手なものは？',
          question_hobby: '最近夢中になっている遊びは？',
          question_talent: '最近新しくできるようになったことは？',
          history_title: '質問の記録',
          compose_new: '新しい答えを書く',
          compose_edit: 'この答えを編集',
          compose_question: '質問をつくる',
          compose_save: '自分の答えを添える',
          profile_of: '{{name}}の最近',
          author_note: '{{author}}の答え',
          author_preserve_note: '{{author}}の答え · 以前の答えも残ります',
          preserve_note: '以前の答えもそのまま残ります。',
          save_complete: '最近の姿に答えを残しました。',
          custom_category: 'わたしたちだけの質問',
        },
        now_profile: {
          back_to_profile: '{{name}}の最近',
          back_to_moments: '私たちの思い出',
          timeline_badge: '最近の姿',
          timeline_open_aria: '{{question}}: {{answer}} · 回答履歴を見る',
          question_history_description: '家族が残した答えをまとめました。',
          answer_count: '答え{{count}}件',
          newest_first: '新しい順',
          show_all_answers: 'すべての答えを見る',
          edit_answer: '答えを編集',
          open_question_history: '{{question}}の答えの記録を見る',
          author_me_only: '書いた人：私',
          delete_failed: '削除できませんでした。答えは残っています。もう一度お試しください。',
          delete_conflict_error: '答えが変更されました。最新の内容を確認してから削除してください。',

          cancel_delete_record: 'キャンセル',
          question_records: 'この質問の記録',
          filter_question: '質問',
          all_questions: 'すべての質問',
          history_count: '答え{{count}}件 · 新しい順',
          reset_filters: 'すべての記録を表示',
          edit_record_short: '編集',
          delete_record_short: '削除',
          delete_record_only_hint: 'この答えだけを削除します。質問とほかの答えは残ります。',
          deleting_record: '削除中…',
          confirm_delete_record: '答えを削除',

          all_records: 'すべての記録',
          overview_title: '{{name}}の最近の姿',
          history_heading: '{{name}}に残したお話',
          filter_author: '書いた人で絞り込む',
          filter_all: 'すべて',
          filter_mine: '自分の答え',
          filter_family: '家族の答え',
          history_empty: 'ここに表示する記録はまだありません。',
          mine_answer: '自分が書いた答え',
          family_answer: '家族が書いた答え',
          unknown_answer: '書いた人は不明',
          mom_answer: 'ママが書いた答え',
          dad_answer: 'パパが書いた答え',
          add_my_answer: '自分も答えを残す',
          answer_menu: '答えのメニュー',
          create_my_answer_title: '自分の答えを残す',
          edit_my_answer_title: '自分の答えを編集',
          answer_heading_my: '私から見た{{baby}}の姿',
          answer_description_my: '赤ちゃんの今を家族と分かち合いましょう。',
          me_short: '私',
          author_me: '書いた人：私 · {{author}}',
          save_my_answer: '自分の答えを保存',
          save_latest_hint: '新しい答えが最近の姿に表示されます。',
          save_history_hint: 'この日付の答えが「すべての記録」に追加されます。',
          save_preserves_history_hint: '以前の答えは「すべての記録」に残ります。',

          close: '閉じる',
          likes_label: '好きなこと',
          dislikes_label: '苦手なこと',
          hobby_label: '趣味',
          talent_label: '得意なこと',
          add_question: '＋ 質問を追加',
          question_placeholder: '質問を書く',
          answer_placeholder: 'タップして書く',
          history_title: 'これまでの答え',
          view_records: '{{count}}件の記録を見る',
          records_title: '回答の記録',
          records_hint: '日付と書いた人を確認して、見たい記録を選んでください。',
          viewing_earlier: '以前の記録を表示しています',
          back_to_latest: '最新の回答を見る',
          latest_record: '最新の記録',
          remove_question: '質問を削除',
          open_aria: '{{name}}の最近の姿を見る',
          entry_hint: '写真をタップすると、\n{{name}}の最近の姿が開きます。',
          overview_hint: '質問ごとに、いちばん新しい答えを表示しています。',
          loading: '最近の姿を読み込んでいます。',
          load_failed: '最近の姿を読み込めませんでした。もう一度接続してください。',
          retry: 'もう一度読み込む',
          save_failed: '保存できませんでした。入力内容は残っているので、もう一度お試しください。',
          conflict_error: '別の画面で内容が変わりました。入力内容は残っています。閉じて最新の答えを確認してください。',
          permission_error: 'この内容を変更する権限がありません。自分が見つけた姿は新しい答えとして残せます。',
          legacy_available: 'この端末に以前の記録があります',
          legacy_import_hint: 'この端末に保存された答えを、家族と一緒に見られるよう取り込みます。',
          import_legacy: '以前の記録を取り込む',
          legacy_author: '以前の記録',
          author_mom: 'ママ',
          author_dad: 'パパ',
          author_family: '家族',
          author_with_role: '{{role}} · {{name}}',
          question_by: '{{name}}が作った質問',
          question_author_suffix: 'が作った質問',
          current_answer: 'いま',
          new_answer_short: '新しい答え',
          first_answer_hint: '赤ちゃんのことを教えてください',
          choose_answer: '誰が見つけた姿ですか？',
          saving: '保存中…',
          empty_answer: '最初の答えを待っています',
          new_answer: '新しい答えを残す',
          first_answer: '最初の答えを書く',
          first_record_hint: '最初の記録です',
          more_records: '+{{count}}件',
          question_menu: '質問メニュー',
          rename_question: '質問名を変更',
          new_answer_title: '新しい答えを残す',
          edit_answer_title: 'この答えを編集',
          new_question_title: '質問をもう1つ',
          rename_question_title: '質問名を変更',
          answer_label: '答え',
          question_label: '質問',
          answer_required_error: '答えをひとこと入力してください。',
          question_required_error: '質問をひとこと入力してください。',
          character_limit_error: '空白を含め{{limit}}文字まで入力できます。',
          two_line_error: '吹き出しで2行以内になるよう、少し短くしてください。',
          save_answer_action: 'この答えを残す',
          answer_editor_heading: '{{name}}のいまを残しましょう',
          question_editor_heading: '{{name}}のことをもっと知りたい',
          answer_editor_subtitle: '赤ちゃんに代わって、ひとこと残しましょう。',
          question_editor_subtitle: '家族だけの質問を作りましょう。',
          custom_question_label: 'わが家の質問',
          two_line_hint: '空白を含む · 最大2行',
          character_count: '{{count}} / {{limit}}',
          author_writes_as: '{{author}}として残す',
          question_examples_title: 'こんな質問はどうですか？',
          question_examples_hint: '選んだあとで変えられます',
          question_example_play: '好きな遊びは？',
          question_example_sleep: '寝る前の習慣は？',
          question_example_smile: 'どんな時に笑う？',
          observed_at_label: 'いつごろの姿ですか？',
          observed_at_hint: '今日入力しても、その姿が見られた日付で残せます。',
          add_record_action: '新しい記録を追加',
          save_edit_action: '編集した答えを保存',
          add_question_action: 'この質問に答える',
          save_question_action: '質問を保存',
          edit_record: 'この答えを編集',
          delete_record: 'この答えを削除',
          delete_record_confirm: 'この記録だけを削除しますか？',
          delete_question_confirm: '「{{question}}」とすべての記録を削除しますか？',
          duplicate_question_error: '同じ質問があります。別の名前を入力してください。',
          date_required_error: '日付を選択してください。',
          future_date_error: '未来の日付は記録できません。',
          before_birthday_error: '誕生日以降の日付を選択してください。',
          discard_changes_confirm: '入力中の内容を破棄して閉じますか？',
          storage_failed: 'この端末に答えを保存できませんでした。閉じる前にもう一度確認してください。',
          storage_recovery_warning: 'この端末の以前の記録を読み込めませんでした。元のデータはそのまま残し、新しい答えは家族と一緒に見る記録に保存します。',
        },
        band: {
          small: '小さめ',
          average: '平均範囲',
          big: '大きめ',
        },
        peer_comparison_label: '同月齢と比較',
        percentile_scale_end: '100パーセンタイル',
        percentile_summary: {
          check: '{{percentile}}パーセンタイル · 確認が必要',
          small: '{{percentile}}パーセンタイル · 小さめ',
          average: '{{percentile}}パーセンタイル · 平均範囲',
          big: '{{percentile}}パーセンタイル · 大きめ',
          very_big: '{{percentile}}パーセンタイル · かなり大きめ',
        },
        percentile_description: {
          check: '成長の状態を一度確認してください',
          small: '同月齢の子より小さめです',
          average: '同月齢の子の平均範囲です',
          big: '同月齢の子より大きめです',
          very_big: '同月齢の子よりかなり大きめです',
        },
        percentile_peer_label: '同月齢100人の中で',
        percentile_pill: {
          check: '{{percentile}}番目 · 確認が必要',
          small: '{{percentile}}番目 · 小さめ',
          average: '{{percentile}}番目 · 平均範囲',
          big: '{{percentile}}番目 · 大きめ',
          very_big: '{{percentile}}番目 · かなり大きめ',
        },
        percentile_comment: {
          unknown: '成長記録を保存すると\n同月齢との比較を表示します。',
          check: '全体パーセンタイルの{{percentile}}番目、\n成長の確認が必要です。🙏🏻',
          small: '全体パーセンタイルの{{percentile}}番目、\n同月齢より小さめです。🌱',
          average: '全体パーセンタイルの{{percentile}}番目、\n同月齢の平均範囲です 💕',
          big: '全体パーセンタイルの{{percentile}}番目、\n同月齢より大きめです。💪🏻',
          very_big: '全体パーセンタイルの{{percentile}}番目、\n同月齢よりかなり大きめです。💪🏻',
        },
        records_title: '測定記録',
        date: '日付',
        metrics: {
          weight: '体重',
          height: '身長',
          head: '頭囲',
        },
      },
      care_visual_settings: {
        title: '表示方法の設定',
        subtitle: '色はそのままに、区別しやすい表示へ切り替えます。',
        default: {
          title: '基本表示',
          desc: 'これまで通り、色を中心に表示します。',
        },
        distinct: {
          title: '区別を強調',
          desc: 'パターンとマーカーを表示して、似た色の区画を見分けやすくします。',
        },
        marker_categories: {
          title: 'どのカテゴリにマーカーを表示しますか？',
          desc: '選択したカテゴリの区画に小さなマーカーを追加します。',
        },
      },
      category: {
        mothers_milk: '母乳',
        formula: '粉ミルク',
        baby_food: '離乳食',
        nappy: 'おむつ',
        sleep: '睡眠',
        pumping: '搾乳',
        stored_mothers_milk: '搾乳授乳',
        hospital: '病院',
        bath: 'お風呂',
        memo: 'メモ',
        temperature: '体温',
        medication: '投薬',
        snack: 'おやつ',
        tummy_time: 'タミータイム',
        play: '遊び',
      },
      today: {
        no_baby_idx: 'お子様の識別情報が見つかりません。アプリから再度アクセスしてください。',
        quick_actions: 'ショートカット',
        share_excel_aria: '書き出す',
        tap_to_show_details: 'タップして詳細を表示',
        tap_to_hide_details: 'もう一度タップすると大きく表示',
        previous_day_aria: '前の日',
        next_day_aria: '次の日',
        pattern: {
          title: '1日の流れ',
          no_data: '記録が増えると、1日の流れをまとめて表示します。',
          sleep_prefix: '睡眠{{sleep}}・',
          sleep_only: '睡眠は合計{{sleep}}です。',
          sleep_split_only: '合計{{sleep}}の睡眠が{{count}}回に分けて記録されています。',
          sleep_record_only: '睡眠記録が{{count}}件あります。',
          activity_count_only: '{{prefix}}ケア記録が{{count}}件あります。',
          activity_sparse: '{{prefix}}{{period}}にケア記録が{{count}}件あります。',
          activity_peak: '{{prefix}}{{period}}のケア記録が最も多いです。',
          activity_double_peak: '{{prefix}}{{first}}と{{second}}にケア記録が多いです。',
          activity_spread: '{{prefix}}{{periods}}にケア記録がまんべんなくあります。',
          activity_across: '{{prefix}}ケア記録が{{periods}}に分かれています。',
          period: {
            dawn: '早朝',
            morning: '午前',
            afternoon: '午後',
            evening: '夜',
          },
        },
        summary: {
          title: 'リズムまとめ',
          subtitle: '間隔と量をひと目で',
          no_data: '記録なし',
          no_interval: '-',
          interval: {
            sleep: '起きていた時間',
            feeding: '授乳間隔',
            nappy: 'おむつ間隔',
            pumping: '搾乳間隔',
          },
          sleep: '睡眠',
          feeding: '授乳・食事',
          nappy: '排泄',
          pumping: '搾乳',
          total_sleep: '合計睡眠時間',
          sessions: '回数',
          total_intake: '摂取量の合計',
          total_feeding: '授乳の合計',
          nappy_total: 'おむつ交換',
          nappy_detail: 'うんち・おしっこ',
          total_pump: '搾乳量の合計',
          avg_pump: '平均/回',
        },
        history: {
          no_records_for_date: '{{date}}の記録がまだありません',
          load_previous: '前日の記録を取り込む',
          yesterday: '昨日',
          today: '今日',
          tomorrow_first_record: '翌日の最初の記録',
        },
      },
      compare: {
        toggle: '比べる',
        comparing: '比較中',
        yesterday: '昨日',
        week_ago: '1週間前',
        month_ago: '1か月前',
        select_date: '日付を選ぶ',
        exit: '比較を終了',
      },
      stats: {
        compare_title: '日ごとの記録を比べる',
        times: '回',
        hour: '時間',
        minute: '分',
        volume: '量',
        duration: '時間',
        increase: '増加',
        decrease: '減少',
      },
      nappy: {
        poo: 'うんち',
        fee: 'おしっこ',
      },
      discover: {
        toast_on: '✨ 最新情報をいち早くお知らせします！',
        toast_off: '通知がオフになりました',
        toast_save_failed: 'サーバーへの保存に失敗しました',
        badge_ai_insight: 'AIベースの育児インサイト',
        hero_title_line1: 'お子さまのリズムに',
        hero_title_line2: '合わせた通知を\nお届けします！',
        hero_desc_line1: '睡眠・授乳を記録で\nお子さまのリズムを分析し\n30分前に通知をお届けします',
        hero_desc_line2: '',
        btn_notify_on: '通知を設定しました',
        btn_notify_off: 'リリース通知を受け取る',
        notify_confirm: 'この機能がリリースされたらすぐにお知らせします',
        section_available: '今すぐ使えます',
        section_coming: '近日公開',
        section_coming_time: '2025年上半期予定',
        section_coming_badge: '準備中',
        tag_coming_soon: 'Coming Soon',
        section_more_title: 'すくすくぱしゃと共に成長を',
        section_more_desc_line1: '毎日の記録が集まって、',
        section_more_desc_line2: 'お子様だけの特別な成長物語になります',
        cards: {
          sleep_insight: {
            title: '授乳間隔のパターン分析',
            desc: '授乳時間と間隔からリズムを見つけます',
            details: '機械学習による分析'
          },
          feeding_rhythm: {
            title: 'パーソナライズ授乳ガイド',
            desc: 'お子様の授乳パターンを理解し、共に成長しましょう',
            details: 'パーソナライズされたインサイト'
          },
          growth_insight: {
            title: '成長発達インサイト',
            desc: 'お子様の成長パターンと発達段階を分析します',
            details: 'データに基づく分析'
          }
        }
      },
        story: {
          meta: {
            home_title: 'すくすくぱしゃ - お子さまの一日',
            record_title: '撮影 - すくすくぱしゃ',
          },
          home: {
            brand: 'すくすくぱしゃ',
            logo_alt: 'ロゴ',
            hero_title_line1: '📹 お子さまの一日を',
            hero_title_line2: '5秒動画で記録',
            hero_desc: '横向き動画をアップロードするか、横向きで撮影してください。',
            split_count_suffix: '分割',
          },
        landing: {
          intro: {
            aria_label: 'すくすくぱしゃ 紹介動画',
            dismiss_hint: 'ダブルタップで閉じ、次回からは表示されません',
          },
        },
        common: {
          back: '戻る',
          capture: '撮影',
          upload: 'アップロード',
          close: '閉じる',
        },
        capture: {
          ready: '撮影準備',
          recording: '撮影中... 5秒',
          guide_title: 'この中に合わせてください',
          guide_description: '明るい範囲が最終動画に入ります',
          switch_camera: 'カメラ切り替え',
        },
        draft: {
          saving: '一時保存中...',
          saved: '一時保存しました',
          retention_note: '最大{{days}}日間保存',
          save_failed: '一時保存に失敗しました',
          load_failed: '一時保存データを読み込めませんでした',
          retry: '再試行',
        },
        crop: {
          title: '表示範囲を調整',
          description: 'ドラッグで位置を合わせ、ズームを調整します',
          apply: '適用',
          adjust: '位置調整',
          zoom: 'ズーム',
          reset: '中央へ',
          visible_area: '最終表示範囲',
        },
        frame: {
          title: 'フレーム',
        },
        frame_style: {
          none: 'なし',
          white: 'ホワイト',
          rainbow: 'レインボー',
          pastel: 'パステル',
        },
        play_mode: {
          title: '再生方法',
          simultaneous: '同時再生',
          sequential: '順番再生',
          sequential_reveal: '順番登場',
        },
        preview: {
          title: 'プレビュー',
          open: 'プレビュー & ダウンロード',
          download: '動画をダウンロード',
          exporting: '書き出し中...',
          created_by: 'created by',
          filename_prefix: 'sukusuku_pasha_',
        },
        export: {
          loading: {
            preparing: {
              title: '動画を準備しています',
              subtitle: 'フォントとメディアを読み込み中',
            },
            rendering: {
              title: '動画を作成中',
              subtitle: 'もう少しです · {{percent}}%',
            },
            saving: {
              title: '動画を保存中',
              subtitle: 'まもなくギャラリーに表示されます',
            },
            hint: 'この画面を閉じずにお待ちください',
          },
        },
        debug: {
          title: 'デバッグログ',
          clear: 'クリア',
          media_recorder: 'MediaRecorder',
          capture_stream: 'captureStream',
          native_bridge: 'ネイティブブリッジ',
          preview_slot: 'プレビュー スロット {{index}}',
          export_slot: '書き出しスロット {{index}}',
          empty: 'まだイベントがありません。',
        },
        errors: {
          template_not_found: 'テンプレートが見つかりません。',
          catalog_load_failed: 'サーバーエラーでテンプレートを読み込めませんでした',
          catalog_load_description: 'しばらくしてからもう一度お試しください。繰り返し失敗する場合はサーバー確認が必要です。',
          catalog_load_retry: '再試行',
          camera_blocked: 'カメラ権限がブロックされています。ブラウザ設定で許可してください。',
          camera_allow: 'カメラ権限を許可してください。',
          camera_missing: 'カメラが見つかりません。',
          camera_busy: 'カメラが別のアプリで使用中です。',
          camera_failed: 'カメラへのアクセスに失敗しました。',
          file_type_unsupported: '動画ファイルを選択してください。',
          file_read_failed: '選択したファイルを読み込めませんでした。もう一度お試しください。',
          image_export_failed: '画像の書き出しに失敗しました。',
          export_failed: '書き出しに失敗しました。もう一度お試しください。',
        },
        subscription: {
          badge_alt: 'サブスク限定',
          recorder_banner_title: '登録するとこのテンプレートを利用できます',
          recorder_banner_description: '新規会員は初回登録で2週間無料体験！',
          recorder_banner_label: 'サブスク案内を見る',
          gate_title: 'サブスク限定テンプレートです',
          gate_description: 'このテンプレートはサブスクリプション加入者のみ利用できます。登録してさらに多くのストーリーを作成しましょう。',
          gate_cancel: 'あとで',
          gate_cta: 'サブスクを見る',
        },
      },
      baby_management: {
        back: '戻る',
        title: '赤ちゃん管理',
        description: '登録されている赤ちゃんのプロフィールを確認してください。',
        profile_list: '赤ちゃんプロフィール一覧',
        loading: '赤ちゃんのプロフィールを読み込んでいます',
        fetch_failed: '赤ちゃんのプロフィールを読み込めませんでした',
        no_baby: '赤ちゃん情報が見つかりません',
        no_profiles: '表示する赤ちゃんのプロフィールがありません',
        unnamed_baby: '名前のない赤ちゃん',
        unknown_family: '家族情報なし',
        family_name: '所属する家族 · {{name}}',
        family_label: '家族',
        born_on: '誕生日 {{date}}',
        due_on: '出産予定 {{date}}',
        height: '身長',
        weight: '体重',
        head_circumference: '頭囲',
        current_baby: '現在の赤ちゃん',
        profile_alt: '{{name}}のプロフィール画像',
        retry: 'もう一度試す',
        gender_boy: '男の子',
        gender_girl: '女の子',
        gender_unknown: 'まだ分からない',
        dday_label: 'D-day',
        age_label: '年齢',
        age_year: '{{years}}年',
        age_years_months_days: '{{years}}年{{months}}ヶ月{{days}}日',
        age_months_days: '{{months}}ヶ月{{days}}日',
        age_months: '{{months}} ヶ月',
        age_days: '{{days}}日',
        age_pregnancy: '妊娠{{weeks}}週{{days}}日目',
        view_detail: '詳しく見る',
        open_detail: '{{name}}の詳細を見る',
        detail_title: '{{name}}の情報',
        close_detail: '詳細を閉じる',
        basic_info: '基本情報',
        growth_info: '成長記録',
        name_label: '名前',
        gender_label: '性別',
        birthday_label: '誕生日',
        due_date_label: '出産予定日',
        due_date_short_label: '予定日',
        not_set: '未入力',
      },
      permission: {
        back: '戻る',
        title: '家族の権限／ニックネーム管理',
        invite_family: '家族を招待',
        invite_banner_title: '家族を招待してみましょう。',
        invite_banner_desc: '一緒に写真をアップロードして見られます。',
        family_management_title: '家族管理',
        family_select_desc: '管理する家族を選択してください',
        current_family: '現在の家族',
        family_info_title: '家族情報',
        family_name_label: '家族名',
        family_name_edit_title: '家族名を変更',
        family_name_edit_desc: '家族に表示する名前を入力してください。',
        family_name_fallback_desc: '家族名がまだ設定されていないため、赤ちゃんの名前を一時的に使用しています。',
        family_name_max_length: '最大10文字まで入力できます。',
        family_name_save_failed: '家族名を保存できませんでした。',
        family_settings_save_failed: '家族設定を保存できませんでした。',
        comment_visibility_label: 'コメント公開範囲',
        comment_visibility_title: 'コメント公開範囲',
        comment_visibility_desc: '写真にコメントできる家族グループを選択してください。',
        comment_group_mom: 'ママの家族',
        comment_group_dad: 'パパの家族',
        comment_group_online: 'オンライン家族',
        comment_group_none: 'コメントを使用しない',
        calendar_start_label: 'カレンダー開始日',
        calendar_start_title: 'カレンダー開始日',
        calendar_start_desc: '誕生日をカレンダーでどのように数えるか選択してください。',
        calendar_start_d0: '誕生日 D+0',
        calendar_start_d1: '誕生日 D+1',
        calendar_start_d0_desc: '病院などで一般的に使われる開始日です。',
        calendar_start_d1_desc: '1歳の誕生日が365日目になる開始日です。',
        family_members_title: '家族構成員',
        member_info_title: '構成員情報',
        basic_info_title: '基本情報',
        nickname_label_short: '呼び名',
        family_group_label: '家族グループ',
        family_group_title: '家族グループを選択',
        family_group_parents: '両親',
        family_group_paternal: 'パパの家族',
        family_group_maternal: 'ママの家族',
        family_group_online: 'オンライン家族',
        relationship_label: '関係',
        relationship_title: '関係を選択',
        permissions_title: '権限',
        detail_carer_title_short: '育児記録',
        save_changes: '変更を保存',
        apply: '適用',
        loading_title: '家族情報を読み込んでいます',
        loading_desc: '権限とニックネームを確認中です。少々お待ちください。',
        view_switch_aria: '家族の表示方法',
        view_list: 'リスト',
        view_family_map: '家族図',
        family_map_maternal_side: 'ママ側',
        family_map_paternal_side: 'パパ側',
        family_map_other_family: '心でつながる家族',
        family_map_center: 'わが家の赤ちゃん',
        family_map_baby: '赤ちゃん',
        family_map_open_member: '{{name}}を見る',
        baby_family: '{{name}}の家族',
        fetch_failed: '家族情報を読み込めませんでした',
        save_failed: '権限の保存に失敗しました',
        nickname_change: 'ニックネーム変更',
        nickname_saving: '保存中...',
        nickname_modal_desc: '家族内で表示するニックネームや呼び名を入力してください。',
        nickname_label: 'ニックネーム／呼び名',
        nickname_max_length: '最大32文字まで入力できます。',
        nickname_too_long: 'ニックネームは32文字以内で入力してください。',
        nickname_cancel: 'キャンセル',
        nickname_save: '保存',
        nickname_required: 'ニックネームを入力してください。',
        nickname_save_failed: 'ニックネームを保存できませんでした',
        no_more_history: 'これ以上戻れるページがありません。',
        confirm_leader_assign: '一度管理者に指定すると、その本人だけが解除できます。指定しますか？',
        no_baby_id: '赤ちゃん情報が見つかりません',
        no_families: '表示する家族がありません',
        no_members: '表示する構成員がいません',
        member_count: '構成員 {{count}}名',
        chip_uploader: 'アップロード',
        chip_carer: '育児記録',
        chip_downloader: 'ダウンロード',
        leader: '管理者',
        me: '自分',
        profile_alt: '{{name}}のプロフィール画像',
        relationship_mom: 'ママ',
        relationship_dad: 'パパ',
        relationship_maternal_grandfather: '母方のおじいちゃん',
        relationship_maternal_grandmother: '母方のおばあちゃん',
        relationship_paternal_grandfather: '父方のおじいちゃん',
        relationship_paternal_grandmother: '父方のおばあちゃん',
        relationship_maternal_relative: '母方の親戚',
        relationship_paternal_relative: '父方の親戚',
        relationship_grandfather: 'おじいちゃん',
        relationship_grandmother: 'おばあちゃん',
        relationship_relative: '親戚',
        relationship_other_family: '親族ほか',
        detail_upload_title: 'アップロード',
        detail_upload_desc: '写真や動画をアップロードできます。',
        detail_carer_title: '育児記録の閲覧/作成',
        detail_carer_desc: '育児記録を見たり入力したりできます。',
        detail_download_title: 'ダウンロード',
        detail_download_desc: '写真を端末に保存できます。',
        detail_comment_title: 'コメント',
        detail_comment_desc: '写真にコメントを残せます。',
        detail_leader_add_title: '管理者に追加',
        detail_leader_remove_title: '管理者から除外',
        detail_leader_desc: '家族構成員を招待し、メニューごとの権限を管理できます。',
        detail_leader_locked_desc: '親グループは管理者権限が固定です。',
      },
      subscription_benefits: {
        back: '戻る',
        title: 'サブスク特典',
        refresh: '再読み込み',
        loading: 'サブスク特典を読み込んでいます',
        status_label: 'サブスク状態',
        status_active_title: 'サブスク特典が有効です',
        status_active_desc: 'このアカウントでサブスク限定機能を利用できます。',
        status_inactive_title: '有効なサブスク特典はありません',
        status_inactive_desc: 'サブスク限定機能は登録後に利用できます。',
        status_unknown_title: 'サブスク状態を確認中です',
        status_unknown_desc: 'アプリから家族情報を受け取ると特典を確認できます。',
        family_waiting_title: '家族情報を待っています',
        family_waiting_desc: 'サブスク権限の確認にはアプリから渡される家族情報が必要です。',
        partial_error_title: '一部の情報を読み込めませんでした',
        partial_error_desc: 'しばらくしてから再読み込みすると最新の特典状態を確認できます。',
        benefits_label: '特典',
        benefits_title: '現在確認できる特典',
        active_count: '{{count}}/{{total}} 有効',
        enabled: '有効',
        disabled: '無効',
        benefit_story_title: '3秒ストーリーのサブスクテンプレート',
        benefit_story_desc: 'サブスク限定テンプレートとウォーターマークなしの書き出しを利用できます。',
        benefit_ads_title: '広告なし利用',
        benefit_ads_desc: '広告削除権限が有効かどうかを確認します。',
        benefit_special_video_title: 'スペシャル動画',
        benefit_special_video_desc: 'スペシャル動画機能の権限が有効かどうかを確認します。',
        benefit_search_title: '検索機能',
        benefit_search_desc: '検索機能の権限が有効かどうかを確認します。',
        account_label: 'アカウント情報',
        signup_date: '登録日',
        permission_source: '権限情報',
        source_loaded: '確認済み',
        source_waiting: '待機中',
        unknown: '確認不可',
        status_subscribed: '登録中',
        status_cancellation_scheduled: '解約予定',
        status_not_subscribed: '未登録',
        status_checking: '確認中',
        next_billing_date: '次回決済予定日は{{date}}です。',
        benefit_available_until: '{{date}}まで特典を利用できます。',
        next_billing_unknown: '次回決済予定日を確認しています。',
        benefit_period_unknown: '特典の利用期間を確認できません。',
        select_family: '家族を選択',
        family_label: '{{names}}の家族',
        receiver_family: '特典を受ける家族: {{family}}',
        current_user_subscribed: '会員様は登録中です。',
        other_family_subscribed: 'ほかの家族が登録中です。',
        subscription_owner_checking: 'サブスク状態を確認しています。',
        all_family_active_title: '家族全員が特典を受けています。',
        only_me: '会員様だけが特典を受けています。',
        only_me_upgrade_desc: '家族全員で特典を利用したい場合は、\nサブスクをアップグレードしてください。',
        upgrade_inquiry: 'アップグレードを問い合わせる',
        other_family_receiving_title: 'ほかの家族が\nサブスク特典を受けています。',
        other_family_receiving_desc: 'サブスク特典を受けられるのは1つの家族だけです。\n現在表示している家族は特典を受けていません。',
        all_family_ending_title: '家族全員が受けていた特典は\nまもなく終了します。',
        all_family_active_desc: '家族全員が以下のサブスク特典を受けています。',
        all_family_active_desc_with_count: '{{family}}の{{count}}名全員が\n以下のサブスク特典を受けています。',
        keep_all_subscription_benefits: 'すべてのサブスク特典を維持する',
        subscription_issue_title: 'サブスク情報を確認できませんでした。',
        subscription_issue_desc: '登録中なのに特典が表示されない場合は、カスタマーサポートへお問い合わせください。',
        contact_support: '問題があります',
        manage_subscription: 'サブスクを管理',
        explore_other_subscriptions: 'ほかのサブスクを見る',
        cancel_subscription: 'サブスクを解約',
        debug_status_title: '登録中と判断した根拠',
        debug_status_copy: 'コピー',
        debug_status_copied: 'コピー済み',
        debug_status_copy_failed: 'コピー失敗',
        debug_status_close: '閉じる',
        terms_title: 'サブスクに関する案内',
        legal_links_label: '利用規約と個人情報に関する案内',
        terms_of_use: '利用規約',
        privacy_policy: '個人情報取扱方針',
        config_error_title: '特典情報を読み込めませんでした',
        config_error_desc: 'しばらくしてからもう一度試すと最新のサブスク特典を確認できます。',
        retry: 'もう一度試す',
      },
      family_piggy_bank: {
        back: '戻る',
        title: '家族とチーズを集める',
        intro_title: '家族のチーズ貯金箱',
        intro_desc: '家族と一緒にチーズを少しずつ貯めましょう。',
        intro_skip: 'スキップ',
        vault_label: '家族の貯金箱',
        level_name_1: 'ちびクラちゃん',
        level_name_2: 'ぽかぽかクラちゃん',
        level_name_3: 'たのもしいクラちゃん',
        level_name_4: 'きらきらクラちゃん',
        level_name_5: 'ひだまりクラちゃん',
        mascot_idle_1: '家族のチーズがたまっています！',
        mascot_idle_2: '一緒に集めると心強いね',
        mascot_idle_3: '貯金箱をタップしてみてね',
        mascot_idle_4: '今日もチーズを集めよう！',
        mascot_react_1: 'もぐもぐ！とってもおいしい 💛',
        mascot_react_2: 'おなかいっぱい~ ありがとう！',
        mascot_react_3: '体がだんだんあったかくなってきた~',
        mascot_pet_1: 'えへへ、くすぐったい！',
        mascot_pet_2: 'ぽかぽか~ 気持ちいいな',
        mascot_pet_3: 'ふふ、また遊びに来てね！',
        pet_aria: 'クラちゃんをなでる',
        total_tag: '家族がくれたチーズ 🧀 {{count}}個',
        family_together: '家族{{count}}人で一緒に集めています',
        available_cheese: '私のチーズ',
        cheese_count: '{{count}}個',
        available_cheese_label: '持っているチーズ{{count}}個',
        balance_retry: '再読み込み',
        feed_button: '貯金箱にチーズを入れる',
        deposit_open: 'チーズを貯める',
        deposit_sheet_eyebrow: 'FAMILY VAULT',
        deposit_sheet_title: 'いくつ貯めますか？',
        deposit_sheet_desc: '持っているチーズから好きな数を選べます。',
        deposit_close: '閉じる',
        deposit_amount_label: '貯めるチーズ',
        deposit_amount_aria: '貯めるチーズの数',
        deposit_slider_aria: '貯めるチーズの数を調整',
        deposit_preset_quarter: '25%',
        deposit_preset_half: '半分',
        deposit_preset_all: '全部',
        deposit_available: '保有 {{count}}個',
        deposit_remaining: '残り {{count}}個',
        deposit_confirm: '{{count}}個貯める',
        deposit_expiration_preview: '今貯めると{{date}}まで利用できます。',
        cheese_unit: '個',
        earn_link: 'チーズをもっと集める',
        earn_title: 'チーズの集め方',
        earn_intro: '写真を投稿して家族を招待しましょう。',
        earn_method_1_title: '写真をアップロード',
        earn_method_1_desc: '今日の写真を投稿してチーズをもらえます',
        earn_method_2_title: '家族を招待',
        earn_method_2_desc: '家族と一緒ならもっと早くたまります',
        earn_method_3_title: 'ミッション',
        earn_method_3_desc: '新しいミッションを準備中です',
        earn_close: '確認',
        deposit_empty: 'チーズがありません。もっと集めましょう！',
        deposit_title: '家族の貯金箱への入金',
        deposit_processing: 'チーズを入れています...',
        deposit_failed: 'チーズを入れられませんでした。もう一度お試しください。',
        deposit_retry: 'チーズ情報を確認できませんでした。しばらくしてからもう一度お試しください。',
        deposit_pending: 'チーズの差し引き結果を確認しています。重複を防ぐため、しばらくしてから残高をご確認ください。',
        deposit_balance_changed: 'チーズ残高が変更されました。確認してからもう一度お試しください。',
        deposit_success: '{{count}}個入れました · {{date}}まで利用できます。',
        deposit_success_mixed: '期限付き{{expiringCount}}個と無期限{{nonExpiringCount}}個を入れました。',
        deposit_success_non_expiring: '無期限チーズを{{count}}個入れました。',
        expiration_policy: '獲得したチーズは貯金から6か月、購入チーズは期限なく利用できます。',
        next_expiration: '次の有効期限 {{date}}',
        expired: '期限切れ',
        family_member: '家族',
        history_open: '貯金履歴',
        history_title: 'チーズ貯金履歴',
        history_description: '家族が貯めたチーズと有効期限を件ごとに確認できます。',
        history_count: '最近の{{count}}件',
        history_amount_label: '貯めたチーズ',
        history_amount: '{{count}}個',
        history_depositor_label: '貯めた人',
        history_deposited_at_label: '貯金日',
        history_expires_at_label: '有効期限',
        history_available: '利用可能',
        history_no_expiration: 'なし',
        history_empty: 'まだチーズが貯まっていません。',
        me_name: '私',
        member_mom: 'ママ',
        member_dad: 'パパ',
        member_grandma: 'おばあちゃん',
        use_cta: 'フォトブック・写真プリントへ',
        use_toast: 'チーズショップを準備中です！🧀',
        memory_title: '最近集めたチーズ',
        activity_deposit: '{{name}}さんが{{count}}個集めました',
        activity_deposit_me: '私が{{count}}個集めました',
        time_just_now: 'たった今',
        time_hours_ago: '{{count}}時間前',
        time_yesterday: '昨日',
        demo_reset: 'デモデータをリセット',
        demo_reset_done: 'デモデータをリセットしました',
      },
      intro: {
        title: '授乳間隔の統計が',
        subtitle: '大きく生まれ変わりました！',
        desc: 'すくすくぱしゃの新しい授乳間隔を体験しましょう',
        cta: '詳しく見る'
      },
      tour: {
        skip: 'スキップ',
        next: '次へ',
        done: '完了',
        steps: {
          tabs: {
            title: 'タブの紹介',
            desc: '日別・週別の統計とリズムの流れまで一目で！AI授乳リズム分析も準備中です'
          },
          today: {
            title: '今日タブ',
            desc: 'タイムテーブルをタップして、時間帯別の詳細を確認しましょう'
          },
          week: {
            title: '週間タブ',
            desc: '一週間のパターンを一目で！お子様のリズムを把握しましょう'
          },
          week_excel: {
            title: '週間記録のダウンロード',
            desc: 'ボタン一つで週間記録を表として保存'
          },
          discover: {
            title: '発見タブ',
            desc: '睡眠・授乳を記録するとAIがリズムを分析し、タイミング通知を自動でお届けします。公開中の機能と準備中の機能をひとところで。'
          }
        }
      },
      beta: {
        badge: '🧪 ベータテスター募集中',
        app_name: 'すくすくぱしゃの新しいAI機能',
        title: 'ねんね',
        title_highlight: '予報',
        subtitle: 'AIが見つける赤ちゃんの最適な睡眠時間',
        intro_lead_line1: '「今寝かせるべき...？」',
        intro_lead_line2: 'もう悩まないでください',
        intro_body_line1: '赤ちゃんにはそれぞれ異なる睡眠リズムがあります。',
        intro_body_line2: 'ねんね予報が記録を分析して、',
        intro_body_line3: '眠りにつくのに良い時間にお知らせします 🔔',
        phone_time: '午後1:24',
        notif_app: 'すくすくぱしゃ',
        notif_when: '今',
        notif_title: '🍯 ねんね予報の時間です！',
        notif_body_line1: '今が寝かしつけに良い時間です。',
        notif_body_line2: 'あと約10分でねんね予報の時間です。',
        phone_extra: '午前の睡眠記録完了',
        phone_caption: '実際の通知プレビュー',
        how_title: '✨ こうやって動きます',
        how_subtitle: 'いつも通り記録するだけ、あとはAIにおまかせ',
        step1_title: 'いつも通り記録する',
        step1_desc: 'すくすくぱしゃで授乳、睡眠、おむつを記録してください',
        step2_title: 'AIがパターンを分析',
        step2_desc: '記録をもとにお子様だけの睡眠リズムを把握します',
        step3_title: 'ねんね予報アラート到着 🔔',
        step3_desc: '寝かしつけに良いタイミングでプッシュ通知をお届けします',
        tag_feeding: '🍼 授乳',
        tag_sleep: '💤 睡眠',
        tag_diaper: '🧷 おむつ',
        benefits_title: '🎁 ベータテスター特典',
        benefits_subtitle: '一番に体験して、一緒に作りましょう',
        benefit1_title: '誰よりも先に使える + 無料',
        benefit1_desc: 'ベータ期間中、ねんね予報アラートを無料でお試しください',
        benefit2_title: '開発チームと直接コミュニケーション',
        benefit2_desc: 'ご意見がすぐに反映されます',
        cta_button: '🍯 ねんね予報ベータに申し込む',
        cta_note: '12ヶ月以下の赤ちゃんの保護者対象',
        auto_notice_title: '睡眠を記録すると自動でお知らせ対象になります',
        auto_notice_desc: 'お申し込み不要。赤ちゃんの睡眠を記録するだけで、ねんね予報のお知らせを自動でお届けします。',
        registered_notice_title: 'すでにねんね予報のお知らせ対象です',
        registered_notice_desc: '睡眠を記録すると、寝かしつけに良い時間にねんね予報のお知らせをお届けします。',
        faq_title: 'よくある質問',
        faq1_q: 'Q. たくさん記録する必要がありますか？',
        faq1_a: 'いいえ！最初は同じ月齢の赤ちゃんのパターンに基づいてお知らせし、記録が溜まると自動的にお子様に合ったパーソナライズ通知に進化します。',
        faq2_q: 'Q. ベータ期間中に費用はかかりますか？',
        faq2_a: 'ベータテスト期間中は完全無料です。皆さまの貴重なフィードバックが最大の助けになります 🙏',
        status_pending: 'お申し込みを受け付けました',
        status_approved: 'ベータテストが承認されました！ 🎉',
        status_already: 'すでにお申し込み済みです',
        status_full: '募集は締め切りました',
        apply_failed: 'お申し込みに失敗しました。再度お試しください',
        apply_success: 'ベータテストのお申し込みが完了しました！ 🎉',
        apply_success_title: 'ベータ申請が完了しました！',
        apply_success_desc: 'まもなくねんね予報通知をお届けします',
        apply_success_confirm: '確認',
        already_applied_title: 'すでに申請済みです',
        already_applied_desc: 'この赤ちゃんはすでにねんね予報ベータに申請済みです。承認後にお知らせいたします！',
        family_applied_title: '家族がすでに申請しました',
        family_applied_desc: '同じ赤ちゃんで他の家族がすでにベータ申請を完了しています。',
        status_cancelled: 'ベータ参加がキャンセルされました',
        cancelled_title: 'ベータ参加がキャンセルされました',
        cancelled_desc: 'ベータテストへの参加がキャンセルされました。ご質問がある場合はカスタマーサポートまでご連絡ください。',
        footer: '© 2026 すくすくぱしゃ',
        back: '戻る',
        no_eligible_babies: '申請可能な赤ちゃんがいません。',
        select_baby_title: '赤ちゃんを選択してください',
        select_baby_desc: 'ベータサービスに申し込む赤ちゃんを選んでください。',
        sleep_banner_title: 'ねんね予報',
        sleep_banner_subtitle: '眠りに良い時間をお知らせ',
      },
      letter: {
        title: '代表ミミからの手紙',
        nav_title: 'ミミからの手紙',
        close: '閉じる',
        kicker: 'すくすくぱしゃをつくった人より',
        hero_title: 'わが子の今日が、\n家族の一生の宝物になるから。',
        hero_subtitle: '大切に残してくださった時間を、これからも長く守りたい。その思いを込めて、私自身の言葉でお手紙を書きます。',
        open_cue: '手紙をひらく',
        edition_default: '感謝を込めたお手紙',
        issue: 'FOR YOUR FAMILY',
        to_line: 'お子さまの今日を信じて託してくださったあなたへ',
        greeting: 'こんにちは。\nすくすくぱしゃをつくっているミミです。',
        p1: '私も子育て中の親です。慌ただしい一日を終え、子どもが眠ったあと、今日撮った写真をいつまでも眺めていた夜が何度もありました。',
        p2: 'すくすくぱしゃを信じて、お子さまの大切な日々をここに残してくださり、本当にありがとうございます。忙しい毎日の中で、写真一枚、動画一本を選んで残す。そのひとつひとつに、たくさんの愛情が込められていると感じています。',
        p3: '初めての笑顔、腕の中にすっぽり収まっていた小さな体、家族で笑い合ったひととき。今は見慣れた毎日も、いつかもう一度見返したい思い出になるのでしょう。そのとき、今日の気持ちにまた出会えるよう、お預かりした記録を大切に守っていきます。',
        p4: '寄せてくださった信頼に、よりよいサービスでお応えしていきます。記録を残すときはもっと使いやすく、見返すときは心が温かくなるように。ひとつひとつ丁寧に見直し、改善を重ねていきます。',
        photo_caption: '小さな手の中にある今日を、いつまでも覚えていられるように',
        promise_label: 'ミミからのお約束',
        promise_heading: '速さより正しさを、\n多さより長く残ることを。',
        promise: 'ご家族の大切な記録を扱うことに慣れてしまわないよう、毎日初心に戻って向き合います。',
        closing: 'ありがとうございます。\nお子さまの今日のそばで、これからも丁寧につくり続けます。',
        sign_role: 'すくすくぱしゃをつくった人',
        sign_name: '代表 ミミより',
        postscript: '今日も大切な一日を残してくださり、ありがとうございます。',
        feedback: {
          label: 'お手紙に気持ちを届ける',
          support: '応援しています',
          touched: '心に届きました',
          write: '匿名でひとこと残す',
          fold: 'また今度にする',
          placeholder: '伝えたいことがあれば、気軽に書いてください。',
          privacy: '匿名で届き、チームだけが読むことができます。',
          count: '{{current}} / {{limit}}',
          send: '気持ちを送る',
          sending: '送信しています…',
          reaction_thanks: 'お気持ちが届きました。ありがとうございます。',
          message_thanks: 'ありがとうございます。大切に読ませていただきます。',
          error: 'まだ届いていないようです。少し待って、もう一度お試しください。',
          already_sent: 'このお手紙には、すでにメッセージをいただいています。今書いた内容は追加で送信されていません。',
        },
        opening_alt: '思い出の写真と封筒、万年筆が置かれた温かな風景',
        hands_alt: '大人の指をしっかり握る赤ちゃんの小さな手',
        closing_alt: 'リボンと封蝋で閉じられた手紙と万年筆',
        variant: {
          family_hd: {
            label: 'ファミリー高画質プラン 感謝のお手紙',
            p1: '私も子育て中の親です。慌ただしい一日を終え、子どもが眠ったあと、今日撮った写真をいつまでも眺めていた夜が何度もありました。',
            p2: 'その思いからつくったすくすくぱしゃを信じ、ファミリー高画質プランでご一緒くださり、本当にありがとうございます。',
            p3: 'これからは今日の声や表情をFHD高画質で、より鮮明に残せます。何年後に見返しても、その日の温度まで薄れないように。',
            p4: 'もっと丁寧に確かめ、ずっと安心して使えるものをつくります。あなたが思い出に心を注ぐあいだ、見えないところの心配は私たちが先に引き受けます。',
          },
          family_hd_trial: {
            label: 'ファミリー高画質プラン 体験のお手紙',
            p1: '私も子育て中の親です。慌ただしい一日を終え、子どもが眠ったあと、今日撮った写真をいつまでも眺めていた夜が何度もありました。',
            p2: 'ファミリー高画質プランを初めて体験してくださり、ありがとうございます。お子さまの今日を一緒に守れることをうれしく思います。',
            p3: '体験中は、今日の声や表情をFHD高画質で残してみてください。何年後に見返しても、その日の温度が鮮やかでありますように。',
            p4: '短い体験が、長く信頼できるご縁の始まりになるよう、見えないところまで丁寧に整えます。',
          },
          family: {
            label: 'ファミリープラン 感謝のお手紙',
            p1: '私も子育て中の親です。慌ただしい一日を終え、子どもが眠ったあと、今日撮った写真をいつまでも眺めていた夜が何度もありました。',
            p2: 'その思いからつくったすくすくぱしゃを信じ、ファミリープランでご一緒くださり、本当にありがとうございます。',
            p3: '長い動画も、写真の多い日も、急がずゆっくり残してください。特別な出来事がなくても、お子さまとの何気ない一日には残す価値があります。',
            p4: 'もっと丁寧に確かめ、ずっと安心して使えるものをつくります。あなたが思い出に心を注ぐあいだ、見えないところの心配は私たちが先に引き受けます。',
          },
          family_trial: {
            label: 'ファミリープラン 体験のお手紙',
            p1: '私も子育て中の親です。慌ただしい一日を終え、子どもが眠ったあと、今日撮った写真をいつまでも眺めていた夜が何度もありました。',
            p2: 'ファミリープランを初めて体験してくださり、ありがとうございます。お子さまの今日を一緒に守れることをうれしく思います。',
            p3: '体験中は、長い動画も写真の多い日も、ゆったり残してみてください。何気ない毎日まで物語になっていく喜びを感じていただけますように。',
            p4: '短い体験が、長く信頼できるご縁の始まりになるよう、見えないところまで丁寧に整えます。',
          },
          adfree: {
            label: '広告なしプラン 感謝のお手紙',
            p1: '私も子育て中の親です。慌ただしい一日を終え、子どもが眠ったあと、今日撮った写真をいつまでも眺めていた夜が何度もありました。',
            p2: 'その思いからつくったすくすくぱしゃを信じ、広告なしプランでご一緒くださり、本当にありがとうございます。',
            p3: 'ほかのものに気を取られず、お子さまの記録だけをゆっくり見つめてください。ご家族の思い出をひらく時間が、少しでも静かで満ち足りたものになりますように。',
            p4: 'もっと丁寧に確かめ、ずっと安心して使えるものをつくります。あなたが思い出に心を注ぐあいだ、見えないところの心配は私たちが先に引き受けます。',
          },
          adfree_trial: {
            label: '広告なしプラン 体験のお手紙',
            p1: '私も子育て中の親です。慌ただしい一日を終え、子どもが眠ったあと、今日撮った写真をいつまでも眺めていた夜が何度もありました。',
            p2: '広告のない画面を初めて体験してくださり、ありがとうございます。お子さまの今日を一緒に守れることをうれしく思います。',
            p3: '体験中は、ほかのものに気を取られず、お子さまの記録だけをゆっくり見つめてください。その時間が少しでも穏やかになりますように。',
            p4: '短い体験が、長く信頼できるご縁の始まりになるよう、見えないところまで丁寧に整えます。',
          },
        },
      },
      belly_clock: {
        banner_title: 'ぺこぺこ予報',
        banner_subtitle: '次の授乳タイミングを前もってお知らせ',
        back: '戻る',
        title: 'ぺこぺこ予報',
        hero_title: 'わが子だけのリズムを\n読み取れます',
        hero_desc: '授乳間隔をAIが分析し、\n次の授乳タイミングの\n30分前に通知をお届けします',
        how_title: '仕組み',
        step1_title: '記録',
        step1_desc: '毎日の授乳時間を記録すると',
        step2_title: 'AI分析',
        step2_desc: 'AIがお子さまの授乳間隔を分析します',
        step3_title: '事前通知',
        step3_desc: '次の授乳タイミングの\n30分前にそっとお知らせします',
        benefits_title: 'こんなに便利',
        benefit1_title: '授乳のタイミングを逃さない',
        benefit1_desc: '忙しくても次の授乳時間を忘れません',
        benefit2_title: 'お出かけ・お昼寝の計画',
        benefit2_desc: '次の授乳タイミングに合わせて一日を計画',
        benefit3_title: '夜間授乳に備える',
        benefit3_desc: '夜間の授乳リズムを前もって見通して備えます',
        trust_note: '赤ちゃんによって授乳リズムは異なります。\nぺこぺこ予報は赤ちゃんの最近のパターンを反映して\nタイミングを提案します。',
        notice_title: '授乳通知を受け取るには',
        notice_desc: '母乳・ミルクは授乳開始時刻に加えて、\n授乳量または授乳時間（授乳の継続時間）を記録してください。\n開始時刻のみの場合、\n次回の授乳タイミングを予測できません。\n離乳食・おやつの記録は通知に影響しません。\n授乳間隔が日によって大きく異なる場合、\n通知がずれたり届かないことがあります。',
      },
      sleep: {
        status_title: 'ねんね予報',
        awake_time: '起きている時間',
        readiness: 'ねんね予報',
        recommended_time: '推奨睡眠時間',
        confidence: '予測精度',
        confidence_high: '十分な記録があります',
        confidence_low: '記録を収集中です',
        weekly_pattern: '週間睡眠パターン',
         no_session: 'まだ分析中の記録がありません',
         no_session_desc: '睡眠を記録するとAIがリズムを分析し、ねんね予報の通知を自動でお届けします。',
         no_active_session: '今は眠っています',
         no_active_session_desc: '赤ちゃんが起きたらねんね予報を分析します',
         sleeping_duration: '{{time}}間睡眠中',
         awake_no_session: '起きています',
         awake_no_session_desc: '睡眠時間の予測情報がまだありません',
        load_failed: 'ねんね予報を読み込めませんでした',
        load_failed_desc: 'しばらくしてからもう一度お試しください。',
        retry: '再試行',
        guide_how_title: '仕組み',
        guide_step1_title: '記録',
        guide_step1_desc: 'いつもどおり睡眠時間を記録すると',
        guide_step2_title: 'AI分析',
        guide_step2_desc: 'AIが赤ちゃんの睡眠リズムを分析します',
        guide_step3_title: '事前通知',
        guide_step3_desc: '眠りやすいタイミングにお知らせします',
        guide_benefits_title: 'こんなに便利',
        guide_benefit1_title: '寝かせどきを逃さない',
        guide_benefit1_desc: '眠りやすい時間に合わせてお知らせします',
        guide_benefit2_title: '一日のリズムを計画',
        guide_benefit2_desc: 'お昼寝・夜寝のタイミングに合わせて一日を計画',
        guide_benefit3_title: '記録するほど精度アップ',
        guide_benefit3_desc: '睡眠記録が増えるほど、赤ちゃんに合わせていきます',
        guide_trust_note: '赤ちゃんによって睡眠リズムは異なります。ねんね予報は赤ちゃんの最近のパターンを反映してタイミングを提案します。',
        minutes: '分',
        score: '点',
        wake_window: '活動時間',
        back: '戻る',
        go_to_status: 'ねんね予報を見る',
        hours: '時間',
        days_unit: '日',
        remaining: 'あと約{{minutes}}で推奨睡眠時間',
        overtime: '{{minutes}}超過',
        prediction_range: '予測範囲',
        personalized: 'パーソナライズ',
        age_based: '月齢ベース',
        analysis_basis: 'AI分析基盤',
        analysis_improving: '記録が増えるほど正確になります',
        status_active: 'まだ元気いっぱい',
        status_little_tired: '少し疲れてきました',
        status_drowsy: 'そろそろ眠そう',
        status_sleep_now: '今がおやすみのタイミング！',
        status_overtime: '睡眠時間を過ぎています',
        awake_time_elapsed: '起きてから{{time}}',
        baby_default_name: '赤ちゃん',
        baby_dday: '生後{{days}}日',
      },
      books: {
        badge: 'photo book',
        theme: {
          badge: 'フォトブックを作る',
          title: 'どの時間を\n一冊にまとめますか？',
          intro: '残したい期間を自分で選んでから、写真を選びます。',
          subtitle: '生後{{months}}か月を基準におすすめを用意しました。',
          previewLabel: '最近の写真で表示したフォトブックのプレビュー',
          previewTitle: '赤ちゃんのフォトブック',
          previewSubtitle: '大切な時間を一冊に',
          periodPrimaryTitle: '期間を選ぶ',
          periodPrimaryDesc: '年・月・月齢・最初の100日',
          browseBadge: '期間選択',
          browseTitle: 'どの期間を残しますか？',
          browseDesc: '基準を選んでから、正確な期間を指定します。',
          browseBack: '最初に戻る',
          browseActionTitle: '別の記録を選ぶ',
          browseActionDesc: '年別・月別・月齢別・記念日',
          periodBadge: '期間選択',
          periodBack: '別の記録に戻る',
          periodGuideTitle: 'カレンダー期間',
          milestoneGuideTitle: '成長の節目',
          recommendedBadge: '今のおすすめ',
          recommendedShort: 'おすすめ',
          recommendedAgeBadge: '今のおすすめ・生後{{months}}か月',
          recommendedAction: 'この記録を選ぶ',
          yearlyTitle: '年間記録',
          yearlyMeta: '{{count}}年',
          yearlyShort: '年別',
          yearlyBrowseDesc: '一年の変化を時系列でまとめます。',
          yearlyPickerDesc: '本にする年を選んでください。',
          monthlyTitle: '月間記録',
          monthlyMeta: '{{count}}か月',
          monthlyShort: '月別',
          monthlyBrowseDesc: '写真が多い一か月を気軽にまとめます。',
          monthlyPickerDesc: '本にする月を選んでください。',
          ageMonthTitle: '月齢別記録',
          ageMonthMeta: '{{count}}期間',
          ageMonthShort: '月齢別',
          ageMonthBrowseDesc: '月齢ごとの成長を比べやすくまとめます。',
          ageMonthPickerDesc: '本にする月齢を選んでください。',
          choiceCount: '{{count}}件',
          directTitle: '自分で写真を選びます',
          directDesc: '期間を決めず、これまで通り自由に選びます。',
          directPrimaryTitle: '期間を決めずに写真を選ぶ',
          directPrimaryDesc: '好きな写真からすぐに選べます。',
          preparingSuggestions: '誕生日を基準におすすめを確認中です。写真選びはすぐに始められます。',
          noBirthday: '誕生日を確認できませんでした。写真を直接選んで始められます。',
          selected: '選んだテーマ',
          dateRange: '{{range}}の写真',
          back: 'テーマ選択に戻る',
          choice: {
            yearlyTitle: '{{year}}年の成長記録',
            yearlySubtitle: '{{year}}年の瞬間を一冊にまとめます。',
            yearlyReason: '一年の変化を時系列で振り返れます。',
            monthlyTitle: '{{year}}年{{month}}月の記録',
            monthlySubtitle: '一か月の日常と変化をまとめます。',
            monthlyReason: '写真が多い月を気軽に一冊にできます。',
            ageMonthTitle: '{{month}}か月目の記録',
            ageMonthSubtitle: '{{month}}か月目の日々をまとめます。',
            ageMonthProgressSubtitle: '{{month}}か月目、今日までの記録です。',
            ageMonthReason: '月齢ごとの変化を比べやすい記録です。',
            first100Title: '赤ちゃんの最初の100日',
            first100ProgressTitle: '100日を迎えるまで',
            first100Subtitle: '生まれた日から100日までの記録です。',
            first100ProgressSubtitle: '生まれた日から今日までをまとめます。',
            first100Reason: '最初の100日の変化を一つの流れで残せます。',
            firstYearTitle: '初誕生日までの記録',
            firstYearProgressTitle: '初誕生日を迎えるまで',
            firstYearSubtitle: '生まれた日から初誕生日までをまとめます。',
            firstYearProgressSubtitle: '生まれた日から今日までの成長記録です。',
            firstYearReason: '最初の一年の成長を一冊で振り返れます。',
            fromBirthTitle: '生まれた日から今日まで',
            fromBirthSubtitle: '初めて会った日から今日までをすべてまとめます。',
            fromBirthReason: 'これまでの時間をひと目で振り返れます。',
          },
        },
        autoSetup: {
          close: '閉じる',
          back: '戻る',
          step: '{{current}} / 2',
          periodLabel: '期間を選択しました',
          densityTitle: '写真をどのくらい入れますか？',
          densityDesc: '期間全体を確認し、選んだボリュームに合わせて良い瞬間を選びます。',
          recommended: 'おすすめ',
          density: {
            highlightsTitle: '大切な瞬間だけ',
            highlightsDesc: '似た写真を減らし、代表的な場面を中心にまとめます。',
            balancedTitle: 'バランスよく',
            balancedDesc: '大切な節目と日常の瞬間をバランスよく残します。',
            richTitle: 'たっぷり',
            richDesc: '同じ日の表情や場面も、より多く残します。',
            estimate: '約{{count}}枚',
          },
          next: '次へ',
          manual: '自分で写真を選ぶ',
          layoutStepLabel: 'レイアウトの好み',
          layoutTitle: 'どんな雰囲気にしますか？',
          layoutDesc: '写真と記録を確認し、選んだ方向に合わせてページごとに構成します。',
          layout: {
            autoTitle: '写真に合わせる',
            autoDesc: '写真の比率や記録に合わせて、ページごとに整えます。',
            photoForwardTitle: '写真を大きく',
            photoForwardDesc: '一つの場面を大きく見せるページを多めにします。',
            storyTitle: '記録と一緒に',
            storyDesc: '写真の記録が自然に読めるように配置します。',
            collageTitle: '複数の場面を一緒に',
            collageDesc: '近い時間の写真をまとめ、リズムのある構成にします。',
          },
          loadingCandidates: '期間内の写真{{count}}枚を確認しています。',
          candidatesReady: '{{count}}枚を確認・約{{target}}枚を選択予定',
          noPhotos: '選んだ期間に写真がありません。',
          loadFailed: '期間内の写真をすべて読み込めませんでした。',
          create: '写真を選んで本を構成する',
          creatingTitle: 'フォトブックの最初の構成を作っています',
          creatingDesc: '似た瞬間を整理し、期間の流れと写真の記録が自然につながるように構成します。',
          stageCollected: '期間内の写真を確認済み',
          stageSelecting: '代表的な場面を選択中',
          stagePreparing: 'ページ構成を準備中',
          cancel: 'キャンセル',
          errorTitle: '自動構成を完了できませんでした',
          failed: '少し待ってからもう一度お試しください。',
          retry: 'もう一度試す',
          selected: '期間全体から{{count}}枚の瞬間を選びました。',
        },
        pick: {
          title: 'フォトブックに入れる\n瞬間を選びましょう',
          addTitle: '写真を追加して\nもう一度構成しましょう',
          addProgress: 'あと{{count}}枚選ぶと、もう一度おすすめと配置を行います。',
          addReady: '追加選択が完了しました。もう一度おすすめと配置ができます。',
          cancelAdd: '写真の追加をキャンセル',
        },
        photos: {
          source: '最近アップロードした写真',
          previewSource: '最近の写真プレビュー',
          reload: '写真を再読み込み',
          recentRange: '今月',
          monthRange: '{{month}}の写真',
          autoPick: 'おすすめ写真{{count}}枚を自動選択',
          autoAdd: '不足している写真を自動追加',
          autoPickDesc: '撮影日順に並べ、後から外すこともできます。',
          selectAllLoaded: '読み込んだ写真{{count}}枚をすべて選択',
          allLoadedSelected: '読み込んだ写真をすべて選択済み',
          maxSelected: '最大{{count}}枚を選択済み',
          autoLoadMore: '次の写真を自動で読み込む',
          loadingMore: '写真を読み込み中...',
          loadMore: '写真をさらに表示',
          loadMoreRetry: '写真の読み込みを再試行',
          ddayBadge: 'D+{{count}}',
          recent: '最近',
          pickNumber: '{{number}}枚目の写真を選択',
          emptyTitle: '表示できる写真がありません。',
          emptyDesc: 'アプリに保存された最近の写真が見つかりませんでした。',
        },
        album: {
          missingBridge: 'アプリのアルバムに接続できませんでした。',
          loadFailed: '写真を読み込めませんでした。',
          loadMoreFailed: '写真を追加で読み込めませんでした。',
        },
        tray: {
          hint: 'フォトブック用の写真を集めています',
          empty: '写真をタップするとここに集まります',
          expandedTitle: '選んだ写真 {{count}}枚',
          collapse: '閉じる',
          remove: '選択から外す',
          open: '選んだ写真を見る',
          count: '{{count}}枚選びました',
        },
        draft: {
          saving: '保存中',
          saved: '自動保存済み',
          saveFailed: '保存できませんでした',
          continueTitle: '作成中のフォトブックを続ける',
          continueMeta: '{{title}} · {{photos}}枚 · {{pages}}ページ',
        },
        edit: {
          undo: '元に戻す',
          redo: 'やり直す',
        },
        action: {
          reset: '選んだ写真をすべてクリアしました。',
          resetLabel: '選択をリセット',
          autoSelected: '最近の瞬間から{{count}}枚を自動で選びました。',
          allLoadedSelected: '読み込んだ写真{{count}}枚をすべて選びました。',
          photoLimitReached: '写真は最大{{count}}枚まで選択できます。',
          needMorePhotos: '現在{{count}}枚です。{{needed}}枚まで選んでください。',
          addedToPage: '選んだ写真を指定したページに追加しました。',
          removedFromBook: 'フォトブックから写真を外しました。',
          photoPositionSaved: '写真の位置を保存しました。',
          photoPositionReset: '写真の位置を自動配置に戻しました。',
          keepExistingForPageAdd: 'ページに写真を追加している間は、現在の写真を外さないでください。',
          aiPolishFailed: '編集内容はそのままです。AIのおすすめだけ再試行してください。',
          pageDeleted: 'ページを削除しました。',
          titleSuggestionApplied: '提案されたタイトルを適用しました。',
          coverPhotoChanged: '表紙写真を変更しました。',
          coverStyleChanged: '本文ページはそのままで、表紙デザインを変更しました。',
          undo: '前の編集に戻しました。',
          redo: '編集をやり直しました。',
        },
        cta: {
          pickAtLeastOne: '写真を選んでください',
          pickPhotoProgress: '写真 {{current}} / {{needed}}枚',
          next: '{{count}}枚で次へ',
          previewNow: '{{count}}枚でプレビュー',
          recompose: '{{count}}枚でもう一度構成',
          planning: 'フォトブックのレイアウトを構成しています...',
          aiRequired: 'AIレイアウトが必要です',
          layoutRequired: '先にレイアウトを準備してください',
          creating: '印刷用PDFを作成中...',
          createDraft: '完成版を確認',
        },
        defaultTitle: 'わが家のベビーフォトブック',
        create: {
          failed: '印刷用PDFを作成できませんでした。',
          missingPhoto: 'レイアウトに使用した写真が見つかりません。再配置してください。',
          pageCountMismatch: '印刷ページ数が一致しません。もう一度お試しください。',
          progress: {
            preparing: '印刷仕様を準備しています',
            rendering: '写真をページに配置しています',
            uploading: '印刷ページを保存しています',
            finalizing: '最終PDFを作成しています',
            count: '{{current}} / {{total}}ページ',
          },
        },
        subject: {
          loadingTitle: '写真の顔を確認しています',
          loadingDetail: '顔と人物の位置を確認し、切れにくいレイアウトを準備しています。',
          inlineLoading: '顔が切れないよう写真を確認しています。{{current}} / {{total}}',
        },
        template: {
          back: '写真選択に戻る',
          badge: 'layout',
          title: 'フォトブックの構成を確認してください',
          subtitle: '{{count}}枚 · {{range}}',
          noDate: '日付情報なし',
          recommendedProduct: 'おすすめ商品',
          bookSettings: 'タイトルとサイズ',
          settingsSummary: '{{spec}} · {{photos}}枚',
          coverIncluded: '{{name}}の表紙',
          photoCount: '{{count}}枚',
          advanced: 'サイズと詳細情報',
          bookSpec: 'ブックサイズ',
          bookTitle: 'ブックタイトル',
          bookTitlePlaceholder: 'わが家のベビーフォトブック',
          spec: '仕様',
          range: '期間',
          minimumPages: '最小{{count}}ページ',
          needPhotosTitle: 'あと{{count}}枚選ぶと、より充実した構成になります。',
          needPhotos: 'この仕様の推奨写真枚数は{{count}}枚です。現在{{current}}枚を選んでいます。',
          addPhotos: '写真を追加で選ぶ',
          loading: 'テンプレートを読み込み中...',
          errorTitle: 'テンプレートを読み込めませんでした。',
          retry: '再試行',
          loadFailed: 'テンプレートを読み込めませんでした。',
          authRequired: 'フォトブックテンプレートの読み込みにはアプリ認証が必要です。',
          missingConfig: 'Sweetbook APIキーが必要です。',
          empty: '使用できるフォトブックテンプレートがありません。',
          previewNote: '表示中の配置と同じ座標で印刷ページを作成します。生成後に最終PDFも確認できます。',
          recommendation: {
            loadingTitle: '写真の構成を確認しています',
            loadingDetail: '写真の向き、撮影時刻、シーン構成をもとに最適なブックサイズを選んでいます。',
            inlineLoading: '写真に合うブックサイズを選んでいます。',
            failed: 'ブックサイズを自動で選べませんでした。',
            fallback: '自動選択を完了できなかったため、標準サイズを適用しました。',
            manual: '自分で選んだブックサイズです。',
            mixedOrientation: '縦横の写真が混在しているため、多様なコラージュに適しています。',
            recordRich: '写真をゆったり見せやすいサイズです。',
            largeCollection: '多くのシーンをゆったり収められるサイズです。',
            landscapeRich: '横向き写真を大きく見せやすい、ゆとりのある仕様です。',
            portraitRich: '縦向き写真の割合に合う仕様です。',
            lowBlankPages: '選んだ写真枚数で空白ページを最小限にできます。',
            balanced: '写真枚数とページ構成のバランスが最も良いサイズです。',
          },
        },
        previewDraft: {
          title: 'フォトブックレイアウト',
          subtitle: '選択した写真をもとに作成したページ構成です。',
          editorialLabel: 'AI編集方針',
          titleSuggestion: 'タイトル案',
          applyTitle: '適用',
          manualEditsSafe: 'もう一度おすすめしても、手動で編集した部分は保持されます。',
          addPhotos: '写真を追加',
          loadingTitle: 'フォトブックを構成しています',
          loadingPrepareDetail: '写真とブック仕様をまとめて確認しています。',
          loadingSubjectDetail: '顔が切れないよう写真を確認しています。{{current}} / {{total}}',
          loadingDetail: '写真の撮影時刻、向き、記録を確認し、各ページを構成しています。',
          loadingPreviewDetail: '完成するページを最後に確認しています。',
          errorTitle: 'レイアウトを作成できませんでした',
          planFailed: '自動レイアウトを作成できませんでした。',
          planUnavailable: '使用できるページレイアウトが見つかりませんでした。',
          cover: '表紙',
          frontCover: '表表紙',
          coverShort: '表紙',
          pageLabel: '本文 {{current}} / {{total}}',
          previous: '前のページ',
          next: '次のページ',
          estimatedPages: '約{{count}}ページ',
          compactSummary: '{{photos}}枚 · {{pages}}ページ構成 · 印刷{{printPages}}ページ',
          layoutPages: '写真配置 {{count}}ページ',
          photoCoverage: '選択{{selected}}枚 · 配置{{used}}枚',
          printPages: '印刷仕様 {{count}}ページ',
          blankPageNotice: '写真は{{layoutCount}}ページに配置しました。印刷物はこの仕様に合わせて{{printCount}}ページとなり、残りは空白ページで補います。',
          autoLayout: '1ページ最大{{count}}枚',
          aiLayout: 'AIレイアウト',
          fallbackLayout: '基本レイアウト',
          aiFallback: 'AIのおすすめを利用できないため、検証済みの基本レイアウトを表示しています。',
          retryAi: 'AIでもう一度整える',
          sceneCount: '{{count}}シーン',
          collageCount: 'コラージュ{{count}}ページ',
          recordCount: '記録{{count}}件を反映',
          lowQuality: '低解像度の写真{{count}}枚は印刷品質が低下する可能性があります。',
          authRequired: 'プレビューにはアプリ認証が必要です。',
          loadFailed: 'プレビュー情報を読み込めませんでした。',
          editor: {
            open: 'このページを編集',
            tapPage: 'このページを詳しく編集',
            tapHint: 'ページをタップすると、写真・文章・デザインを詳しく編集できます。',
            close: '編集を閉じる',
            done: '完了',
            coverEditorTitle: '表紙を編集',
            pageEditorTitle: '{{page}}ページを編集',
            tabsLabel: 'ページ編集ツール',
            tabs: {
              photos: '写真',
              text: '文章',
              style: 'デザイン',
            },
            photoTabHint: 'このページの写真、順番、表示位置を調整します。',
            textTabHint: 'このページだけに表示する日付と文章を整えます。',
            styleTabHint: 'このページに合う写真配置と紙の背景を選びます。',
            coverDetails: '表紙の内容',
            bookTitle: '本のタイトル',
            noDateRange: '写真の日付が表紙に表示されます。',
            coverPhoto: '表紙写真',
            changeCover: '表紙写真を変更',
            changeCoverDesc: '本の第一印象になる写真を選んでください。',
            coverSafe: '顔が安全に表示されます',
            coverCaution: '表紙の切り抜きを確認してください',
            coverUnsafe: '顔が切れる可能性があります',
            coverUnknown: '表紙プレビューで切り抜きを確認してください',
            useRecommendedCover: '顔が見やすいおすすめ写真を使う',
            coverStyle: '表紙デザイン',
            coverStyleHint: '本文ページはそのままで、表紙だけ変更します。',
            recommendedShort: 'おすすめ',
            safeShort: '安全',
            cropRiskShort: '切れ注意',
            checkShort: '確認',
            coverPhotoOption: '表紙写真{{number}} · {{status}}',
            pagePhotos: 'このページの写真{{count}}枚',
            addToPage: '写真を追加',
            fitFaces: '顔に合わせる',
            refinePage: 'このページを提案',
            heroPhoto: 'メイン写真',
            photoNumber: '写真{{number}}',
            heroShort: 'メイン',
            editedShort: '編集済み',
            dragPhoto: '写真{{number}}の順番を変更',
            editPhoto: '編集',
            replaceShort: '交換',
            deleteShort: '削除',
            setHero: 'メイン写真に設定',
            moveEarlier: '前へ移動',
            moveLater: '後ろへ移動',
            adjustPhoto: '写真の位置を調整',
            position: '位置',
            replacePhoto: '写真を差し替え',
            removePhoto: '本から削除',
            chooseNewPhoto: 'アルバムから新しい写真を追加',
            pageText: 'ページの文章',
            date: '日付',
            pageTitle: 'タイトル',
            comment: 'コメント',
            commentPlaceholder: 'この瞬間に残したい物語を書いてください。',
            focusDate: '日付を編集',
            focusTitle: 'タイトルを編集',
            focusComment: 'コメントを編集',
            closeWriting: '文章編集を閉じる',
            layout: '写真レイアウト',
            decorate: 'ページを飾る',
            backgroundHint: '写真と文章はそのままに、紙の背景色だけを変えます。',
            pickCover: '表紙写真を選択',
            pickAdd: 'このページに移す写真',
            pickReplace: '差し替える写真を選択',
            moveNotice: '別のページの写真を選ぶと、このページへ移動します。',
            swapNotice: '2枚の写真のページ位置を入れ替えます。',
            coverNotice: '本文ページの配置は変わりません。',
            closePicker: '写真選択を閉じる',
            fromPage: '{{page}}ページ',
            noMoveCandidates: '移動できる他の写真がありません。',
            addFirstPage: '最初の本文ページを追加',
            addPageFull: 'ページを追加',
            movePageEarlier: 'ページを前へ移動',
            earlier: '前へ',
            addPage: '追加',
            deletePageShort: '削除',
            movePageLater: 'ページを後ろへ移動',
            later: '後ろへ',
            deletePageWithPhotos: 'このページと写真{{count}}枚を本から外しますか？',
            deleteBlankPage: 'この空白ページを削除しますか？',
            cancel: 'キャンセル',
            confirmDeletePage: 'ページを削除',
            backgroundOptions: {
              original: '基本',
              cream: 'クリーム',
              blush: 'ピンク',
              sky: '空色',
              sage: 'グリーン',
              lilac: 'ライラック',
            },
            layoutOptions: {
              aspectAware: '写真比率に合わせる',
              fullBleed: 'ページ全面',
              singleFramed: '余白あり',
              heroPair: '大 + 小',
              splitVertical: '左右分割',
              splitHorizontal: '上下分割',
              trioFocus: '1枚を強調',
              trioRow: '横並び',
              quadGrid: '2 x 2グリッド',
              storyGrid: 'ストーリーグリッド',
              collageFive: 'メイン写真コラージュ',
              collageSix: '3 x 2コラージュ',
              evenGrid: '均等グリッド',
            },
          },
        },
        result: {
          badge: 'print PDF',
          title: '印刷用PDFが完成しました',
          preparingTitle: '印刷用PDFを作成中です',
          subtitle: 'まだ注文も決済も行われていません。編集後に新しいPDFを作成できます。',
          pageCount: 'ページ',
          estimate: '予想価格',
          cover: '表紙',
          content: '本文構成',
          automaticContent: 'AI自動構成 · {{count}}ページ',
          editLayout: '編集して作り直す',
          startOver: 'この結果を削除',
          deleting: '結果を削除しています...',
          deleteFailed: '結果を削除できませんでした。',
        },
        preview: {
          cover: '表紙',
          contents: '本文',
          open: '開く',
          refresh: 'PDFの状態を更新',
          coverTitle: '表紙PDFプレビュー',
          contentsTitle: '本文PDFプレビュー',
          notReadyTitle: 'PDFを準備中です',
          notReadyDesc: '準備ができ次第、プレビューを自動で開きます。',
          note: 'WebViewでPDFが表示されない場合は、「開く」を使ってください。',
          failed: 'PDFの生成に失敗しました。レイアウトを確認して再試行してください。',
          refreshFailed: 'PDFの状態を更新できませんでした。',
        },
      },
      printv2: {
        ui: {
          pickTitle: '写真を選ぶ',
          quantityValue_one: '{{count}}枚',
          quantityValue_other: '{{count}}枚',
          productSummary: '{{size}} · {{min}}枚から',
          priceSummary: '1枚 {{amount}}',
          removePhoto: '写真を外す',
          editThisPhoto: 'この写真を編集',
          edited: '編集済み',
          previewOpen: '{{number}}枚目の仕上がりを拡大',
          previewTitle: '仕上がり {{current}} / {{total}}',
          previewClose: 'プレビューを閉じる',
          previewLoading: '仕上がりを準備しています',
          zoomIn: 'タップして拡大',
          zoomOut: 'タップして縮小',
          requestNotice: '現在はお支払いなしで注文を受け付けています。',
          quantityLabel: '{{number}}枚目の写真のプリント枚数',
          decreaseQuantity: '{{number}}枚目の写真の枚数を減らす',
          increaseQuantity: '{{number}}枚目の写真の枚数を増やす',
          quantityValue: '{{count}}枚',
          selectedCount: '写真{{photos}}枚 · プリント{{prints}}枚',
          minimumRemaining: 'あと{{count}}枚必要です',
          designTitle: 'デザイン',
          designAppliesAll: 'すべての写真に適用されます',
          designChange: '変更',
          designDone: '完了',
          removeAllCaptions: 'すべての写真の文言を削除（{{count}}枚）',
          removeAllCaptionsConfirm: '{{count}}枚の写真の文言をすべて削除しますか？',
          removeAllCaptionsAction: 'すべて削除',
          removeAllCaptionsDone: '{{count}}枚の文言を削除しました',
          currentPhoto: '写真 {{current}} / {{total}}',
          photoList: '写真一覧',
          previousPhoto: '前の写真',
          nextPhoto: '次の写真',
          cropAction: '写真の位置',
          captionAction: 'テキスト',
          shippingLater: '送料は住所の入力後に表示されます。',
          toDecorate: '写真をデザイン',
          returnToDecorate: 'デザインに戻る',
          toAddress: 'お届け先を入力',
          reviewPreviewHint: '写真の位置とテキストを確認してください。',
          previewMore: 'あと{{count}}枚表示',
          previewFailed: 'プレビューを読み込めませんでした',
          previewRetry: '再読み込み',
          editPhoto: '{{number}}枚目の写真を編集',
          backToStart: '最初に戻る',
          addressTitle: 'お届け先',
        },
        draft: {
          saving: '保存中…',
          saved: '保存済み',
          failedRetry: '保存失敗 · 再試行',
          conflict: '別の端末で変更されました',
          deleted: '削除された一時保存',
          rejected: '保存できません',
          failedDesc: '自動保存が止まりました。編集内容はこの端末に残っています。',
          retry: '再試行',
          conflictDesc: '最新の内容を読み込むか、今の編集をコピーとして保存してください。',
          reloadLatest: '最新の内容を読み込む',
          saveCopy: 'コピーとして保存',
          deletedDesc: '別の端末で削除されました。今の編集はそのまま残っています。',
          tooLargeDesc: '内容が多すぎて保存できません。写真を減らしてください。',
          rejectedDesc: '保存できない内容があります。編集を変えると再び保存されます。',
          defaultTitle: '{{month}}月の写真プリント',
          reloaded: '最新の内容を読み込みました。',
        },
        recommend: {
          action: '{{count}}枚おすすめ',
          loading: '選んでいます',
          undo: 'おすすめを取り消す',
          none: 'おすすめできる写真が見つかりませんでした。',
          failed: 'おすすめできませんでした。もう一度お試しください。',
        },
        printCheck: {
          face: '顔が{{edges}}の端で切れるかもしれません',
          lowResolution: '画質が低く、ぼやけてプリントされるかもしれません',
          both: '顔が切れ、画質もぼやけるかもしれません',
          summary: 'プリント前に確認したい写真{{count}}枚',
          adjust: '位置を調整',
          review: '確認',
          next: '次の写真',
          marker: 'プリント前に確認',
          orderFace: '顔が切れそうな写真{{count}}枚',
          orderLowResolution: '画質が低い写真{{count}}枚',
        },
        photoViewer: {
          open: '{{label}}の写真を大きく見る',
          close: '写真を閉じる',
          select: 'この写真を選ぶ',
          deselect: '選択を解除',
          loading: '写真を読み込んでいます',
          failed: '写真を読み込めませんでした',
        },
        collections: {
          entry: 'お気に入り',
          sourceLabel: '写真の取得元',
          regularPhotos: 'すべての写真',
          nextPageHint: 'ここには写真がありません。続きを読み込んでください。',
          noPhotos: 'このお気に入りにはプリントできる写真がありません。',
          loadMore: '次の写真を読み込む',
          title: 'お気に入りを選ぶ',
          loading: 'お気に入りを読み込んでいます…',
          loginRequired: 'アプリでログインするとお気に入りを表示できます。',
          retry: '再試行',
          loadFailed: 'お気に入りを読み込めませんでした。',
          empty: 'お気に入りがありません。',
          favorites: 'お気に入り',
          itemCount: '{{count}}件',
        },
        draftAction: {
          title: '一時保存',
          close: '閉じる',
          keepEditing: '編集を続ける',
          viewDrafts: '一時保存の一覧を見る',
          loginShort: 'ログインが必要',
          needsAttention: '未保存',
          loginRequired: 'アプリでログインすると一時保存できます。',
          unsaved: 'まだ保存されていません。しばらくしてからもう一度お試しください。',
        },
        draftManager: {
          pending: '保存待ち',
          nameLabel: '下書きの名前',
          nameInputLabel: '下書きの名前を入力',
          nameRequired: '名前を1〜80文字で入力してください。',
          cancelRename: 'キャンセル',
          confirmRename: '名前を保存',
          rename: '名前を変更',
          renameButton: '名前を変更',
          copyName: '{{title}}のコピー',
          failed: '処理できませんでした。しばらくしてからもう一度お試しください。',
          emptyTitle: '一時保存はまだありません',
          editing: '編集中',
          manageLabel: '{{title}}を管理',
          manage: '管理',
          close: '閉じる',
          justNow: 'たった今',
          duplicate: 'コピーを作成',
          delete: '削除',
          deleteTitle: '「{{title}}」を削除しますか？',
          deleteDesc: '削除すると元に戻せません。元の写真や受付済みの注文はそのままです。',
          cancel: 'キャンセル',
          deleteConfirm: '削除',
          renameLabel: '新しい名前',
          copyLabel: 'コピーの名前',
          renameConfirm: '名前を保存',
          copyConfirm: 'コピーを保存',
          busy: '処理中です。少々お待ちください。',
          unsaved: '今の編集はまだ保存されていません。しばらくしてからもう一度お試しください。',
          changed: '別の端末で変更されました。最新の内容を確認してからもう一度お試しください。',
          actionFailed: '変更できませんでした。しばらくしてからもう一度お試しください。',
          deletedDone: '「{{title}}」を削除しました。',
          renamedDone: '「{{title}}」に名前を変更しました。',
          copiedDone: '「{{title}}」のコピーを作成しました。',
          continueEditing: '編集中のプリント',
        },
        shop: {
          pricePending: '価格を確認中',
          retryQuote: '再試行',
          authRequired: 'アプリでログインしてからもう一度お試しください。',
          quoteFailed: '価格を読み込めませんでした。',
          uploading: '注文書を作成しています',
          uncertain: '注文書を確認中です。重複注文を防ぐため、少しお待ちください。お問い合わせの際は下のリクエスト番号をお伝えください。',
          failed: '注文書を開けませんでした。もう一度お試しください。',
          storageUnavailable: '注文情報を保存できませんでした。しばらくしてからもう一度お試しください。',
          requestId: 'リクエスト番号: {{id}}',
          estimate: '送料と割引は注文書で確認できます。',
          tooLarge: 'ファイルが大きすぎます。写真や枚数を減らして分けて注文してください。',
          checkout: '注文へ進む',
        },
        drafts: {
          title: '一時保存',
          loadFailed: '一時保存を読み込めませんでした · 再試行',
          collapse: '閉じる',
          showAll: 'すべて表示',
          counts: '写真{{photoCount}}枚 · プリント{{printCount}}枚',
          savedAgo: '{{time}}に保存',
          loadMore: 'もっと見る',
          loadMoreFailed: '一時保存をこれ以上読み込めませんでした。',
          openMissing: '削除された一時保存のため、一覧から外しました。',
          openFailed: '一時保存を開けませんでした。しばらくしてからもう一度お試しください。',
        },
        start: {
          newTitle: '新しく作る',
          unsavedTitle: '保存されていない編集があります',
          unsavedDesc: '新しく作ると、今の編集は消えます。',
          unsavedBack: '編集に戻る',
          unsavedDiscard: '破棄して新しく作る',
          pickPhotos: '写真を選ぶ',
        },
        orderReview: {
          title: '仕上がりを確認',
        },
        result: {
          cleanupTitle: '「{{title}}」の一時保存を残しますか？',
          cleanupFallbackTitle: 'このプリント',
          cleanupDesc: '残しておくと同じ写真で再注文できます。',
          cleanupFailed: '削除できませんでした。しばらくしてからもう一度お試しください。',
          cleanupRemove: '削除',
          cleanupKeep: '残しておく',
          cleanupDone: '一時保存から削除しました',
        },
      },
      prints: {
        pick: {
          title: 'どの瞬間を\nプリントしますか？',
          periodBadge: '写真を選ぶ',
        },
        steps: {
          label: '進行ステップ',
          pick: '選ぶ',
          review: '確認',
          decorate: '飾る',
          order: '注文',
        },
        resume: {
          title: '作成途中のプリントがあります',
          counts: '写真{{photoCount}}枚 · プリント{{printCount}}枚',
          discard: '新しく作る',
          continue: '続きから作る',
          restored: '一時保存を読み込みました。',
        },
        review: {
          badge: '選択を確認',
          title: '選んだ写真を確認してください',
          counts: '写真{{photoCount}}枚 · プリント{{printCount}}枚',
          minimumLeft: '最低枚数まであと{{count}}枚です',
          minimumReady: '注文できる枚数になりました',
          copiesHint: '次の画面で写真ごとのプリント枚数を調整できます。',
          remove: '{{number}}枚目の写真を外す',
          empty: '選んだ写真がありません。',
          addMore: '写真を追加する',
        },
        orderReview: {
          badge: '最終確認',
          title: '完成イメージを確認してください',
          desc: 'お届け先を入力する前に、枚数とデザインをもう一度確認します。',
          preview: '選んだ写真のプレビュー',
          quantity: '枚数',
          counts: '写真{{photoCount}}枚 · プリント{{printCount}}枚',
          finish: '表面',
          frame: 'フレーム',
          frameValue: '{{number}}番',
          caption: 'テキスト',
          captionCount: '{{count}}枚',
          morePhotos: '+{{count}}枚',
          sampleHint: '代表{{count}}枚でプリントデザインを確認しましょう',
          sameDesign: '同じデザインでプリントされます',
          previewItem: 'プリント完成イメージ',
          longCaptionWarning: '20文字を超えるテキストが{{count}}枚あります',
          unitPrice: '単価',
          amount: '目安金額',
        },
        periodFlow: {
          start: {
            badge: '写真プリント',
            title: '写真をどこから選びますか？',
            desc: '期間を指定するか、最近の写真から選べます。',
            autoTitle: '期間を指定して探す',
            autoDesc: '月齢・月・年で写真を絞り込みます。',
            manualTitle: '最近の写真から選ぶ',
            manualDesc: '最近の写真からすぐに選べます。',
            notice: '現在選んでいる写真はそのまま残ります。',
            back: '最初の選択に戻る',
          },
          badge: '期間を選択',
          title: '写真の期間を選んでください',
          intro: '見たい写真の期間をひとつ選んでください。',
          previewLabel: '最近の写真を使ったポラロイドプレビュー',
          recentThreeMonthsTitle: '最近3か月',
          recentThreeMonthsDesc: '最近の表情と成長をバランスよく集めます。',
          recommendedBadge: '最近の写真からおすすめ',
          minimumHint: '写真が十分にある場合は{{count}}枚以上おすすめします',
          findByPeriod: '別の期間を見る',
          directBadge: '自分で選ぶ',
          directTitle: '写真を自分で選ぶ',
          directDesc: 'アルバムを見ながら、気に入った写真を選びます。',
          backToSelection: '写真選択に戻る',
          loading: 'おすすめ写真を選んでいます…',
          loadingDesc: '写真を確認し、似ている写真を整理しています。',
          loadingCount: '写真{{count}}枚を確認しています',
          collectingCount: '写真{{count}}枚を集めて確認しています',
          loadingBridge: 'アルバムに接続しています',
          errorTitle: '写真を選べませんでした',
          error: '写真をおすすめできませんでした。しばらくしてからもう一度お試しください。',
          noPhotos: '選んだ期間に写真がありません。',
          retry: 'もう一度試す',
          changePeriod: '期間を変更',
          resultBadge: '自動選択の結果',
          reviewTitle: '選んだ写真を\n確認しますか？',
          noSuitableReviewTitle: '写真を一緒に\n選びましょう',
          result: '{{total}}枚から{{selected}}枚を選びました',
          resultDesc: '似た写真を減らし、期間全体からバランスよく集めました。',
          insufficient: 'この期間の写真{{count}}枚をすべて入れました。',
          insufficientDesc: '最低{{count}}枚になるまで写真を追加するか、期間を広げてください。',
          analysisUnavailable: '写真を最後まで確認できませんでした。しばらくしてからもう一度お試しください。',
          resultAll: 'この期間の写真{{count}}枚をすべて選びました。',
          resultAllDesc: '下で写真を自由に追加・削除できます。',
          resultAllLimited: 'まず{{count}}枚を選びました。',
          resultAllLimitedDesc: '下で選んだ写真を確認・調整してください。',
          resultNoSuitable: 'おすすめできる写真が見つかりませんでした。',
          resultHighlights: '{{selected}}枚を選んでおきました。',
          resultHighlightsInsufficient: '良い写真を{{selected}}枚選びました。',
          resultNoAddition: '現在{{total}}枚選択しています。',
          resultWithExisting: '{{added}}枚を追加し、合計{{total}}枚を選択しました。',
          resultHighlightsDesc: '似た場面を整理し、期間全体からバランスよく選びました。',
          resultHighlightsWithDiaryDesc: '写真メモのある{{count}}枚を優先し、似ている写真を整理しました。',
          resultHighlightsInsufficientDesc: '最低枚数まであと{{count}}枚必要です。下から選ぶか、別の期間を開いてください。',
          suitableCount: 'おすすめ候補 {{count}}枚',
          excludedCount: '確認する写真 {{count}}枚',
          diarySelectedCount: '写真メモ {{count}}枚',
          similarScenesReduced: '似ている写真を整理',
          autoFillRemaining: 'あと{{count}}枚おすすめ',
          directFillRemaining: '下からあと{{count}}枚を選ぶ',
          autoPickMinimum: '{{count}}枚おすすめ',
          backToManualSelection: '自分で選ぶ画面に戻る',
          cancelRecommend: 'やめて自分で選ぶ',
          recommendAssistDesc: '選んだ写真はそのままに、足りない{{count}}枚だけおすすめします。',
          recommendAssistAction: 'おすすめを見る',
          recommendationPhotoBadge: 'おすすめ',
          recommendationPreviewBadge: 'おすすめを確認',
          recommendationPreviewTitle: '{{count}}枚をおすすめします',
          recommendationPreviewDesc: '今選んでいる写真は残したまま、下の写真だけ追加します。',
          recommendationDismiss: '自分で選び続ける',
          recommendationApply: 'おすすめ写真を追加',
          recommendationApplied: 'おすすめ写真を{{count}}枚追加しました。',
          recommendationDismissed: 'おすすめは追加しませんでした。',
          recommendationUndone: 'おすすめで追加した写真を外しました。',
          undoRecommendation: 'おすすめだけ取り消す',
          widenPeriod: '別の期間を見る',
          reviewAlbumTitle: 'すべての写真から追加する',
          reviewAlbumHint: '黄色の数字は選択順です。写真をタップすると追加・削除できます。',
          addPhotoLabel: '{{label}}の写真を追加',
          removePhotoLabel: '{{label}}の写真を外す',
          mode: {
            title: '写真をどう選びますか？',
            desc: 'おすすめを受け取るか、自分で見ながら選べます。',
            counting: '写真を数えています',
            photoCount: '{{count}}枚以上',
            recommended: 'おすすめ',
            highlightsTitle: 'かわいい瞬間だけ',
            highlightsDesc: '人物がきれいに見え、プリントしたときにかわいく仕上がる写真をおすすめします。',
            allTitle: 'この期間の写真をすべて選ぶ',
            allDesc: 'この期間から一度に最大200枚まで選べます。',
            autoTitle: '{{count}}枚おすすめ',
            autoFillTitle: '最大{{count}}枚おすすめ',
            continueWithSelection: '今の選択で続ける',
            continueWithSelectionDesc: '選んだ写真を確認し、必要なところだけ整えます。',
            autoDesc: '記録、表情、画質を見て、先に写真をおすすめします。',
            quantityCurrent: '自動選択 {{count}}枚',
            quantityChange: '枚数を変更',
            quantityLabel: '自動選択の枚数',
            quantityBasic: '標準',
            quantityComfortable: '多め',
            quantityMany: 'たっぷり',
            manualTitle: '自分で選ぶ',
            manualDesc: '写真を見ながら、気に入ったものだけ自分で選びます。',
            notice: '自動で選んだ後も、写真を自由に追加・削除できます。',
            autoLoading: '良い瞬間を選んでいます…',
            autoLoadingDesc: '記録と表情を確認しながら、似た場面を一枚ずつ整理しています。',
            allLoading: '写真を選んでいます…',
            allLoadingDesc: 'この期間の写真を選んでいます。',
            highlightsLoading: 'かわいい瞬間を探しています…',
            highlightsLoadingDesc: '写真メモがある写真を優先し、残りの写真も確認しています。',
            analysisProgress: '{{completed}} / {{total}}枚を確認中',
            changeMode: '別の選び方',
          },
          browse: {
            title: '別の期間も見ますか？',
            hint: 'すでに選んだ写真はそのまま残ります。',
            shortLabel: 'ほかの時期',
            loadMore: '写真をもっと見る',
            loadingMore: '写真をさらに読み込んでいます…',
            loadMoreHint: '下にスクロールすると、次の写真を自動で読み込みます。',
          },
          picker: {
            badge: '期間を選ぶ',
            desc: '期間を選ぶと、その期間の写真を表示します。',
            back: '期間の種類に戻る',
            yearlyTitle: '年を選択',
            monthlyTitle: '月を選択',
            ageMonthTitle: '月齢を選択',
          },
          options: {
            special: '特別な時期',
            monthly: '月ごと',
            monthlyDesc: '1か月分ずつ振り返る',
            ageMonth: '月齢ごと',
            ageMonthDesc: '月ごとの成長記録',
            yearly: '年ごと',
            yearlyDesc: '一年の変化をバランスよく',
            first100: 'はじめの100日',
            firstYear: '1歳まで',
            fromBirth: '生まれた日から',
          },
          period: {
            yearlyTitle: '{{year}}年',
            yearlyDesc: '一年の変化を時系列で選びます。',
            monthlyTitle: '{{year}}年{{month}}月',
            monthlyDesc: 'ひと月の日常から素敵な瞬間を選びます。',
            ageMonthTitle: '{{month}}か月頃',
            ageMonthDesc: 'この時期の表情と変化を集めます。',
            first100Desc: '出会った日から100日までを集めます。',
            firstYearDesc: '最初の一年の成長をバランスよく集めます。',
            fromBirthDesc: '出会った日から今日までを振り返ります。',
          },
        },
        photos: {
          source: '最近アップロードした写真',
          previewSource: '最近の写真プレビュー',
          loadingPeriod: '{{period}}の写真を読み込み中',
          pickHint: '写真をタップして選んでください',
          reload: '写真を再読み込み',
          viewLabel: '写真の表示方法',
          gridView: '写真',
          recordsView: 'メモ',
          noRecord: 'メモなし',
          recordOverLimit: '20文字超過',
          period: '写真の期間',
          recentRange: '最近',
          monthRange: '{{month}}からの写真',
          olderMonth: '前の月の写真を見る',
          newerMonth: '次の月の写真を見る',
          autoLoadMore: '次の写真を自動で読み込む',
          loadingMore: '写真を読み込み中...',
          ddayBadge: 'D+{{count}}',
          recent: '最近',
          pickNumber: '{{number}}番目の写真を選択',
          emptyTitle: '写真がありません',
          emptyDesc: 'アプリに保存された最近の写真が見つかりませんでした。',
        },
        album: {
          missingBridge: 'アプリのアルバムに接続できませんでした。',
          loadFailed: '写真を読み込めませんでした。',
          loadMoreFailed: '写真をさらに読み込めませんでした。',
          requestFailed: '写真を読み込めませんでした。（エラー {{status}}）',
        },
        tray: {
          empty: '写真をタップするとここに追加されます',
          expandedTitle: '選んだ写真 {{count}}枚',
          collapse: '閉じる',
          remove: '選択から外す',
          open: '選んだ写真を見る',
          count: '{{count}}枚選択中',
          floatingCount: '選択中の写真 · {{count}}枚',
          floatingCountDetailed: '写真{{photoCount}}枚 · プリント{{printCount}}枚',
          hint: 'タップして選択内容を確認',
          minHint: '{{count}}枚から注文できます',
        },
        decorate: {
          title: 'かわいく仕上げましょう',
          hint: '写真の日付・記録・スタンプを直接タップしてみてください',
          hintBase: 'オプションを選ぶとすぐプレビューに反映されます',
          recordsLoaded: '写真メモのある写真も{{count}}枚読み込みました。',
          longRecordMarker: 'テキスト20文字超過',
          longRecordCurrent: 'このテキストは最初の20文字だけプリントされます',
          longRecordSummary: '20文字を超えるテキスト{{count}}枚',
          longRecordLimit: 'プリントされるのは最初の20文字までです。',
          longRecordOriginalSafe: '元の写真メモはそのまま残ります。',
          nextLongRecord: '次の長いテキスト',
          nextLongRecordShort: '次の写真メモ',
          reviewLongRecords: '確認',
          editDiary: 'プリント用テキストを編集',
          addDiary: 'テキストを追加',
          editDiaryShort: 'テキスト編集',
          restoreDiaryShort: 'テキストを戻す',
          diarySaved: 'テキストを変更しました。',
          diaryRemoved: 'テキストを外しました。',
          diaryRestored: '元のメモに戻しました。',
          editDate: '日付表示を変更',
        },
        diaryEditor: {
          title: 'テキスト編集',
          safeHint: '元のメモは変わりません。',
          close: 'テキスト編集を閉じる',
          original: '元のメモ',
          originalCount: '{{count}}文字',
          noOriginal: 'メモはありません。',
          printText: 'プリントするテキスト',
          placeholder: '一緒にプリントするテキスト',
          limit: '最大20文字までプリントされます。',
          useOriginal: '元のメモに戻す',
          remove: 'テキストを外す',
          cancel: 'キャンセル',
          apply: '適用',
        },
        chip: {
          frame: 'フレーム',
          font: 'フォント',
          date: '日付テキスト',
          stamp: 'スタンプ',
          finish: '表面仕上げ',
          dateBase: '日付表示',
        },
        crop: {
          title: '写真の位置調整',
          hint: 'ドラッグで移動、ピンチやスライダーで拡大できます',
          adjust: '写真の位置調整',
          zoom: 'ズーム倍率',
          reset: 'リセット',
          done: '適用',
          close: '閉じる',
        },
        design: {
          lang: { none: 'なし', both: '日付・生後日数', dday: '生後日数のみ', date: '日付のみ' },
        },
        polaroid: {
          lang: {
            kor: '한국어',
            eng: 'English',
            jpn: '日本語',
            korSample: '태어난 지 154일  2026.06.03',
            engSample: '2026.06.03  D+154',
            jpnSample: '生まれてから154日  2026.06.03',
          },
          font: {
            writer: '手書き',
            gothic: 'ゴシック',
            pen: 'ペン字',
            lineSeedJp: 'LINE Seed JP',
            bizUdpMincho: 'BIZ UDPMincho',
            kiwiMaru: 'Kiwi Maru',
            japaneseSample: 'すくすく育つ毎日',
          },
          stamp: {
            month: '月齢',
            ageMonths: '年齢+月齢',
            year: '年齢',
            none: 'なし',
            monthSample: '5ヶ月',
            ageMonthsSample: '1歳3ヶ月',
            yearSample: '1歳',
            select: '{{number}}番目のスタンプ',
          },
          frame: {
            select: '{{number}}番目のフレーム',
            fallback: 'デフォルトのフレームを表示しています。',
          },
        },
        finish: {
          matteLabel: 'マット',
          glareLabel: '光沢',
          matteDesc: '指紋が付きにくい',
          glareDesc: '色が鮮やか',
        },
        quantity: { decrease: '枚数を減らす', increase: '枚数を増やす' },
        unit: { sheet: '枚' },
        action: {
          minimumFilled: '最低注文枚数に合わせました。',
          reset: 'すべて消しました。',
          saved: 'プリント構成を保存しました。',
          needPhoto: '先に写真を選んでください。',
          resetLabel: '選択をクリア',
          resetPrompt: '選んだ写真とプリント設定をすべて消しますか？',
          resetCancel: 'キャンセル',
          resetConfirm: 'すべて消す',
        },
        config: { fallback: 'プリント設定を読み込めなかったため、デフォルトの3x4設定を表示しています。' },
        cta: {
          prev: '戻る',
          pickPhoto: '写真を選んでください',
          toDecorate: '{{count}}枚選択済み · 次へ',
          toReview: '写真{{photoCount}}枚 · プリント{{printCount}}枚を確認',
          editPhotos: '写真{{count}}枚を編集',
          needMore: 'あと{{left}}枚で注文できます',
          belowMinHint: '注文は{{min}}枚から · 現在{{current}}枚',
          request: 'アプリで注文',
          requestCount: '{{count}}枚注文',
          toAddress: '{{count}}枚 · {{amount}} · お届け先入力',
          toOrderReview: '仕上がりを確認',
          toAddressDetailed: '{{count}}枚 · {{amount}} · お届け先入力',
        },
        video: {
          badge: 'MEMORY MOVIE',
          button: '思い出ムービーにする',
          buttonDesc: 'イントロ・アウトロ付き · 最大{{count}}枚',
          previewLabel: 'ムービーに入るポラロイドのプレビュー',
          creatingTitle: '思い出ムービーを作っています…',
          creatingDesc: 'ポラロイドを一枚ずつめくりながらつないでいます。',
          progress: '{{percent}}%',
          cancel: '作成をやめる',
          saved: '思い出ムービーを保存しました。',
          failed: 'ムービーを作れませんでした。しばらくしてからもう一度お試しください。',
          unsupported: 'この端末では動画の保存に対応していません。',
          introTitle: 'わたしたちの小さな瞬間',
          introSubtitle: '{{count}}枚のポラロイドで振り返る思い出',
          outroTitle: '今日の瞬間を、いつまでも',
          outroSubtitle: '一緒に過ごした時間を一枚ずつ残しました。',
        },
        faceReview: {
          checking: '顔の位置を確認中...',
          title: '顔が切れてしまうかも',
          desc: '顔がプリント範囲の端にかかっています。位置を調整するか、そのまま進んでください。',
          faceCrop: '顔がプリント範囲の{{edges}}の端にかかっています',
          photoOrder: '選んだ写真の{{number}}枚目',
          faceCount: '検出した顔: {{count}}件',
          adjustOne: 'この写真を調整する',
          adjust: '調整する',
          continue: '確認して注文へ進む',
          toast: 'プリント範囲を調整してください。',
          detectFailed: '顔の確認に失敗しました。',
          detectFailedHint: 'もう一度確認するか、確認せずに進めます。',
          retryCta: '確認失敗 · もう一度試す',
          retry: 'もう一度確認',
          skip: '確認せずに進む',
          skipped: '顔の自動確認をスキップしました。完成イメージをご自身で確認してください。',
          edge: {
            left: '左',
            right: '右',
            top: '上',
            bottom: '下',
            outside: '外側',
          },
        },
        footer: {
          selected: '選択{{photoCount}}枚 · 合計{{printCount}}枚',
          minimumLeft: 'あと{{left}}枚！',
          ready: '注文の準備OK',
          progress: '{{printCount}} / {{count}}枚',
        },
        address: {
          title: 'どこへお届けしますか？',
          subtitle: '日本国内の住所へお届けします',
          name: 'お名前（氏名）',
          namePlaceholder: '山田 花子',
          kana: 'フリガナ',
          kanaPlaceholder: 'ヤマダ ハナコ',
          postal: '郵便番号（〒）',
          postalHint: '7桁入力すると住所が自動で入ります',
          postalNotFound: 'この郵便番号の住所が見つかりませんでした。直接入力してください。',
          postalFailed: '住所の自動入力に失敗しました。直接入力してください。',
          prefecture: '都道府県',
          prefectureSelect: '選択',
          city: '市区町村',
          cityPlaceholder: '千代田区千代田',
          address1: '番地',
          address1Placeholder: '1-1-1',
          address2: '建物名・部屋番号（任意）',
          address2Placeholder: 'マンション名 101号室',
          phone: '電話番号',
          email: 'メールアドレス（任意）',
          requiredField: '必須項目です',
          postalInvalid: '郵便番号を7桁で入力してください。',
          phoneInvalid: '電話番号を10〜11桁で入力してください。',
          emailInvalid: 'メールアドレスの形式を確認してください。',
        },
        pay: {
          cta: '注文する',
          needAddress: 'お届け先を入力してください',
          preparing: '注文中…',
          uploading: '写真をアップロード中…',
          uploadTitle: '注文を送信しています',
          uploadHint: 'この画面を閉じずにお待ちください。',
          uploadProgress: '{{done}}/{{total}}枚',
          failed: '注文に失敗しました。しばらくしてからもう一度お試しください。',
          uploadFailed: '注文は受け付けましたが、ファイルのアップロードに失敗しました。ご注文番号 {{orderNum}} を添えてお問い合わせください。',
          summaryCount: '写真{{count}}枚',
          summaryCountDetailed: '写真{{photoCount}}枚 · プリント{{printCount}}枚',
          summaryUnit: '1枚あたり{{unit}}',
          total: '目安金額',
          productSubtotal: '商品小計',
          shippingFee: '送料',
          totalWithShipping: '合計',
          paymentFeeNotice: 'カードテストは円の合計を同じ数値のウォンのテスト金額として使います。実際の請求はありません。',
          selectRegion: '配送地域を選択してください',
          testCheckout: 'Tossカードテスト · {{amount}}',
          cardTest: 'カードテスト',
          withoutPayment: '支払いなしで進む',
          testNotConfigured: 'テスト決済の準備ができていないため、注文していません。',
          requestCheckout: '注文を申し込む',
          testNotice: 'カードテストは実際に請求されません。',
          tossTestAmount: 'テスト決済金額：{{amount}}',
          tossOrderName: '写真プリントテスト',
          testSuccessDesc: 'カードのテスト決済が完了しました。実際の請求・プリント制作・発送は行われません。',
          retryConfirmation: '決済を再確認',
          shippingLater: '日本国内の配送先地域別料金が適用されます',
          shippingEstimate: '地域別送料 {{min}}〜{{max}}',
        },
        store: {
          productLabel: 'プリント商品',
          productKind: '写真プリント',
          productName: 'ポラロイド 3×4',
          unitSuffix: '1枚・税込',
          minLabel: '最低枚数',
          minValue: '{{count}}枚から',
          billingLabel: '料金',
          billingValue: '1枚ごとの都度払い · 定期課金ではありません',
          shippingLabel: '送料',
          shippingValue: '日本国内の地域別 {{min}}〜{{max}}',
        },
        legal: {
          navLabel: '店舗情報',
          scta: '特定商取引法に基づく表記',
          contact: 'お問い合わせ',
          contactLine: 'メール {{email}}',
          companyLine: '{{shop}} · {{company}} · {{email}}',
          back: 'プリント画面に戻る',
        },
        contact: {
          title: 'お問い合わせ',
          supportTitle: '日本語でお問い合わせいただけます',
          supportDescription: 'すくすくぱしゃの写真プリントに関するお問い合わせを、メール・電話で受け付けています。',
          emailLabel: 'カスタマーサポートメール',
          phoneLabel: '日本語カスタマーサポート電話番号',
          prepareTitle: 'ご注文に関するお問い合わせに必要な情報',
          prepareDescription: '注文番号（またはご注文時のメールアドレス）\nお問い合わせ内容',
          defectNote: '破損・印刷不良・誤配送・数量不足に関するお問い合わせは、不具合を確認できる商品・梱包の写真も添えてください。',
          internationalCallNote: '韓国の電話番号です。ご利用の通信会社によって国際通話料金がかかる場合があります。',
        },
        scta: {
          sellerTitle: '事業者情報',
          shopName: 'ショップ名',
          seller: '販売業者',
          representative: '運営責任者',
          address: '所在地',
          addressValue: '〒07325 ソウル特別市永登浦区議事堂大路83、O2タワー ソウルフィンテックラボ 4・5・6・8・17・19F',
          phone: '日本語カスタマーサポート電話番号',
          email: 'カスタマーサポートメール',
          industry: '業種',
          industryValue: 'モバイルアプリケーション／ソフトウェア',
          business: '事業内容',
          businessValue: '家族向け写真・子どもの成長記録アプリの運営。保護者やご家族が写真・動画・子育ての思い出を安全に保存、整理、共有できるサービスを提供しています。',
          storeUrl: 'ストアURL',
          productTitle: '商品・サービスの価格',
          product: '商品・サービスの種類',
          productValue: 'カスタム写真プリント（ポラロイド等）',
          price: '価格帯',
          priceValue: '価格はストアページ内の各商品に対して表示されているとおりです。写真プリントは1枚ごとの都度払いです。',
          otherFees: '商品代金以外に必要な料金',
          otherFeesValue: '送料は販売者が定める地域別料金で、日本国内（北海道・沖縄県を除く）は3,000円、北海道は4,000円、沖縄県は5,000円です。離島は所属する都道府県の料金を適用します。この送料は配送会社の公示運賃ではありません。消費税は商品価格に含まれます。 Toss Paymentsのカードテスト決済です。円の合計と同じ数値を韓国ウォン（KRW）のテスト金額として使用します。為替換算ではなく、実際の請求はありません。',
          delivery: '配送/サービスの提供',
          deliveryValue: '本商品はご注文後に制作を開始するカスタムメイド商品のため、ご注文後7〜14営業日以内に発送いたします。',
          methods: 'お支払い方法',
          methodsValue: 'Toss Paymentsのカードテスト決済',
          timing: '支払時期',
          timingValue: 'カード認証後にテスト決済を承認します。実際の請求はありません。',
          returnTitle: '返品・キャンセルポリシー',
          returnValue: '本商品は受注生産のカスタムプリント商品のため、お客様都合によるご注文後のキャンセル・返品・交換はお受けできません。破損・印刷不良・誤配送・数量不足があった場合は、商品到着後7日以内に、注文番号（またはご注文時のメールアドレス）、不具合の内容、確認できる商品・梱包の写真を添えて、{{email}}までご連絡ください。確認後、無償で再制作・交換または不足分の発送を行い、必要な返送料・再送料は当社が負担いたします。交換が困難な場合は対象商品の代金を返金し、全品返品の場合は当初の送料も返金いたします。返送前に必ずカスタマーサポートの案内をご確認ください。',
          supportLanguage: '対応言語',
          supportLanguageValue: 'メール・電話で日本語のお問い合わせに対応いたします。韓国の電話番号のため、国際通話料金がかかる場合があります。',
        },
        result: {
          checking: 'お支払い結果を確認しています...',
          requestedTitle: 'ご注文を受け付けました！',
          requestedDesc: 'ご注文を確認してからご案内します。',
          successTitle: 'お支払いが完了しました！',
          successDesc: 'プリントしてお届けします。',
          pendingTitle: 'ご入金をお待ちしています',
          pendingDesc: 'ご入金を確認後、プリントを開始します。期限内にお支払いください。',
          cancelledTitle: 'お支払いが完了しませんでした',
          cancelledDesc: '選んだ写真はそのまま残っているので、もう一度お試しいただけます。',
          errorTitle: 'お支払い結果を確認できませんでした',
          errorDesc: '少し時間をおいて再度ご確認ください。問題が続く場合はお問い合わせください。',
          orderNum: 'ご注文番号',
          count: 'プリント枚数',
          countValue: '{{count}}枚',
          amount: 'お支払い金額',
          retry: 'もう一度試す',
          done: 'OK',
        },
      },
      announce: {
        title: '🍯 ねんね予報 ベータテストのご案内',
        badge: 'Beta Test',
        feature_title: '✨ ねんね予報とは？',
        feature_desc1: '赤ちゃんの睡眠記録を分析し、眠りにつくのに良い時間にお知らせします。',
        feature_desc2: '記録が增えるほど、お子様に合わせたパーソナライズ通知に進化します。',
        feature_desc3: '最初から同じ月齢の赤ちゃんのパターンを参考にお手伝いします。',
        record_title: '📝 睡眠記録をこまめにつけてください',
        record_desc: '睡眠記録をこまめに登録していただくと、より正確な通知を受け取れます。',
        notify_title: '🔔 ベータ通知について',
        notify_desc: '睡眠を記録すると、お申し込み不要で自動的にねんね予報通知の対象になります。',
        note_individual: 'ねんね予報は天気予報のように参考としてご利用ください。赤ちゃんの体調、成長ペース、環境によって実際の睡眠タイミングは異なることがあります。赤ちゃんの睡眠サインも合わせて確認してください。',
        note_under60: '生後60日未満の赤ちゃんは体内時計が形成される時期であり、睡眠パターンが不規則なため、予測精度が低くなる場合があります。',
        note_medical: '本サービスは一般的な睡眠情報を提供するものであり、医療的なアドバイスに代わるものではありません。赤ちゃんの睡眠について心配なことがあれば、小児科専門医にご相談ください。',
        note_guide: 'ねんね予報は保護者の皆様の判断をサポートする補助ツールです。',
        button_label: '🍯 ベータテストのご案内',
        close: '閉じる',
      },
    },
  },
} as const;

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'ko',
    supportedLngs: ['ko', 'en', 'ja'],
    detection: {
      order: ['termreportQuerystring', 'termreportCachedLanguage', 'navigator'],
      caches: ['termreportCachedLanguage'],
      convertDetectedLanguage: normalizeDetectedLanguage,
    },
    interpolation: { escapeValue: false },
  });

export default i18n;
